/**
 * Ticketing cluster view module — Phase 2.1 of the admin-frontend
 * decomposition. This file is the eventual home of every screen in the
 * "ticketing" sidebar group: the technician board, the customer-care
 * supervisor board, the ticket detail panel, comments, pending messages,
 * tech-closure review, etc.
 *
 * For 2.1.a we land just one self-contained sub-section — the "ticket
 * regions" admin (small list/CRUD + a `<select>` populator) — to validate
 * the new convention. Subsequent slices (2.1.b through 2.1.e) extend
 * `window.adminViews.tickets` with more sub-views.
 *
 * Boot contract: `admin.html` loads this script after the three Phase-1
 * modules (api-client, auth-session, notifications) and before `admin.js`.
 * The IIFE attaches `window.adminViews.tickets`. Sub-views expose a
 * minimum surface of `mount()` (idempotent first-render hookup), `refresh()`
 * (re-fetch and re-render), plus any helpers callers in `admin.js` need.
 *
 * Phase 3.2.c: introducing direct imports incrementally per sub-view.
 * Sub-views fully on imports: techReview, customerBoard.
 * Sub-views still on `configure({helpers})`: list, create, detail,
 * detailInteractions, detailComments, technicianBoard, pendingMessages.
 */

import { escapeHtml, escapeRegExp, formatRelativeTime, formatDateTimeShort, formatDateTimeFull, formatResolutionDayLabel, sanitizeHtml, stripHtml, looksLikeHtml, formatCurrency, formatNumber, toPlainText, cssEscapeIdent, renderHtmlOrText, renderEmailFrame, getFormattingConfig } from '../../src/lib/format.js';
import { queueTicketNotification } from '../../src/lib/ticket-notifications.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole, canAccessTicketing, canCreateTickets, canEditTickets, hasCustomerBoardAccess, getCurrentAdminUser } from '../../src/lib/roles.js';
import {
  formatTicketStatus, formatTicketComplaint, formatTicketPriority, formatTicketAssignee,
  formatLaneCollapsedLabel, describeDuration,
  extractRoleNames, userIsSupervisor, userEligibleForTicketAssignment,
  deriveSupervisorAdminRoles, describeSupervisorTargets,
  formatAdminUserLabel, formatAssignmentOptionLabel, adminUserSortKey, primaryAdminUserName,
  resolveTicketDisplay,
  TICKET_STATUS_LABELS, TICKET_PRIORITY_OPTIONS, TICKET_PRIORITY_LABELS, TICKET_COMPLAINT_OPTIONS,
  CONTACT_METHOD_OPTIONS, CONTACT_METHOD_LABELS, CHANNEL_PREFERRED_CONTACT,
  ENQUIRY_TYPE_OPTIONS,
  formatAiRecommendations,
  TICKET_CLOSE_TEMPLATES, TICKET_ASSIGNEE_LABELS,
  TICKET_VISIBILITY_LABELS,
  COMMENT_REVIEW_LABELS, COMMENT_EDITABLE_STATES, COMMENT_RETRY_STATES,
  COMMENT_OVERRIDE_STATES, COMMENT_SUPERVISOR_ROLES,
  ASSIGN_MODE_ASSIGN, ASSIGN_MODE_ESCALATE,
  FIELD_TECH_ROLE, FIELD_TECH_SUPERVISOR_ROLE,
  renderBoardQueuePanel,
} from '../../src/lib/ticket-utils.js';
import {
  ensureTechnicianUsersLoaded, ensureSupervisorUsersLoaded, ensureTechSupervisorUsersLoaded,
  getTechnicianAdminUsers, getSupervisorAdminUsers, getTechSupervisorAdminUsers,
} from '../../src/lib/admin-roster.js';
import { switchToView } from '../../src/lib/view-router.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

// ----- Cluster-shared state (was admin.js) ----------------------------
// `ticketCreatePrefill` bridges the ticket-list "create from row" flow to
// the ticket-create dialog. It used to live in admin.js but only the
// tickets cluster reads/writes it.
const ticketCreatePrefill = new Map();
const consumeCreatePrefill = (id) => {
  const fallback = ticketCreatePrefill.get(id);
  if (fallback) ticketCreatePrefill.delete(id);
  return fallback || null;
};

const getCurrentUserId = () => {
  const me = getCurrentAdminUser();
  return me && me.id != null ? String(me.id) : '';
};


  const views = (window.adminViews = window.adminViews || {});

  // ---- Ticket regions sub-view -----------------------------------------
  // Owns the list at `#ticketRegionList`, the create form at
  // `#ticketRegionForm`, and the dropdown at `#ticketCustomerEditRegion`
  // (used by the support-customer edit dialog in admin.js — exposed via
  // `refreshSelect()`).
  const regionState = { items: [], loading: false };
  let regionDom = null;
  let regionMounted = false;

  function resolveRegionDom() {
    if (regionDom) return regionDom;
    regionDom = {
      form: document.getElementById('ticketRegionForm'),
      list: document.getElementById('ticketRegionList'),
      input: document.getElementById('ticketRegionInput'),
      select: document.getElementById('ticketCustomerEditRegion'),
    };
    return regionDom;
  }

  function renderRegionSelect() {
    const dom = resolveRegionDom();
    const select = dom.select;
    if (!select) return;
    const current = select.value;
    select.innerHTML = '<option value="">Select region</option>';
    (regionState.items || []).forEach((item) => {
      const opt = document.createElement('option');
      opt.value = item.name;
      opt.textContent = item.name;
      select.appendChild(opt);
    });
    if (current && !select.value) {
      const opt = document.createElement('option');
      opt.value = current;
      opt.textContent = current;
      select.appendChild(opt);
    }
    select.value = current || '';
  }

  function renderRegionList() {
    const dom = resolveRegionDom();
    const list = dom.list;
    if (!list) return;
    list.innerHTML = '';
    if (regionState.loading) {
      list.innerHTML = '<div style="color:var(--sub); font-size:13px;">Loading regions…</div>';
      return;
    }
    const items = Array.isArray(regionState.items) ? regionState.items : [];
    if (!items.length) {
      list.innerHTML = '<div style="color:var(--sub); font-size:13px;">No regions added yet.</div>';
      return;
    }
    items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'config-tile';
      card.dataset.regionId = item.id;
      const label = document.createElement('label');
      label.style.display = 'flex';
      label.style.flexDirection = 'column';
      label.style.gap = '4px';
      label.innerHTML = '<span style="font-size:12px; color:var(--sub);">Region</span>';
      const input = document.createElement('input');
      input.type = 'text';
      input.dataset.regionInput = item.id;
      input.value = item.name || '';
      label.appendChild(input);
      card.appendChild(label);
      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.gap = '6px';
      actions.style.marginTop = '8px';
      const saveBtn = document.createElement('button');
      saveBtn.className = 'btn';
      saveBtn.type = 'button';
      saveBtn.dataset.regionSave = item.id;
      saveBtn.textContent = 'Save';
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'btn ghost danger';
      deleteBtn.type = 'button';
      deleteBtn.dataset.regionDelete = item.id;
      deleteBtn.textContent = 'Delete';
      actions.appendChild(saveBtn);
      actions.appendChild(deleteBtn);
      card.appendChild(actions);
      list.appendChild(card);
    });
  }

  async function loadRegions() {
    regionState.loading = true;
    renderRegionList();
    try {
      const items = await api.fetch(api.routes.adminRegions, { method: 'GET' });
      regionState.items = Array.isArray(items) ? items : [];
    } catch (err) {
      regionState.items = [];
      notify.showFlash(err.message || 'Failed to load regions', 'error');
    } finally {
      regionState.loading = false;
      renderRegionList();
      renderRegionSelect();
    }
  }

  async function ensureRegionsLoaded() {
    if (regionState.loading) return;
    if (regionState.items.length) return;
    await loadRegions();
  }

  async function submitNewRegion() {
    const dom = resolveRegionDom();
    const name = dom.input ? dom.input.value.trim() : '';
    if (!name) return;
    if (dom.form) dom.form.dataset.pending = '1';
    try {
      await api.fetch(api.routes.adminRegions, { method: 'POST', body: JSON.stringify({ name }) });
      if (dom.input) dom.input.value = '';
      await loadRegions();
    } catch (err) {
      notify.showFlash(err.message || 'Failed to add region', 'error');
    } finally {
      if (dom.form) dom.form.dataset.pending = '0';
    }
  }

  async function handleRegionListClick(event) {
    const saveBtn = event.target.closest('[data-region-save]');
    const deleteBtn = event.target.closest('[data-region-delete]');
    if (!saveBtn && !deleteBtn) return;
    const regionId = saveBtn ? saveBtn.dataset.regionSave : deleteBtn.dataset.regionDelete;
    if (!regionId) return;
    const card = event.target.closest('[data-region-id]') || event.target.closest('.config-tile');
    const input = card ? card.querySelector('[data-region-input]') : null;
    if (deleteBtn) {
      try {
        await api.fetch(api.routes.adminRegion(regionId), { method: 'DELETE' });
        await loadRegions();
      } catch (err) {
        notify.showFlash(err.message || 'Failed to delete region', 'error');
      }
      return;
    }
    if (saveBtn && input) {
      const newName = (input.value || '').trim();
      if (!newName) {
        notify.showFlash('Region name cannot be empty.', 'error');
        return;
      }
      try {
        await api.fetch(api.routes.adminRegion(regionId), {
          method: 'PATCH',
          body: JSON.stringify({ name: newName }),
        });
        await loadRegions();
      } catch (err) {
        notify.showFlash(err.message || 'Failed to update region', 'error');
      }
    }
  }

  function mountRegions() {
    if (regionMounted) {
      // Idempotent: if mount was already called, just refresh data.
      loadRegions().catch(() => {});
      return;
    }
    const dom = resolveRegionDom();
    if (dom.form) {
      dom.form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitNewRegion().catch(() => {});
      });
    }
    if (dom.list) {
      dom.list.addEventListener('click', (event) => {
        handleRegionListClick(event).catch(() => {});
      });
    }
    regionMounted = true;
    loadRegions().catch(() => {});
  }

  views.tickets = views.tickets || {};
  views.tickets.regions = {
    mount: mountRegions,
    refresh: () => loadRegions(),
    ensureLoaded: () => ensureRegionsLoaded(),
    refreshSelect: () => renderRegionSelect(),
    // Read-only access for callers that need the current list (e.g. the
    // support-customer edit dialog reading whether to fetch).
    getItems: () => regionState.items.slice(),
  };

  // ---- Pending messages cluster (Phase 2.1.b) --------------------------
  // The cluster lands in four reviewable cuts (2.1.b.1 → 2.1.b.4). This
  // file currently owns 2.1.b.1 (the dashboard tile); subsequent slices
  // grow `views.tickets.pendingMessages` further.
  //
  // Cross-cutting helpers admin.js owns are injected via `configure()` so
  // the module stays free of admin-SPA assumptions. Defaults are safe
  // no-ops / passthroughs so unit-loading the file in isolation doesn't
  // throw.
  // Phase 3.2.c: pmHelpers populated from imports + sibling lookups.
  // (No longer reassignable — was `let` with configure-injected
  // overrides; now `const` with stable bindings.)
  const pmHelpers = {
    formatRelativeTime,
    escapeHtml,
    formatDateTimeFull,
    describeDuration,
    looksLikeHtml,
    renderHtmlOrText: (target, value, opts) => renderHtmlOrText(target, value, opts),
    renderEmailFrame: (target, value, opts) => renderEmailFrame(target, value, opts),
    stripHtml,
    formatAttachmentSize: (bytes) => formatAttachmentSize(bytes),
    formatAiRecommendations,
    getTicketStatusLabels: () => TICKET_STATUS_LABELS,
    getTicketPriorityLabels: () => TICKET_PRIORITY_LABELS,
    getTicketComplaintOptions: () => TICKET_COMPLAINT_OPTIONS,
    getEnquiryTypeOptions: () => ENQUIRY_TYPE_OPTIONS,
    getContactMethodOptions: () => CONTACT_METHOD_OPTIONS,
    getChannelPreferredContact: () => CHANNEL_PREFERRED_CONTACT,
    openDialog,
    openTicketDetailAfterCreation: (id, ticket) => openTicketDetailAfterCreation(id, ticket),
    openTicketConflictDialog: (opts) => openTicketConflictDialog(opts),
    closeTicketConflictDialog: () => closeTicketConflictDialog(),
    getTicketConflictSupportCustomerId: () => {
      const i = views.tickets && views.tickets.detailInteractions;
      const state = i && typeof i.getConflictState === 'function' ? i.getConflictState() : null;
      return state ? state.supportCustomerId : null;
    },
    reloadTickets: (opts) => reloadTickets(opts),
    populateAssignmentOptions: (opts) => populateAssignmentOptions(opts),
    ensureTechnicianUsersLoaded,
    ensureSupervisorUsersLoaded,
    getAssignHelpDefault: () => {
      const el = document.getElementById('pendingMessageAssignHelp');
      return el ? el.textContent : '';
    },
    switchView: (name) => switchToView(name),
    canAccessTicketing,
    // Dialog openers + recording fetcher: each is invoked by the
    // pending-messages list-row click handler. Function declarations
    // are hoisted, so referencing them here (defined later in the
    // file) resolves at call time.
    openDetailDialog: (id, options) => openPendingMessageDialog(id, options),
    openForwardDialog: (record) => openPmForwardDialog(record),
    openFollowupDialog: (record, options) => openPmFollowupDialog(record, options),
    fetchRecording: (id, btn) => fetchPendingMessageRecording(id, btn),
    // Currently-selected pending-message accessor + mutator. The state
    // (`pmCurrent`) lives further down in the module; arrow functions
    // close over the binding so module-load order is fine — calls only
    // happen at click/event time, by which point `pmCurrent` is
    // initialised. Six callsites (see `callHelper('setCurrent', ...)`
    // and `callHelper('getCurrent')`) previously failed silently
    // because these weren't registered, which is why the "Mark as
    // follow-up" button inside the review dialog showed "Select a
    // pending message first" — `getCurrent` returned undefined.
    getCurrent: () => pmCurrent,
    setCurrent: (record) => { pmCurrent = record; },
    // Detail-pane re-render + Convert-button enable/disable when a record
    // changes underneath the open dialog. Two more silent-no-op victims
    // surfaced by the warning patch — without these, the live-update
    // path in `upsertPendingMessageRecord` (called by the WS handler
    // and the optimistic-update flow) would leave the dialog stale.
    populateDetail: (record) => populatePendingMessageDetail(record),
    updateConvertState: (record) => updatePendingMessageConvertState(record),
  };

  // ---- Dashboard sub-view (2.1.b.1) ------------------------------------
  // Owns the analytics tile at the top of the Pending Messages screen:
  // window selector (last N days), refresh button, summary cards, and
  // 9 breakdown tables (channel × day, conversion stats, daily totals,
  // discard reasons, discard-rate-by-channel, discard-rate-by-reason,
  // agent breakdown, follow-up outcomes, backlog age buckets).
  //
  // Self-contained: no calls into the rest of the pending-messages cluster.
  // Reads two admin.js helpers via the injected `pmHelpers`; everything
  // else lives here.
  const pmDashboardChannelLabels = {
    email: 'Email',
    whatsapp: 'WhatsApp',
    pbx: 'PBX',
    app: 'INET-Go',
    unknown: 'Unknown',
  };
  const pmDashboardReasonLabels = {
    duplicate: 'Duplicate message',
    spam: 'Spam / irrelevant',
    missing_info: 'Missing critical info',
    wrong_channel: 'Wrong channel',
    test_message: 'Test message',
    customer_unreachable: 'Customer unreachable',
    other: 'Other',
    unspecified: 'Not captured',
  };
  const pmDashboardFollowupLabels = {
    appended: 'Appended to existing ticket',
    new_ticket: 'New ticket created',
    unknown: 'Unknown outcome',
  };
  const pmDashboardBacklogLabels = {
    under_2h: 'Under 2 hours',
    over_2h: 'Over 2 hours',
    over_6h: 'Over 6 hours',
    over_24h: 'Over 24 hours',
    total: 'Total pending',
  };

  const pmDashboardState = {
    days: 7,
    loading: false,
    loaded: false,
    error: null,
    data: null,
    lastFetchedAt: 0,
    loadingPromise: null,
  };

  let pmDashboardDom = null;
  let pmDashboardMounted = false;

  function resolvePmDashboardDom() {
    if (pmDashboardDom) return pmDashboardDom;
    pmDashboardDom = {
      root: document.getElementById('pendingDashboard'),
      status: document.getElementById('pendingDashboardStatus'),
      windowLabel: document.getElementById('pendingDashboardWindow'),
      windowSelect: document.getElementById('pendingDashboardWindowSelect'),
      refreshBtn: document.getElementById('pendingDashboardRefreshBtn'),
      updatedAt: document.getElementById('pendingDashboardUpdatedAt'),
      metrics: document.getElementById('pendingDashboardMetrics'),
      channelHead: document.getElementById('pendingDashboardChannelHead'),
      channelBody: document.getElementById('pendingDashboardChannelBody'),
      conversionBody: document.getElementById('pendingDashboardConversionBody'),
      dailyBody: document.getElementById('pendingDashboardDailyBody'),
      discardBody: document.getElementById('pendingDashboardDiscardBody'),
      discardNote: document.getElementById('pendingDashboardDiscardNote'),
      discardRateChannelBody: document.getElementById('pendingDashboardDiscardRateChannelBody'),
      discardRateReasonBody: document.getElementById('pendingDashboardDiscardRateReasonBody'),
      agentBody: document.getElementById('pendingDashboardAgentBody'),
      followupBody: document.getElementById('pendingDashboardFollowupBody'),
      backlogBody: document.getElementById('pendingDashboardBacklogBody'),
    };
    return pmDashboardDom;
  }

  function formatPmDashboardCount(value) {
    const numeric = Number(value || 0);
    try {
      return formatNumber(numeric);
    } catch {
      return String(numeric);
    }
  }

  function formatPmDashboardPercent(value) {
    const numeric = Number(value || 0);
    return `${Math.round(numeric * 100)}%`;
  }

  function formatPmDashboardDayLabel(value) {
    if (!value) return '—';
    const raw = String(value);
    const iso = raw.length === 10 ? `${raw}T00:00:00Z` : raw;
    try {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return raw;
      return date.toLocaleDateString(getFormattingConfig().locale, { weekday: 'short', day: '2-digit', month: 'short' });
    } catch {
      return raw;
    }
  }

  function formatPmDashboardWindow(days) {
    const value = Number(days || 7);
    return `Last ${value} day${value === 1 ? '' : 's'}`;
  }

  async function loadPmDashboard({ force = false, skipIfFresh = false } = {}) {
    const dom = resolvePmDashboardDom();
    if (!dom.root) {
      // eslint-disable-next-line no-console
      console.warn('loadPmDashboard: pending-messages dashboard root missing — feature will not load.');
      return;
    }
    if (pmDashboardState.loading) return pmDashboardState.loadingPromise;
    if (skipIfFresh && pmDashboardState.loaded && !pmDashboardState.error) {
      return;
    }
    if (!force && pmDashboardState.loaded && (Date.now() - pmDashboardState.lastFetchedAt < 60000)) {
      return;
    }
    pmDashboardState.loading = true;
    pmDashboardState.error = null;
    renderPmDashboard();
    const params = new URLSearchParams();
    params.set('lookback_days', String(pmDashboardState.days || 7));
    const request = (async () => {
      try {
        const payload = await api.fetch(api.routes.adminPendingMessagesDashboard(params.toString()), { method: 'GET' });
        pmDashboardState.data = payload || null;
        pmDashboardState.loaded = true;
        pmDashboardState.lastFetchedAt = Date.now();
      } catch (err) {
        pmDashboardState.error = err && err.message ? err.message : 'Failed to load dashboard';
        pmDashboardState.data = null;
        pmDashboardState.loaded = false;
      } finally {
        pmDashboardState.loading = false;
        pmDashboardState.loadingPromise = null;
        renderPmDashboard();
      }
    })();
    pmDashboardState.loadingPromise = request;
    return request;
  }

  function renderPmDashboard() {
    const dom = resolvePmDashboardDom();
    if (!dom.root) return;
    const { escapeHtml, formatRelativeTime } = pmHelpers;
    const loading = pmDashboardState.loading;
    const data = pmDashboardState.data;
    const error = pmDashboardState.error;

    if (dom.status) {
      if (loading) {
        dom.status.textContent = 'Loading…';
      } else if (error) {
        dom.status.textContent = error;
      } else {
        dom.status.textContent = '';
      }
    }
    if (dom.windowLabel) {
      dom.windowLabel.textContent = formatPmDashboardWindow(pmDashboardState.days);
    }
    if (dom.updatedAt) {
      dom.updatedAt.textContent = pmDashboardState.lastFetchedAt
        ? `Updated ${formatRelativeTime(new Date(pmDashboardState.lastFetchedAt).toISOString())}`
        : '';
    }

    if (!data) {
      if (dom.metrics) dom.metrics.innerHTML = '';
      const emptyRow = (cols) => `<tr><td colspan="${cols}" style="color:var(--sub);">${loading ? 'Loading…' : 'No data yet.'}</td></tr>`;
      if (dom.channelBody) dom.channelBody.innerHTML = emptyRow(8);
      if (dom.conversionBody) dom.conversionBody.innerHTML = emptyRow(5);
      if (dom.dailyBody) dom.dailyBody.innerHTML = emptyRow(6);
      if (dom.discardBody) dom.discardBody.innerHTML = emptyRow(3);
      if (dom.discardRateChannelBody) dom.discardRateChannelBody.innerHTML = emptyRow(3);
      if (dom.discardRateReasonBody) dom.discardRateReasonBody.innerHTML = emptyRow(3);
      if (dom.agentBody) dom.agentBody.innerHTML = emptyRow(5);
      if (dom.followupBody) dom.followupBody.innerHTML = emptyRow(2);
      if (dom.backlogBody) dom.backlogBody.innerHTML = emptyRow(2);
      if (dom.discardNote) dom.discardNote.textContent = '';
      return;
    }

    const summary = data.summary || {};
    if (dom.metrics) {
      const cards = [
        { label: 'Incoming messages', value: formatPmDashboardCount(summary.incoming || 0) },
        { label: 'Converted to tickets', value: formatPmDashboardCount(summary.converted || 0) },
        { label: 'Discarded', value: formatPmDashboardCount(summary.discarded || 0) },
        { label: 'Follow-ups linked', value: formatPmDashboardCount(summary.followups || 0) },
        { label: 'Forwarded', value: formatPmDashboardCount(summary.forwarded || 0) },
        { label: 'Pending backlog', value: formatPmDashboardCount(summary.pending || 0) },
        {
          label: 'Conversion rate',
          value: `${Math.round((summary.conversion_rate || 0) * 100)}%`,
          sub: 'Converted / processed',
        },
      ];
      dom.metrics.innerHTML = cards
        .map((card) => `
          <div class="pending-dashboard-card">
            <div class="label">${escapeHtml(card.label)}</div>
            <div class="value">${escapeHtml(card.value)}</div>
            ${card.sub ? `<div class="sub">${escapeHtml(card.sub)}</div>` : ''}
          </div>
        `)
        .join('');
    }

    const daily = Array.isArray(data.daily) ? data.daily : [];
    const days = daily.map((item) => item.day).filter(Boolean);
    if (dom.channelHead) {
      const headCells = ['<th>Channel</th>']
        .concat(days.map((day) => `<th>${escapeHtml(formatPmDashboardDayLabel(day))}</th>`))
        .join('');
      dom.channelHead.innerHTML = headCells;
    }
    if (dom.channelBody) {
      const channelRows = [];
      const channelDaily = Array.isArray(data.channel_daily) ? data.channel_daily : [];
      const channelSet = new Set(channelDaily.map((row) => row.channel));
      const channelOrder = ['email', 'whatsapp', 'pbx', 'app', 'unknown'];
      const extraChannels = Array.from(channelSet).filter((channel) => !channelOrder.includes(channel));
      const channels = channelOrder.concat(extraChannels);
      const channelMap = new Map();
      channelDaily.forEach((row) => {
        if (!row || !row.channel || !row.day) return;
        channelMap.set(`${row.channel}::${row.day}`, Number(row.messages || 0));
      });
      channels.forEach((channel) => {
        if (!channelSet.has(channel)) return;
        const cells = days.map((day) => {
          const count = channelMap.get(`${channel}::${day}`) || 0;
          return `<td>${escapeHtml(formatPmDashboardCount(count))}</td>`;
        });
        channelRows.push(
          `<tr><td>${escapeHtml(pmDashboardChannelLabels[channel] || channel)}</td>${cells.join('')}</tr>`
        );
      });
      dom.channelBody.innerHTML = channelRows.length
        ? channelRows.join('')
        : '<tr><td colspan="8" style="color:var(--sub);">No channel activity recorded.</td></tr>';
    }

    if (dom.conversionBody) {
      const conversions = Array.isArray(data.conversion_stats) ? data.conversion_stats : [];
      if (!conversions.length) {
        dom.conversionBody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">No conversions recorded.</td></tr>';
      } else {
        dom.conversionBody.innerHTML = conversions
          .map((entry) => `
            <tr>
              <td>${escapeHtml(pmDashboardChannelLabels[entry.channel] || entry.channel)}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.conversions || 0))}</td>
              <td>${escapeHtml(entry.min_minutes != null ? Number(entry.min_minutes).toFixed(1) : '—')}</td>
              <td>${escapeHtml(entry.avg_minutes != null ? Number(entry.avg_minutes).toFixed(1) : '—')}</td>
              <td>${escapeHtml(entry.median_minutes != null ? Number(entry.median_minutes).toFixed(1) : '—')}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.dailyBody) {
      if (!daily.length) {
        dom.dailyBody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">No daily metrics recorded.</td></tr>';
      } else {
        dom.dailyBody.innerHTML = daily
          .map((entry) => `
            <tr>
              <td>${escapeHtml(formatPmDashboardDayLabel(entry.day))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.incoming || 0))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.converted || 0))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.discarded || 0))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.followups || 0))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.forwarded || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.discardBody) {
      const reasons = Array.isArray(data.discard_reasons) ? data.discard_reasons : [];
      if (!reasons.length) {
        dom.discardBody.innerHTML = '<tr><td colspan="3" style="color:var(--sub);">No discard reasons captured yet.</td></tr>';
        if (dom.discardNote) dom.discardNote.textContent = '';
      } else {
        dom.discardBody.innerHTML = reasons
          .map((entry) => {
            const reason = entry.reason || 'unspecified';
            const note = Array.isArray(entry.notes) && entry.notes.length ? entry.notes[0] : '—';
            return `
              <tr>
                <td>${escapeHtml(pmDashboardReasonLabels[reason] || reason)}</td>
                <td>${escapeHtml(formatPmDashboardCount(entry.count || 0))}</td>
                <td>${escapeHtml(note)}</td>
              </tr>
            `;
          })
          .join('');
        if (dom.discardNote) {
          dom.discardNote.textContent = 'Add a short note when discarding to improve reason insights.';
        }
      }
    }

    if (dom.discardRateChannelBody) {
      const rates = Array.isArray(data.discard_rates) ? data.discard_rates : [];
      if (!rates.length) {
        dom.discardRateChannelBody.innerHTML = '<tr><td colspan="3" style="color:var(--sub);">No discard rates recorded.</td></tr>';
      } else {
        dom.discardRateChannelBody.innerHTML = rates
          .map((entry) => `
            <tr>
              <td>${escapeHtml(pmDashboardChannelLabels[entry.key] || entry.key)}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.count || 0))}</td>
              <td>${escapeHtml(formatPmDashboardPercent(entry.rate || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.discardRateReasonBody) {
      const rates = Array.isArray(data.discard_reason_rates) ? data.discard_reason_rates : [];
      if (!rates.length) {
        dom.discardRateReasonBody.innerHTML = '<tr><td colspan="3" style="color:var(--sub);">No discard reason rates recorded.</td></tr>';
      } else {
        dom.discardRateReasonBody.innerHTML = rates
          .map((entry) => `
            <tr>
              <td>${escapeHtml(pmDashboardReasonLabels[entry.key] || entry.key)}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.count || 0))}</td>
              <td>${escapeHtml(formatPmDashboardPercent(entry.rate || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.agentBody) {
      const agents = Array.isArray(data.agent_breakdown) ? data.agent_breakdown : [];
      if (!agents.length) {
        dom.agentBody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">No agent activity recorded.</td></tr>';
      } else {
        dom.agentBody.innerHTML = agents
          .map((entry) => `
            <tr>
              <td>${escapeHtml(entry.agent || 'Unassigned')}</td>
              <td>${escapeHtml(formatPmDashboardCount((entry.tickets_opened || 0) + (entry.auto_converted_messages || 0)))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.discarded_messages || 0))}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.forwarded_messages || 0))}</td>
              <td>${escapeHtml(formatPmDashboardPercent(entry.conversion_rate || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.followupBody) {
      const outcomes = Array.isArray(data.followup_outcomes) ? data.followup_outcomes : [];
      if (!outcomes.length) {
        dom.followupBody.innerHTML = '<tr><td colspan="2" style="color:var(--sub);">No follow-ups recorded.</td></tr>';
      } else {
        dom.followupBody.innerHTML = outcomes
          .map((entry) => `
            <tr>
              <td>${escapeHtml(pmDashboardFollowupLabels[entry.outcome] || entry.outcome)}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.count || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }

    if (dom.backlogBody) {
      const backlog = Array.isArray(data.backlog_age) ? data.backlog_age : [];
      if (!backlog.length) {
        dom.backlogBody.innerHTML = '<tr><td colspan="2" style="color:var(--sub);">No backlog metrics recorded.</td></tr>';
      } else {
        dom.backlogBody.innerHTML = backlog
          .map((entry) => `
            <tr>
              <td>${escapeHtml(pmDashboardBacklogLabels[entry.bucket] || entry.bucket)}</td>
              <td>${escapeHtml(formatPmDashboardCount(entry.count || 0))}</td>
            </tr>
          `)
          .join('');
      }
    }
  }

  function mountPmDashboard() {
    if (pmDashboardMounted) {
      // Idempotent: just refresh data on subsequent mounts.
      loadPmDashboard({ skipIfFresh: true }).catch(() => {});
      return;
    }
    const dom = resolvePmDashboardDom();
    if (dom.windowSelect) {
      dom.windowSelect.value = String(pmDashboardState.days || 7);
      dom.windowSelect.addEventListener('change', () => {
        const value = Number.parseInt(dom.windowSelect.value, 10);
        pmDashboardState.days = Number.isFinite(value) && value > 0 ? value : 7;
        loadPmDashboard({ force: true }).catch(() => {});
      });
    }
    if (dom.refreshBtn) {
      dom.refreshBtn.addEventListener('click', () => {
        loadPmDashboard({ force: true }).catch(() => {});
      });
    }
    pmDashboardMounted = true;
    loadPmDashboard({ skipIfFresh: true }).catch(() => {});
  }

  // ---- List sub-view (2.1.b.2) -----------------------------------------
  // Owns the pending-messages table at `#pendingMessagesView`: filters
  // (status / channel / search), pagination, row rendering, age-bucket
  // indicators, and the in-memory record cache.
  //
  // The table click handler dispatches to the four dialog/recording
  // openers via `pmHelpers.{openDetailDialog, openForwardDialog,
  // openFollowupDialog, fetchRecording}`. All four are now local
  // functions in this file (post 2.1.b.3 landing) — they're surfaced on
  // `pmHelpers` as the click handler's contract still expects, but the
  // `pmHelpers` indirection is cosmetic at this point and could be
  // removed by calling the local functions directly.
  const PM_FETCH_LIMIT = 25;
  const PM_SLA_TARGET_MS = 5 * 60 * 1000;
  const PM_AGE_CRITICAL_MS = 15 * 60 * 1000;
  const PM_AGE_ESCALATED_MS = 30 * 60 * 1000;

  const pmListState = {
    status: 'pending',
    channel: 'all',
    search: '',
    items: [],
    loading: false,
    selectedId: null,
    loaded: false,
    stale: false,
    lastFetchedAt: 0,
    limit: PM_FETCH_LIMIT,
    offset: 0,
    total: 0,
    loadingPromise: null,
  };

  const pmDetailLoads = new Map();
  let pmListDom = null;
  let pmListMounted = false;
  let pmListSearchTimer = null;
  let pmListAgeTimer = null;

  function resolvePmListDom() {
    if (pmListDom) return pmListDom;
    pmListDom = {
      view: document.getElementById('pendingMessagesView'),
      body: document.getElementById('pendingMessagesBody'),
      statusFilter: document.getElementById('pendingMessagesStatusFilter'),
      channelFilter: document.getElementById('pendingMessagesChannelFilter'),
      searchInput: document.getElementById('pendingMessagesSearch'),
      refresh: document.getElementById('pendingMessagesRefresh'),
      footer: document.getElementById('pendingMessagesFooter'),
      pageInfo: document.getElementById('pendingMessagesPageInfo'),
      prev: document.getElementById('pendingMessagesPrev'),
      next: document.getElementById('pendingMessagesNext'),
    };
    return pmListDom;
  }

  function normalizePendingStatus(value) {
    if (value == null) return '';
    return String(value).trim().toLowerCase();
  }

  function normalizePendingChannel(value) {
    if (value == null) return '';
    return String(value).trim().toLowerCase();
  }

  function normalizePendingSearch(value) {
    if (value == null) return '';
    return String(value).trim().toLowerCase();
  }

  function pendingSearchDigits(value) {
    return String(value || '').replace(/\D+/g, '');
  }

  function pendingMessageCreatedAt(record) {
    if (!record) return 0;
    const raw = record.created_at || record.createdAt || null;
    const parsed = raw ? Date.parse(raw) : Number.NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function pendingMessageMatchesFilter(record) {
    if (!record) return false;
    const filterValue = normalizePendingStatus(pmListState.status);
    let statusMatch = true;
    if (filterValue && filterValue !== 'all') {
      const filters = filterValue.split(',').map((item) => item.trim()).filter(Boolean);
      if (filters.length) {
        const recordStatus = normalizePendingStatus(record.status);
        statusMatch = filters.includes(recordStatus);
      }
    }
    if (!statusMatch) return false;
    const channelValue = normalizePendingChannel(pmListState.channel);
    const recordChannel = normalizePendingChannel(record.channel);
    if (channelValue && channelValue !== 'all' && channelValue !== recordChannel) {
      return false;
    }
    const searchValue = normalizePendingSearch(pmListState.search);
    if (!searchValue) return true;
    const contactValue = normalizePendingSearch(record.customer_contact || '');
    if (searchValue.includes('@')) {
      return recordChannel === 'email' && contactValue.includes(searchValue);
    }
    const searchDigits = pendingSearchDigits(searchValue);
    if (searchDigits) {
      const contactDigits = pendingSearchDigits(contactValue);
      return (recordChannel === 'pbx' || recordChannel === 'whatsapp') && contactDigits.includes(searchDigits);
    }
    return contactValue.includes(searchValue);
  }

  function isPendingMessagesViewActive() {
    return !!document.querySelector('.nav .nav-view[data-view="pending_messages"].active');
  }

  function maybeCloseDetailDialog() {
    const dlg = document.getElementById('pendingMessageDlg');
    if (dlg && dlg.open) {
      try { dlg.close(); } catch { dlg.open = false; }
    }
  }

  // Helper-missing warnings are deduplicated by name so a hot-path call
  // site doesn't spam the console — first occurrence is enough to surface
  // the contract gap.
  const _missingHelperWarned = new Set();
  function warnMissingHelper(name) {
    if (_missingHelperWarned.has(name)) return;
    _missingHelperWarned.add(name);
    const available = pmHelpers ? Object.keys(pmHelpers).sort().join(', ') : '(pmHelpers missing)';
    // eslint-disable-next-line no-console
    console.warn(`[tickets] pmHelpers.${name} is not a function — feature will be a no-op. Available: ${available}`);
  }

  function callHelper(name, ...args) {
    const fn = pmHelpers && pmHelpers[name];
    if (typeof fn !== 'function') {
      warnMissingHelper(name);
      return undefined;
    }
    try {
      return fn(...args);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[tickets] pmHelpers.${name} threw`, err);
      return undefined;
    }
  }

  function upsertPendingMessageRecord(record) {
    if (!record) return false;
    const id = record.id != null ? String(record.id) : '';
    if (!id) return false;
    const matchesFilter = pendingMessageMatchesFilter(record);
    const idx = pmListState.items.findIndex((item) => String(item.id) === id);
    let changed = false;
    if (!matchesFilter) {
      if (idx !== -1) {
        pmListState.items.splice(idx, 1);
        changed = true;
        if (pmListState.selectedId === id) {
          pmListState.selectedId = null;
          callHelper('setCurrent', null);
          maybeCloseDetailDialog();
          callHelper('updateConvertState', null);
        }
      }
      return changed;
    }
    if (idx !== -1) {
      pmListState.items.splice(idx, 1);
      changed = true;
    }
    const recordTimestamp = pendingMessageCreatedAt(record);
    const insertIdx = pmListState.items.findIndex((item) => pendingMessageCreatedAt(item) > recordTimestamp);
    if (insertIdx === -1) {
      pmListState.items.push(record);
    } else {
      pmListState.items.splice(insertIdx, 0, record);
    }
    const limit = pmListState.limit || PM_FETCH_LIMIT;
    if (pmListState.items.length > limit) {
      pmListState.items.length = limit;
    }
    if (pmListState.selectedId === id) {
      callHelper('setCurrent', record);
      callHelper('populateDetail', record);
      callHelper('populateAssignmentOptions');
      callHelper('updateConvertState', record);
    }
    return true;
  }

  function removePendingMessageById(messageId) {
    const id = messageId != null ? String(messageId) : '';
    if (!id) return false;
    const idx = pmListState.items.findIndex((item) => String(item.id) === id);
    if (idx === -1) return false;
    pmListState.items.splice(idx, 1);
    if (pmListState.selectedId === id) {
      pmListState.selectedId = null;
      callHelper('setCurrent', null);
      maybeCloseDetailDialog();
      callHelper('updateConvertState', null);
    }
    return true;
  }

  async function fetchPendingMessageDetail(messageId) {
    const id = messageId != null ? String(messageId) : '';
    if (!id) return null;
    if (pmDetailLoads.has(id)) {
      return pmDetailLoads.get(id);
    }
    const request = (async () => {
      try {
        const record = await api.fetch(api.routes.adminPendingMessage(id), { method: 'GET' });
        const changed = upsertPendingMessageRecord(record);
        pmListState.loaded = true;
        pmListState.stale = false;
        if (changed) {
          renderPendingMessages();
        }
        return record;
      } catch (err) {
        if (err && err.status === 404) {
          const removed = removePendingMessageById(id);
          if (removed) {
            renderPendingMessages();
          }
          return null;
        }
        throw err;
      } finally {
        pmDetailLoads.delete(id);
      }
    })();
    pmDetailLoads.set(id, request);
    return request;
  }

  async function loadPendingMessages(options = {}) {
    const dom = resolvePmListDom();
    if (!dom.view) {
      // eslint-disable-next-line no-console
      console.warn('loadPendingMessages: #pendingMessagesView missing — list will not load.');
      return;
    }
    const { keepSelection = false, skipIfFresh = false } = options;
    if (skipIfFresh && pmListState.loaded && !pmListState.stale) {
      return;
    }
    if (pmListState.loadingPromise) {
      return pmListState.loadingPromise;
    }
    pmListState.loading = true;
    renderPendingMessages();
    const params = new URLSearchParams();
    const limit = pmListState.limit || PM_FETCH_LIMIT;
    params.set('limit', String(limit));
    const offset = pmListState.offset || 0;
    params.set('offset', String(offset));
    if (pmListState.status && pmListState.status !== 'all') {
      params.append('status', pmListState.status);
    }
    if (pmListState.channel && pmListState.channel !== 'all') {
      params.append('channel', pmListState.channel);
    }
    const searchValue = pmListState.search ? pmListState.search.trim() : '';
    if (searchValue) {
      params.set('search', searchValue);
    }
    const request = (async () => {
      let refetchWithAdjustedOffset = false;
      try {
        const data = await api.fetch(api.routes.adminPendingMessages(params.toString()), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : [];
        const payloadLimit = typeof data?.limit === 'number' ? data.limit : limit;
        const payloadOffset = typeof data?.offset === 'number' ? data.offset : offset;
        const payloadTotal = typeof data?.total === 'number' ? data.total : (payloadOffset + items.length);
        pmListState.limit = payloadLimit || limit;
        pmListState.offset = payloadOffset || 0;
        pmListState.total = Math.max(0, payloadTotal || 0);
        pmListState.items = items;
        pmListState.loaded = true;
        pmListState.stale = false;
        pmListState.lastFetchedAt = Date.now();
        if (
          items.length === 0
          && pmListState.total > 0
          && pmListState.offset >= pmListState.total
          && pmListState.offset > 0
        ) {
          const adjustedOffset = Math.max(0, pmListState.total - pmListState.limit);
          if (adjustedOffset !== pmListState.offset) {
            pmListState.offset = adjustedOffset;
            refetchWithAdjustedOffset = true;
          }
        }
        if (!keepSelection) {
          pmListState.selectedId = null;
          callHelper('setCurrent', null);
          callHelper('updateConvertState', null);
        } else if (pmListState.selectedId) {
          const current = pmListState.items.find((item) => String(item.id) === String(pmListState.selectedId));
          if (current) {
            callHelper('setCurrent', current);
            callHelper('populateDetail', current);
            callHelper('populateAssignmentOptions');
            callHelper('updateConvertState', current);
          }
        }
        const navActive = isPendingMessagesViewActive();
        if (navActive) {
          setPmIndicator(false);
        } else {
          const hasPending = items.some((item) => normalizePendingStatus(item.status) === 'pending');
          setPmIndicator(hasPending);
        }
      } catch (err) {
        notify.showFlash(err.message || 'Failed to load messages', 'error');
        pmListState.items = [];
        pmListState.loaded = false;
        pmListState.stale = true;
        pmListState.total = 0;
        pmListState.offset = 0;
        if (!isPendingMessagesViewActive()) {
          setPmIndicator(false);
        }
      } finally {
        pmListState.loading = false;
        pmListState.loadingPromise = null;
        renderPendingMessages();
        if (refetchWithAdjustedOffset) {
          loadPendingMessages({ keepSelection: false });
        }
      }
    })();
    pmListState.loadingPromise = request;
    return request;
  }

  function changePendingMessagesPage(direction) {
    const limit = pmListState.limit || PM_FETCH_LIMIT;
    const total = pmListState.total || 0;
    const offset = pmListState.offset || 0;
    let nextOffset = offset;
    if (direction === 'next') {
      const candidate = offset + limit;
      if (total && candidate >= total) {
        const adjusted = total > limit ? Math.max(0, total - limit) : 0;
        nextOffset = adjusted;
      } else {
        nextOffset = candidate;
      }
    } else if (direction === 'prev') {
      nextOffset = Math.max(0, offset - limit);
    }
    if (nextOffset === offset) return;
    pmListState.offset = nextOffset;
    pmListState.stale = true;
    loadPendingMessages({ keepSelection: false });
  }

  function formatPendingSlaCountdown(ageMs = 0) {
    const remaining = PM_SLA_TARGET_MS - ageMs;
    const describe = pmHelpers.describeDuration || ((ms) => `${Math.round(Math.abs(ms) / 1000)}s`);
    if (remaining <= 0) {
      return `Overdue ${describe(-remaining)}`;
    }
    return `${describe(remaining)} left`;
  }

  function updatePendingMessageAgeIndicators() {
    const dom = resolvePmListDom();
    if (!dom.body) return;
    const now = Date.now();
    const rows = dom.body.querySelectorAll('tr[data-received-at]');
    rows.forEach((row) => {
      const receivedAt = row.dataset.receivedAt;
      if (!receivedAt) return;
      const created = new Date(receivedAt);
      if (Number.isNaN(created.getTime())) return;
      const ageMs = now - created.getTime();
      const slaCell = row.querySelector('.pending-sla-cell');
      if (slaCell) {
        slaCell.textContent = formatPendingSlaCountdown(ageMs);
      }
      row.classList.remove('pending-age-warning', 'pending-age-critical', 'pending-age-escalated');
      if (ageMs >= PM_AGE_ESCALATED_MS) {
        row.classList.add('pending-age-critical', 'pending-age-escalated');
      } else if (ageMs >= PM_AGE_CRITICAL_MS) {
        row.classList.add('pending-age-critical');
      } else if (ageMs >= PM_SLA_TARGET_MS) {
        row.classList.add('pending-age-warning');
      }
    });
  }

  function schedulePendingAgeRefresh() {
    if (pmListAgeTimer) return;
    pmListAgeTimer = setInterval(updatePendingMessageAgeIndicators, 15000);
  }

  function renderPendingMessages() {
    const dom = resolvePmListDom();
    if (!dom.body) return;
    const limit = pmListState.limit || PM_FETCH_LIMIT;
    const offset = pmListState.offset || 0;
    const totalKnown = pmListState.total || 0;
    const pageCount = pmListState.items.length;
    const hasPrev = offset > 0;
    const hasNext = totalKnown
      ? (offset + pageCount) < totalKnown
      : pageCount === limit;
    if (dom.footer) {
      const hideFooter = !hasPrev && !hasNext && offset === 0 && (totalKnown === 0 || totalKnown <= limit);
      dom.footer.hidden = hideFooter;
    }
    if (dom.pageInfo) {
      if (pmListState.loading) {
        dom.pageInfo.textContent = 'Loading…';
      } else if (!pageCount) {
        const totalDisplay = totalKnown || 0;
        dom.pageInfo.textContent = totalDisplay ? `Showing 0 of ${totalDisplay}` : 'No messages';
      } else {
        const start = offset + 1;
        const end = offset + pageCount;
        const totalDisplay = totalKnown || end;
        dom.pageInfo.textContent = `Showing ${start}–${end} of ${totalDisplay}`;
      }
    }
    if (dom.prev) dom.prev.disabled = pmListState.loading || !hasPrev;
    if (dom.next) dom.next.disabled = pmListState.loading || !hasNext;
    dom.body.innerHTML = '';
    if (pmListState.loading) {
      dom.body.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>';
      return;
    }
    if (!pmListState.items.length) {
      dom.body.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No messages in this view.</td></tr>';
      return;
    }

    const looksLikeHtml = pmHelpers.looksLikeHtml || (() => false);
    const renderHtmlOrText = pmHelpers.renderHtmlOrText || ((target, value) => { if (target) target.textContent = String(value || ''); });
    const formatDateTimeFull = pmHelpers.formatDateTimeFull || ((value) => String(value || ''));

    pmListState.items.forEach((item, idx) => {
      const tr = document.createElement('tr');
      tr.dataset.id = String(item.id);
      tr.dataset.receivedAt = item.created_at || '';
      tr.dataset.requiresCallback = item.metadata && item.metadata.requires_callback ? '1' : '0';

      const channelKey = String(item.channel || '').toLowerCase();
      const channelLabel = channelKey.toUpperCase();
      const statusLabel = item.status ? item.status.replace(/_/g, ' ') : '';
      const createdAt = item.created_at ? new Date(item.created_at) : null;
      const ageMs = createdAt ? Date.now() - createdAt.getTime() : 0;

      if (ageMs >= PM_AGE_ESCALATED_MS) {
        tr.classList.add('pending-age-critical', 'pending-age-escalated');
      } else if (ageMs >= PM_AGE_CRITICAL_MS) {
        tr.classList.add('pending-age-critical');
      } else if (ageMs >= PM_SLA_TARGET_MS) {
        tr.classList.add('pending-age-warning');
      }

      const numberCell = document.createElement('td');
      numberCell.textContent = String(offset + idx + 1);
      tr.appendChild(numberCell);

      const channelCell = document.createElement('td');
      channelCell.textContent = channelLabel;
      tr.appendChild(channelCell);

      const contactCell = document.createElement('td');
      const contactValue = item.customer_contact || '';
      const isEmailContact = /@/.test(contactValue);
      if (isEmailContact) {
        const link = document.createElement('a');
        link.href = `mailto:${encodeURIComponent(contactValue)}`;
        link.textContent = contactValue;
        contactCell.appendChild(link);
      } else {
        contactCell.textContent = contactValue || '-';
      }
      tr.appendChild(contactCell);

      const summaryCell = document.createElement('td');
      summaryCell.className = 'pending-summary';
      const attachmentCount = Array.isArray(item.attachments) ? item.attachments.length : 0;
      if (attachmentCount > 0) {
        const attachmentIndicator = document.createElement('div');
        attachmentIndicator.className = 'pending-attachment-indicator';
        attachmentIndicator.title = attachmentCount === 1 ? '1 attachment' : `${attachmentCount} attachments`;
        attachmentIndicator.setAttribute('aria-label', attachmentIndicator.title);
        attachmentIndicator.textContent = `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`;
        summaryCell.appendChild(attachmentIndicator);
      }

      const previewEl = document.createElement('div');
      previewEl.className = 'message-preview';
      const previewSource = item.preview || item.ai_summary || '';
      if (looksLikeHtml(previewSource)) {
        renderHtmlOrText(previewEl, previewSource, { fallback: '(no message preview)' });
      } else {
        const plain = previewSource || '(no message preview)';
        const truncated = plain.length > 120 ? `${plain.slice(0, 117)}…` : plain;
        renderHtmlOrText(previewEl, truncated, { fallback: '(no message preview)', allowHtml: false });
      }
      summaryCell.appendChild(previewEl);
      tr.appendChild(summaryCell);

      const statusCell = document.createElement('td');
      const badge = document.createElement('span');
      badge.className = 'badge';
      badge.textContent = statusLabel;
      statusCell.appendChild(badge);
      tr.appendChild(statusCell);

      const receivedCell = document.createElement('td');
      receivedCell.textContent = item.created_at ? formatDateTimeFull(item.created_at) : '—';
      tr.appendChild(receivedCell);

      const slaCell = document.createElement('td');
      slaCell.className = 'pending-sla-cell';
      slaCell.textContent = formatPendingSlaCountdown(ageMs);
      tr.appendChild(slaCell);

      const actionsCell = document.createElement('td');
      const reviewBtn = document.createElement('button');
      reviewBtn.className = 'btn';
      reviewBtn.dataset.pendingAction = 'view';
      reviewBtn.dataset.id = String(item.id);
      reviewBtn.textContent = 'Review';
      actionsCell.appendChild(reviewBtn);

      if (item.status === 'pending') {
        const followupBtn = document.createElement('button');
        followupBtn.className = 'btn';
        followupBtn.dataset.pendingAction = 'followup';
        followupBtn.dataset.id = String(item.id);
        followupBtn.textContent = 'Mark as follow-up';
        actionsCell.appendChild(followupBtn);

        if (channelKey === 'email') {
          const forwardBtn = document.createElement('button');
          forwardBtn.className = 'btn';
          forwardBtn.dataset.pendingAction = 'forward';
          forwardBtn.dataset.id = String(item.id);
          forwardBtn.textContent = 'Forward';
          actionsCell.appendChild(forwardBtn);
        }
      }
      tr.appendChild(actionsCell);

      dom.body.appendChild(tr);
    });

    updatePendingMessageAgeIndicators();
    schedulePendingAgeRefresh();
  }

  function handlePendingMessagesTableClick(event) {
    const btn = event.target.closest('[data-pending-action]');
    if (!btn) return;
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    const id = btn.dataset.id;
    if (!id) return;
    const action = btn.dataset.pendingAction;
    if (action === 'view' || action === 'convert') {
      Promise.resolve(callHelper('openDetailDialog', id)).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[tickets] openDetailDialog rejected', err);
      });
    } else if (action === 'forward') {
      const record = pmListState.items.find((item) => String(item.id) === String(id));
      callHelper('openForwardDialog', record || null);
    } else if (action === 'followup') {
      const record = pmListState.items.find((item) => String(item.id) === String(id));
      if (record) {
        Promise.resolve(callHelper('openFollowupDialog', record)).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[tickets] openFollowupDialog rejected', err);
        });
      } else {
        notify.showFlash('Message not found for follow-up', 'error');
      }
    } else if (action === 'fetch-recording') {
      Promise.resolve(callHelper('fetchRecording', id, btn)).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[tickets] fetchRecording rejected', err);
      });
    }
  }

  function mountPmList() {
    if (pmListMounted) {
      loadPendingMessages({ skipIfFresh: true });
      return;
    }
    const dom = resolvePmListDom();
    if (!dom.view) return;
    if (dom.statusFilter) {
      dom.statusFilter.addEventListener('change', () => {
        const value = dom.statusFilter.value || 'pending';
        pmListState.status = value;
        pmListState.offset = 0;
        pmListState.total = 0;
        pmListState.stale = true;
        loadPendingMessages();
      });
    }
    if (dom.channelFilter) {
      dom.channelFilter.value = pmListState.channel || 'all';
      dom.channelFilter.addEventListener('change', () => {
        const value = dom.channelFilter.value || 'all';
        pmListState.channel = value;
        pmListState.offset = 0;
        pmListState.total = 0;
        pmListState.stale = true;
        loadPendingMessages();
      });
    }
    if (dom.searchInput) {
      dom.searchInput.value = pmListState.search || '';
      dom.searchInput.addEventListener('input', () => {
        const value = dom.searchInput.value || '';
        pmListState.search = value;
        pmListState.offset = 0;
        pmListState.total = 0;
        pmListState.stale = true;
        if (pmListSearchTimer) {
          clearTimeout(pmListSearchTimer);
        }
        pmListSearchTimer = setTimeout(() => {
          loadPendingMessages();
        }, 300);
      });
    }
    if (dom.refresh) {
      dom.refresh.addEventListener('click', () => loadPendingMessages({ keepSelection: true }));
    }
    if (dom.prev) {
      dom.prev.addEventListener('click', () => changePendingMessagesPage('prev'));
    }
    if (dom.next) {
      dom.next.addEventListener('click', () => changePendingMessagesPage('next'));
    }
    if (dom.body) {
      dom.body.addEventListener('click', handlePendingMessagesTableClick);
    }
    pmListMounted = true;
  }

  views.tickets.pendingMessages = views.tickets.pendingMessages || {};
  views.tickets.pendingMessages.dashboard = {
    mount: mountPmDashboard,
    refresh: (opts) => loadPmDashboard(opts),
  };
  // ---- Forward dialog (2.1.b.3-fwd-followup) ---------------------------
  // Owns the "forward this email to another agent" flow triggered from the
  // pending-messages list and detail screens. State and DOM are local; the
  // only external surface is `open(record)` (call from a list-row's
  // forward button) and `mount()` (called once from initPendingMessagesView).
  const pmForwardState = { users: [], loading: false, loaded: false };
  let pmForwardDom = null;
  let pmForwardMounted = false;

  function resolvePmForwardDom() {
    if (pmForwardDom) return pmForwardDom;
    pmForwardDom = {
      dlg: document.getElementById('pendingMessageForwardDlg'),
      form: document.getElementById('pendingMessageForwardForm'),
      userSelect: document.getElementById('pendingMessageForwardUser'),
      submitBtn: document.getElementById('pendingMessageForwardSubmit'),
    };
    return pmForwardDom;
  }

  async function ensurePmForwardUsersLoaded(force = false) {
    if (pmForwardState.loading) return;
    if (pmForwardState.loaded && !force) return;
    pmForwardState.loading = true;
    try {
      const params = new URLSearchParams();
      params.set('limit', '200');
      params.set('skip', '0');
      const data = await api.fetch(`${api.routes.users}?${params.toString()}`, { method: 'GET' });
      const items = Array.isArray(data) ? data : (data.items || []);
      pmForwardState.users = items
        .filter((user) => user && user.email)
        .map((user) => ({ ...user, roles: Array.isArray(user?.roles) ? user.roles.slice() : [] }));
      pmForwardState.loaded = true;
      populatePmForwardUsers();
    } catch (err) {
      notify.showFlash(err.message || 'Failed to load agents', 'error');
    } finally {
      pmForwardState.loading = false;
    }
  }

  function populatePmForwardUsers(selectedId = null) {
    const dom = resolvePmForwardDom();
    if (!dom.userSelect) return;
    const previousValue = selectedId || dom.userSelect.value;
    dom.userSelect.innerHTML = '<option value="" disabled selected>Select agent…</option>';
    pmForwardState.users
      .sort((a, b) => String(a.username || '').localeCompare(String(b.username || '')))
      .forEach((user) => {
        const option = document.createElement('option');
        option.value = String(user.id);
        const labelParts = [user.username || `User #${user.id}`];
        if (user.email) labelParts.push(`(${user.email})`);
        option.textContent = labelParts.join(' ');
        dom.userSelect.appendChild(option);
      });
    if (previousValue) {
      dom.userSelect.value = String(previousValue);
    }
  }

  async function openPmForwardDialog(record) {
    const dom = resolvePmForwardDom();
    if (!dom.dlg || !record) {
      // eslint-disable-next-line no-console
      if (!dom.dlg) console.warn('openPmForwardDialog: #pendingMessageForwardDlg missing.');
      return;
    }
    await ensurePmForwardUsersLoaded();
    populatePmForwardUsers();
    dom.form.dataset.messageId = String(record.id);
    if (dom.submitBtn) {
      dom.submitBtn.disabled = false;
      dom.submitBtn.textContent = 'Send forward';
    }
    dom.form.dataset.pending = '0';
    callHelper('openDialog', dom.dlg);
    if (dom.userSelect) {
      setTimeout(() => dom.userSelect.focus(), 40);
    }
  }

  function closePmForwardDialog() {
    const dom = resolvePmForwardDom();
    if (!dom.dlg) return;
    try { dom.dlg.close(); } catch { dom.dlg.open = false; }
    if (dom.form) {
      delete dom.form.dataset.messageId;
      dom.form.reset();
    }
  }

  async function handlePmForwardSubmit(event) {
    event.preventDefault();
    const dom = resolvePmForwardDom();
    if (!dom.form || !dom.userSelect) return;
    if (dom.form.dataset.pending === '1') return;
    const messageId = dom.form.dataset.messageId;
    const userId = dom.userSelect.value;
    if (!messageId || !userId) {
      notify.showFlash('Select an agent to forward this message.', 'error');
      return;
    }
    dom.form.dataset.pending = '1';
    if (dom.submitBtn) {
      dom.submitBtn.disabled = true;
      dom.submitBtn.textContent = 'Forwarding…';
    }
    try {
      await api.fetch(api.routes.adminPendingMessageForward(messageId), {
        method: 'POST',
        body: JSON.stringify({ user_id: Number(userId) }),
      });
      const userInfo = pmForwardState.users.find((user) => String(user.id) === String(userId));
      const label = userInfo && (userInfo.username || userInfo.email)
        ? (userInfo.username || userInfo.email)
        : 'selected agent';
      notify.showFlash(`Message forwarded to ${label}.`);
      closePmForwardDialog();
      await loadPendingMessages({ keepSelection: true });
    } catch (err) {
      let message = err && err.message ? err.message : 'Failed to forward message';
      if (message === 'forward_user_missing_email') {
        message = 'Selected agent does not have an email address on file.';
      } else if (message === 'forward_user_not_found') {
        message = 'Unable to find the selected agent account.';
      } else if (message === 'forward_email_failed') {
        message = 'Could not send the email forward. Please try again or contact support.';
      }
      notify.showFlash(message, 'error');
    } finally {
      dom.form.dataset.pending = '0';
      if (dom.submitBtn) {
        dom.submitBtn.disabled = false;
        dom.submitBtn.textContent = 'Send forward';
      }
    }
  }

  function mountPmForward() {
    if (pmForwardMounted) return;
    const dom = resolvePmForwardDom();
    if (dom.form) {
      dom.form.addEventListener('submit', (event) => {
        handlePmForwardSubmit(event).catch(() => {});
      });
    }
    pmForwardMounted = true;
  }

  // ---- Followup dialog (2.1.b.3-fwd-followup) --------------------------
  // Owns the "link this message as a follow-up to an existing ticket"
  // flow. Like the forward dialog, fully self-contained: state + DOM
  // local, and the only external entry points are `open(record)` and
  // `mount()`.
  const pmFollowupState = {
    messageId: null,
    items: [],
    loading: false,
    selectedId: null,
    query: '',
  };
  let pmFollowupSearchTimer = null;
  let pmFollowupDom = null;
  let pmFollowupMounted = false;

  function resolvePmFollowupDom() {
    if (pmFollowupDom) return pmFollowupDom;
    pmFollowupDom = {
      btn: document.getElementById('pendingMessageFollowupBtn'),
      dlg: document.getElementById('pendingMessageFollowupDlg'),
      form: document.getElementById('pendingMessageFollowupForm'),
      search: document.getElementById('pendingMessageFollowupSearch'),
      results: document.getElementById('pendingMessageFollowupResults'),
      status: document.getElementById('pendingMessageFollowupStatus'),
      submitBtn: document.getElementById('pendingMessageFollowupSubmit'),
    };
    return pmFollowupDom;
  }

  function setPmFollowupStatus(message, tone = 'info') {
    const dom = resolvePmFollowupDom();
    if (!dom.status) return;
    dom.status.textContent = message || '';
    const colorMap = { info: 'var(--sub)', warn: '#b26b00', error: 'var(--danger)' };
    dom.status.style.color = colorMap[tone] || colorMap.info;
  }

  function resetPmFollowupDialog() {
    const dom = resolvePmFollowupDom();
    pmFollowupState.messageId = null;
    pmFollowupState.items = [];
    pmFollowupState.loading = false;
    pmFollowupState.selectedId = null;
    pmFollowupState.query = '';
    if (pmFollowupSearchTimer) {
      clearTimeout(pmFollowupSearchTimer);
      pmFollowupSearchTimer = null;
    }
    if (dom.search) dom.search.value = '';
    if (dom.results) dom.results.innerHTML = '';
    if (dom.submitBtn) {
      dom.submitBtn.disabled = true;
      dom.submitBtn.textContent = 'Link to ticket';
    }
    setPmFollowupStatus('', 'info');
  }

  function renderPmFollowupResults() {
    const dom = resolvePmFollowupDom();
    if (!dom.results) return;
    const escapeHtml = pmHelpers.escapeHtml;
    const ticketStatusLabels = (pmHelpers.getTicketStatusLabels && pmHelpers.getTicketStatusLabels()) || {};
    const ticketPriorityLabels = (pmHelpers.getTicketPriorityLabels && pmHelpers.getTicketPriorityLabels()) || {};
    dom.results.innerHTML = '';
    if (pmFollowupState.loading) {
      const loadingRow = document.createElement('div');
      loadingRow.className = 'pending-followup-empty';
      loadingRow.textContent = 'Searching tickets…';
      dom.results.appendChild(loadingRow);
      if (dom.submitBtn) dom.submitBtn.disabled = true;
      return;
    }
    if (!pmFollowupState.items.length) {
      const emptyRow = document.createElement('div');
      emptyRow.className = 'pending-followup-empty';
      emptyRow.textContent = 'No matching tickets yet.';
      dom.results.appendChild(emptyRow);
      if (dom.submitBtn) dom.submitBtn.disabled = true;
      return;
    }
    pmFollowupState.items.forEach((item) => {
      const wrapper = document.createElement('label');
      wrapper.className = 'pending-followup-item';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'pendingFollowupTicket';
      input.value = String(item.id);
      input.checked = pmFollowupState.selectedId === String(item.id);
      input.addEventListener('change', () => {
        pmFollowupState.selectedId = String(item.id);
        if (dom.submitBtn) {
          dom.submitBtn.disabled = false;
        }
      });
      const details = document.createElement('div');
      details.className = 'pending-followup-ticket';
      const statusLabel = ticketStatusLabels[item.status] || item.status;
      const priorityLabel = ticketPriorityLabels[item.priority] || item.priority;
      const shortCode = item.ticket_short_code || String(item.id).slice(0, 8);
      const customerName = escapeHtml(item.support_customer?.display_name || 'Unknown customer');
      const ticketMeta = [
        escapeHtml(statusLabel || ''),
        escapeHtml(priorityLabel || ''),
      ].filter(Boolean).join(' • ');
      details.innerHTML = `
        <div style="font-weight:600;">${escapeHtml(shortCode)} — ${escapeHtml(item.subject || 'Untitled ticket')}</div>
        <div class="ticket-meta">${ticketMeta || '—'}</div>
        <div class="ticket-meta">${customerName}</div>
      `;
      wrapper.appendChild(input);
      wrapper.appendChild(details);
      dom.results.appendChild(wrapper);
    });
    if (dom.submitBtn) {
      dom.submitBtn.disabled = !pmFollowupState.selectedId;
    }
  }

  async function loadPmFollowupCandidates(options = {}) {
    if (!pmFollowupState.messageId) return;
    const dom = resolvePmFollowupDom();
    let query = options.query !== undefined ? options.query : pmFollowupState.query;
    query = (query || '').trim();
    pmFollowupState.query = query;
    if (dom.search && dom.search.value.trim() !== query) {
      dom.search.value = query;
    }
    if (query.length < 2) {
      pmFollowupState.items = [];
      renderPmFollowupResults();
      setPmFollowupStatus('Enter at least 2 characters to search tickets.', 'info');
      return;
    }
    pmFollowupState.loading = true;
    pmFollowupState.selectedId = null;
    renderPmFollowupResults();
    setPmFollowupStatus('Searching tickets…', 'info');
    try {
      const data = await api.fetch(api.routes.adminPendingMessageFollowupCandidates(pmFollowupState.messageId, query), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      pmFollowupState.items = items.map((item) => ({
        id: item.id,
        subject: item.subject,
        status: item.status,
        priority: item.priority,
        channel: item.channel,
        ticket_short_code: item.ticket_short_code,
        support_customer: item.support_customer || null,
      }));
      if (!pmFollowupState.items.length) {
        setPmFollowupStatus('No matching tickets found.', 'warn');
      } else {
        setPmFollowupStatus(`Found ${pmFollowupState.items.length} ticket${pmFollowupState.items.length === 1 ? '' : 's'}.`, 'info');
      }
    } catch (err) {
      pmFollowupState.items = [];
      setPmFollowupStatus(err?.message || 'Failed to search tickets', 'error');
    } finally {
      pmFollowupState.loading = false;
      renderPmFollowupResults();
    }
  }

  async function openPmFollowupDialog(record, options = {}) {
    const dom = resolvePmFollowupDom();
    if (!dom.dlg || !record) {
      // eslint-disable-next-line no-console
      if (!dom.dlg) console.warn('openPmFollowupDialog: #pendingMessageFollowupDlg missing.');
      return;
    }
    resetPmFollowupDialog();
    pmFollowupState.messageId = record.id;
    const meta = record.metadata || {};
    const initialQuery = (options.query || meta.customer_name || meta.name || record.customer_contact || '').trim();
    pmFollowupState.query = initialQuery;
    if (dom.search) dom.search.value = initialQuery;
    callHelper('openDialog', dom.dlg);
    if (initialQuery.length >= 2) {
      await loadPmFollowupCandidates({ query: initialQuery });
    } else {
      setPmFollowupStatus('Enter a name or phone number to search tickets.', 'info');
    }
    setTimeout(() => {
      if (dom.search) dom.search.focus();
    }, 40);
  }

  function closePmFollowupDialog() {
    const dom = resolvePmFollowupDom();
    if (!dom.dlg) return;
    try { dom.dlg.close(); } catch { dom.dlg.open = false; }
    resetPmFollowupDialog();
  }

  async function handlePmFollowupSubmit(event) {
    event.preventDefault();
    const dom = resolvePmFollowupDom();
    if (!dom.form || !pmFollowupState.messageId) return;
    if (pmFollowupState.loading) return;
    const ticketId = pmFollowupState.selectedId;
    if (!ticketId) {
      notify.showFlash('Select a ticket to link this message.', 'error');
      return;
    }
    pmFollowupState.loading = true;
    if (dom.submitBtn) {
      dom.submitBtn.disabled = true;
      dom.submitBtn.textContent = 'Linking…';
    }
    try {
      const ticket = await api.fetch(api.routes.adminPendingMessageFollowup(pmFollowupState.messageId), {
        method: 'POST',
        body: JSON.stringify({ ticket_id: ticketId }),
      });
      notify.showFlash('Message linked as follow-up.');
      closePmFollowupDialog();
      maybeCloseDetailDialog();
      await loadPendingMessages();
      callHelper('openTicketDetailAfterCreation', ticket && ticket.id, ticket);
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to link follow-up', 'error');
    } finally {
      pmFollowupState.loading = false;
      if (dom.submitBtn) {
        dom.submitBtn.disabled = false;
        dom.submitBtn.textContent = 'Link to ticket';
      }
    }
  }

  function handlePmFollowupSearchInput() {
    const dom = resolvePmFollowupDom();
    if (!dom.search) return;
    const value = dom.search.value;
    if (pmFollowupSearchTimer) clearTimeout(pmFollowupSearchTimer);
    pmFollowupSearchTimer = setTimeout(() => {
      pmFollowupSearchTimer = null;
      loadPmFollowupCandidates({ query: value }).catch(() => {});
    }, 280);
  }

  function mountPmFollowup() {
    if (pmFollowupMounted) return;
    const dom = resolvePmFollowupDom();
    if (dom.form) {
      dom.form.addEventListener('submit', (event) => {
        handlePmFollowupSubmit(event).catch(() => {});
      });
    }
    if (dom.dlg) {
      dom.dlg.addEventListener('close', resetPmFollowupDialog);
    }
    if (dom.search) {
      dom.search.addEventListener('input', handlePmFollowupSearchInput);
    }
    if (dom.btn) {
      dom.btn.addEventListener('click', () => {
        const current = callHelper('getCurrent');
        if (current) {
          openPmFollowupDialog(current).catch(() => {});
        } else {
          notify.showFlash('Select a pending message first.', 'error');
        }
      });
    }
    pmFollowupMounted = true;
  }

  // ---- Sidebar badge + access gate (2.1.b.4) ---------------------------
  // The "navPendingMessages" item in the sidebar grows a `.nav-alert` class
  // when there's something for the user to attend to. Originally lived in
  // admin.js; the indicator state is genuinely pending-messages-specific
  // so it belongs alongside the rest of the cluster.
  let pmAlertActive = false;
  let pmNavEl = null;
  function resolvePmNav() {
    if (pmNavEl) return pmNavEl;
    pmNavEl = document.getElementById('navPendingMessages');
    return pmNavEl;
  }
  function setPmIndicator(active) {
    pmAlertActive = Boolean(active);
    const nav = resolvePmNav();
    if (!nav) return;
    nav.classList.toggle('nav-alert', pmAlertActive);
  }

  // Access gate: lives here so the WS dispatcher can short-circuit before
  // touching any pending-messages internals. Delegates to admin.js's
  // role gate via an injected helper.
  function canAccessPendingMessages() {
    const fn = pmHelpers.canAccessTicketing;
    if (typeof fn !== 'function') {
      warnMissingHelper('canAccessTicketing');
      return true;
    }
    return Boolean(fn());
  }

  views.tickets.pendingMessages.list = {
    mount: mountPmList,
    refresh: (opts) => loadPendingMessages(opts),
    isViewActive: isPendingMessagesViewActive,
    canAccess: canAccessPendingMessages,
    setIndicator: setPmIndicator,
    isAlertActive: () => pmAlertActive,
    normalizeStatus: normalizePendingStatus,
    upsert: upsertPendingMessageRecord,
    remove: removePendingMessageById,
    fetchDetail: fetchPendingMessageDetail,
    changePage: changePendingMessagesPage,
    matchesFilter: pendingMessageMatchesFilter,
    // Selection state — admin.js (and 2.1.b.3 sub-view, once it lands)
    // can read/write the currently-selected record without owning the
    // backing store.
    setSelectedId: (id) => { pmListState.selectedId = id != null ? String(id) : null; },
    getSelectedId: () => pmListState.selectedId,
    // Records — admin.js looks these up from the WS dispatcher and dialog
    // handlers without reaching into private state.
    getRecordById: (id) => pmListState.items.find((item) => String(item.id) === String(id)) || null,
    getItems: () => pmListState.items.slice(),
    isLoaded: () => pmListState.loaded,
    isStale: () => pmListState.stale,
    setStale: (value) => { pmListState.stale = !!value; },
    rerender: renderPendingMessages,
    updateAgeIndicators: updatePendingMessageAgeIndicators,
  };
  views.tickets.pendingMessages.forward = {
    mount: mountPmForward,
    open: (record) => openPmForwardDialog(record),
    close: closePmForwardDialog,
  };
  views.tickets.pendingMessages.followup = {
    mount: mountPmFollowup,
    open: (record, options) => openPmFollowupDialog(record, options),
    close: closePmFollowupDialog,
  };

  // ---- Detail dialog cluster (2.1.b.3-detail) --------------------------
  // Owns the main pending-message detail dialog plus its three nested
  // sub-flows: enquiry creation, ticket conversion, and discard. Also owns
  // the "currently-selected message" pointer (pmCurrent), the form
  // dirtiness flag (stored as a dataset attribute on the form element),
  // the dedup'd "new pending message" toast helper, and the call-recording
  // fetch-and-attach action.
  //
  // External coupling — admin.js still owns the cross-cutting machinery
  // that doesn't naturally belong here, and the detail module reaches into
  // it via `pmHelpers`:
  //   • `populateAssignmentOptions` + ensureTechnicianUsersLoaded /
  //     ensureSupervisorUsersLoaded — shared with ticket-creation, will
  //     move when 2.1.d (ticket detail / board) lands.
  //   • `openTicketConflictDialog` + `closeTicketConflictDialog` +
  //     `getTicketConflictSupportCustomerId` — same.
  //   • `reloadTickets` + `openTicketDetailAfterCreation` — same.
  //   • `switchView` — sidebar router still in admin.js.
  //   • `formatAttachmentSize`, `formatAiRecommendations` — admin-domain
  //     formatters that aren't worth carrying here yet.
  //   • Constants `TICKET_COMPLAINT_OPTIONS`, `ENQUIRY_TYPE_OPTIONS`,
  //     `CONTACT_METHOD_OPTIONS`, `CHANNEL_PREFERRED_CONTACT` — pulled via
  //     getter helpers so they stay single-source-of-truth in admin.js.

  let pmCurrent = null;
  const pmDetailNotificationRecent = new Map();

  const pmEnquiryState = {
    messageId: null,
    contactCategory: 'prospect',
    supportCustomerId: null,
    matchedProspectId: null,
    matchedProspectLabel: '',
    manualCategoryOverride: false,
    autoDetectTimer: null,
    lastDetectSignature: '',
    currentDetectionKey: null,
    selectedCustomerLabel: '',
  };
  const pmEnquirySuggestionState = {
    query: '',
    items: [],
    loading: false,
    highlight: -1,
    requestKey: null,
    error: '',
  };
  let pmEnquirySuggestionTimer = null;

  let pmDetailDom = null;
  let pmDetailMounted = false;

  function resolvePmDetailDom() {
    if (pmDetailDom) return pmDetailDom;
    pmDetailDom = {
      // Main detail dialog
      dlg: document.getElementById('pendingMessageDlg'),
      title: document.getElementById('pendingMessageTitle'),
      meta: document.getElementById('pendingMessageMeta'),
      preview: document.getElementById('pendingMessagePreview'),
      attachments: document.getElementById('pendingMessageAttachments'),
      form: document.getElementById('pendingMessageForm'),
      subjectInput: document.getElementById('pendingMessageSubject'),
      descriptionInput: document.getElementById('pendingMessageDescription'),
      contactInput: document.getElementById('pendingMessageContact'),
      emailInput: document.getElementById('pendingMessageEmail'),
      nameInput: document.getElementById('pendingMessageName'),
      locationInput: document.getElementById('pendingMessageLocation'),
      complaintSelect: document.getElementById('pendingMessageComplaint'),
      preferredChannelSelect: document.getElementById('pendingMessagePreferredChannel'),
      prioritySelect: document.getElementById('pendingMessagePriority'),
      assignRoleSelect: document.getElementById('pendingMessageAssignRole'),
      assignUserSelect: document.getElementById('pendingMessageAssignUser'),
      assignHelp: document.getElementById('pendingMessageAssignHelp'),
      callbackField: document.getElementById('pendingMessageCallbackField'),
      callbackConfirmed: document.getElementById('pendingMessageCallbackConfirmed'),
      convertBtn: document.getElementById('pendingMessageConvertBtn'),
      enquiryBtn: document.getElementById('pendingMessageEnquiryBtn'),
      followupBtn: document.getElementById('pendingMessageFollowupBtn'),
      discardBtn: document.getElementById('pendingMessageDiscardBtn'),
      // Discard sub-dialog
      discardDlg: document.getElementById('pendingMessageDiscardDlg'),
      discardForm: document.getElementById('pendingMessageDiscardForm'),
      discardReason: document.getElementById('pendingMessageDiscardReason'),
      discardNote: document.getElementById('pendingMessageDiscardNote'),
      discardConfirm: document.getElementById('pendingMessageDiscardConfirm'),
      // Enquiry sub-dialog
      enquiryDlg: document.getElementById('pendingMessageEnquiryDlg'),
      enquiryForm: document.getElementById('pendingMessageEnquiryForm'),
      enquiryStatus: document.getElementById('pendingMessageEnquiryStatus'),
      enquiryDupWarning: document.getElementById('pendingEnquiryDupWarning'),
      enquirySubmitBtn: document.getElementById('pendingMessageEnquirySubmit'),
      enquiryNameInput: document.getElementById('pendingEnquiryNameInput'),
      enquiryNameSuggestions: document.getElementById('pendingEnquiryNameSuggestions'),
      enquiryPhoneInput: document.getElementById('pendingEnquiryPhoneInput'),
      enquiryEmailInput: document.getElementById('pendingEnquiryEmailInput'),
      enquiryCityInput: document.getElementById('pendingEnquiryCityInput'),
      enquiryAddressInput: document.getElementById('pendingEnquiryAddressInput'),
      enquiryChannelSelect: document.getElementById('pendingEnquiryChannelSelect'),
      enquiryTypeSelect: document.getElementById('pendingEnquiryTypeSelect'),
      enquiryNotesInput: document.getElementById('pendingEnquiryNotesInput'),
      enquiryCustomerToggle: document.getElementById('pendingEnquiryCustomerToggle'),
      enquiryContactTypeField: document.getElementById('pendingEnquiryContactTypeField'),
      enquiryCustomerBadge: document.getElementById('pendingEnquiryCustomerBadge'),
    };
    return pmDetailDom;
  }

  // ---- Form dirtiness tracking (data-attribute-backed) ----
  function isPendingMessageFormDirty() {
    const dom = resolvePmDetailDom();
    return Boolean(dom.form && dom.form.dataset && dom.form.dataset.dirty === '1');
  }

  function markPendingMessageDirty() {
    const dom = resolvePmDetailDom();
    if (dom.form && dom.form.dataset) dom.form.dataset.dirty = '1';
  }

  function resetPendingMessageDirty() {
    const dom = resolvePmDetailDom();
    if (dom.form && dom.form.dataset) dom.form.dataset.dirty = '0';
  }

  function pendingMessageRequiresCallback(record) {
    const dom = resolvePmDetailDom();
    const source = record || pmCurrent || null;
    let flag = null;
    if (source && source.metadata) {
      flag = source.metadata.requires_callback;
      if (flag == null && source.metadata.missed_call) flag = true;
    }
    if (flag == null && dom.form && dom.form.dataset) {
      const datasetFlag = dom.form.dataset.requiresCallback;
      if (datasetFlag === '1' || datasetFlag === 'true') flag = true;
    }
    if (typeof flag === 'string') {
      const normalized = flag.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return Boolean(flag);
  }

  function updatePendingMessageConvertState(record) {
    const dom = resolvePmDetailDom();
    if (!dom.convertBtn) return;
    const source = record || pmCurrent;
    const statusBlock = !source || source.status !== 'pending';
    const needsCallback = pendingMessageRequiresCallback(source);
    const confirmationMissing = needsCallback
      && dom.callbackConfirmed
      && !dom.callbackConfirmed.checked;
    const hasSubject = dom.subjectInput && dom.subjectInput.value.trim().length > 0;
    const hasDescription = dom.descriptionInput && dom.descriptionInput.value.trim().length > 0;
    const hasName = dom.nameInput && dom.nameInput.value.trim().length > 0;
    const hasPhone = dom.contactInput && dom.contactInput.value.trim().length > 0;
    const hasLocation = dom.locationInput && dom.locationInput.value.trim().length > 0;
    const hasComplaint = dom.complaintSelect && dom.complaintSelect.value;
    const hasPreferredChannel = dom.preferredChannelSelect && dom.preferredChannelSelect.value;
    const requiredMissing = !hasSubject || !hasDescription || !hasName || !hasPhone
      || !hasLocation || !hasComplaint || !hasPreferredChannel;
    if (dom.assignRoleSelect) dom.assignRoleSelect.disabled = statusBlock;
    if (dom.assignUserSelect) dom.assignUserSelect.disabled = statusBlock;
    dom.convertBtn.disabled = statusBlock || confirmationMissing || requiredMissing;
    if (dom.enquiryBtn) dom.enquiryBtn.disabled = statusBlock;
    if (dom.followupBtn) dom.followupBtn.disabled = statusBlock;
  }

  function populatePendingMessageAssignmentOptions() {
    const dom = resolvePmDetailDom();
    const fn = pmHelpers.populateAssignmentOptions;
    if (typeof fn !== 'function') {
      warnMissingHelper('populateAssignmentOptions');
      return;
    }
    fn({
      roleSelect: dom.assignRoleSelect,
      userSelect: dom.assignUserSelect,
      helpEl: dom.assignHelp,
      helpDefault: pmHelpers.getAssignHelpDefault ? pmHelpers.getAssignHelpDefault() : '',
    });
  }

  // ---- Enquiry dialog ----
  function setPendingEnquiryStatus(message, tone = 'info') {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryStatus) return;
    if (!message) {
      dom.enquiryStatus.textContent = '';
      dom.enquiryStatus.className = 'dlg-status';
      return;
    }
    dom.enquiryStatus.textContent = message;
    const baseClass = ['dlg-status'];
    if (tone === 'success') baseClass.push('success');
    else if (tone === 'error') baseClass.push('error');
    dom.enquiryStatus.className = baseClass.join(' ');
  }

  // Prominent duplicate-guard banner, mirroring the new-prospect form. Pass a
  // falsy message to hide it. The auto-detect drives this whenever the typed
  // phone/email matches an existing customer or prospect.
  function setPendingEnquiryDupWarning(message) {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryDupWarning) return;
    if (!message) {
      dom.enquiryDupWarning.style.display = 'none';
      dom.enquiryDupWarning.textContent = '';
      return;
    }
    dom.enquiryDupWarning.textContent = `⚠ ${message}`;
    dom.enquiryDupWarning.style.display = '';
  }

  function ensurePendingEnquiryTypeOptions() {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryTypeSelect) return;
    if (dom.enquiryTypeSelect.options.length) return;
    const opts = (pmHelpers.getEnquiryTypeOptions && pmHelpers.getEnquiryTypeOptions()) || [];
    opts.forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      dom.enquiryTypeSelect.appendChild(option);
    });
    dom.enquiryTypeSelect.value = 'service_quality';
  }

  function clearPendingEnquirySuggestions() {
    const dom = resolvePmDetailDom();
    pmEnquirySuggestionState.items = [];
    pmEnquirySuggestionState.loading = false;
    pmEnquirySuggestionState.highlight = -1;
    pmEnquirySuggestionState.requestKey = null;
    pmEnquirySuggestionState.error = '';
    pmEnquirySuggestionState.query = '';
    if (dom.enquiryNameSuggestions) {
      dom.enquiryNameSuggestions.innerHTML = '';
      dom.enquiryNameSuggestions.hidden = true;
    }
  }

  function renderPendingEnquirySuggestions() {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryNameSuggestions) return;
    const escapeHtml = pmHelpers.escapeHtml;
    const { items, loading, error, highlight, query } = pmEnquirySuggestionState;
    if (loading) {
      dom.enquiryNameSuggestions.innerHTML = '<div class="typeahead-empty">Searching…</div>';
      dom.enquiryNameSuggestions.hidden = false;
      return;
    }
    if (error) {
      dom.enquiryNameSuggestions.innerHTML = `<div class="typeahead-empty">${escapeHtml(error)}</div>`;
      dom.enquiryNameSuggestions.hidden = false;
      return;
    }
    if (!items.length || query.length < 2) {
      dom.enquiryNameSuggestions.innerHTML = query.length >= 2 ? '<div class="typeahead-empty">No matches</div>' : '';
      dom.enquiryNameSuggestions.hidden = query.length < 2 || !items.length;
      return;
    }
    const content = items.map((item, idx) => {
      const selected = highlight === idx;
      const primary = item.label || item.support_customer_name;
      const accountLine = `${item.support_customer_name}${item.porta_customer_id ? ` • ${item.porta_customer_id}` : ''}`;
      const secondary = item.match_kind === 'relation'
        ? `${item.relation_name || ''}${item.relation_role ? ` • ${item.relation_role}` : ''}`
        : accountLine;
      const description = item.match_kind === 'relation' ? `Account • ${accountLine}` : accountLine;
      return `
        <button type="button" class="typeahead-suggestion" data-index="${idx}" role="option" aria-selected="${selected ? 'true' : 'false'}">
          <span class="typeahead-primary">${escapeHtml(primary)}</span>
          <span class="typeahead-secondary">${escapeHtml(description || secondary)}</span>
        </button>
      `;
    }).join('');
    dom.enquiryNameSuggestions.innerHTML = content;
    dom.enquiryNameSuggestions.hidden = false;
  }

  async function loadPendingEnquirySuggestions(query) {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryNameSuggestions) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      clearPendingEnquirySuggestions();
      return;
    }
    const requestKey = `${trimmed}:${Date.now()}`;
    pmEnquirySuggestionState.query = trimmed;
    pmEnquirySuggestionState.requestKey = requestKey;
    pmEnquirySuggestionState.loading = true;
    pmEnquirySuggestionState.error = '';
    pmEnquirySuggestionState.highlight = -1;
    renderPendingEnquirySuggestions();
    try {
      const payload = await api.fetch(api.routes.adminPendingMessageEnquirySuggestions(trimmed, 8), { method: 'GET' });
      if (pmEnquirySuggestionState.requestKey !== requestKey) return;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      pmEnquirySuggestionState.items = items;
      pmEnquirySuggestionState.loading = false;
      pmEnquirySuggestionState.highlight = items.length ? 0 : -1;
      renderPendingEnquirySuggestions();
    } catch (err) {
      if (pmEnquirySuggestionState.requestKey !== requestKey) return;
      pmEnquirySuggestionState.loading = false;
      pmEnquirySuggestionState.items = [];
      pmEnquirySuggestionState.error = err && err.message ? err.message : 'Search failed';
      renderPendingEnquirySuggestions();
    }
  }

  function hidePendingEnquirySuggestions() {
    const dom = resolvePmDetailDom();
    if (dom.enquiryNameSuggestions) dom.enquiryNameSuggestions.hidden = true;
    pmEnquirySuggestionState.highlight = -1;
  }

  function selectPendingEnquirySuggestion(index) {
    const dom = resolvePmDetailDom();
    const suggestion = pmEnquirySuggestionState.items[index];
    if (!suggestion) return;
    pmEnquiryState.manualCategoryOverride = true;
    pmEnquiryState.supportCustomerId = suggestion.support_customer_id;
    pmEnquiryState.matchedProspectId = null;
    pmEnquiryState.matchedProspectLabel = '';
    const displayLabel = suggestion.match_kind === 'relation'
      ? (suggestion.relation_name || suggestion.support_customer_name)
      : suggestion.support_customer_name;
    pmEnquiryState.selectedCustomerLabel = displayLabel;
    if (dom.enquiryNameInput) dom.enquiryNameInput.value = displayLabel;
    if (dom.enquiryPhoneInput && suggestion.primary_phone && !dom.enquiryPhoneInput.value.trim()) {
      dom.enquiryPhoneInput.value = suggestion.primary_phone;
    }
    if (dom.enquiryEmailInput && suggestion.primary_email && !dom.enquiryEmailInput.value.trim()) {
      dom.enquiryEmailInput.value = suggestion.primary_email;
    }
    if (dom.enquiryCityInput && suggestion.city && !dom.enquiryCityInput.value.trim()) {
      dom.enquiryCityInput.value = suggestion.city;
    }
    if (dom.enquiryAddressInput && suggestion.address && !dom.enquiryAddressInput.value.trim()) {
      dom.enquiryAddressInput.value = suggestion.address;
    }
    setPendingEnquiryContactCategory('customer', { force: true, silent: true });
    setPendingEnquiryStatus(`Linked to ${suggestion.support_customer_name}`, 'success');
    clearPendingEnquirySuggestions();
  }

  function handlePendingEnquiryNameInput() {
    const dom = resolvePmDetailDom();
    if (pmEnquirySuggestionTimer) {
      clearTimeout(pmEnquirySuggestionTimer);
      pmEnquirySuggestionTimer = null;
    }
    if (pmEnquiryState.supportCustomerId && pmEnquiryState.selectedCustomerLabel) {
      const typed = dom.enquiryNameInput && dom.enquiryNameInput.value
        ? dom.enquiryNameInput.value.trim() : '';
      if (!typed || typed.toLowerCase() !== pmEnquiryState.selectedCustomerLabel.toLowerCase()) {
        pmEnquiryState.supportCustomerId = null;
        pmEnquiryState.selectedCustomerLabel = '';
        pmEnquiryState.manualCategoryOverride = false;
        // The customer link is broken -> fall back to prospect and hide the
        // (now read-only) "Existing customer" indicator instead of leaving it stale.
        setPendingEnquiryContactCategory('prospect', { force: true, silent: true });
        setPendingEnquiryStatus('');
      }
    }
    const value = dom.enquiryNameInput ? dom.enquiryNameInput.value : '';
    if (!value || value.trim().length < 2) {
      clearPendingEnquirySuggestions();
      return;
    }
    pmEnquirySuggestionTimer = setTimeout(() => {
      loadPendingEnquirySuggestions(value);
    }, 220);
  }

  function handlePendingEnquiryNameKeyDown(event) {
    const { items, highlight } = pmEnquirySuggestionState;
    if (!items.length) {
      if (event.key === 'Escape') hidePendingEnquirySuggestions();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = highlight + 1 >= items.length ? 0 : highlight + 1;
      pmEnquirySuggestionState.highlight = next;
      renderPendingEnquirySuggestions();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = highlight <= 0 ? items.length - 1 : highlight - 1;
      pmEnquirySuggestionState.highlight = next;
      renderPendingEnquirySuggestions();
      return;
    }
    if (event.key === 'Enter') {
      if (highlight >= 0 && highlight < items.length) {
        event.preventDefault();
        selectPendingEnquirySuggestion(highlight);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      hidePendingEnquirySuggestions();
    }
  }

  function populatePendingEnquiryFields(data = {}, options = {}) {
    const dom = resolvePmDetailDom();
    const { force = false } = options;
    const setValue = (input, value) => {
      if (!input) return;
      const next = value == null ? '' : String(value);
      if (!force && input.value) return;
      if (!next) return;
      input.value = next;
    };
    setValue(dom.enquiryNameInput, data.full_name || data.contact_name || data.name);
    setValue(dom.enquiryPhoneInput, data.phone_number || data.contact_phone);
    setValue(dom.enquiryEmailInput, data.email || data.contact_email);
    setValue(dom.enquiryCityInput, data.city || data.contact_city || data.location);
    setValue(dom.enquiryAddressInput, data.street_address || data.address || data.contact_address);
  }

  // The "Contact type" control is a read-only status indicator, not an input.
  // It is shown (ticked + locked) only when the enquiry is linked to an existing
  // customer; otherwise it is hidden and the contact is a prospect automatically.
  function renderEnquiryContactType() {
    const dom = resolvePmDetailDom();
    const isCustomer = pmEnquiryState.contactCategory === 'customer'
      && !!pmEnquiryState.supportCustomerId;
    if (dom.enquiryContactTypeField) {
      dom.enquiryContactTypeField.style.display = isCustomer ? 'flex' : 'none';
    }
    if (dom.enquiryCustomerToggle) {
      dom.enquiryCustomerToggle.checked = isCustomer;
      dom.enquiryCustomerToggle.disabled = true; // status indicator, never editable
    }
    if (dom.enquiryCustomerBadge) {
      const label = pmEnquiryState.selectedCustomerLabel || 'existing customer';
      dom.enquiryCustomerBadge.textContent = `Existing customer — ${label}`;
    }
  }

  function setPendingEnquiryContactCategory(category, options = {}) {
    const dom = resolvePmDetailDom();
    const { force = false, silent = false } = options;
    const normalized = category === 'customer' ? 'customer' : 'prospect';
    if (!force && pmEnquiryState.contactCategory === normalized) {
      renderEnquiryContactType();
      return;
    }
    pmEnquiryState.contactCategory = normalized;
    renderEnquiryContactType();
    if (silent) return;
    if (normalized === 'customer') {
      if (pmEnquiryState.supportCustomerId) {
        setPendingEnquiryStatus('Matched existing customer', 'success');
      } else {
        setPendingEnquiryStatus('Marked contact as customer', 'info');
      }
    } else if (pmEnquiryState.matchedProspectLabel) {
      setPendingEnquiryStatus(`Matched prospect ${pmEnquiryState.matchedProspectLabel}`, 'success');
    } else {
      setPendingEnquiryStatus('');
    }
  }

  function pmNormalizePhoneDigits(value) {
    return value ? String(value).replace(/\D+/g, '') : '';
  }

  function pmResolveContactLabel(values) {
    for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '';
  }

  function pmPickBestCustomerMatch(items, criteria) {
    if (!Array.isArray(items) || !items.length) return null;
    const { email, phoneDigits } = criteria;
    let fallback = null;
    for (const item of items) {
      const itemEmail = item && item.primary_email ? String(item.primary_email).trim().toLowerCase() : '';
      if (email && itemEmail && itemEmail === email) return item;
      if (!fallback && phoneDigits) {
        const itemDigits = pmNormalizePhoneDigits(item && item.primary_phone);
        if (itemDigits && itemDigits === phoneDigits) fallback = item;
      }
    }
    return fallback;
  }

  function pmPickBestProspectMatch(items, criteria) {
    if (!Array.isArray(items) || !items.length) return null;
    const { email, phoneDigits } = criteria;
    let fallback = null;
    for (const item of items) {
      const itemEmail = item && item.email ? String(item.email).trim().toLowerCase() : '';
      if (email && itemEmail && itemEmail === email) return item;
      if (!fallback && phoneDigits) {
        const itemDigits = pmNormalizePhoneDigits(item && item.phone_number);
        if (itemDigits && itemDigits === phoneDigits) fallback = item;
      }
    }
    return fallback;
  }

  function schedulePendingEnquiryAutoDetect(options = {}) {
    const { immediate = false, silent = false, force = false } = options;
    if (pmEnquiryState.autoDetectTimer) {
      clearTimeout(pmEnquiryState.autoDetectTimer);
      pmEnquiryState.autoDetectTimer = null;
    }
    const run = () => {
      pmEnquiryState.autoDetectTimer = null;
      autoDetectPendingEnquiryContact({ silent, force }).catch(() => {});
    };
    if (immediate) run();
    else pmEnquiryState.autoDetectTimer = setTimeout(run, 350);
  }

  async function autoDetectPendingEnquiryContact(options = {}) {
    const dom = resolvePmDetailDom();
    const { silent = false, force = false } = options;
    if (!pmEnquiryState.messageId) return;
    const phoneRaw = dom.enquiryPhoneInput && dom.enquiryPhoneInput.value
      ? dom.enquiryPhoneInput.value.trim() : '';
    const emailRaw = dom.enquiryEmailInput && dom.enquiryEmailInput.value
      ? dom.enquiryEmailInput.value.trim().toLowerCase() : '';
    const phoneDigits = pmNormalizePhoneDigits(phoneRaw);
    const signature = `${emailRaw}|${phoneDigits}`;
    if (!force && pmEnquiryState.lastDetectSignature === signature) return;
    pmEnquiryState.lastDetectSignature = signature;
    if (!phoneRaw && !emailRaw) {
      pmEnquiryState.supportCustomerId = null;
      pmEnquiryState.matchedProspectId = null;
      pmEnquiryState.matchedProspectLabel = '';
      setPendingEnquiryDupWarning('');
      if (!pmEnquiryState.manualCategoryOverride) {
        setPendingEnquiryContactCategory('prospect', { force: true, silent: true });
      }
      if (!silent) setPendingEnquiryStatus('');
      return;
    }
    const detectionKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    pmEnquiryState.currentDetectionKey = detectionKey;
    if (!silent) setPendingEnquiryStatus('Checking contact records…', 'info');
    try {
      const queries = [...new Set([emailRaw, phoneRaw].filter(Boolean))];
      const customerResults = await Promise.all(
        queries.map((term) => {
          const params = new URLSearchParams();
          params.set('limit', '5');
          params.set('search', term);
          return api.fetch(api.routes.crmCustomers(params.toString()), { method: 'GET' })
            .catch((err) => { console.warn('pending_enquiry_customer_lookup_failed', err); return null; });
        }),
      );
      const customerItems = [];
      customerResults.forEach((result) => {
        if (result && Array.isArray(result.items)) {
          result.items.forEach((item) => {
            if (!customerItems.some((existing) => existing.id === item.id)) customerItems.push(item);
          });
        }
      });
      const prospectResults = await Promise.all(
        queries.map((term) =>
          api.fetch(api.routes.crmProspectSearch(term, 5), { method: 'GET' })
            .catch((err) => { console.warn('pending_enquiry_prospect_lookup_failed', err); return null; }),
        ),
      );
      const prospectItems = [];
      prospectResults.forEach((result) => {
        if (Array.isArray(result)) {
          result.forEach((item) => {
            if (!prospectItems.some((existing) => existing.id === item.id)) prospectItems.push(item);
          });
        }
      });
      if (pmEnquiryState.currentDetectionKey !== detectionKey) return;
      const matchCriteria = { email: emailRaw, phoneDigits };
      const customerMatch = pmPickBestCustomerMatch(customerItems, matchCriteria);
      if (customerMatch) {
        pmEnquiryState.supportCustomerId = customerMatch.id;
        pmEnquiryState.matchedProspectId = null;
        pmEnquiryState.matchedProspectLabel = '';
        const label = pmResolveContactLabel([
          customerMatch.display_name, customerMatch.primary_email, customerMatch.primary_phone,
        ]) || 'existing customer';
        pmEnquiryState.selectedCustomerLabel = label;
        if (!pmEnquiryState.manualCategoryOverride) {
          setPendingEnquiryContactCategory('customer', { force: true, silent: true });
        } else {
          renderEnquiryContactType();
        }
        setPendingEnquiryStatus(`Matched customer ${label}`, 'success');
        setPendingEnquiryDupWarning(`This contact is already a customer — ${label}. The enquiry will be linked to them instead of creating a duplicate.`);
        return;
      }
      const prospectMatch = pmPickBestProspectMatch(prospectItems, matchCriteria);
      if (prospectMatch) {
        pmEnquiryState.supportCustomerId = null;
        pmEnquiryState.matchedProspectId = prospectMatch.id;
        pmEnquiryState.matchedProspectLabel = pmResolveContactLabel([
          prospectMatch.full_name, prospectMatch.email, prospectMatch.phone_number,
        ]);
        if (!pmEnquiryState.manualCategoryOverride) {
          setPendingEnquiryContactCategory('prospect', { force: true, silent: true });
        }
        const label = pmEnquiryState.matchedProspectLabel || 'existing prospect';
        setPendingEnquiryStatus(`Matched prospect ${label}`, 'success');
        setPendingEnquiryDupWarning(`This contact is already a prospect — ${label}. The enquiry will be attached to the existing prospect instead of creating a duplicate.`);
        return;
      }
      if (pmEnquiryState.manualCategoryOverride && pmEnquiryState.supportCustomerId) {
        // A customer was deliberately linked via the name search — keep it.
        renderEnquiryContactType();
        return;
      }
      pmEnquiryState.supportCustomerId = null;
      pmEnquiryState.matchedProspectId = null;
      pmEnquiryState.matchedProspectLabel = '';
      pmEnquiryState.selectedCustomerLabel = '';
      setPendingEnquiryDupWarning('');
      // No customer/prospect match -> a prospect is created automatically; the
      // contact-type indicator stays hidden (no decision for the agent to make).
      setPendingEnquiryContactCategory('prospect', { force: true, silent: true });
      if (!silent) {
        setPendingEnquiryStatus('No matching customer found — a new prospect will be created.', 'info');
      } else {
        setPendingEnquiryStatus('');
      }
    } catch (err) {
      if (pmEnquiryState.currentDetectionKey !== detectionKey) return;
      console.warn('pending_enquiry_autodetect_failed', err);
      if (!silent) setPendingEnquiryStatus('Could not check contact records', 'error');
    }
  }

  function resetPendingMessageEnquiryDialog() {
    const dom = resolvePmDetailDom();
    pmEnquiryState.supportCustomerId = null;
    pmEnquiryState.matchedProspectId = null;
    pmEnquiryState.matchedProspectLabel = '';
    pmEnquiryState.manualCategoryOverride = false;
    pmEnquiryState.lastDetectSignature = '';
    pmEnquiryState.currentDetectionKey = null;
    pmEnquiryState.selectedCustomerLabel = '';
    setPendingEnquiryDupWarning('');
    if (pmEnquiryState.autoDetectTimer) {
      clearTimeout(pmEnquiryState.autoDetectTimer);
      pmEnquiryState.autoDetectTimer = null;
    }
    ensurePendingEnquiryTypeOptions();
    clearPendingEnquirySuggestions();
    setPendingEnquiryContactCategory('prospect', { force: true, silent: true });
    if (dom.enquiryNameInput) dom.enquiryNameInput.value = '';
    if (dom.enquiryPhoneInput) dom.enquiryPhoneInput.value = '';
    if (dom.enquiryEmailInput) dom.enquiryEmailInput.value = '';
    if (dom.enquiryCityInput) dom.enquiryCityInput.value = '';
    if (dom.enquiryAddressInput) dom.enquiryAddressInput.value = '';
    if (dom.enquiryChannelSelect) dom.enquiryChannelSelect.value = '';
    if (dom.enquiryTypeSelect) dom.enquiryTypeSelect.value = 'service_quality';
    if (dom.enquiryNotesInput) dom.enquiryNotesInput.value = '';
    setPendingEnquiryStatus('');
  }

  function closePendingMessageEnquiryDialog() {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryDlg) return;
    pmEnquiryState.messageId = null;
    try { dom.enquiryDlg.close(); } catch { dom.enquiryDlg.open = false; }
  }

  async function openPendingMessageEnquiryDialog(record) {
    const dom = resolvePmDetailDom();
    if (!dom.enquiryDlg || !record) {
      // eslint-disable-next-line no-console
      if (!dom.enquiryDlg) console.warn('openPendingMessageEnquiryDialog: #pendingMessageEnquiryDlg missing.');
      return;
    }
    pmEnquiryState.messageId = record.id;
    resetPendingMessageEnquiryDialog();
    ensurePendingEnquiryTypeOptions();
    const contactValue = record.customer_contact || '';
    const isEmailContact = /@/.test(contactValue);
    if (dom.enquiryNameInput) {
      if (record.metadata && record.metadata.customer_name) {
        dom.enquiryNameInput.value = record.metadata.customer_name;
      } else if (record.metadata && record.metadata.from_name) {
        dom.enquiryNameInput.value = record.metadata.from_name;
      }
    }
    if (dom.enquiryPhoneInput && !isEmailContact && contactValue) {
      dom.enquiryPhoneInput.value = contactValue;
    }
    if (dom.enquiryEmailInput && isEmailContact) dom.enquiryEmailInput.value = contactValue;
    if (dom.enquiryNotesInput) {
      const preview = record.preview || record.ai_summary || '';
      dom.enquiryNotesInput.value = preview ? preview.slice(0, 500) : '';
    }
    try {
      const context = await api.fetch(api.routes.adminPendingMessageContactContext(record.id), { method: 'GET' });
      if (context) {
        const contextContact = {
          full_name: context.customer_name || context.prospect_name,
          contact_name: context.customer_name || context.prospect_name,
          phone_number: context.customer_phone || context.prospect_phone,
          contact_phone: context.customer_phone || context.prospect_phone,
          email: context.customer_email || context.prospect_email,
          contact_email: context.customer_email || context.prospect_email,
          city: context.customer_location || context.prospect_city,
          contact_city: context.customer_location || context.prospect_city,
          street_address: context.prospect_address,
          contact_address: context.prospect_address,
        };
        populatePendingEnquiryFields(contextContact, { force: false });
        if (context.support_customer_id) {
          pmEnquiryState.supportCustomerId = context.support_customer_id;
          pmEnquiryState.matchedProspectId = null;
          pmEnquiryState.matchedProspectLabel = '';
          const customerLabel = context.customer_name || context.customer_email || context.customer_phone
            || String(context.support_customer_id);
          pmEnquiryState.selectedCustomerLabel = customerLabel;
          setPendingEnquiryContactCategory('customer', { force: true });
          setPendingEnquiryStatus(customerLabel ? `Matched customer ${customerLabel}` : 'Matched existing customer', 'success');
        } else {
          pmEnquiryState.supportCustomerId = null;
          if (context.prospect_id) {
            pmEnquiryState.matchedProspectId = context.prospect_id;
            pmEnquiryState.matchedProspectLabel = context.prospect_name || context.prospect_email
              || context.prospect_phone || String(context.prospect_id);
          } else if (context.prospect_name || context.prospect_email || context.prospect_phone) {
            pmEnquiryState.matchedProspectId = null;
            pmEnquiryState.matchedProspectLabel = context.prospect_name || context.prospect_email
              || context.prospect_phone || '';
          } else {
            pmEnquiryState.matchedProspectId = null;
            pmEnquiryState.matchedProspectLabel = '';
          }
          if (pmEnquiryState.matchedProspectId || pmEnquiryState.matchedProspectLabel) {
            setPendingEnquiryContactCategory('prospect', { force: true });
            setPendingEnquiryStatus(
              pmEnquiryState.matchedProspectLabel
                ? `Matched prospect ${pmEnquiryState.matchedProspectLabel}`
                : 'Matched existing prospect',
              'success',
            );
          }
        }
      }
      if (dom.enquiryNameInput) {
        pmEnquiryState.selectedCustomerLabel = dom.enquiryNameInput.value
          ? dom.enquiryNameInput.value.trim() : '';
      }
    } catch (err) {
      console.warn('pending_message_contact_context_failed', err);
    }
    callHelper('openDialog', dom.enquiryDlg);
    schedulePendingEnquiryAutoDetect({ immediate: true, silent: true, force: true });
  }

  async function handlePendingMessageEnquirySubmit(event) {
    event.preventDefault();
    const dom = resolvePmDetailDom();
    if (!pmEnquiryState.messageId) {
      notify.showFlash('Message not available', 'error');
      return;
    }
    const prospectName = dom.enquiryNameInput ? dom.enquiryNameInput.value.trim() : '';
    if (!prospectName) {
      notify.showFlash('Enter the prospect name before creating an enquiry.', 'error');
      return;
    }
    const prospectPhone = dom.enquiryPhoneInput && dom.enquiryPhoneInput.value.trim()
      ? dom.enquiryPhoneInput.value.trim() : '';
    const prospectEmail = dom.enquiryEmailInput && dom.enquiryEmailInput.value.trim()
      ? dom.enquiryEmailInput.value.trim() : '';
    const prospectCity = dom.enquiryCityInput && dom.enquiryCityInput.value.trim()
      ? dom.enquiryCityInput.value.trim() : '';
    const prospectAddress = dom.enquiryAddressInput && dom.enquiryAddressInput.value.trim()
      ? dom.enquiryAddressInput.value.trim() : '';
    const enquiryType = dom.enquiryTypeSelect && dom.enquiryTypeSelect.value
      ? dom.enquiryTypeSelect.value : 'other';
    if (!prospectPhone && !prospectEmail) {
      notify.showFlash('Provide a phone number or email for the contact.', 'error');
      return;
    }
    if (dom.enquirySubmitBtn) {
      dom.enquirySubmitBtn.disabled = true;
      dom.enquirySubmitBtn.textContent = 'Creating…';
    }
    const contactCategory = pmEnquiryState.contactCategory || 'prospect';
    const payload = {
      contact_name: prospectName,
      contact_phone: prospectPhone || null,
      contact_email: prospectEmail || null,
      contact_city: prospectCity || null,
      contact_address: prospectAddress || null,
      channel: dom.enquiryChannelSelect && dom.enquiryChannelSelect.value
        ? dom.enquiryChannelSelect.value : null,
      description: dom.enquiryNotesInput && dom.enquiryNotesInput.value.trim()
        ? dom.enquiryNotesInput.value.trim() : null,
      contact_category: contactCategory,
      enquiry_type: enquiryType || 'other',
    };
    if (pmCurrent && String(pmCurrent.id) === String(pmEnquiryState.messageId)) {
      payload.message_body = pmCurrent.preview || pmCurrent.ai_summary || null;
    }
    if (contactCategory === 'customer') {
      if (pmEnquiryState.supportCustomerId) payload.support_customer_id = pmEnquiryState.supportCustomerId;
    } else if (pmEnquiryState.matchedProspectId) {
      payload.prospect_id = pmEnquiryState.matchedProspectId;
    } else {
      payload.new_prospect = {
        full_name: prospectName,
        phone_number: prospectPhone || null,
        email: prospectEmail || null,
        city: prospectCity || null,
        street_address: prospectAddress || null,
      };
    }
    try {
      await api.fetch(api.routes.adminPendingMessageEnquiry(pmEnquiryState.messageId), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      notify.showFlash('Enquiry recorded from intake message.');
      closePendingMessageEnquiryDialog();
      maybeCloseDetailDialog();
      await loadPendingMessages();
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to create enquiry', 'error');
    } finally {
      if (dom.enquirySubmitBtn) {
        dom.enquirySubmitBtn.disabled = false;
        dom.enquirySubmitBtn.textContent = 'Create enquiry';
      }
    }
  }

  // ---- Detail dialog (population, recording, conversion, discard) ----
  function extractRecordingHintFromPayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const recording = payload.recording;
    if (!recording) return null;
    if (typeof recording === 'string') {
      const trimmed = recording.trim();
      return trimmed || null;
    }
    if (typeof recording === 'object') {
      const keys = ['recording', 'name', 'filename', 'file', 'path'];
      for (const key of keys) {
        const value = recording[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    }
    return null;
  }

  function getPendingMessageRecordingHint(record) {
    if (!record || !record.metadata) return null;
    const pbxMeta = record.metadata.pbx;
    if (!pbxMeta || typeof pbxMeta !== 'object') return null;
    const attachmentMeta = pbxMeta.recording_attachment;
    if (attachmentMeta && typeof attachmentMeta === 'object') {
      if (attachmentMeta.error && !(attachmentMeta.storage_key || attachmentMeta.download_url)) return null;
      const stored = attachmentMeta.recording_id || attachmentMeta.filename;
      if (typeof stored === 'string' && stored.trim()) return stored.trim();
    }
    const candidates = [];
    const lastPayload = pbxMeta.last_payload;
    if (lastPayload && typeof lastPayload === 'object') {
      candidates.push(lastPayload);
      if (lastPayload.msg && typeof lastPayload.msg === 'object') candidates.push(lastPayload.msg);
    }
    if (record.raw_payload && typeof record.raw_payload === 'object') {
      candidates.push(record.raw_payload);
      if (record.raw_payload.msg && typeof record.raw_payload.msg === 'object') candidates.push(record.raw_payload.msg);
    }
    for (const payload of candidates) {
      const hint = extractRecordingHintFromPayload(payload);
      if (hint) return hint;
    }
    return null;
  }

  function applyPendingMessageUpdate(record) {
    if (!record) return;
    upsertPendingMessageRecord(record);
    if (pmCurrent && String(pmCurrent.id) === String(record.id)) {
      pmCurrent = record;
      populatePendingMessageDetail(record);
      populatePendingMessageAssignmentOptions();
    }
  }

  async function fetchPendingMessageRecording(id, button) {
    if (!id) return;
    if (button) {
      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.textContent = 'Fetching…';
    }
    try {
      const payload = await api.fetch(api.routes.adminPendingMessageRecording(id), { method: 'POST' });
      if (payload && typeof payload === 'object') applyPendingMessageUpdate(payload);
      notify.showFlash('Call recording attached to intake message.', 'success');
      loadPendingMessages({ keepSelection: true }).catch(() => {});
    } catch (err) {
      const detail = (err && err.body && err.body.detail) || (err && err.detail) || '';
      let message = 'Unable to fetch recording';
      if (detail === 'recording_fetch_failed') {
        message = 'Could not retrieve the recording from PBX. Try again later.';
      } else if (detail === 'recording_unavailable' || detail === 'recording_id_missing'
                 || detail === 'recording_metadata_missing') {
        message = 'No recording is available for this call.';
      } else if (err && err.message) {
        message = err.message;
      }
      notify.showFlash(message, 'error');
      loadPendingMessages({ keepSelection: true }).catch(() => {});
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = button.dataset.originalText || 'Fetch call recording';
        delete button.dataset.originalText;
      }
    }
  }

  function populatePendingMessageDetail(record) {
    const dom = resolvePmDetailDom();
    const escapeHtml = pmHelpers.escapeHtml;
    const formatDateTimeFull = pmHelpers.formatDateTimeFull || ((v) => String(v || ''));
    const looksLikeHtml = pmHelpers.looksLikeHtml || (() => false);
    const renderHtmlOrText = pmHelpers.renderHtmlOrText
      || ((target, value) => { if (target) target.textContent = String(value || ''); });
    const renderEmailFrame = pmHelpers.renderEmailFrame
      || ((target, value) => renderHtmlOrText(target, value, { allowHtml: false }));
    const stripHtml = pmHelpers.stripHtml || ((v) => String(v || ''));
    const formatAttachmentSize = pmHelpers.formatAttachmentSize || ((b) => `${b}B`);
    const formatAiRecommendations = pmHelpers.formatAiRecommendations || (() => '');
    const TICKET_COMPLAINT_OPTIONS = (pmHelpers.getTicketComplaintOptions
      && pmHelpers.getTicketComplaintOptions()) || [];
    const CHANNEL_PREFERRED_CONTACT = (pmHelpers.getChannelPreferredContact
      && pmHelpers.getChannelPreferredContact()) || {};
    const CONTACT_METHOD_OPTIONS = (pmHelpers.getContactMethodOptions
      && pmHelpers.getContactMethodOptions()) || [];

    if (dom.title) dom.title.textContent = `${record.channel?.toUpperCase() || 'Message'} intake`;
    if (dom.meta) {
      const rows = [];
      rows.push(`<div><strong>Channel:</strong> ${escapeHtml(String(record.channel || ''))}</div>`);
      rows.push(`<div><strong>Status:</strong> ${escapeHtml(record.status || '')}</div>`);
      const contactValue = record.customer_contact || '';
      const isEmailContact = /@/.test(contactValue);
      if (contactValue) {
        rows.push(
          `<div><strong>Contact:</strong> ${isEmailContact ? `<a href="mailto:${escapeHtml(contactValue)}">${escapeHtml(contactValue)}</a>` : escapeHtml(contactValue)}</div>`,
        );
      }
      if (record.source) rows.push(`<div><strong>Source ID:</strong> ${escapeHtml(record.source)}</div>`);
      if (record.created_at) rows.push(`<div><strong>Received:</strong> ${escapeHtml(formatDateTimeFull(record.created_at))}</div>`);
      if (record.metadata && record.metadata.requires_callback) {
        rows.push('<div class="pending-meta-alert">Callback required before converting.</div>');
      }
      const correlation = record.metadata && record.metadata.correlation;
      if (correlation && String(correlation.status || '').toLowerCase() === 'unmatched') {
        const reason = correlation.reason ? ` (${escapeHtml(String(correlation.reason))})` : '';
        rows.push(`<div class="pending-meta-alert">Unmatched email reply${reason}.</div>`);
      }
      dom.meta.innerHTML = rows.join('');
    }
    const recommendationText = formatAiRecommendations(record.ai_recommendations);
    if (dom.preview) {
      dom.preview.innerHTML = '<h4 style="margin:0 0 4px;">Message preview</h4>';
      const previewBody = document.createElement('div');
      previewBody.className = 'message-preview-body';
      const rp = record.raw_payload || {};
      // Prefer the rich HTML part; render it isolated in a sandboxed iframe so its
      // CSS applies without leaking into the admin UI (and scripts never run).
      const richHtml = rp.html_body || rp.html || '';
      const plainText = record.preview || rp.plain_body || rp.text || '';
      if (looksLikeHtml(richHtml)) {
        renderEmailFrame(previewBody, richHtml, { fallbackText: plainText });
      } else if (looksLikeHtml(plainText)) {
        // Some channels put HTML in `preview` itself — still sandbox it.
        renderEmailFrame(previewBody, plainText, { fallbackText: plainText });
      } else {
        renderHtmlOrText(previewBody, plainText || '(no message preview)', { fallback: '(no message preview)', allowHtml: false });
      }
      dom.preview.appendChild(previewBody);
    }
    if (dom.attachments) {
      const attachments = Array.isArray(record.attachments) ? record.attachments : [];
      const hasAudioAttachment = attachments.some((att) => {
        if (!att || typeof att !== 'object') return false;
        const mime = String(att.mime_type || '').toLowerCase();
        if (mime.startsWith('audio/')) return (att.download_url || att.url || att.presigned_url || att.data_url);
        if (att.type && String(att.type).toLowerCase() === 'audio') {
          return att.download_url || att.url || att.presigned_url || att.data_url;
        }
        return false;
      });
      const recordingHint = getPendingMessageRecordingHint(record);
      let attachmentsHtml = '';
      if (attachments.length) {
        const list = attachments.map((att) => {
          if (!att || typeof att !== 'object') return '';
          const name = att.name || att.filename || att.key || 'attachment';
          const href = att.download_url || att.url || att.presigned_url || att.data_url || '';
          const mime = String(att.mime_type || '').toLowerCase();
          const sizeLabel = att.size_bytes ? ` (${formatAttachmentSize(att.size_bytes)})` : '';
          const escapedName = escapeHtml(String(name));
          const escapedHref = escapeHtml(String(href));
          const pbxInfo = att.pbx_recording && att.pbx_recording.recording_id
            ? ` data-recording-id="${escapeHtml(String(att.pbx_recording.recording_id))}"` : '';
          if (href && (mime.startsWith('audio/') || (att.type && String(att.type).toLowerCase() === 'audio'))) {
            return `
              <li class="pending-attachment-audio"${pbxInfo}>
                <audio controls preload="metadata" src="${escapedHref}"></audio>
                <div class="pending-attachment-meta">
                  <a href="${escapedHref}" target="_blank" rel="noopener">${escapedName}</a>${sizeLabel}
                </div>
              </li>`;
          }
          const linkAttrs = href ? ` href="${escapedHref}" target="_blank" rel="noopener"` : '';
          return `<li${pbxInfo}><a${linkAttrs}>${escapedName}</a>${sizeLabel}</li>`;
        }).join('');
        attachmentsHtml = `<h4 style="margin:0 0 4px;">Attachments</h4><ul>${list}</ul>`;
      }
      if (recordingHint && !hasAudioAttachment) {
        attachmentsHtml += `
          <div class="pending-recording-fetch">
            <button type="button" class="btn" data-pending-action="fetch-recording" data-id="${escapeHtml(String(record.id))}">
              Fetch call recording
            </button>
          </div>`;
      }
      dom.attachments.innerHTML = attachmentsHtml;
    }
    if (dom.subjectInput) {
      const channelName = (record.channel || '').toLowerCase();
      if (channelName === 'pbx') {
        const timestamp = record.created_at ? formatDateTimeFull(record.created_at) : '';
        const subjectText = timestamp ? `Incoming call: ${timestamp}` : 'Incoming call';
        dom.subjectInput.value = subjectText;
      } else {
        dom.subjectInput.value = record.subject || record.ai_summary || record.preview || '';
      }
    }
    const metadataEmail = (record.metadata && (record.metadata.from_email || record.metadata.email)) || '';
    const contactValue = record.customer_contact || '';
    const isEmailContact = /@/.test(contactValue);
    if (dom.contactInput) dom.contactInput.value = isEmailContact ? '' : contactValue;
    if (dom.emailInput) dom.emailInput.value = isEmailContact ? contactValue : (record.customer_email || metadataEmail || '');
    if (dom.nameInput) dom.nameInput.value = (record.metadata && record.metadata.customer_name) || '';
    const metadataLocation = (record.metadata && record.metadata.location) || '';
    if (dom.locationInput) dom.locationInput.value = metadataLocation;
    const metadataPreferredChannel = (record.metadata && (record.metadata.preferred_channel || record.metadata.primary_contact_method)) || '';
    const channelPreferred = CHANNEL_PREFERRED_CONTACT[(record.channel || '').toLowerCase()] || '';
    if (dom.preferredChannelSelect) {
      const presetPreferred = metadataPreferredChannel || channelPreferred || '';
      dom.preferredChannelSelect.value = CONTACT_METHOD_OPTIONS.includes(presetPreferred) ? presetPreferred : '';
    }
    if (dom.descriptionInput) {
      const rawPreviewText = record.preview || (record.raw_payload && (record.raw_payload.text || record.raw_payload.html)) || '';
      let descriptionSource = record.ai_summary || stripHtml(rawPreviewText) || '';
      const recBlock = recommendationText ? `AI recommendations:\n${recommendationText}` : '';
      if (recBlock) {
        descriptionSource = descriptionSource ? `${descriptionSource}\n\n${recBlock}` : recBlock;
      }
      dom.descriptionInput.value = descriptionSource || '';
    }
    if (dom.complaintSelect && !dom.complaintSelect.options.length) {
      dom.complaintSelect.innerHTML = '<option value="" disabled selected>Select complaint type…</option>';
      [...TICKET_COMPLAINT_OPTIONS]
        .slice()
        .sort((a, b) => a.replace(/_/g, ' ').localeCompare(b.replace(/_/g, ' ')))
        .forEach((value) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value.replace(/_/g, ' ');
          dom.complaintSelect.appendChild(opt);
        });
    }
    if (dom.complaintSelect) {
      const suggestedComplaint = (record.metadata && record.metadata.complaint_type)
        || (record.ai_recommendations && record.ai_recommendations.complaint_type) || '';
      dom.complaintSelect.value = suggestedComplaint || '';
    }
    if (dom.prioritySelect) dom.prioritySelect.value = 'p3_high';
    if (dom.form) dom.form.dataset.messageId = String(record.id);
    if (dom.form) dom.form.dataset.requiresCallback = pendingMessageRequiresCallback(record) ? '1' : '0';
    resetPendingMessageDirty();
    if (dom.callbackField) dom.callbackField.hidden = !pendingMessageRequiresCallback(record);
    if (dom.callbackConfirmed) dom.callbackConfirmed.checked = false;
    if (dom.assignRoleSelect) dom.assignRoleSelect.value = '';
    if (dom.assignUserSelect) dom.assignUserSelect.value = '';
    populatePendingMessageAssignmentOptions();
    updatePendingMessageConvertState(record);
    if (dom.discardBtn) dom.discardBtn.disabled = record.status !== 'pending';
    if (dom.followupBtn) dom.followupBtn.disabled = record.status !== 'pending';
  }

  async function prefillPendingMessageContactContext(record) {
    const dom = resolvePmDetailDom();
    if (!record) return;
    if ((record.channel || '').toLowerCase() !== 'pbx') return;
    const contactInput = dom.contactInput;
    if (!contactInput) return;
    const contactValue = contactInput.value ? contactInput.value.trim() : '';
    if (!contactValue || contactValue.includes('@')) return;
    const digits = contactValue.replace(/\D+/g, '');
    if (digits.length < 5) return;
    const hasName = dom.nameInput && dom.nameInput.value.trim().length > 0;
    const hasLocation = dom.locationInput && dom.locationInput.value.trim().length > 0;
    if (hasName && hasLocation) return;
    try {
      const context = await api.fetch(api.routes.adminPendingMessageContactContext(record.id), { method: 'GET' });
      if (!context) return;
      let updated = false;
      if (dom.nameInput && !hasName && context.customer_name) {
        dom.nameInput.value = context.customer_name; updated = true;
      }
      if (dom.locationInput && !hasLocation && context.customer_location) {
        dom.locationInput.value = context.customer_location; updated = true;
      }
      if (updated) updatePendingMessageConvertState(record);
    } catch (err) {
      console.warn('pending_message_contact_context_failed', err);
    }
  }

  async function openPendingMessageDialog(id, options = {}) {
    const dom = resolvePmDetailDom();
    if (!dom.dlg) {
      // eslint-disable-next-line no-console
      console.warn('openPendingMessageDialog: #pendingMessageDlg missing.');
      return;
    }
    const record = views.tickets.pendingMessages.list.getRecordById(id);
    if (!record) {
      notify.showFlash('Message not found', 'error');
      return;
    }
    views.tickets.pendingMessages.list.setSelectedId(record.id);
    pmCurrent = record;
    populatePendingMessageDetail(record);
    populatePendingMessageAssignmentOptions();
    if (typeof pmHelpers.ensureTechnicianUsersLoaded === 'function') {
      pmHelpers.ensureTechnicianUsersLoaded()
        .then(() => populatePendingMessageAssignmentOptions()).catch(() => {});
    }
    if (typeof pmHelpers.ensureSupervisorUsersLoaded === 'function') {
      pmHelpers.ensureSupervisorUsersLoaded()
        .then(() => populatePendingMessageAssignmentOptions()).catch(() => {});
    }
    callHelper('openDialog', dom.dlg);
    prefillPendingMessageContactContext(record).catch(() => {});
    if (!options.skipFocus && dom.subjectInput) {
      setTimeout(() => dom.subjectInput.focus(), 40);
    }
  }

  async function submitPendingMessageConversion(event) {
    event.preventDefault();
    const dom = resolvePmDetailDom();
    if (!dom.form || !pmCurrent) return;
    if (dom.form.dataset.pending === '1') return;
    const id = pmCurrent.id;
    const subject = dom.subjectInput ? dom.subjectInput.value.trim() : '';
    const description = dom.descriptionInput ? dom.descriptionInput.value.trim() : '';
    const phone = dom.contactInput ? dom.contactInput.value.trim() : '';
    const name = dom.nameInput ? dom.nameInput.value.trim() : '';
    const location = dom.locationInput ? dom.locationInput.value.trim() : '';
    const complaint = dom.complaintSelect ? dom.complaintSelect.value : '';
    const preferredChannel = dom.preferredChannelSelect ? dom.preferredChannelSelect.value : '';
    const assignRole = dom.assignRoleSelect ? dom.assignRoleSelect.value : '';
    const assignUserValue = dom.assignUserSelect ? dom.assignUserSelect.value : '';
    const missingFields = [];
    if (!subject) missingFields.push('subject');
    if (!description) missingFields.push('description');
    if (!name) missingFields.push('customer name');
    if (!phone) missingFields.push('customer phone number');
    if (!location) missingFields.push('customer location');
    if (!complaint) missingFields.push('complaint type');
    if (!preferredChannel) missingFields.push('preferred contact channel');
    if (missingFields.length) {
      const last = missingFields.pop();
      const message = missingFields.length ? `${missingFields.join(', ')} and ${last}` : last;
      notify.showFlash(`Enter the ${message} before converting this message.`, 'error');
      return;
    }
    if (pendingMessageRequiresCallback(pmCurrent)
        && dom.callbackConfirmed && !dom.callbackConfirmed.checked) {
      notify.showFlash('Confirm that you have returned the call before converting this message.', 'error');
      return;
    }
    const email = dom.emailInput ? dom.emailInput.value.trim() : '';
    const payload = {
      subject,
      description,
      priority: dom.prioritySelect ? dom.prioritySelect.value : 'p3_high',
      complaint_type: complaint || null,
      customer_phone: phone || null,
      customer_email: email || null,
      customer_display_name: name || null,
      location,
      preferred_channel: preferredChannel || null,
    };
    if (assignRole) payload.assignee_role = assignRole;
    if (assignUserValue) {
      const parsedAssignee = parseInt(assignUserValue, 10);
      if (!Number.isNaN(parsedAssignee)) payload.assignee_user_id = parsedAssignee;
    }
    dom.form.dataset.pending = '1';
    if (dom.convertBtn) {
      dom.convertBtn.disabled = true;
      dom.convertBtn.textContent = 'Converting…';
    }
    try {
      const ticket = await api.fetch(api.routes.adminPendingMessageConvert(id), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      callHelper('closeTicketConflictDialog');
      if (dom.dlg) {
        try { dom.dlg.close(); } catch { dom.dlg.open = false; }
      }
      notify.showFlash('Ticket created from message');
      await loadPendingMessages();
      const reload = pmHelpers.reloadTickets;
      if (typeof reload === 'function') {
        reload({ selectId: ticket && ticket.id ? ticket.id : undefined }).catch(() => {});
      }
    } catch (err) {
      const detail = err && err.body && err.body.detail;
      const retryConvert = async (connectionId) => {
        const resolvedId = Number.parseInt(String(connectionId), 10);
        if (Number.isNaN(resolvedId)) {
          throw new Error('Invalid connection selection. Please choose a different connection.');
        }
        const conflictSupportCustomerId = pmHelpers.getTicketConflictSupportCustomerId
          ? pmHelpers.getTicketConflictSupportCustomerId() : null;
        const connectionPayload = {
          ...payload,
          affected_connection_id: resolvedId,
        };
        if (conflictSupportCustomerId) connectionPayload.support_customer_id = conflictSupportCustomerId;
        if (dom.convertBtn) {
          dom.convertBtn.disabled = true;
          dom.convertBtn.textContent = 'Converting…';
        }
        dom.form.dataset.pending = '1';
        try {
          const ticket = await api.fetch(api.routes.adminPendingMessageConvert(id), {
            method: 'POST',
            body: JSON.stringify(connectionPayload),
          });
          callHelper('closeTicketConflictDialog');
          if (dom.dlg) {
            try { dom.dlg.close(); } catch { dom.dlg.open = false; }
          }
          notify.showFlash('Ticket created from message');
          await loadPendingMessages();
          callHelper('openTicketDetailAfterCreation', ticket && ticket.id, ticket);
        } catch (retryErr) {
          const retryDetail = retryErr && retryErr.body && retryErr.body.detail;
          if (retryDetail && retryDetail.code === 'connection_selection_required') {
            callHelper('openTicketConflictDialog', {
              source: 'pending_message',
              detail: retryDetail,
              pendingMessageId: pmCurrent ? pmCurrent.id : null,
              resolve: retryConvert,
            });
            return;
          }
          if (retryDetail && retryDetail.code === 'active_ticket_exists') {
            callHelper('openTicketConflictDialog', {
              source: 'pending_message',
              detail: retryDetail,
              pendingMessageId: pmCurrent ? pmCurrent.id : null,
            });
            return;
          }
          notify.showFlash(retryErr && retryErr.message ? retryErr.message : 'Failed to create ticket', 'error');
        } finally {
          dom.form.dataset.pending = '0';
          if (dom.convertBtn) {
            dom.convertBtn.disabled = false;
            dom.convertBtn.textContent = 'Convert to ticket';
          }
        }
      };
      if (detail && detail.code === 'connection_selection_required') {
        callHelper('openTicketConflictDialog', {
          source: 'pending_message',
          detail,
          pendingMessageId: pmCurrent ? pmCurrent.id : null,
          resolve: retryConvert,
        });
      } else if (detail && detail.code === 'active_ticket_exists') {
        callHelper('openTicketConflictDialog', {
          source: 'pending_message',
          detail,
          pendingMessageId: pmCurrent ? pmCurrent.id : null,
        });
      } else {
        let message = err && err.message ? err.message : 'Failed to convert message';
        if (/porta_customer_id/i.test(message)) {
          message = 'We could not match this customer. Confirm the phone number or use Identify Customer to link Porta before converting.';
        }
        notify.showFlash(message, 'error');
      }
    } finally {
      dom.form.dataset.pending = '0';
      if (dom.convertBtn) {
        dom.convertBtn.disabled = false;
        dom.convertBtn.textContent = 'Convert to ticket';
      }
    }
  }

  function buildPendingMessageDiscardUrl(id, reason, note) {
    const params = new URLSearchParams();
    if (reason) params.set('reason', reason);
    if (note) params.set('note', note);
    const qs = params.toString();
    return `${api.routes.adminPendingMessage(id)}${qs ? `?${qs}` : ''}`;
  }

  function openPendingMessageDiscardDialog() {
    const dom = resolvePmDetailDom();
    if (!pmCurrent) {
      notify.showFlash('No message selected', 'error');
      return;
    }
    if (!dom.discardDlg || !dom.discardReason) {
      handlePendingMessageDiscardConfirm().catch(() => {});
      return;
    }
    dom.discardReason.value = '';
    if (dom.discardNote) dom.discardNote.value = '';
    if (dom.discardConfirm) dom.discardConfirm.disabled = true;
    dom.discardDlg.showModal();
  }

  async function handlePendingMessageDiscardConfirm() {
    const dom = resolvePmDetailDom();
    if (!pmCurrent) {
      notify.showFlash('No message selected', 'error');
      return;
    }
    const id = pmCurrent.id;
    const reason = dom.discardReason ? dom.discardReason.value : '';
    const note = dom.discardNote ? dom.discardNote.value : '';
    if (dom.discardReason && !reason) {
      notify.showFlash('Select a discard reason before continuing.', 'error');
      return;
    }
    if (dom.discardConfirm) {
      dom.discardConfirm.disabled = true;
      dom.discardConfirm.textContent = 'Discarding…';
    }
    try {
      const url = buildPendingMessageDiscardUrl(id, reason, note);
      await api.fetch(url, { method: 'DELETE' });
      notify.showFlash('Message discarded');
      if (dom.dlg) { try { dom.dlg.close(); } catch { dom.dlg.open = false; } }
      if (dom.discardDlg) { try { dom.discardDlg.close(); } catch { dom.discardDlg.open = false; } }
      await loadPendingMessages();
      loadPmDashboard({ force: true }).catch(() => {});
    } catch (err) {
      notify.showFlash(err.message || 'Failed to discard message', 'error');
    } finally {
      if (dom.discardConfirm) {
        dom.discardConfirm.disabled = false;
        dom.discardConfirm.textContent = 'Discard';
      }
    }
  }

  function handlePendingMessageDialogClick(event) {
    const btn = event.target.closest('[data-pending-action]');
    if (!btn) return;
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    const action = btn.dataset.pendingAction;
    if (action === 'fetch-recording') {
      const id = btn.dataset.id || (pmCurrent && pmCurrent.id);
      if (!id) return;
      fetchPendingMessageRecording(id, btn).catch(() => {});
    }
  }

  // ---- Toast helper for new pending messages from the WS dispatcher ----
  function queuePendingMessageNotification({ messageId, channel, subject, customerContact }) {
    const key = messageId ? `pending:${messageId}` : `pending:${Date.now()}`;
    const now = Date.now();
    const lastShown = pmDetailNotificationRecent.get(key) || 0;
    if (now - lastShown < 2500) return;
    pmDetailNotificationRecent.set(key, now);
    const channelLabel = channel ? channel.toUpperCase() : 'PENDING';
    const trimmedSubject = typeof subject === 'string' ? subject.trim() : '';
    const trimmedContact = typeof customerContact === 'string' ? customerContact.trim() : '';
    const body = trimmedSubject || trimmedContact || `New ${channelLabel} message received`;
    notify.pushToast({
      id: `${key}-${now}`,
      title: `New ${channelLabel} message`,
      message: body,
      icon: '📨',
      kind: 'info',
      autoCloseMs: 20000,
      onClick: () => {
        setPmIndicator(false);
        callHelper('switchView', 'pending_messages');
      },
      data: { messageId },
    });
  }

  function mountPmDetail() {
    if (pmDetailMounted) return;
    const dom = resolvePmDetailDom();
    if (dom.dlg) {
      dom.dlg.addEventListener('click', handlePendingMessageDialogClick);
    }
    if (dom.form) {
      dom.form.addEventListener('submit', (event) => {
        submitPendingMessageConversion(event).catch(() => {});
      });
    }
    if (dom.discardBtn) {
      dom.discardBtn.addEventListener('click', openPendingMessageDiscardDialog);
    }
    if (dom.discardForm) {
      dom.discardForm.addEventListener('submit', (event) => {
        event.preventDefault();
        handlePendingMessageDiscardConfirm().catch(() => {});
      });
    }
    if (dom.discardReason) {
      dom.discardReason.addEventListener('change', () => {
        if (dom.discardConfirm) dom.discardConfirm.disabled = !dom.discardReason.value;
      });
    }
    if (dom.callbackConfirmed) {
      dom.callbackConfirmed.addEventListener('change', () => {
        markPendingMessageDirty();
        updatePendingMessageConvertState();
      });
    }
    const convertInputs = [
      dom.subjectInput, dom.descriptionInput, dom.nameInput,
      dom.contactInput, dom.locationInput, dom.complaintSelect,
      dom.preferredChannelSelect,
    ];
    convertInputs.forEach((input) => {
      if (!input) return;
      const eventName = input.tagName === 'SELECT' ? 'change' : 'input';
      input.addEventListener(eventName, () => {
        markPendingMessageDirty();
        updatePendingMessageConvertState();
      });
    });
    if (dom.assignRoleSelect) {
      dom.assignRoleSelect.addEventListener('change', () => markPendingMessageDirty());
    }
    if (dom.assignUserSelect) {
      dom.assignUserSelect.addEventListener('change', () => markPendingMessageDirty());
    }
    // Enquiry dialog wiring
    if (dom.enquiryForm) {
      dom.enquiryForm.addEventListener('submit', (event) => {
        handlePendingMessageEnquirySubmit(event).catch(() => {});
      });
    }
    if (dom.enquiryDlg) {
      dom.enquiryDlg.addEventListener('close', () => {
        pmEnquiryState.messageId = null;
        clearPendingEnquirySuggestions();
      });
    }
    if (dom.enquiryNameInput) {
      dom.enquiryNameInput.addEventListener('input', handlePendingEnquiryNameInput);
      dom.enquiryNameInput.addEventListener('keydown', handlePendingEnquiryNameKeyDown);
      dom.enquiryNameInput.addEventListener('blur', () => {
        setTimeout(() => hidePendingEnquirySuggestions(), 150);
      });
    }
    if (dom.enquiryNameSuggestions) {
      dom.enquiryNameSuggestions.addEventListener('mousedown', (event) => {
        const btn = event.target.closest('[data-index]');
        if (!btn) return;
        event.preventDefault();
        const idx = Number(btn.dataset.index);
        if (Number.isFinite(idx)) selectPendingEnquirySuggestion(idx);
      });
    }
    if (dom.enquiryPhoneInput) {
      dom.enquiryPhoneInput.addEventListener('input', () => schedulePendingEnquiryAutoDetect());
    }
    if (dom.enquiryEmailInput) {
      dom.enquiryEmailInput.addEventListener('input', () => schedulePendingEnquiryAutoDetect());
    }
    // The "Contact type" control is a read-only status indicator now (auto-set
    // from the customer lookup / name search), so it has no manual change handler.
    if (dom.enquiryBtn) {
      dom.enquiryBtn.addEventListener('click', () => {
        if (pmCurrent) openPendingMessageEnquiryDialog(pmCurrent).catch(() => {});
        else notify.showFlash('Select a pending message first.', 'error');
      });
    }
    pmDetailMounted = true;
  }

  views.tickets.pendingMessages.detail = {
    mount: mountPmDetail,
    open: (id, options) => openPendingMessageDialog(id, options),
    close: () => maybeCloseDetailDialog(),
    queueIncomingNotification: queuePendingMessageNotification,
    // Read/write hooks the list module uses through pmHelpers (so the
    // forward/followup/list code stays decoupled from this file's specific
    // private state).
    getCurrent: () => pmCurrent,
    setCurrent: (record) => { pmCurrent = record; },
    populateDetail: populatePendingMessageDetail,
    populateAssignmentOptions: populatePendingMessageAssignmentOptions,
    updateConvertState: updatePendingMessageConvertState,
    fetchRecording: (id, btn) => fetchPendingMessageRecording(id, btn),
    isFormDirty: isPendingMessageFormDirty,
  };

  // ---- Customer-care supervisor board (Phase 2.1.c) --------------------
  // Owns the customer-care kanban view (`#customerBoardView`):
  //   - The lane strip with drag/drop reassignment (`#customerBoardLanes`).
  //   - The daily-trend mini-dashboard at the top of the view
  //     (`#customerBoardDailyTrend` / `…Legend` / `…Note`).
  //   - The refresh button + "stale board" scheduler invoked from WS deltas.
  //
  // What stays in admin.js:
  //   - `hasCustomerBoardAccess()` (role-gate uses ADMIN_ME), injected as
  //     `cbHelpers.hasAccess`.
  //   - Shared formatters used by both ticket boards
  //     (`formatRelativeTime`, `formatDateTimeShort`,
  //     `formatLaneCollapsedLabel`, `formatTicketPriority`,
  //     `formatTicketStatus`, `formatTicketComplaint`, `formatSlaStateLabel`,
  //     `formatSlaDueText`, `buildTechnicianInitials`,
  //     `formatResolutionDayLabel`).
  //   - Shared drag UX (`handleBoardLaneDragOver`/`Leave` + the document-
  //     level `handleBoardDragAutoScroll`).
  //   - Cross-cluster navigation (`openTicketDetailFromBoard`) and the
  //     supervisor reassignment dialog (`openBoardAssignDialog`) which are
  //     part of the main ticket board / detail panel (2.1.d).
  // Once 2.1.d lands those helpers move into this module too.
  const CUSTOMER_BOARD_COLLAPSE_STORAGE_KEY = 'inet_customer_board_collapsed_v1';

  function loadCustomerBoardCollapsed() {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(CUSTOMER_BOARD_COLLAPSE_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(
        parsed
          .map((key) => (key == null ? '' : String(key).trim()))
          .filter(Boolean),
      );
    } catch (err) {
      console.warn('Failed to read customer board lane preferences', err);
      return new Set();
    }
  }

  function persistCustomerBoardCollapsed() {
    if (typeof localStorage === 'undefined') return;
    try {
      const lanes = customerBoardState.collapsedLanes ? Array.from(customerBoardState.collapsedLanes) : [];
      localStorage.setItem(CUSTOMER_BOARD_COLLAPSE_STORAGE_KEY, JSON.stringify(lanes));
    } catch (err) {
      console.warn('Failed to persist customer board lane preferences', err);
    }
  }

  let customerBoardInitialized = false;
  let customerBoardMounted = false;
  const customerBoardState = {
    lanes: [],
    loading: false,
    initialized: false,
    stale: false,
    generatedAt: null,
    dragTicketId: null,
    dragSourceLane: null,
    collapsedLanes: loadCustomerBoardCollapsed(),
    // Supervisor-parked tickets are held back unless the board's toggle asks for
    // them; the count is reported by the board either way.
    showParked: false,
    parkedCount: 0,
  };
  const customerBoardParkedState = {
    loading: false,
    pending: 0,
    over5: 0,
    over10: 0,
    oldestParkedAt: null,
    error: null,
  };
  const customerBoardDailyState = {
    loading: false,
    agents: [],
    items: [],
    timezone: 'UTC',
    generatedAt: null,
    error: null,
  };
  let customerBoardRefreshTimer = null;
  let customerBoardDom = null;

  // Phase 3.2.c: cbHelpers is now populated from imports + local
  // references. `formatSlaStateLabel` / `formatSlaDueText` are no-ops
  // (they were never defined in admin.js — latent dead code).
  // `buildTechnicianInitials` is inlined; the canonical version lives
  // in technicians.js but tickets.js doesn't import from sibling views
  // (3.2.d will resolve cross-view imports).
  const cbHelpers = {
    hasAccess: () => hasCustomerBoardAccess(),
    formatRelativeTime,
    formatDateTimeShort,
    formatLaneCollapsedLabel,
    formatTicketPriority,
    formatTicketStatus,
    formatTicketComplaint,
    formatSlaStateLabel: (state) => (state ? String(state) : 'SLA'),
    formatSlaDueText: () => '',
    buildTechnicianInitials: (label) => {
      const raw = String(label || '').trim();
      if (!raw) return '?';
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    formatResolutionDayLabel,
    handleBoardLaneDragOver: (event) => {
      event.preventDefault();
      const laneBody = event.currentTarget;
      laneBody.classList.add('dragover');
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    },
    handleBoardLaneDragLeave: (event) => {
      event.currentTarget.classList.remove('dragover');
    },
    openBoardAssignDialog: (context, target) => {
      const tb = views.tickets && views.tickets.technicianBoard;
      if (tb) tb.openAssignDialog(context, target);
    },
    openTicketDetailFromBoard: (id, lane, opts) => openTicketDetailFromBoard(id, lane, opts),
  };

  function resolveCustomerBoardDom() {
    if (customerBoardDom) return customerBoardDom;
    customerBoardDom = {
      view: document.getElementById('customerBoardView'),
      board: document.getElementById('customerBoard'),
      status: document.getElementById('customerBoardStatus'),
      refreshBtn: document.getElementById('customerBoardRefreshBtn'),
      scroll: document.getElementById('customerBoardScroll'),
      lanes: document.getElementById('customerBoardLanes'),
      empty: document.getElementById('customerBoardEmpty'),
      dailyTrend: document.getElementById('customerBoardDailyTrend'),
      dailyNote: document.getElementById('customerBoardDailyNote'),
      dailyLegend: document.getElementById('customerBoardDailyLegend'),
      dailyStrip: document.getElementById('customerBoardDailyStrip'),
      showParked: document.getElementById('customerBoardShowParked'),
      parkedBadge: document.getElementById('customerBoardParkedBadge'),
      parkedCard: document.getElementById('customerBoardParkedCard'),
      parkedTotal: document.getElementById('parkedCardTotal'),
      parkedPending: document.getElementById('parkedCardPending'),
      parkedOver5: document.getElementById('parkedCardOver5'),
      parkedOver10: document.getElementById('parkedCardOver10'),
      parkedNote: document.getElementById('parkedCardNote'),
    };
    return customerBoardDom;
  }

  function renderCustomerBoardParkedCard() {
    const dom = resolveCustomerBoardDom();
    if (!dom.parkedCard) return;
    const st = customerBoardParkedState;
    const set = (el, value) => { if (el) el.textContent = value; };
    if (st.error) {
      set(dom.parkedTotal, '—');
      set(dom.parkedPending, '—');
      set(dom.parkedOver5, '—');
      set(dom.parkedOver10, '—');
      set(dom.parkedNote, 'Failed to load');
      return;
    }
    set(dom.parkedTotal, String(st.pending));
    set(dom.parkedPending, String(st.pending));
    set(dom.parkedOver5, String(st.over5));
    set(dom.parkedOver10, String(st.over10));
    if (!st.pending) {
      set(dom.parkedNote, 'Nothing parked.');
    } else if (st.oldestParkedAt) {
      const days = Math.floor((Date.now() - new Date(st.oldestParkedAt).getTime()) / 86400000);
      set(dom.parkedNote, `Oldest parked ${days} day${days === 1 ? '' : 's'} ago.`);
    } else {
      set(dom.parkedNote, '');
    }
    // The toggle badge and the card read from the same numbers, so they cannot disagree.
    if (dom.parkedBadge) {
      dom.parkedBadge.textContent = String(st.pending);
      dom.parkedBadge.hidden = !st.pending;
    }
  }

  async function loadCustomerBoardParkedSummary() {
    const dom = resolveCustomerBoardDom();
    if (!dom.parkedCard || !cbHelpers.hasAccess() || customerBoardParkedState.loading) return;
    customerBoardParkedState.loading = true;
    try {
      const data = await api.fetch(api.routes.adminCustomerBoardParkedSummary, { method: 'GET' });
      customerBoardParkedState.pending = Number(data?.pending || 0);
      customerBoardParkedState.over5 = Number(data?.older_than_5_days || 0);
      customerBoardParkedState.over10 = Number(data?.older_than_10_days || 0);
      customerBoardParkedState.oldestParkedAt = data?.oldest_parked_at || null;
      customerBoardParkedState.error = null;
    } catch (err) {
      customerBoardParkedState.error = err;
    } finally {
      customerBoardParkedState.loading = false;
      renderCustomerBoardParkedCard();
    }
  }

  function renderCustomerBoardDailyDashboard(items = []) {
    const dom = resolveCustomerBoardDom();
    if (!dom.dailyTrend) return;
    dom.dailyTrend.innerHTML = '';
    if (dom.dailyLegend) dom.dailyLegend.innerHTML = '';
    const agents = Array.isArray(customerBoardDailyState.agents) ? customerBoardDailyState.agents : [];
    if (!Array.isArray(items) || !items.length || !agents.length) {
      dom.dailyTrend.innerHTML = '<div style="color:var(--sub); font-size:12px;">No data</div>';
      return;
    }
    const maxTotal = Math.max(
      ...items.flatMap((entry) => {
        const rows = Array.isArray(entry.breakdown) ? entry.breakdown : [];
        return rows.map((row) => Number(row.opened || 0) + Number(row.assigned || 0) + Number(row.closed || 0));
      }),
      1,
    );
    const legendItems = [
      { key: 'opened', label: 'Opened', color: '#3b82f6' },
      { key: 'assigned', label: 'Assigned', color: '#f59e0b' },
      { key: 'closed', label: 'Closed', color: '#22c55e' },
    ];
    if (dom.dailyLegend) {
      legendItems.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'tech-resolution-legend-item';
        const swatch = document.createElement('span');
        swatch.className = 'tech-resolution-legend-swatch';
        swatch.style.background = item.color;
        const text = document.createElement('span');
        text.textContent = item.label;
        row.appendChild(swatch);
        row.appendChild(text);
        dom.dailyLegend.appendChild(row);
      });
    }
    items.forEach((entry) => {
      const dayEl = document.createElement('div');
      dayEl.className = 'tech-resolution-day';
      const bars = document.createElement('div');
      bars.className = 'tech-resolution-day-bars';
      const breakdownRows = Array.isArray(entry.breakdown) ? entry.breakdown : [];
      const breakdownMap = new Map();
      breakdownRows.forEach((row) => {
        if (row && row.admin_user_id != null) breakdownMap.set(String(row.admin_user_id), row);
      });
      let dayTotal = 0;
      agents.forEach((agent) => {
        const row = breakdownMap.get(String(agent.admin_user_id)) || {};
        const opened = Number(row.opened || 0);
        const assigned = Number(row.assigned || 0);
        const closed = Number(row.closed || 0);
        const total = opened + assigned + closed;
        dayTotal += total;
        const techEl = document.createElement('div');
        techEl.className = 'tech-resolution-tech';
        const countEl = document.createElement('div');
        countEl.className = 'tech-resolution-tech-count';
        countEl.textContent = String(total);
        const stack = document.createElement('div');
        stack.className = 'customer-board-daily-stack';
        const scaledHeight = Math.max(total ? 8 : 3, Math.round(56 * (total / maxTotal)));
        stack.style.height = `${scaledHeight}px`;
        [
          { key: 'opened', value: opened },
          { key: 'assigned', value: assigned },
          { key: 'closed', value: closed },
        ].forEach((segment) => {
          const seg = document.createElement('span');
          seg.className = `customer-board-daily-segment segment-${segment.key}`;
          seg.style.flexGrow = String(Math.max(0, segment.value));
          seg.title = `${entry.day}: ${agent.label} (${segment.key}: ${segment.value})`;
          stack.appendChild(seg);
        });
        const initial = document.createElement('div');
        initial.className = 'tech-resolution-tech-initial';
        initial.textContent = cbHelpers.buildTechnicianInitials(agent.label);
        techEl.appendChild(countEl);
        techEl.appendChild(stack);
        techEl.appendChild(initial);
        bars.appendChild(techEl);
      });
      const dayLabel = document.createElement('div');
      dayLabel.className = 'tech-resolution-label';
      dayLabel.textContent = `${cbHelpers.formatResolutionDayLabel(entry.day)} · ${dayTotal}`;
      dayEl.appendChild(bars);
      dayEl.appendChild(dayLabel);
      dom.dailyTrend.appendChild(dayEl);
    });
  }

  async function loadCustomerCareQueuePanel() {
    const panel = document.getElementById('customerCareQueuePanel');
    if (!panel) return;
    try {
      const data = await api.fetch(api.routes.adminCustomerCareQueueSummary, { method: 'GET' });
      renderBoardQueuePanel(panel, (data && data.rows) || [], { icon: '🎧', title: 'Agent queues' });
    } catch (err) {
      panel.innerHTML = '<div class="muted" style="font-size:12px;">Queue summary unavailable.</div>';
      console.error('loadCustomerCareQueuePanel failed:', err);
    }
  }

  async function loadCustomerBoardDailyDashboard(options = {}) {
    const dom = resolveCustomerBoardDom();
    if (!dom.dailyTrend || !cbHelpers.hasAccess() || customerBoardDailyState.loading) return;
    const silent = Boolean(options.silent);
    customerBoardDailyState.loading = true;
    if (dom.dailyNote && !silent) dom.dailyNote.textContent = 'Loading…';
    if (!silent) {
      dom.dailyTrend.innerHTML = '<div style="color:var(--sub); font-size:12px;">Loading…</div>';
    }
    try {
      const data = await api.fetch(api.routes.adminCustomerBoardDashboard, { method: 'GET' });
      const items = Array.isArray(data?.days) ? data.days : [];
      customerBoardDailyState.agents = Array.isArray(data?.agents) ? data.agents : [];
      customerBoardDailyState.items = items;
      customerBoardDailyState.timezone = data?.timezone || 'UTC';
      customerBoardDailyState.generatedAt = data?.generated_at || new Date().toISOString();
      customerBoardDailyState.error = null;
      renderCustomerBoardDailyDashboard(items);
      loadCustomerCareQueuePanel();   // queue panel beside the chart (fire-and-forget)
      if (dom.dailyNote) {
        const relative = cbHelpers.formatRelativeTime(customerBoardDailyState.generatedAt);
        const absolute = cbHelpers.formatDateTimeShort(customerBoardDailyState.generatedAt);
        dom.dailyNote.textContent = `Updated ${relative}${absolute && relative !== absolute ? ` (${absolute})` : ''}`;
      }
    } catch (err) {
      customerBoardDailyState.error = err;
      dom.dailyTrend.innerHTML = '<div style="color:var(--err); font-size:12px;">Failed to load trend</div>';
      if (dom.dailyNote) dom.dailyNote.textContent = '';
    } finally {
      customerBoardDailyState.loading = false;
    }
  }

  async function loadCustomerBoard(options = {}) {
    const dom = resolveCustomerBoardDom();
    if (!dom.lanes || !cbHelpers.hasAccess()) {
      // eslint-disable-next-line no-console
      if (!dom.lanes) console.warn('loadCustomerBoard: #customerBoardLanes missing — board will not load.');
      return;
    }
    const silent = Boolean(options.silent);
    customerBoardState.loading = true;
    if (dom.status && !silent) {
      dom.status.textContent = 'Loading ticket board…';
    }
    try {
      const boardUrl = customerBoardState.showParked
        ? `${api.routes.adminCustomerBoard}?include_parked=true`
        : api.routes.adminCustomerBoard;
      const [boardResult, dashboardResult] = await Promise.allSettled([
        api.fetch(boardUrl, { method: 'GET' }),
        loadCustomerBoardDailyDashboard({ silent: true }),
        loadCustomerBoardParkedSummary(),
      ]);
      if (dashboardResult.status === 'rejected' && !silent) {
        console.warn('Customer board daily dashboard failed to load', dashboardResult.reason);
      }
      if (boardResult.status !== 'fulfilled') {
        throw boardResult.reason || new Error('Failed to load ticket board');
      }
      const data = boardResult.value;
      const lanes = Array.isArray(data?.lanes) ? data.lanes : [];
      customerBoardState.lanes = lanes;
      // The board reports the parked count whether or not it included them, so the
      // badge stays right even if the summary request failed.
      if (typeof data?.parked_count === 'number') {
        customerBoardState.parkedCount = data.parked_count;
        const dom2 = resolveCustomerBoardDom();
        if (dom2.parkedBadge) {
          dom2.parkedBadge.textContent = String(data.parked_count);
          dom2.parkedBadge.hidden = !data.parked_count;
        }
      }
      customerBoardState.generatedAt = data && data.generated_at ? data.generated_at : null;
      customerBoardState.initialized = true;
      customerBoardState.stale = false;
      renderCustomerBoard();
    } catch (err) {
      customerBoardState.lanes = [];
      customerBoardState.initialized = true;
      customerBoardState.stale = true;
      if (dom.status) {
        dom.status.textContent = err && err.message ? err.message : 'Failed to load ticket board';
      }
      if (dom.lanes) dom.lanes.innerHTML = '';
      if (dom.empty) dom.empty.hidden = false;
    } finally {
      customerBoardState.loading = false;
    }
  }

  function setCustomerBoardLaneCollapsed(laneKey, collapsed) {
    if (!laneKey) return;
    if (!customerBoardState.collapsedLanes) {
      customerBoardState.collapsedLanes = new Set();
    }
    if (collapsed) customerBoardState.collapsedLanes.add(laneKey);
    else customerBoardState.collapsedLanes.delete(laneKey);
    persistCustomerBoardCollapsed();
    renderCustomerBoard();
  }

  function toggleCustomerBoardLaneCollapsed(laneKey) {
    if (!laneKey) return;
    const collapsed = customerBoardState.collapsedLanes && customerBoardState.collapsedLanes.has(laneKey);
    setCustomerBoardLaneCollapsed(laneKey, !collapsed);
  }

  function renderCustomerBoard() {
    const dom = resolveCustomerBoardDom();
    if (!dom.lanes) return;
    const lanes = Array.isArray(customerBoardState.lanes) ? customerBoardState.lanes : [];
    dom.lanes.innerHTML = '';
    if (!lanes.length) {
      if (dom.empty) dom.empty.hidden = false;
    } else {
      if (dom.empty) dom.empty.hidden = true;
      lanes.forEach((lane) => {
        const laneSection = document.createElement('section');
        laneSection.className = 'tickets-board-lane';
        const laneKey = lane && lane.key ? String(lane.key) : '';
        laneSection.dataset.laneKey = laneKey;
        laneSection.dataset.adminUserId = lane && lane.admin_user_id != null ? String(lane.admin_user_id) : '';
        const isSupervisorLane = laneKey === 'customer_supervisors';
        if (!isSupervisorLane) {
          laneSection.classList.add(lane && lane.active ? 'lane-shift-active' : 'lane-shift-inactive');
        }
        const metrics = lane && lane.metrics ? lane.metrics : {};
        const tickets = Array.isArray(lane && lane.tickets) ? lane.tickets : [];
        const fullLabel = lane && lane.label ? lane.label : (isSupervisorLane ? 'Supervisor Queue' : 'Agent');
        const totalCount = metrics.total != null ? metrics.total : tickets.length;
        const collapsed = Boolean(laneKey) && customerBoardState.collapsedLanes.has(laneKey);
        if (collapsed) laneSection.classList.add('collapsed');

        const header = document.createElement('header');
        header.className = 'tickets-board-lane-header';

        const titleRow = document.createElement('div');
        titleRow.className = 'tickets-board-lane-title';
        const title = document.createElement('strong');
        title.textContent = collapsed ? cbHelpers.formatLaneCollapsedLabel(fullLabel) : fullLabel;
        title.title = fullLabel;
        titleRow.appendChild(title);

        const countBadge = document.createElement('span');
        countBadge.className = 'tickets-board-lane-count';
        countBadge.textContent = String(totalCount != null ? totalCount : 0);
        countBadge.hidden = !collapsed;
        titleRow.appendChild(countBadge);

        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'tickets-board-lane-toggle';
        toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        toggleBtn.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} lane ${fullLabel}`);
        toggleBtn.title = collapsed ? 'Expand lane' : 'Collapse lane';
        toggleBtn.textContent = collapsed ? '▸' : '▾';
        toggleBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          if (laneKey) toggleCustomerBoardLaneCollapsed(laneKey);
        });
        if (!laneKey) toggleBtn.disabled = true;
        titleRow.appendChild(toggleBtn);
        header.appendChild(titleRow);

        const meta = document.createElement('div');
        meta.className = 'tickets-board-lane-meta';
        const queueSpan = document.createElement('span');
        queueSpan.textContent = `Queue ${totalCount != null ? totalCount : 0}`;
        meta.appendChild(queueSpan);
        if (!isSupervisorLane) {
          const shiftSpan = document.createElement('span');
          shiftSpan.textContent = lane && lane.active ? 'On shift' : 'Off shift';
          meta.appendChild(shiftSpan);
        } else {
          const supervisorSpan = document.createElement('span');
          supervisorSpan.textContent = 'Supervisors';
          meta.appendChild(supervisorSpan);
        }
        header.appendChild(meta);
        laneSection.appendChild(header);

        const body = document.createElement('div');
        body.className = 'tickets-board-lane-body';
        body.dataset.laneKey = laneKey;
        body.dataset.adminUserId = lane && lane.admin_user_id != null ? String(lane.admin_user_id) : '';
        body.dataset.laneLabel = fullLabel;
        body.dataset.laneActive = (isSupervisorLane || (lane && lane.active)) ? '1' : '0';
        body.addEventListener('dragover', cbHelpers.handleBoardLaneDragOver);
        body.addEventListener('dragleave', cbHelpers.handleBoardLaneDragLeave);
        body.addEventListener('drop', handleCustomerBoardLaneDrop);
        body.hidden = collapsed;

        tickets.forEach((ticket) => {
          body.appendChild(buildCustomerBoardCard(lane, ticket));
        });

        laneSection.appendChild(body);
        dom.lanes.appendChild(laneSection);
      });
    }

    if (dom.status) {
      if (customerBoardState.loading) {
        dom.status.textContent = 'Loading ticket board…';
      } else if (customerBoardState.generatedAt) {
        const relative = cbHelpers.formatRelativeTime(customerBoardState.generatedAt);
        const absolute = cbHelpers.formatDateTimeShort(customerBoardState.generatedAt);
        dom.status.textContent = `Updated ${relative}${absolute && relative !== absolute ? ` (${absolute})` : ''}`;
      } else {
        dom.status.textContent = lanes.length ? '' : 'No customer-care tickets to display.';
      }
    }
  }

  function buildCustomerBoardCard(lane, ticket) {
    const card = document.createElement('article');
    card.className = 'tickets-board-card';
    card.dataset.ticketCard = String(ticket.id);
    card.dataset.ticketId = String(ticket.id);
    const laneKey = lane && lane.key ? String(lane.key) : '';
    card.dataset.laneKey = laneKey;
    const slaState = (ticket.sla_state || '').toLowerCase();
    if (slaState) {
      card.dataset.sla = slaState;
    }
    const draggable = cbHelpers.hasAccess();
    card.draggable = draggable;
    if (draggable) {
      card.addEventListener('dragstart', handleCustomerBoardCardDragStart);
      card.addEventListener('dragend', handleCustomerBoardCardDragEnd);
    }

    const badges = document.createElement('div');
    badges.className = 'ticket-badges';
    const slaBadge = document.createElement('span');
    slaBadge.className = `ticket-badge sla ${slaState || 'na'}`;
    slaBadge.textContent = cbHelpers.formatSlaStateLabel(slaState);
    if (ticket.due_at) {
      slaBadge.title = cbHelpers.formatSlaDueText(ticket);
    }
    badges.appendChild(slaBadge);

    const priorityBadge = document.createElement('span');
    priorityBadge.className = 'ticket-badge priority';
    priorityBadge.textContent = cbHelpers.formatTicketPriority(ticket.priority || '');
    badges.appendChild(priorityBadge);

    const unreadBadge = document.createElement('span');
    unreadBadge.className = 'ticket-badge unread';
    unreadBadge.textContent = String(ticket.unread_count != null ? ticket.unread_count : 0);
    badges.appendChild(unreadBadge);

    card.appendChild(badges);

    const header = document.createElement('div');
    header.className = 'tickets-board-card-header';
    const shortCode = ticket && ticket.meta ? ticket.meta.ticket_short_code : null;
    const customerName = ticket && ticket.support_customer && ticket.support_customer.display_name
      ? ticket.support_customer.display_name
      : 'Customer';
    const headerText = document.createElement('span');
    headerText.textContent = shortCode ? `${customerName} • #${shortCode}` : customerName;
    header.appendChild(headerText);
    const statusSpan = document.createElement('span');
    const statusValue = String(ticket.status || '').toLowerCase();
    statusSpan.className = `tickets-board-card-status status-${statusValue}`;
    statusSpan.textContent = cbHelpers.formatTicketStatus(ticket.status);
    header.appendChild(statusSpan);
    card.appendChild(header);

    const title = document.createElement('div');
    title.className = 'tickets-board-card-title';
    title.textContent = ticket.subject || '(no subject)';
    card.appendChild(title);

    const subline = document.createElement('div');
    subline.className = 'tickets-board-card-sub';
    if (ticket.meta && ticket.meta.customer_address) {
      const addressSpan = document.createElement('span');
      addressSpan.textContent = ticket.meta.customer_address;
      subline.appendChild(addressSpan);
    }
    if (ticket.support_customer && ticket.support_customer.primary_phone) {
      const phoneSpan = document.createElement('span');
      phoneSpan.textContent = ticket.support_customer.primary_phone;
      subline.appendChild(phoneSpan);
    }
    if (ticket.meta && ticket.meta.affected_connection_label) {
      const connectionSpan = document.createElement('span');
      connectionSpan.textContent = ticket.meta.affected_connection_label;
      subline.appendChild(connectionSpan);
    }
    if (subline.childNodes.length) {
      card.appendChild(subline);
    }

    const metaLine = document.createElement('div');
    metaLine.className = 'tickets-board-card-meta';
    if (ticket.complaint_type) {
      const complaintSpan = document.createElement('span');
      complaintSpan.textContent = cbHelpers.formatTicketComplaint(ticket.complaint_type);
      metaLine.appendChild(complaintSpan);
    }
    const updatedAt = ticket.updated_at || ticket.created_at;
    if (updatedAt) {
      const updatedSpan = document.createElement('span');
      updatedSpan.textContent = cbHelpers.formatRelativeTime(updatedAt);
      metaLine.appendChild(updatedSpan);
    }
    card.appendChild(metaLine);

    const previewText = ticket.last_message_preview || ticket.description || '';
    if (previewText) {
      const preview = document.createElement('div');
      preview.className = 'tickets-board-card-meta';
      preview.textContent = previewText;
      card.appendChild(preview);
    }

    // A parked ticket only appears here via the toggle, so say why it is parked and
    // for how long — otherwise the reader has to open it to find out.
    if (ticket.supervisor_parked_at) {
      card.classList.add('parked');
      const held = document.createElement('div');
      held.className = 'tickets-board-card-meta';
      const days = Math.floor((Date.now() - new Date(ticket.supervisor_parked_at).getTime()) / 86400000);
      const reason = ticket.supervisor_parked_reason || 'no reason recorded';
      held.textContent = `Parked ${days} day${days === 1 ? '' : 's'} — ${reason}`;
      card.appendChild(held);
    }

    const actions = document.createElement('div');
    actions.className = 'tickets-board-card-actions';
    const detailBtn = document.createElement('button');
    detailBtn.type = 'button';
    detailBtn.className = 'btn ghost';
    detailBtn.dataset.boardAction = 'details';
    detailBtn.dataset.ticketId = String(ticket.id);
    detailBtn.textContent = 'Details';
    actions.appendChild(detailBtn);
    const parkBtn = document.createElement('button');
    parkBtn.type = 'button';
    parkBtn.className = 'btn ghost';
    parkBtn.dataset.boardAction = ticket.supervisor_parked_at ? 'unpark' : 'park';
    parkBtn.dataset.ticketId = String(ticket.id);
    parkBtn.textContent = ticket.supervisor_parked_at ? 'Unpark' : 'Park';
    parkBtn.title = ticket.supervisor_parked_at
      ? 'Return this ticket to the supervisor queue'
      : 'Hold this ticket off the queue pending special intervention';
    actions.appendChild(parkBtn);
    card.appendChild(actions);

    return card;
  }

  async function parkCustomerBoardTicket(ticketId) {
    const reason = window.prompt(
      'Why is this ticket being parked? (shown on the board and in the audit trail)');
    if (reason === null) return;
    if (!reason.trim()) {
      notify.showFlash('A reason is required to park a ticket.', 'error');
      return;
    }
    try {
      await api.fetch(api.routes.adminTicketPark(ticketId), {
        method: 'POST', body: JSON.stringify({ reason: reason.trim() }),
      });
      notify.showFlash('Ticket parked.', 'success');
      await loadCustomerBoard();
    } catch (err) {
      notify.showFlash((err && err.message) || 'Failed to park the ticket.', 'error');
    }
  }

  async function unparkCustomerBoardTicket(ticketId) {
    if (!window.confirm('Return this ticket to the supervisor queue?')) return;
    try {
      await api.fetch(api.routes.adminTicketUnpark(ticketId), {
        method: 'POST', body: JSON.stringify({ note: null }),
      });
      notify.showFlash('Ticket returned to the queue.', 'success');
      await loadCustomerBoard();
    } catch (err) {
      notify.showFlash((err && err.message) || 'Failed to unpark the ticket.', 'error');
    }
  }

  function findCustomerBoardTicket(ticketId) {
    const id = String(ticketId);
    const lanes = Array.isArray(customerBoardState.lanes) ? customerBoardState.lanes : [];
    for (const lane of lanes) {
      const tickets = Array.isArray(lane.tickets) ? lane.tickets : [];
      const match = tickets.find((ticket) => String(ticket.id) === id);
      if (match) {
        return { lane, ticket: match };
      }
    }
    return null;
  }

  function handleCustomerBoardClick(event) {
    const btn = event.target.closest('[data-board-action]');
    if (!btn) return;
    const ticketId = btn.dataset.ticketId;
    if (!ticketId) return;
    const context = findCustomerBoardTicket(ticketId);
    if (!context) {
      notify.showFlash('Ticket not found on board', 'error');
      return;
    }
    const action = btn.dataset.boardAction;
    if (action === 'details') {
      cbHelpers.openTicketDetailFromBoard(String(ticketId), context.lane);
    } else if (action === 'park') {
      parkCustomerBoardTicket(String(ticketId));
    } else if (action === 'unpark') {
      unparkCustomerBoardTicket(String(ticketId));
    }
  }

  function handleCustomerBoardCardDragStart(event) {
    if (!cbHelpers.hasAccess()) return;
    const card = event.currentTarget;
    const ticketId = card && card.dataset ? card.dataset.ticketId : null;
    if (!ticketId) return;
    customerBoardState.dragTicketId = ticketId;
    customerBoardState.dragSourceLane = card.dataset.laneKey || null;
    card.classList.add('dragging');
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      try { event.dataTransfer.setData('text/plain', ticketId); } catch {}
    }
  }

  function handleCustomerBoardCardDragEnd(event) {
    const card = event.currentTarget;
    card.classList.remove('dragging');
    customerBoardState.dragTicketId = null;
    customerBoardState.dragSourceLane = null;
  }

  function handleCustomerBoardLaneDrop(event) {
    event.preventDefault();
    const laneBody = event.currentTarget;
    laneBody.classList.remove('dragover');
    if (!cbHelpers.hasAccess()) {
      notify.showFlash('You do not have permission to reassign tickets.', 'error');
      return;
    }
    const ticketId = customerBoardState.dragTicketId || (event.dataTransfer ? event.dataTransfer.getData('text/plain') : null);
    if (!ticketId) return;
    const targetLaneKey = laneBody.dataset.laneKey || '';
    const targetAdminUserId = laneBody.dataset.adminUserId ? Number(laneBody.dataset.adminUserId) : null;
    const isSupervisorLane = targetLaneKey === 'customer_supervisors';
    const laneActive = laneBody.dataset.laneActive !== '0';
    const sourceLane = customerBoardState.dragSourceLane;
    if (sourceLane && targetLaneKey && sourceLane === targetLaneKey) {
      return;
    }
    if (!isSupervisorLane && !laneActive) {
      notify.showFlash('That agent is currently off shift. Ask them to clock in before assigning tickets.', 'error');
      return;
    }
    const context = findCustomerBoardTicket(ticketId);
    if (!context) {
      notify.showFlash('Ticket not found on board', 'error');
      return;
    }
    cbHelpers.openBoardAssignDialog(context, {
      laneKey: targetLaneKey,
      adminUserId: isSupervisorLane ? null : targetAdminUserId,
      laneLabel: laneBody.dataset.laneLabel || 'Queue',
    });
  }

  function scheduleCustomerBoardRefresh() {
    customerBoardState.stale = true;
    if (!cbHelpers.hasAccess()) return;
    if (customerBoardRefreshTimer) return;
    customerBoardRefreshTimer = setTimeout(() => {
      customerBoardRefreshTimer = null;
      loadCustomerBoard({ silent: true }).catch(() => {});
    }, 600);
  }

  function mountCustomerBoard() {
    const dom = resolveCustomerBoardDom();
    if (!customerBoardMounted) {
      if (dom.lanes) {
        dom.lanes.addEventListener('click', handleCustomerBoardClick);
      }
      if (dom.refreshBtn) {
        dom.refreshBtn.addEventListener('click', () => {
          if (!cbHelpers.hasAccess()) {
            notify.showFlash('You do not have access to the ticket board.', 'error');
            return;
          }
          loadCustomerBoard().catch(() => {});
        });
      }
      if (dom.showParked) {
        dom.showParked.addEventListener('change', () => {
          customerBoardState.showParked = dom.showParked.checked;
          loadCustomerBoard().catch(() => {});
        });
      }
      customerBoardMounted = true;
    }
    if (!dom.view) return;
    if (!cbHelpers.hasAccess()) {
      if (dom.status) dom.status.textContent = 'Access restricted.';
      if (dom.lanes) dom.lanes.innerHTML = '';
      if (dom.empty) dom.empty.hidden = false;
      if (dom.dailyStrip) dom.dailyStrip.style.display = 'none';
      return;
    }
    if (dom.dailyStrip) dom.dailyStrip.style.display = '';
    if (!customerBoardInitialized) {
      customerBoardInitialized = true;
      loadCustomerBoard().catch(() => {});
      return;
    }
    if (customerBoardState.stale && !customerBoardState.loading) {
      loadCustomerBoard({ silent: true }).catch(() => {});
    } else {
      renderCustomerBoard();
      if (!customerBoardDailyState.loading) {
        loadCustomerBoardDailyDashboard({ silent: true }).catch(() => {});
      }
    }
  }

  views.tickets.customerBoard = {
    mount: mountCustomerBoard,
    refresh: (options) => loadCustomerBoard(options),
    refreshDailyDashboard: (options) => loadCustomerBoardDailyDashboard(options),
    scheduleRefresh: scheduleCustomerBoardRefresh,
    setStale: (value) => { customerBoardState.stale = Boolean(value); },
    isStale: () => Boolean(customerBoardState.stale),
    getState: () => customerBoardState,
    getScrollEl: () => resolveCustomerBoardDom().scroll,
    getBoardEl: () => resolveCustomerBoardDom().board,
    hasAccess: () => cbHelpers.hasAccess(),
  };

  // ---- Technician (main) ticket board (Phase 2.1.d.1) ------------------
  // Owns the technician kanban view (the "board" mode of the Tickets
  // screen):
  //   - The lane strip (`#ticketsBoardLanes`) with one lane per technician
  //     plus a synthetic "supervisors" lane for unassigned/escalated
  //     tickets.
  //   - The supervisor filter trio at the top
  //     (`#ticketsBoardSupervisorFilter` + …Type + …Age).
  //   - The board-assign dialog (`#boardAssignNoteDlg`) that the drag/drop
  //     and "Reassign" actions open.
  //   - Drag/drop card movement (start/end on cards, dragover/leave/drop on
  //     lane bodies). The document-level drag-autoscroll handler stays in
  //     admin.js because it coordinates *both* boards.
  //   - The WS-delta debounce timer (`scheduleRefresh`) fired by the
  //     board-delta dispatcher in admin.js.
  //
  // What stays in admin.js:
  //   - Cross-cluster: `mutateTicket`, `reloadTickets`, `openTechClosureReview`,
  //     `openTicketDetailFromBoard` (cross-cluster nav helper),
  //     `prepareTicketsFiltersForBoardTicket` (mutates list state).
  //   - Shared role gate: `canAccessTicketing`, `hasRole`, the
  //     `FIELD_TECH_SUPERVISOR_ROLE` constant, `ADMIN_ME` (current user).
  //   - The supervisor user lists (`supervisorAdminUsers`,
  //     `techSupervisorAdminUsers`) and their loaders are admin.js
  //     module-level state used by other clusters too — accessed via
  //     getter helpers.
  //   - All ticket formatters (`formatTicketStatus`, `formatTicketPriority`,
  //     `formatTicketComplaint`, `formatTicketAssignee`,
  //     `formatSlaStateLabel`, `formatSlaDueText`) — shared with the
  //     ticket detail panel (2.1.d.3) and ticket list (2.1.d.2).
  //   - `getTechnicianStatusInfo` — shared with the technicians/HR roster.
  //   - The document-level `handleBoardDragAutoScroll` +
  //     `autoScrollBoardContainer` — coordinator across both boards.
  //
  // The 6 external `loadTicketsBoard({silent:true})` callsites in admin.js
  // and the equally many `ticketsBoardState.stale = true` writes are
  // collapsed into `tb.refreshIfVisible({silent:true})` /
  // `tb.setStale(true)` accessors so admin.js never reaches into the
  // module's private state directly.
  const TICKETS_BOARD_COLLAPSE_STORAGE_KEY = 'inet_ticket_board_collapsed_v1';

  function loadTicketsBoardCollapsed() {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(TICKETS_BOARD_COLLAPSE_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      const normalized = parsed
        .map((key) => (key == null ? '' : String(key).trim()))
        .filter((key) => key);
      return new Set(normalized);
    } catch (err) {
      console.warn('Failed to read technician board lane preferences', err);
      return new Set();
    }
  }

  function persistTicketsBoardCollapsed() {
    if (typeof localStorage === 'undefined') return;
    try {
      const lanes = ticketsBoardState.collapsedLanes ? Array.from(ticketsBoardState.collapsedLanes) : [];
      localStorage.setItem(TICKETS_BOARD_COLLAPSE_STORAGE_KEY, JSON.stringify(lanes));
    } catch (err) {
      console.warn('Failed to persist technician board lane preferences', err);
    }
  }

  const ticketsBoardState = {
    lanes: [],
    loading: false,
    initialized: false,
    stale: false,
    supervisorFilter: '',
    supervisorTypeFilter: '',
    supervisorAgeFilter: '',
    generatedAt: null,
    dragTicketId: null,
    dragSourceLane: null,
    collapsedLanes: loadTicketsBoardCollapsed(),
  };
  const boardAssignState = { ticketId: null, adminUserId: null, laneKey: null };
  let ticketsBoardRefreshTimer = null;
  let technicianLaneHighlightTimer = null;
  let technicianBoardMounted = false;
  let technicianBoardDom = null;

  // Cross-boundary helpers — defaults are no-ops so tests can mount the
  // module without admin.js wired up.
  // Phase 3.2.c: tbHelpers populated from imports + sibling lookups.
  const tbHelpers = {
    hasAccess: () => canAccessTicketing(),
    formatRelativeTime,
    formatDateTimeShort,
    formatLaneCollapsedLabel,
    formatTicketPriority,
    formatTicketStatus,
    formatSlaStateLabel: (state) => (state ? String(state) : 'SLA'),
    formatSlaDueText: () => '',
    formatAdminUserLabel,
    adminUserSortKey,
    extractRoleNames,
    getTechnicianStatusInfo: (status, isActive) => {
      const t = window.adminViews && window.adminViews.technicians;
      return t && typeof t.getTechnicianStatusInfo === 'function'
        ? t.getTechnicianStatusInfo(status, isActive)
        : { label: '', className: '' };
    },
    hasRole,
    getCurrentUserId: () => {
      const me = getCurrentAdminUser();
      return me && me.id != null ? String(me.id) : '';
    },
    getFieldTechSupervisorRole: () => FIELD_TECH_SUPERVISOR_ROLE,
    getSupervisorAdminUsers,
    getTechSupervisorAdminUsers,
    ensureSupervisorUsersLoaded,
    ensureTechSupervisorUsersLoaded,
    openDialog,
    showFlash: (msg, type) => notify.showFlash(msg, type),
    mutateTicket: (ticketId, payload) => mutateTicket(ticketId, payload),
    reloadTickets: (opts) => reloadTickets(opts),
    openTechClosureReview: (ticket) => {
      const tr = views.tickets && views.tickets.techReview;
      if (tr && typeof tr.open === 'function') tr.open(ticket);
    },
    openTicketDetailFromBoard: (id, lane, opts) => openTicketDetailFromBoard(id, lane, opts),
  };

  function resolveTechnicianBoardDom() {
    if (technicianBoardDom) return technicianBoardDom;
    technicianBoardDom = {
      board: document.getElementById('ticketsBoard'),
      scroll: document.getElementById('ticketsBoardScroll'),
      lanes: document.getElementById('ticketsBoardLanes'),
      empty: document.getElementById('ticketsBoardEmpty'),
      status: document.getElementById('ticketsBoardStatus'),
      supervisorFilter: document.getElementById('ticketsBoardSupervisorFilter'),
      supervisorTypeFilter: document.getElementById('ticketsBoardSupervisorTypeFilter'),
      supervisorAgeFilter: document.getElementById('ticketsBoardSupervisorAgeFilter'),
      assignDlg: document.getElementById('boardAssignNoteDlg'),
      assignForm: document.getElementById('boardAssignNoteForm'),
      assignSummary: document.getElementById('boardAssignNoteSummary'),
      assignInput: document.getElementById('boardAssignNoteInput'),
      assignSubmit: document.getElementById('boardAssignNoteSubmit'),
      assignCancel: document.getElementById('boardAssignNoteCancel'),
      assignClose: document.getElementById('boardAssignNoteClose'),
    };
    return technicianBoardDom;
  }

  // ---- Lane card metric formatters (board-only) ------------------------
  function formatBoardQueueBreakdown(metrics = {}) {
    const parts = [];
    if (metrics.pending) parts.push(`Pending ${metrics.pending}`);
    if (metrics.in_progress) parts.push(`In progress ${metrics.in_progress}`);
    if (metrics.resolved_by_technician) parts.push(`Review ${metrics.resolved_by_technician}`);
    if (metrics.escalated) parts.push(`Escalated ${metrics.escalated}`);
    return parts.join(' • ');
  }

  function renderQualityBadge(score) {
    if (score == null || Number.isNaN(Number(score))) return null;
    const numeric = Math.round(Number(score));
    const span = document.createElement('span');
    span.className = 'badge';
    if (numeric >= 85) span.classList.add('ok');
    else if (numeric < 70) span.classList.add('warn');
    span.textContent = `Q ${numeric}`;
    span.title = 'Avg quality score';
    return span;
  }

  function resolveSupervisorName(adminUserId) {
    if (adminUserId == null) return '';
    const supervisors = tbHelpers.getSupervisorAdminUsers() || [];
    const match = supervisors.find((user) => Number(user.id) === Number(adminUserId));
    const label = match ? tbHelpers.formatAdminUserLabel(match) : '';
    if (label) return label;
    return `Supervisor #${adminUserId}`;
  }

  function formatSupervisorAssignment(ticket) {
    if (!ticket) return 'Unassigned';
    if (ticket.assignee_user && ticket.assignee_user.id != null) {
      const label = tbHelpers.formatAdminUserLabel(ticket.assignee_user);
      if (label) return label;
    }
    if (ticket.assignee_user_id != null) {
      return resolveSupervisorName(ticket.assignee_user_id);
    }
    return 'Unassigned';
  }

  function summarizeBoardTicketMetrics(tickets = []) {
    const summary = { total: 0, pending: 0, in_progress: 0, waiting_customer: 0, escalated: 0, parked: 0, resolved_by_technician: 0 };
    tickets.forEach((ticket) => {
      summary.total += 1;
      const status = String(ticket.status || '').toLowerCase();
      if (status === 'pending') summary.pending += 1;
      else if (status === 'in_progress') summary.in_progress += 1;
      else if (status === 'waiting_customer') summary.waiting_customer += 1;
      else if (status === 'escalated') summary.escalated += 1;
      else if (status === 'parked') summary.parked += 1;
      else if (status === 'resolved_by_technician') summary.resolved_by_technician += 1;
    });
    return summary;
  }

  function sortTicketsOldestFirst(tickets = []) {
    return tickets.slice().sort((a, b) => {
      const aTime = resolveTicketAgeTime(a);
      const bTime = resolveTicketAgeTime(b);
      return (aTime || 0) - (bTime || 0);
    });
  }

  function resolveTicketAgeTime(ticket) {
    if (!ticket) return 0;
    const candidate = ticket.opened_at || ticket.created_at || ticket.updated_at;
    if (!candidate) return 0;
    const time = Date.parse(candidate);
    return Number.isNaN(time) ? 0 : time;
  }

  function formatTicketAgeLabel(ticket) {
    const time = resolveTicketAgeTime(ticket);
    if (!time) return '';
    const ageMs = Math.max(0, Date.now() - time);
    const dayMs = 24 * 60 * 60 * 1000;
    if (ageMs < dayMs) return 'New today';
    const days = Math.floor(ageMs / dayMs);
    return `${days}d old`;
  }

  function isInstallationTicket(ticket) {
    const complaint = String(ticket && ticket.complaint_type ? ticket.complaint_type : '').toLowerCase();
    return complaint === 'installation';
  }

  function filterSupervisorLaneTickets(tickets = []) {
    const selected = ticketsBoardState.supervisorFilter ? String(ticketsBoardState.supervisorFilter) : '';
    let filtered = tickets.slice();
    if (selected !== '__all__') {
      if (!selected) {
        filtered = filtered.filter((ticket) => ticket.assignee_user_id == null);
      } else {
        filtered = filtered.filter(
          (ticket) => ticket.assignee_user_id == null || String(ticket.assignee_user_id) === selected,
        );
      }
    }

    const typeFilter = ticketsBoardState.supervisorTypeFilter || '';
    if (typeFilter === 'installation') {
      filtered = filtered.filter((ticket) => isInstallationTicket(ticket));
    } else if (typeFilter === 'non_installation') {
      filtered = filtered.filter((ticket) => !isInstallationTicket(ticket));
    }

    const ageFilter = ticketsBoardState.supervisorAgeFilter || '';
    if (ageFilter) {
      const now = Date.now();
      filtered = filtered.filter((ticket) => {
        const referenceTime = resolveTicketAgeTime(ticket);
        if (!referenceTime) return false;
        const ageMs = Math.max(0, now - referenceTime);
        const dayMs = 24 * 60 * 60 * 1000;
        switch (ageFilter) {
          case 'lt_1d':
            return ageMs < dayMs;
          case '1_3d':
            return ageMs >= dayMs && ageMs < 3 * dayMs;
          case '3_7d':
            return ageMs >= 3 * dayMs && ageMs < 7 * dayMs;
          case 'gt_7d':
            return ageMs >= 7 * dayMs;
          default:
            return true;
        }
      });
    }

    return filtered;
  }

  function populateTicketsBoardSupervisorFilter() {
    const dom = resolveTechnicianBoardDom();
    if (!dom.supervisorFilter) return;
    const previous = dom.supervisorFilter.value || '';
    dom.supervisorFilter.innerHTML = '';
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Unassigned for all supervisors';
    dom.supervisorFilter.appendChild(defaultOption);
    const techSupervisors = tbHelpers.getTechSupervisorAdminUsers() || [];
    const fieldTechRole = tbHelpers.getFieldTechSupervisorRole();
    const supervisors = (Array.isArray(techSupervisors) ? techSupervisors.slice() : [])
      .filter((user) => {
        const roles = tbHelpers.extractRoleNames(user);
        return roles.includes(fieldTechRole);
      });
    supervisors.sort((a, b) => tbHelpers.adminUserSortKey(a).localeCompare(tbHelpers.adminUserSortKey(b)));
    const currentUserId = tbHelpers.getCurrentUserId() || '';
    if (!ticketsBoardState.supervisorFilter && tbHelpers.hasRole(fieldTechRole) && currentUserId) {
      const isCurrentInList = supervisors.some((user) => String(user.id) === String(currentUserId));
      if (isCurrentInList) {
        ticketsBoardState.supervisorFilter = String(currentUserId);
      }
    }
    supervisors.forEach((user) => {
      const option = document.createElement('option');
      option.value = String(user.id);
      option.textContent = tbHelpers.formatAdminUserLabel(user) || `Supervisor #${user.id}`;
      dom.supervisorFilter.appendChild(option);
    });
    const desired = ticketsBoardState.supervisorFilter || previous;
    const hasDesired = Array.from(dom.supervisorFilter.options || []).some((opt) => opt.value === desired);
    dom.supervisorFilter.value = hasDesired ? desired : '';
    if (!hasDesired && ticketsBoardState.supervisorFilter) {
      ticketsBoardState.supervisorFilter = '';
    }
  }

  function handleTicketsBoardSupervisorChange(event) {
    const select = event.target;
    if (!select) return;
    ticketsBoardState.supervisorFilter = select.value || '';
    ticketsBoardState.stale = true;
    loadTicketsBoard().catch(() => {});
  }

  function handleTicketsBoardSupervisorTypeChange(event) {
    const select = event.target;
    if (!select) return;
    ticketsBoardState.supervisorTypeFilter = select.value || '';
    renderTicketsBoard();
  }

  function handleTicketsBoardSupervisorAgeChange(event) {
    const select = event.target;
    if (!select) return;
    ticketsBoardState.supervisorAgeFilter = select.value || '';
    renderTicketsBoard();
  }

  function focusTechnicianLane(technicianId) {
    const dom = resolveTechnicianBoardDom();
    if (!dom.lanes) return;
    const id = technicianId != null ? String(technicianId) : '';
    if (!id) return;
    dom.lanes.querySelectorAll('.lane-focus').forEach((el) => el.classList.remove('lane-focus'));
    const lane = dom.lanes.querySelector(`section[data-technician-id="${id}"]`);
    if (!lane) return;
    lane.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    lane.classList.add('lane-focus');
    if (technicianLaneHighlightTimer) {
      clearTimeout(technicianLaneHighlightTimer);
    }
    technicianLaneHighlightTimer = setTimeout(() => {
      lane.classList.remove('lane-focus');
    }, 2000);
  }

  // ---- Lane / card rendering -------------------------------------------
  function setTicketLaneCollapsed(laneKey, collapsed) {
    if (!laneKey) return;
    if (!ticketsBoardState.collapsedLanes) {
      ticketsBoardState.collapsedLanes = new Set();
    }
    if (collapsed) ticketsBoardState.collapsedLanes.add(laneKey);
    else ticketsBoardState.collapsedLanes.delete(laneKey);
    persistTicketsBoardCollapsed();
    renderTicketsBoard();
  }

  function toggleTicketLaneCollapsed(laneKey) {
    if (!laneKey) return;
    const collapsed = ticketsBoardState.collapsedLanes && ticketsBoardState.collapsedLanes.has(laneKey);
    setTicketLaneCollapsed(laneKey, !collapsed);
  }

  function renderTicketsBoard() {
    const dom = resolveTechnicianBoardDom();
    if (!dom.lanes) return;
    const lanes = (Array.isArray(ticketsBoardState.lanes) ? ticketsBoardState.lanes.slice() : [])
      // Supervisor queue always renders first so it's easy to spot.
      .sort((a, b) => ((a && a.key) === 'supervisors' ? 0 : 1) - ((b && b.key) === 'supervisors' ? 0 : 1));
    dom.lanes.innerHTML = '';
    if (!lanes.length) {
      if (dom.empty) dom.empty.hidden = false;
    } else {
      if (dom.empty) dom.empty.hidden = true;
      lanes.forEach((lane) => {
        const laneSection = document.createElement('section');
        laneSection.className = 'tickets-board-lane';
        const laneKey = lane && lane.key ? String(lane.key) : '';
        laneSection.dataset.laneKey = laneKey;
        laneSection.dataset.adminUserId = lane && lane.admin_user_id != null ? String(lane.admin_user_id) : '';
        laneSection.dataset.technicianId = lane && lane.technician_id ? String(lane.technician_id) : '';
        if (laneKey === 'supervisors') laneSection.classList.add('is-supervisor-lane');

        const metrics = lane && lane.metrics ? { ...lane.metrics } : {};
        let tickets = Array.isArray(lane && lane.tickets) ? lane.tickets.slice() : [];
        if (laneKey === 'supervisors') {
          tickets = filterSupervisorLaneTickets(tickets);
          const summary = summarizeBoardTicketMetrics(tickets);
          metrics.total = summary.total;
          metrics.pending = summary.pending;
          metrics.in_progress = summary.in_progress;
          metrics.waiting_customer = summary.waiting_customer;
          metrics.escalated = summary.escalated;
          metrics.parked = summary.parked;
          metrics.resolved_by_technician = summary.resolved_by_technician;
        }
        tickets = sortTicketsOldestFirst(tickets);
        const fullLabel = lane && lane.label ? lane.label : 'Technician';
        const totalCount = metrics.total != null ? metrics.total : tickets.length;
        const collapsed = Boolean(laneKey) && ticketsBoardState.collapsedLanes.has(laneKey);
        if (collapsed) laneSection.classList.add('collapsed');

        const header = document.createElement('header');
        header.className = 'tickets-board-lane-header';

        const titleRow = document.createElement('div');
        titleRow.className = 'tickets-board-lane-title';

        const title = document.createElement('strong');
        title.textContent = collapsed ? tbHelpers.formatLaneCollapsedLabel(fullLabel) : fullLabel;
        title.title = fullLabel;
        titleRow.appendChild(title);

        const countBadge = document.createElement('span');
        countBadge.className = 'tickets-board-lane-count';
        countBadge.textContent = String(totalCount != null ? totalCount : 0);
        countBadge.hidden = !collapsed;
        titleRow.appendChild(countBadge);

        const qualityBadge = renderQualityBadge(metrics.ai_average_score);
        if (qualityBadge) {
          qualityBadge.classList.add('lane-quality');
          titleRow.appendChild(qualityBadge);
        }

        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'tickets-board-lane-toggle';
        toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        toggleBtn.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} lane ${fullLabel}`);
        toggleBtn.title = collapsed ? 'Expand lane' : 'Collapse lane';
        toggleBtn.textContent = collapsed ? '▸' : '▾';
        toggleBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          if (laneKey) toggleTicketLaneCollapsed(laneKey);
        });
        if (!laneKey) toggleBtn.disabled = true;
        titleRow.appendChild(toggleBtn);

        header.appendChild(titleRow);

        const meta = document.createElement('div');
        meta.className = 'tickets-board-lane-meta';
        const queueSpan = document.createElement('span');
        queueSpan.textContent = `Queue ${totalCount != null ? totalCount : 0}`;
        meta.appendChild(queueSpan);

        const breakdownText = formatBoardQueueBreakdown(metrics);
        if (breakdownText) {
          const breakdownEl = document.createElement('span');
          breakdownEl.textContent = breakdownText;
          meta.appendChild(breakdownEl);
        }

        if (lane && lane.technician_status) {
          const statusInfo = tbHelpers.getTechnicianStatusInfo(lane.technician_status, lane.active);
          if (statusInfo && statusInfo.label) {
            const statusBadge = document.createElement('span');
            statusBadge.className = statusInfo.className;
            statusBadge.textContent = statusInfo.label;
            meta.appendChild(statusBadge);
          }
        }

        if (metrics.last_submission_at) {
          const lastUpdate = document.createElement('span');
          lastUpdate.textContent = `Last submission ${tbHelpers.formatRelativeTime(metrics.last_submission_at)}`;
          meta.appendChild(lastUpdate);
        }

        meta.hidden = collapsed;
        header.appendChild(meta);
        laneSection.appendChild(header);

        const body = document.createElement('div');
        body.className = 'tickets-board-lane-body';
        body.dataset.laneKey = laneKey;
        body.dataset.adminUserId = lane && lane.admin_user_id != null ? String(lane.admin_user_id) : '';
        body.dataset.laneLabel = lane && lane.label ? lane.label : 'Technician';
        body.addEventListener('dragover', handleBoardLaneDragOver);
        body.addEventListener('dragleave', handleBoardLaneDragLeave);
        body.addEventListener('drop', handleBoardLaneDrop);
        body.hidden = collapsed;

        tickets.forEach((ticket) => {
          body.appendChild(buildTicketsBoardCard(lane, ticket));
        });

        laneSection.appendChild(body);
        dom.lanes.appendChild(laneSection);
      });
    }

    if (dom.status) {
      if (ticketsBoardState.loading) {
        dom.status.textContent = 'Loading technician board…';
      } else if (ticketsBoardState.generatedAt) {
        const relative = tbHelpers.formatRelativeTime(ticketsBoardState.generatedAt);
        const absolute = tbHelpers.formatDateTimeShort(ticketsBoardState.generatedAt);
        dom.status.textContent = `Updated ${relative}${absolute && relative !== absolute ? ` (${absolute})` : ''}`;
      } else {
        dom.status.textContent = lanes.length ? '' : 'No technician tickets to display.';
      }
    }
  }

  function buildTicketsBoardCard(lane, ticket) {
    const card = document.createElement('article');
    card.className = 'tickets-board-card';
    card.dataset.ticketCard = String(ticket.id);
    card.draggable = true;
    const laneKey = lane && lane.key ? String(lane.key) : '';
    card.dataset.ticketId = String(ticket.id);
    card.dataset.laneKey = laneKey;
    card.addEventListener('dragstart', handleBoardCardDragStart);
    card.addEventListener('dragend', handleBoardCardDragEnd);

    const slaState = (ticket.sla_state || '').toLowerCase();
    card.dataset.sla = slaState || 'na';
    const badges = document.createElement('div');
    badges.className = 'ticket-badges';
    const slaBadge = document.createElement('span');
    slaBadge.className = `ticket-badge sla ${slaState || 'na'}`;
    slaBadge.textContent = tbHelpers.formatSlaStateLabel(slaState);
    if (ticket.due_at) {
      slaBadge.title = tbHelpers.formatSlaDueText(ticket);
    }
    badges.appendChild(slaBadge);
    if (ticket.priority) {
      const priorityBadge = document.createElement('span');
      priorityBadge.className = 'ticket-badge priority';
      priorityBadge.textContent = tbHelpers.formatTicketPriority(ticket.priority || '');
      badges.appendChild(priorityBadge);
    }
    card.appendChild(badges);

    const header = document.createElement('div');
    header.className = 'tickets-board-card-header';
    const shortCode = ticket && ticket.meta ? ticket.meta.ticket_short_code : null;
    const codeSpan = document.createElement('span');
    codeSpan.textContent = shortCode ? `#${shortCode}` : String(ticket.id).slice(0, 8);
    header.appendChild(codeSpan);
    const statusValue = String(ticket.status || '').toLowerCase();
    const statusSpan = document.createElement('span');
    statusSpan.className = `tickets-board-card-status status-${statusValue}`;
    statusSpan.textContent = tbHelpers.formatTicketStatus(ticket.status);
    header.appendChild(statusSpan);
    card.appendChild(header);

    const title = document.createElement('div');
    title.className = 'tickets-board-card-title';
    title.textContent = ticket.subject || '(no subject)';
    card.appendChild(title);

    const subline = document.createElement('div');
    subline.className = 'tickets-board-card-sub';
    if (ticket.support_customer && ticket.support_customer.display_name) {
      const customerSpan = document.createElement('span');
      customerSpan.textContent = ticket.support_customer.display_name;
      subline.appendChild(customerSpan);
    }
    if (ticket.meta && ticket.meta.customer_address) {
      const addressSpan = document.createElement('span');
      addressSpan.textContent = ticket.meta.customer_address;
      subline.appendChild(addressSpan);
    }
    card.appendChild(subline);

    const metaLine = document.createElement('div');
    metaLine.className = 'tickets-board-card-meta';
    if (ticket.priority) {
      const prioritySpan = document.createElement('span');
      prioritySpan.textContent = `Priority ${tbHelpers.formatTicketPriority(ticket.priority)}`;
      metaLine.appendChild(prioritySpan);
    }
    const ageLabel = formatTicketAgeLabel(ticket);
    if (ageLabel) {
      const ageSpan = document.createElement('span');
      ageSpan.textContent = ageLabel;
      metaLine.appendChild(ageSpan);
    }
    if (ticket.meta && ticket.meta.technician_on_site_at) {
      const onSite = document.createElement('span');
      onSite.className = 'tickets-board-on-site';
      onSite.textContent = 'On site';
      metaLine.appendChild(onSite);
    }
    card.appendChild(metaLine);

    if (laneKey === 'supervisors') {
      const supervisorLine = document.createElement('div');
      supervisorLine.className = 'tickets-board-card-meta';
      supervisorLine.textContent = `Supervisor: ${formatSupervisorAssignment(ticket)}`;
      card.appendChild(supervisorLine);
    }

    const actions = document.createElement('div');
    actions.className = 'tickets-board-card-actions';
    const detailBtn = document.createElement('button');
    detailBtn.type = 'button';
    detailBtn.className = 'btn ghost';
    detailBtn.dataset.boardAction = 'details';
    detailBtn.dataset.ticketId = String(ticket.id);
    detailBtn.textContent = 'Details';
    actions.appendChild(detailBtn);
    if (statusValue === 'resolved_by_technician') {
      const reviewBtn = document.createElement('button');
      reviewBtn.type = 'button';
      reviewBtn.className = 'btn primary';
      reviewBtn.dataset.boardAction = 'review';
      reviewBtn.dataset.ticketId = String(ticket.id);
      reviewBtn.textContent = 'Review';
      actions.appendChild(reviewBtn);
    }
    card.appendChild(actions);

    return card;
  }

  function findBoardTicket(ticketId) {
    const id = String(ticketId);
    const lanes = Array.isArray(ticketsBoardState.lanes) ? ticketsBoardState.lanes : [];
    for (const lane of lanes) {
      const tickets = Array.isArray(lane.tickets) ? lane.tickets : [];
      const match = tickets.find((ticket) => String(ticket.id) === id);
      if (match) {
        return { lane, ticket: match };
      }
    }
    return null;
  }

  // ---- Network --------------------------------------------------------
  async function loadTicketsBoard(options = {}) {
    const dom = resolveTechnicianBoardDom();
    if (!dom.lanes) {
      // eslint-disable-next-line no-console
      console.warn('loadTicketsBoard: #ticketsBoardLanes missing — technician board will not load.');
      return;
    }
    const silent = Boolean(options.silent);
    ticketsBoardState.loading = true;
    if (dom.status && !silent) {
      dom.status.textContent = 'Loading technician board…';
    }
    try {
      await Promise.all([
        tbHelpers.ensureSupervisorUsersLoaded().catch(() => {}),
        tbHelpers.ensureTechSupervisorUsersLoaded().catch(() => {}),
      ]);
      populateTicketsBoardSupervisorFilter();
      const params = new URLSearchParams();
      const supervisorFilter = ticketsBoardState.supervisorFilter ? String(ticketsBoardState.supervisorFilter) : '';
      if (supervisorFilter) {
        params.set('supervisor_user_id', supervisorFilter);
      }
      const url = params.toString() ? `${api.routes.adminTicketBoard}?${params.toString()}` : api.routes.adminTicketBoard;
      const data = await api.fetch(url, { method: 'GET' });
      const lanes = Array.isArray(data?.lanes) ? data.lanes : [];
      ticketsBoardState.lanes = lanes;
      ticketsBoardState.generatedAt = data && data.generated_at ? data.generated_at : null;
      ticketsBoardState.initialized = true;
      ticketsBoardState.stale = false;
      renderTicketsBoard();
    } catch (err) {
      ticketsBoardState.lanes = [];
      ticketsBoardState.initialized = true;
      ticketsBoardState.stale = true;
      if (dom.status) {
        dom.status.textContent = err && err.message ? err.message : 'Failed to load technician board';
      }
      if (dom.lanes) dom.lanes.innerHTML = '';
      if (dom.empty) dom.empty.hidden = false;
    } finally {
      ticketsBoardState.loading = false;
    }
  }

  function scheduleTicketsBoardRefresh() {
    ticketsBoardState.stale = true;
    if (ticketsBoardRefreshTimer) return;
    ticketsBoardRefreshTimer = setTimeout(() => {
      ticketsBoardRefreshTimer = null;
      loadTicketsBoard({ silent: true }).catch(() => {});
    }, 600);
  }

  function refreshIfVisible(options = {}) {
    const dom = resolveTechnicianBoardDom();
    if (dom.board && !dom.board.hidden) {
      return loadTicketsBoard(options);
    }
    return Promise.resolve();
  }

  // Convenience for external callers (WS handlers, ticket-action menu,
  // reloadTickets, etc.) that need to say "board data may be stale —
  // refresh it now if visible, otherwise just mark it for the next time
  // the user opens the view." Replaces the 4-line
  // `ticketsBoardState.stale = true; if (ticketsBoard && !ticketsBoard.hidden) loadTicketsBoard({silent:true})`
  // pattern that was scattered across admin.js.
  function markChanged(refreshOptions = { silent: true }) {
    ticketsBoardState.stale = true;
    return refreshIfVisible(refreshOptions);
  }

  // ---- Click + drag handlers ------------------------------------------
  function handleTicketsBoardClick(event) {
    const btn = event.target.closest('[data-board-action]');
    if (!btn) return;
    const ticketId = btn.dataset.ticketId;
    if (!ticketId) return;
    const context = findBoardTicket(ticketId);
    if (!context) {
      notify.showFlash('Ticket not found on board', 'error');
      return;
    }
    const action = btn.dataset.boardAction;
    if (action === 'review') {
      tbHelpers.openTechClosureReview(context.ticket);
    } else if (action === 'details') {
      tbHelpers.openTicketDetailFromBoard(String(ticketId), context.lane, {
        suppressReload: true,
        scrollHighlight: true,
      });
    }
  }

  function handleBoardCardDragStart(event) {
    const card = event.currentTarget;
    const ticketId = card && card.dataset ? card.dataset.ticketId : null;
    if (!ticketId) return;
    ticketsBoardState.dragTicketId = ticketId;
    ticketsBoardState.dragSourceLane = card.dataset.laneKey || null;
    card.classList.add('dragging');
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      try { event.dataTransfer.setData('text/plain', ticketId); } catch {}
    }
  }

  function handleBoardCardDragEnd(event) {
    const card = event.currentTarget;
    card.classList.remove('dragging');
    ticketsBoardState.dragTicketId = null;
    ticketsBoardState.dragSourceLane = null;
  }

  function handleBoardLaneDragOver(event) {
    event.preventDefault();
    const laneBody = event.currentTarget;
    laneBody.classList.add('dragover');
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  function handleBoardLaneDragLeave(event) {
    event.currentTarget.classList.remove('dragover');
  }

  function handleBoardLaneDrop(event) {
    event.preventDefault();
    const laneBody = event.currentTarget;
    laneBody.classList.remove('dragover');
    const ticketId = ticketsBoardState.dragTicketId || (event.dataTransfer ? event.dataTransfer.getData('text/plain') : null);
    if (!ticketId) return;
    const targetLaneKey = laneBody.dataset.laneKey || '';
    const targetAdminUserId = laneBody.dataset.adminUserId ? Number(laneBody.dataset.adminUserId) : null;
    if (targetLaneKey === 'supervisors') {
      notify.showFlash('Drag tickets onto a technician lane to assign them.', 'error');
      return;
    }
    const sourceLane = ticketsBoardState.dragSourceLane;
    if (sourceLane && targetLaneKey && sourceLane === targetLaneKey) {
      return;
    }
    const context = findBoardTicket(ticketId);
    if (!context) {
      notify.showFlash('Ticket not found on board', 'error');
      return;
    }
    openBoardAssignDialog(context, {
      laneKey: targetLaneKey,
      adminUserId: targetAdminUserId,
      laneLabel: laneBody.dataset.laneLabel || 'Technician',
    });
  }

  // ---- Board-assign dialog --------------------------------------------
  function openBoardAssignDialog(context, target) {
    const dom = resolveTechnicianBoardDom();
    if (!dom.assignDlg) return;
    const { ticket } = context;
    boardAssignState.ticketId = ticket.id;
    boardAssignState.adminUserId = target.adminUserId;
    boardAssignState.laneKey = target.laneKey;
    if (dom.assignInput) dom.assignInput.value = '';
    if (dom.assignSummary) {
      const shortCode = ticket.meta && ticket.meta.ticket_short_code ? `#${ticket.meta.ticket_short_code}` : String(ticket.id).slice(0, 8);
      const destination = target.adminUserId != null ? target.laneLabel : 'Unassigned';
      dom.assignSummary.textContent = `Reassign ${shortCode} → ${destination}`;
    }
    tbHelpers.openDialog(dom.assignDlg);
    if (dom.assignInput) dom.assignInput.focus();
  }

  function closeBoardAssignDialog() {
    const dom = resolveTechnicianBoardDom();
    if (!dom.assignDlg) return;
    try { dom.assignDlg.close(); }
    catch { dom.assignDlg.open = false; }
    boardAssignState.ticketId = null;
    boardAssignState.adminUserId = null;
    boardAssignState.laneKey = null;
    if (dom.assignInput) dom.assignInput.value = '';
  }

  async function handleBoardAssignSubmit(event) {
    event.preventDefault();
    if (!boardAssignState.ticketId) return;
    const dom = resolveTechnicianBoardDom();
    const note = dom.assignInput ? dom.assignInput.value.trim() : '';
    if (!note) {
      notify.showFlash('Supervisor note is required for reassignment', 'error');
      if (dom.assignInput) dom.assignInput.focus();
      return;
    }
    if (dom.assignForm) dom.assignForm.dataset.pending = '1';
    if (dom.assignSubmit) dom.assignSubmit.disabled = true;
    try {
      const payload = { assignment_note: note };
      if (boardAssignState.adminUserId == null) {
        payload.assignee_user_id = null;
      } else {
        payload.assignee_user_id = Number(boardAssignState.adminUserId);
      }
      const updated = await tbHelpers.mutateTicket(boardAssignState.ticketId, payload);
      notify.showFlash('Ticket reassigned');
      closeBoardAssignDialog();
      ticketsBoardState.stale = true;
      if (dom.board && !dom.board.hidden) {
        loadTicketsBoard({ silent: true }).catch(() => {});
      }
      const cb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.customerBoard;
      if (cb) {
        cb.setStale(true);
        if (cb.hasAccess()) cb.refresh({ silent: true }).catch(() => {});
      }
      await tbHelpers.reloadTickets({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      notify.showFlash(err && err.message ? err.message : 'Failed to reassign ticket', 'error');
    } finally {
      if (dom.assignForm) dom.assignForm.dataset.pending = '0';
      if (dom.assignSubmit) dom.assignSubmit.disabled = false;
    }
  }

  function mountTechnicianBoard() {
    const dom = resolveTechnicianBoardDom();
    if (technicianBoardMounted) return;
    if (dom.lanes) {
      dom.lanes.addEventListener('click', handleTicketsBoardClick);
    }
    if (dom.supervisorFilter) {
      dom.supervisorFilter.addEventListener('change', handleTicketsBoardSupervisorChange);
    }
    if (dom.supervisorTypeFilter) {
      dom.supervisorTypeFilter.addEventListener('change', handleTicketsBoardSupervisorTypeChange);
    }
    if (dom.supervisorAgeFilter) {
      dom.supervisorAgeFilter.addEventListener('change', handleTicketsBoardSupervisorAgeChange);
    }
    if (dom.assignForm) {
      dom.assignForm.addEventListener('submit', handleBoardAssignSubmit);
    }
    if (dom.assignCancel) {
      dom.assignCancel.addEventListener('click', (event) => { event.preventDefault(); closeBoardAssignDialog(); });
    }
    if (dom.assignClose) {
      dom.assignClose.addEventListener('click', (event) => { event.preventDefault(); closeBoardAssignDialog(); });
    }
    if (dom.assignDlg) {
      dom.assignDlg.addEventListener('cancel', (event) => { event.preventDefault(); closeBoardAssignDialog(); });
    }
    technicianBoardMounted = true;
  }

  views.tickets.technicianBoard = {
    mount: mountTechnicianBoard,
    refresh: (options) => loadTicketsBoard(options),
    refreshIfVisible,
    markChanged,
    scheduleRefresh: scheduleTicketsBoardRefresh,
    setStale: (value) => { ticketsBoardState.stale = Boolean(value); },
    isStale: () => Boolean(ticketsBoardState.stale),
    isInitialized: () => Boolean(ticketsBoardState.initialized),
    isVisible: () => {
      const board = resolveTechnicianBoardDom().board;
      return Boolean(board && !board.hidden);
    },
    populateSupervisorFilter: populateTicketsBoardSupervisorFilter,
    focusLane: focusTechnicianLane,
    openAssignDialog: openBoardAssignDialog,
    closeAssignDialog: closeBoardAssignDialog,
    getState: () => ticketsBoardState,
    getScrollEl: () => resolveTechnicianBoardDom().scroll,
    getBoardEl: () => resolveTechnicianBoardDom().board,
    hasAccess: () => tbHelpers.hasAccess(),
  };

  // ---- Ticket list + filters + pagination (Phase 2.1.d.2) -------------
  // Owns the "list" mode of the Tickets view:
  //   - The filter strip (`#ticketSearch`, status/closed/priority/assignee/
  //     assignee-user/outage/SLA/type/age/sort selects + the "Reset" button).
  //   - The list/board mode toggle buttons (`#ticketModeListBtn`,
  //     `#ticketModeBoardBtn`).
  //   - The table render at `#ticketsTbody` + pagination
  //     (`#ticketsPrev`/`#ticketsNext`/`#ticketsPageInfo`/`#ticketsCount`).
  //   - The `ticketsState` object and the surrounding state vars
  //     (`ticketsInitialized`, the in-flight reload sequence number, the
  //     pending-select id, the suppression flag, the assignee-user manual
  //     flag, and the `unassignedState` cache that backs the "unassigned"
  //     quick view).
  //   - The list-side fetch (`reloadTickets` + `loadTicketDetailById`),
  //     row click → `selectTicket`, post-creation navigation
  //     (`openTicketDetailAfterCreation`), and the cross-cluster
  //     "open ticket from notification / from board" entrypoints.
  //   - The action-menu outside-click handler that closes any open
  //     `<details class="action-menu">` inside the table when the user
  //     clicks elsewhere.
  //
  // What stays in admin.js (injected via `configure({helpers})`):
  //   - The right-side detail panel (`renderTicketDetail`,
  //     `setTicketListCollapsed`, `getTicketDetailCurrent`) —
  //     2.1.d.3 territory.
  //   - The comment thread (`loadTicketComments`) — 2.1.d.5 territory.
  //   - The create-ticket dialog (`openTicketDialog`,
  //     `populateTicketCreateAssignmentOptions`, the `New ticket` button
  //     listener, the create-flow prefill `Map`) — slated for 2.1.d.2b.
  //   - Shared formatters (`formatTicketStatus/Priority/Assignee`,
  //     `formatDateTimeShort/Full`, `escapeHtml`) and the SLA computation
  //     (`computeSla`, `hydrateTicketAssignment`) — shared with the detail
  //     panel and ticket boards.
  //   - The attention-highlight tracker (`ticketAttentionSet`,
  //     `applyTicketAttentionHighlight`, `clearTicketAttention`,
  //     `markTicketAttention`) — couples to the comment thread.
  //   - The router (`switchView`) and role gate (`canAccessTicketing`,
  //     `hasRole`).
  //
  // The 5 external `loadTicketsBoard({silent:true})` callsites in admin.js
  // already collapsed into `tb.markChanged()` during 2.1.d.1; the
  // equivalent transition for this slice is the new
  // `list.openFromNotification(id)` / `list.openFromBoard(id, lane, opts)`
  // entrypoints that bundle the multi-step navigation flow into a single
  // helper instead of having admin.js / the boards reach into
  // `ticketsState` directly.

  const LIST_DEFAULT_CLOSED_FILTER = 'hide';
  const LIST_DEFAULT_TICKET_SORT = 'updated_desc';
  const LIST_TICKET_PRIORITY_RANK = Object.freeze({
    p5_critical: 5,
    p4_urgent: 4,
    p3_high: 3,
    p2_medium: 2,
    p1_low: 1,
  });

  const ticketsState = {
    limit: 20,
    offset: 0,
    items: [],
    latestPage: [],
    filters: {
      search: '',
      status: '',
      priority: '',
      assignee: '',
      assigneeUser: '',
      outage: '',
      slaOnly: false,
      type: '',
      age: '',
      assigned: null,
      closedFilter: LIST_DEFAULT_CLOSED_FILTER,
    },
    selectedId: null,
    loading: false,
    quickMode: null,
    savedAssigneeUserFilter: null,
    viewMode: 'list',
    sort: LIST_DEFAULT_TICKET_SORT,
  };

  let ticketsInitialized = false;
  let ticketsReloadSeq = 0;
  let ticketsPendingSelectId = null;
  let ticketsPendingFilterOverride = null;
  let suppressNextTicketsReload = false;
  let ticketAssigneeUserManual = false;
  let ticketActionMenuEventsBound = false;
  let listMounted = false;
  const unassignedState = { hasAny: false, cache: [] };
  let listDom = null;

  // Phase 3.2.c: listHelpers populated from imports + cross-sub-view
  // lookups. Detail/interactions/comments live in this same file —
  // reach for them via `views.tickets.X` lazily so we don't depend on
  // sub-view declaration order.
  const listHelpers = {
    escapeHtml,
    formatTicketStatus,
    formatTicketPriority,
    formatTicketAssignee,
    formatDateTimeShort,
    formatDateTimeFull,
    computeSla: (ticket) => {
      const d = views.tickets && views.tickets.detail;
      return d && typeof d.computeSla === 'function' ? d.computeSla(ticket) : { label: '', breached: false };
    },
    hydrateTicketAssignment,
    renderTicketDetail: (ticket) => {
      const d = views.tickets && views.tickets.detail;
      if (d && typeof d.render === 'function') d.render(ticket);
    },
    loadTicketComments: (id) => loadTicketComments(id),
    setTicketListCollapsed: (collapsed, opts) => {
      const d = views.tickets && views.tickets.detail;
      if (d && typeof d.setListCollapsed === 'function') d.setListCollapsed(collapsed, opts);
    },
    getTicketDetailCurrent: () => {
      const d = views.tickets && views.tickets.detail;
      return d && typeof d.getCurrent === 'function' ? d.getCurrent() : null;
    },
    getAttentionSet: () => {
      const i = views.tickets && views.tickets.detailInteractions;
      return i && typeof i.getAttentionSet === 'function' ? i.getAttentionSet() : new Set();
    },
    applyAttentionHighlight: (id) => applyTicketAttentionHighlight(id),
    clearTicketAttention: (id) => clearTicketAttention(id),
    switchView: (view, btn) => switchToView(view, btn),
    hasRole,
    canCreateTickets,
    canAccessTicketing,
    getCurrentUserId,
    getCreatePrefill: () => ticketCreatePrefill,
    consumeCreatePrefill,
    ensureTechnicianUsersLoaded,
    populateTicketAssigneeUserFilter: (selected) => populateTicketAssigneeUserFilter(selected),
    getNavTicketsUnassigned: () => document.getElementById('navTicketsUnassigned'),
    getNavTicketsAll: () => document.getElementById('navTicketsAll'),
  };

  function resolveListDom() {
    if (listDom) return listDom;
    listDom = {
      view: document.getElementById('ticketsView'),
      tbody: document.getElementById('ticketsTbody'),
      prev: document.getElementById('ticketsPrev'),
      next: document.getElementById('ticketsNext'),
      pageInfo: document.getElementById('ticketsPageInfo'),
      count: document.getElementById('ticketsCount'),
      search: document.getElementById('ticketSearch'),
      status: document.getElementById('ticketStatusFilter'),
      closed: document.getElementById('ticketClosedFilter'),
      priority: document.getElementById('ticketPriorityFilter'),
      assignee: document.getElementById('ticketAssigneeFilter'),
      assigneeUser: document.getElementById('ticketAssigneeUserFilter'),
      outage: document.getElementById('ticketOutageFilter'),
      sla: document.getElementById('ticketSlaFilter'),
      type: document.getElementById('ticketTypeFilter'),
      age: document.getElementById('ticketAgeFilter'),
      sort: document.getElementById('ticketSortFilter'),
      reset: document.getElementById('ticketResetFilters'),
      layout: document.getElementById('ticketsLayout'),
    };
    return listDom;
  }

  // ---- Status / sort / filter helpers ---------------------------------
  // True when a search is running under the default closed filter, which the search
  // widens (see below) — the count line says so.
  function searchSpansAllStatuses() {
    return Boolean(ticketsState.filters.search)
      && !ticketsState.filters.status
      && !ticketsState.filters.closedFilterTouched;
  }

  // What the list asks the API for. An explicit status tab wins; otherwise we send
  // the open/closed INTENT and the server resolves it from TicketStatus — the one
  // definition of open vs closed. The browser deliberately keeps no status list of
  // its own: that duplicate is what drifted and hid resolved-by-technician tickets.
  // Returns { status, closed } — either may be undefined.
  function resolveTicketStatusQuery() {
    if (ticketsState.filters.status) {
      return { status: ticketsState.filters.status };
    }
    // Searching is a hunt for a particular ticket, so it spans every status unless
    // the agent chose one — including genuinely closed ones, which the default
    // filter hides.
    if (searchSpansAllStatuses()) {
      return { closed: 'include' };
    }
    return { closed: ticketsState.filters.closedFilter || LIST_DEFAULT_CLOSED_FILTER };
  }

  function getTicketTimestamp(ticket) {
    if (!ticket) return 0;
    const value = ticket.updated_at || ticket.created_at;
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  // resolveTicketAgeTime + isInstallationTicket declared once above
  // (Phase 3.2.b strict-mode dedup — script-tag mode silently shadowed;
  // ES module mode rejects).

  function getTicketPriorityScore(ticket) {
    if (!ticket) return 0;
    const priority = (ticket.priority || '').toLowerCase();
    return LIST_TICKET_PRIORITY_RANK[priority] || 0;
  }

  function sortTicketsForView(items, mode = LIST_DEFAULT_TICKET_SORT) {
    if (!Array.isArray(items) || items.length < 2) return items;
    const resolvedMode = mode || LIST_DEFAULT_TICKET_SORT;
    const sorted = items.slice();
    const compareUpdatedDesc = (a, b) => getTicketTimestamp(b) - getTicketTimestamp(a);
    switch (resolvedMode) {
      case 'updated_asc':
        sorted.sort((a, b) => getTicketTimestamp(a) - getTicketTimestamp(b));
        break;
      case 'priority_desc':
        sorted.sort((a, b) => {
          const diff = getTicketPriorityScore(b) - getTicketPriorityScore(a);
          return diff !== 0 ? diff : compareUpdatedDesc(a, b);
        });
        break;
      case 'priority_asc':
        sorted.sort((a, b) => {
          const diff = getTicketPriorityScore(a) - getTicketPriorityScore(b);
          return diff !== 0 ? diff : compareUpdatedDesc(a, b);
        });
        break;
      case 'updated_desc':
      default:
        sorted.sort(compareUpdatedDesc);
        break;
    }
    return sorted;
  }

  // ---- Unassigned indicator + action-menu outside-click ---------------
  function updateUnassignedIndicator(hasAny) {
    const nav = listHelpers.getNavTicketsUnassigned();
    if (!nav) return;
    nav.classList.toggle('nav-alert', Boolean(hasAny));
    nav.dataset.count = hasAny ? '1' : '0';
    if (!hasAny && nav.classList.contains('active')) {
      nav.classList.remove('nav-alert');
    }
  }

  function applyQuickNavClasses(mode) {
    const navUnassigned = listHelpers.getNavTicketsUnassigned();
    const navAll = listHelpers.getNavTicketsAll();
    if (navUnassigned) navUnassigned.classList.toggle('active-quick', mode === 'unassigned');
    if (navAll) navAll.classList.toggle('active-quick', !mode);
  }

  function closeOpenTicketMenus(except) {
    const dom = resolveListDom();
    if (!dom.view) return;
    dom.view.querySelectorAll('details.action-menu[open]').forEach((menu) => {
      if (except && menu === except) return;
      menu.open = false;
    });
  }

  function handleTicketActionMenuOutsideClick(event) {
    const dom = resolveListDom();
    if (!dom.view || !dom.view.isConnected) return;
    const targetMenu = event.target.closest('details.action-menu');
    if (targetMenu && dom.view.contains(targetMenu)) {
      closeOpenTicketMenus(targetMenu);
      return;
    }
    if (dom.view.querySelector('details.action-menu[open]')) {
      closeOpenTicketMenus();
    }
  }

  async function refreshUnassignedSnapshot() {
    try {
      const params = new URLSearchParams();
      params.set('limit', String(ticketsState.limit));
      params.set('assigned', 'false');
      const data = await api.fetch(`${api.routes.adminTickets}?${params.toString()}`, { method: 'GET' });
      const items = Array.isArray(data.items) ? data.items : [];
      const hasAny = items.length > 0;
      unassignedState.hasAny = hasAny;
      unassignedState.cache = items;
      updateUnassignedIndicator(hasAny);
    } catch (err) {
      // Swallow errors to avoid noisy flashes; indicator will refresh on next poll.
    }
  }

  // ---- View-mode toggle (list <> board) -------------------------------
  // The list/board toggle UI was removed; the function is kept because the
  // legacy admin.js view-router still calls it on every view switch and a
  // few internal callers (reload paths) rely on it to flip the layout
  // visibility back on. Mode is forced to 'list' regardless of the argument.
  function setTicketsViewMode(mode) {
    ticketsState.viewMode = 'list';
    const dom = resolveListDom();
    if (dom.layout) {
      dom.layout.style.display = '';
    }
  }

  // ---- Assignee filter (moved here from admin.js in Phase 3.2.c) ----
  function populateTicketAssigneeUserFilter(selectedValue) {
    const dom = resolveListDom();
    const filterEl = dom.assigneeUser;
    if (!filterEl) return;
    const previous = typeof selectedValue !== 'undefined' ? selectedValue : filterEl.value;
    filterEl.innerHTML = '';
    const addOption = (value, label) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      filterEl.appendChild(option);
    };
    addOption('', 'Assigned to: Anyone');
    const seen = new Set(['']);
    const me = getCurrentAdminUser();
    const adminId = me && me.id != null ? String(me.id) : null;
    if (adminId) {
      const meLabel = me.username
        ? `You (${me.username})`
        : me.email
          ? `You (${me.email})`
          : `You (#${adminId})`;
      addOption(adminId, meLabel);
      seen.add(adminId);
    }
    const technicians = getTechnicianAdminUsers();
    const users = Array.isArray(technicians) ? technicians.slice() : [];
    users.sort((a, b) => adminUserSortKey(a).localeCompare(adminUserSortKey(b)));
    users.forEach((user) => {
      if (!user || typeof user.id === 'undefined' || user.id === null) return;
      if (!userEligibleForTicketAssignment(user)) return;
      const id = String(user.id);
      if (seen.has(id)) return;
      seen.add(id);
      addOption(id, formatAdminUserLabel(user));
    });
    if (previous && seen.has(previous)) {
      filterEl.value = previous;
    } else if (!seen.has(previous)) {
      filterEl.value = '';
    }
  }

  // ---- Default assignee filter ---------------------------------------
  function applyDefaultAssigneeUserFilter({ force = false } = {}) {
    const dom = resolveListDom();
    if (!dom.assigneeUser) return;
    if (ticketsState.quickMode === 'unassigned') return;
    if (!force && (ticketAssigneeUserManual || ticketsState.filters.assigneeUser)) return;

    if (listHelpers.hasRole('customer_care_supervisor')) {
      ticketsState.filters.assigneeUser = '';
      if (typeof ticketsState.filters.assigned !== 'boolean') {
        ticketsState.filters.assigned = true;
      }
      if (dom.assigneeUser.value !== '') {
        dom.assigneeUser.value = '';
      }
      ticketAssigneeUserManual = false;
      return;
    }

    const adminId = listHelpers.getCurrentUserId();
    if (!adminId) return;
    ticketsState.filters.assigneeUser = adminId;
    if (dom.assigneeUser.value !== adminId) {
      dom.assigneeUser.value = adminId;
    }
    ticketAssigneeUserManual = false;
  }

  // ---- Quick-mode (Unassigned shortcut) ------------------------------
  function setTicketsQuickView(mode) {
    const dom = resolveListDom();
    const resolved = mode === 'unassigned' ? 'unassigned' : null;
    ticketsState.quickMode = resolved;
    ticketsState.offset = 0;
    if (resolved === 'unassigned') {
      ticketsState.savedAssigneeUserFilter = ticketsState.filters.assigneeUser || null;
      ticketsState.filters.assigned = false;
      ticketsState.filters.assignee = '';
      ticketsState.filters.assigneeUser = '';
      if (dom.assignee) dom.assignee.value = '';
      if (dom.assigneeUser) dom.assigneeUser.value = '';
      ticketAssigneeUserManual = false;
    } else {
      ticketsState.filters.assigned = null;
      if (ticketsState.savedAssigneeUserFilter !== null) {
        const restored = ticketsState.savedAssigneeUserFilter || '';
        ticketsState.savedAssigneeUserFilter = null;
        if (restored) {
          ticketsState.filters.assigneeUser = restored;
          if (dom.assigneeUser) dom.assigneeUser.value = restored;
          const adminId = listHelpers.getCurrentUserId();
          ticketAssigneeUserManual = adminId ? restored !== adminId : true;
        } else {
          applyDefaultAssigneeUserFilter({ force: true });
        }
      } else if (!ticketsState.filters.assigneeUser) {
        applyDefaultAssigneeUserFilter({ force: true });
      }
    }
    applyQuickNavClasses(resolved);
    if (
      resolved === 'unassigned'
      && ticketsInitialized
      && Array.isArray(unassignedState.cache)
      && unassignedState.cache.length
      && ticketsState.offset === 0
    ) {
      const snapshot = unassignedState.cache.slice();
      ticketsState.latestPage = snapshot;
      ticketsState.items = snapshot;
      renderTicketsTable(snapshot, snapshot.length);
      const first = snapshot[0];
      if (first) {
        ticketsState.selectedId = String(first.id);
        listHelpers.renderTicketDetail(first);
        listHelpers.loadTicketComments(first.id);
      }
    }
  }

  function clearTicketsQuickMode() {
    if (!ticketsState.quickMode) return;
    const dom = resolveListDom();
    ticketsState.quickMode = null;
    ticketsState.filters.assigned = null;
    if (ticketsState.savedAssigneeUserFilter !== null) {
      ticketsState.filters.assigneeUser = ticketsState.savedAssigneeUserFilter;
      if (dom.assigneeUser) dom.assigneeUser.value = ticketsState.savedAssigneeUserFilter || '';
      ticketsState.savedAssigneeUserFilter = null;
      const adminId = listHelpers.getCurrentUserId();
      ticketAssigneeUserManual = ticketsState.filters.assigneeUser
        ? (adminId ? ticketsState.filters.assigneeUser !== adminId : true)
        : false;
    } else {
      applyDefaultAssigneeUserFilter();
    }
    applyQuickNavClasses(null);
  }

  function resetTicketFiltersToDefault({ userInitiated = false } = {}) {
    const dom = resolveListDom();
    clearTicketsQuickMode();
    ticketsState.filters = {
      search: '',
      status: '',
      priority: '',
      assignee: '',
      assigneeUser: '',
      outage: '',
      slaOnly: false,
      type: '',
      age: '',
      assigned: null,
      closedFilter: LIST_DEFAULT_CLOSED_FILTER,
      closedFilterTouched: false,
    };
    ticketsState.offset = 0;
    ticketsState.savedAssigneeUserFilter = null;
    ticketsState.sort = LIST_DEFAULT_TICKET_SORT;
    ticketsPendingFilterOverride = null;
    ticketAssigneeUserManual = false;
    if (dom.search) dom.search.value = '';
    if (dom.status) dom.status.value = '';
    if (dom.closed) dom.closed.value = LIST_DEFAULT_CLOSED_FILTER;
    if (dom.priority) dom.priority.value = '';
    if (dom.assignee) dom.assignee.value = '';
    if (dom.assigneeUser) dom.assigneeUser.value = '';
    if (dom.outage) dom.outage.value = '';
    if (dom.sla) dom.sla.checked = false;
    if (dom.type) dom.type.value = '';
    if (dom.age) dom.age.value = '';
    if (dom.sort) dom.sort.value = LIST_DEFAULT_TICKET_SORT;
    if (userInitiated) reloadTickets();
  }

  // ---- Filter override (called from board "Details" navigation) -------
  function applyTicketsFilterOverride(override, options = {}) {
    if (!override) return;
    const dom = resolveListDom();
    const {
      skipQuickMode = false,
      syncControls = true,
      preserve = false,
    } = options;
    const assigneeUser = override.assigneeUser != null ? String(override.assigneeUser) : '';
    const assignee = override.assignee != null ? String(override.assignee) : '';
    const assigned = Object.prototype.hasOwnProperty.call(override, 'assigned') ? override.assigned : null;
    const manualAssigneeUser = Object.prototype.hasOwnProperty.call(override, 'manualAssigneeUser')
      ? override.manualAssigneeUser
      : true;

    if (!skipQuickMode) {
      clearTicketsQuickMode();
    }

    ticketsState.filters.assignee = assignee;
    ticketsState.filters.assigneeUser = assigneeUser;
    if (typeof assigned === 'boolean') {
      ticketsState.filters.assigned = assigned;
    } else {
      ticketsState.filters.assigned = null;
    }
    ticketsState.savedAssigneeUserFilter = null;
    ticketsState.offset = 0;

    if (syncControls) {
      if (dom.assignee && dom.assignee.value !== assignee) {
        dom.assignee.value = assignee;
      }
      if (dom.assigneeUser && dom.assigneeUser.value !== assigneeUser) {
        dom.assigneeUser.value = assigneeUser;
      }
    }

    ticketAssigneeUserManual = Boolean(manualAssigneeUser);

    if (preserve) {
      ticketsPendingFilterOverride = {
        assigneeUser,
        assignee,
        assigned: ticketsState.filters.assigned,
        manualAssigneeUser: ticketAssigneeUserManual,
      };
    } else {
      ticketsPendingFilterOverride = null;
    }
  }

  function prepareTicketsFiltersForBoardTicket(lane) {
    const laneKey = lane && lane.key ? String(lane.key) : '';
    const laneAdminId = lane && lane.admin_user_id != null ? String(lane.admin_user_id) : null;
    const currentAdminId = listHelpers.getCurrentUserId() || null;
    const override = {
      assigneeUser: laneAdminId || '',
      assignee: '',
      assigned: null,
      manualAssigneeUser: !laneAdminId || !currentAdminId || laneAdminId !== currentAdminId,
    };

    if (!laneAdminId) {
      override.assigned = laneKey === 'unassigned' ? false : null;
    }

    if (ticketsInitialized) {
      applyTicketsFilterOverride(override);
    } else {
      ticketsPendingFilterOverride = override;
    }
  }

  // ---- Network + render ----------------------------------------------
  async function reloadTickets(options = {}) {
    const dom = resolveListDom();
    if (!dom.view) return;
    const keepSelection = Boolean(options.keepSelection);
    const explicitSelectId = options.selectId ? String(options.selectId) : null;
    const preservedId = explicitSelectId || (keepSelection ? ticketsState.selectedId : null);
    const requestId = ++ticketsReloadSeq;
    const desiredId = preservedId || ticketsPendingSelectId ? String(preservedId || ticketsPendingSelectId) : null;

    const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
    if (tb) {
      tb.setStale(true);
      if (tb.isInitialized()) tb.refreshIfVisible({ silent: true });
    }

    const params = new URLSearchParams();
    params.set('limit', String(ticketsState.limit));
    params.set('offset', String(ticketsState.offset));
    const statusQuery = resolveTicketStatusQuery();
    if (statusQuery.status) params.append('status', statusQuery.status);
    if (statusQuery.closed) params.append('closed', statusQuery.closed);
    if (ticketsState.filters.priority) params.append('priority', ticketsState.filters.priority);
    if (ticketsState.filters.assignee) params.append('assignee_role', ticketsState.filters.assignee);
    if (ticketsState.filters.assigneeUser) params.append('assignee_user_id', ticketsState.filters.assigneeUser);
    if (ticketsState.filters.search) params.append('search', ticketsState.filters.search);
    if (typeof ticketsState.filters.assigned === 'boolean') {
      params.append('assigned', ticketsState.filters.assigned ? 'true' : 'false');
    }

    const url = `${api.routes.adminTickets}?${params.toString()}`;
    ticketsState.loading = true;
    if (dom.count) dom.count.textContent = 'Loading…';
    try {
      const data = await api.fetch(url, { method: 'GET' });
      if (requestId !== ticketsReloadSeq) return;
      const rawItems = Array.isArray(data.items) ? data.items : [];
      ticketsState.latestPage = rawItems;
      let filtered = rawItems;
      if (ticketsState.filters.outage) {
        const query = ticketsState.filters.outage;
        filtered = filtered.filter((ticket) => {
          const outage = (ticket && (ticket.outage_tag_id || (ticket.meta && ticket.meta.outage_tag))) || '';
          return String(outage).toLowerCase().includes(query);
        });
      }
      if (ticketsState.filters.slaOnly) {
        filtered = filtered.filter((ticket) => listHelpers.computeSla(ticket).breached);
      }
      if (ticketsState.filters.type === 'installation') {
        filtered = filtered.filter((ticket) => isInstallationTicket(ticket));
      } else if (ticketsState.filters.type === 'non_installation') {
        filtered = filtered.filter((ticket) => !isInstallationTicket(ticket));
      }
      if (ticketsState.filters.age) {
        const now = Date.now();
        filtered = filtered.filter((ticket) => {
          const referenceTime = resolveTicketAgeTime(ticket);
          if (!referenceTime) return false;
          const ageMs = Math.max(0, now - referenceTime);
          const dayMs = 24 * 60 * 60 * 1000;
          switch (ticketsState.filters.age) {
            case 'lt_1d':
              return ageMs < dayMs;
            case '1_3d':
              return ageMs >= dayMs && ageMs < 3 * dayMs;
            case '3_7d':
              return ageMs >= 3 * dayMs && ageMs < 7 * dayMs;
            case 'gt_7d':
              return ageMs >= 7 * dayMs;
            default:
              return true;
          }
        });
      }
      filtered = sortTicketsForView(filtered, ticketsState.sort);
      ticketsState.items = filtered;
      renderTicketsTable(filtered, rawItems.length);

      if (filtered.length === 0) {
        if (desiredId) {
          const loaded = await loadTicketDetailById(desiredId, { requestId });
          if (loaded && ticketsPendingSelectId && String(loaded.id) === String(ticketsPendingSelectId)) {
            ticketsPendingSelectId = null;
          }
          return;
        }
        if (ticketsPendingSelectId && ticketsPendingSelectId !== explicitSelectId) {
          ticketsState.selectedId = null;
        }
        ticketsState.selectedId = null;
        listHelpers.renderTicketDetail(null);
        return;
      }
      const targetId = desiredId && filtered.some((t) => String(t.id) === String(desiredId))
        ? desiredId
        : null;
      if (targetId) {
        ticketsState.selectedId = targetId;
        const prefill = listHelpers.getCreatePrefill();
        if (prefill && typeof prefill.delete === 'function') prefill.delete(targetId);
        updateTicketSelectionHighlight();
        const target = findTicketById(targetId);
        if (target) {
          listHelpers.renderTicketDetail(target);
          listHelpers.loadTicketComments(target.id);
          if (ticketsPendingSelectId && targetId === String(ticketsPendingSelectId)) {
            listHelpers.setTicketListCollapsed(true);
          }
        } else {
          listHelpers.renderTicketDetail(null);
        }
        if (ticketsPendingSelectId && targetId === String(ticketsPendingSelectId)) {
          ticketsPendingSelectId = null;
        }
      } else if (desiredId) {
        const fallback = listHelpers.consumeCreatePrefill(desiredId);
        if (fallback) {
          ticketsState.selectedId = desiredId;
          updateTicketSelectionHighlight();
          listHelpers.renderTicketDetail(fallback);
          if (fallback && fallback.id) {
            listHelpers.loadTicketComments(fallback.id);
          }
          if (ticketsPendingSelectId && desiredId === String(ticketsPendingSelectId)) {
            listHelpers.setTicketListCollapsed(true);
          }
        } else {
          const loaded = await loadTicketDetailById(desiredId, { requestId });
          if (loaded && ticketsPendingSelectId && desiredId === String(ticketsPendingSelectId)) {
            ticketsPendingSelectId = null;
          }
        }
      } else {
        ticketsState.selectedId = null;
        updateTicketSelectionHighlight();
        listHelpers.renderTicketDetail(null);
      }
    } catch (err) {
      if (requestId !== ticketsReloadSeq) return;
      ticketsState.items = [];
      ticketsState.latestPage = [];
      if (dom.tbody) {
        dom.tbody.innerHTML = `<tr><td colspan="8" style="color:var(--sub);">${listHelpers.escapeHtml(err.message || 'Failed to load tickets')}</td></tr>`;
      }
      if (dom.count) {
        dom.count.textContent = err && err.status === 404 ? 'Ticketing feature disabled.' : '';
      }
      ticketsState.selectedId = null;
      listHelpers.renderTicketDetail(null);
    } finally {
      if (requestId === ticketsReloadSeq) {
        ticketsState.loading = false;
      }
    }
  }

  async function loadTicketDetailById(ticketId, { requestId } = {}) {
    if (!ticketId) return null;
    try {
      const ticket = await api.fetch(api.routes.adminTicket(ticketId), { method: 'GET' });
      if (requestId && requestId !== ticketsReloadSeq) return null;
      ticketsState.selectedId = String(ticket.id || ticketId);
      updateTicketSelectionHighlight();
      listHelpers.renderTicketDetail(ticket);
      listHelpers.loadTicketComments(ticket.id);
      listHelpers.setTicketListCollapsed(true);
      return ticket;
    } catch (err) {
      if (requestId && requestId !== ticketsReloadSeq) return null;
      ticketsState.selectedId = null;
      updateTicketSelectionHighlight();
      listHelpers.renderTicketDetail(null);
      return null;
    }
  }

  function openTicketFromNotification(ticketId) {
    if (!ticketId) return;
    const id = String(ticketId);
    listHelpers.switchView('tickets');
    resetTicketFiltersToDefault();
    listHelpers.clearTicketAttention(id);
    ticketsPendingSelectId = id;
    ticketsState.offset = 0;
    const delay = ticketsInitialized ? 0 : 150;
    setTimeout(() => {
      reloadTickets({ keepSelection: false, selectId: id }).catch(() => {});
      listHelpers.setTicketListCollapsed(true);
    }, delay);
  }

  function openTicketDetailAfterCreation(ticketId, ticketPayload = null) {
    if (!ticketId) return Promise.resolve();
    const id = String(ticketId);
    listHelpers.switchView('tickets');
    setTicketsViewMode('list');
    listHelpers.setTicketListCollapsed(false, { focusList: true });
    ticketsPendingSelectId = id;
    ticketsState.offset = 0;
    if (ticketPayload && typeof ticketPayload === 'object') {
      const hydrated = listHelpers.hydrateTicketAssignment({ ...ticketPayload });
      const prefill = listHelpers.getCreatePrefill();
      if (prefill && typeof prefill.set === 'function') {
        prefill.set(id, hydrated);
      }
    }
    return reloadTickets({ keepSelection: false, selectId: id })
      .then(() => {
        const target = findTicketById(id);
        if (target) {
          const prefill = listHelpers.getCreatePrefill();
          if (prefill && typeof prefill.delete === 'function') prefill.delete(id);
          selectTicket(id);
          return;
        }
        const fallback = listHelpers.consumeCreatePrefill(id);
        if (fallback) {
          ticketsState.selectedId = id;
          updateTicketSelectionHighlight();
          listHelpers.renderTicketDetail(fallback);
          if (fallback && fallback.id) {
            listHelpers.loadTicketComments(fallback.id);
          }
        }
      })
      .catch(() => {
        const fallback = listHelpers.consumeCreatePrefill(id);
        if (fallback) {
          ticketsState.selectedId = id;
          updateTicketSelectionHighlight();
          listHelpers.renderTicketDetail(fallback);
        }
      });
  }

  function openTicketDetailFromBoard(ticketId, lane, options = {}) {
    const targetId = String(ticketId);
    ticketsPendingSelectId = targetId;
    prepareTicketsFiltersForBoardTicket(lane);
    if (options.suppressReload) suppressNextTicketsReload = true;
    listHelpers.switchView('tickets');
    setTicketsViewMode('list');
    ticketsState.offset = 0;
    const promise = reloadTickets({ keepSelection: true, selectId: targetId }).catch(() => {});
    if (options.scrollHighlight) {
      promise.finally(() => {
        setTimeout(() => {
          const card = document.querySelector(`[data-ticket-card="${CSS.escape(targetId)}"]`);
          if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            card.classList.add('ticket-card--highlight');
            setTimeout(() => card.classList.remove('ticket-card--highlight'), 1500);
            const detailBtn = card.querySelector('[data-ticket-action="details"]');
            if (detailBtn) detailBtn.focus({ preventScroll: true });
          }
        }, 200);
      });
    }
    return promise;
  }

  function renderTicketsTable(items, rawCount) {
    const dom = resolveListDom();
    if (!dom.tbody) return;
    dom.tbody.innerHTML = '';
    if (!items || !items.length) {
      dom.tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No tickets match the filters.</td></tr>';
    } else {
      const attentionSet = listHelpers.getAttentionSet() || new Set();
      items.forEach((ticket, idx) => {
        const hydrated = listHelpers.hydrateTicketAssignment(ticket);
        const tr = document.createElement('tr');
        tr.dataset.id = String(hydrated.id);
        if (ticketsState.selectedId && String(hydrated.id) === String(ticketsState.selectedId)) tr.classList.add('selected');
        if (attentionSet.has(String(hydrated.id))
          && (!ticketsState.selectedId || String(ticketsState.selectedId) !== String(hydrated.id))) {
          tr.classList.add('ticket-new-message');
        }
        const subject = (hydrated.subject || '').trim() || '(no subject)';
        const subjectDisplay = subject.length > 70 ? `${subject.slice(0, 67)}…` : subject;
        const customer = (hydrated.support_customer && hydrated.support_customer.display_name) || '-';
        const statusLabel = listHelpers.formatTicketStatus(hydrated.status);
        const priorityLabel = listHelpers.formatTicketPriority(hydrated.priority);
        const assigneeLabel = listHelpers.formatTicketAssignee(hydrated.assignee_role, hydrated.assignee_user);
        const assigneeTitle = hydrated.assignee_user && (hydrated.assignee_user.email || hydrated.assignee_user.username)
          ? `${hydrated.assignee_user.email || ''}${hydrated.assignee_user.email && hydrated.assignee_user.username ? ' • ' : ''}${hydrated.assignee_user.username || ''}`
          : '';
        const sla = listHelpers.computeSla(hydrated);
        const updated = listHelpers.formatDateTimeShort(hydrated.updated_at || hydrated.created_at);
        const escape = listHelpers.escapeHtml;
        tr.innerHTML = `
          <td>${ticketsState.offset + idx + 1}</td>
          <td title="${escape(subject)}">${escape(subjectDisplay)}</td>
          <td>${escape(customer)}</td>
          <td>${escape(statusLabel)}</td>
          <td>${escape(priorityLabel)}</td>
          <td${assigneeTitle ? ` title="${escape(assigneeTitle)}"` : ''}>${escape(assigneeLabel)}</td>
          <td title="${hydrated.due_at ? escape(listHelpers.formatDateTimeFull(hydrated.due_at)) : 'No SLA target'}">${escape(sla.label)}</td>
          <td>${escape(updated)}</td>`;
        if (String(hydrated.status || '').toLowerCase() === 'escalated') {
          tr.classList.add('ticket-escalated');
        }
        dom.tbody.appendChild(tr);
      });
    }
    if (dom.prev) dom.prev.disabled = ticketsState.offset === 0;
    if (dom.next) dom.next.disabled = rawCount < ticketsState.limit;
    if (dom.pageInfo) dom.pageInfo.textContent = `${Math.floor(ticketsState.offset / ticketsState.limit) + 1}`;
    if (dom.count) {
      dom.count.textContent = `${items ? items.length : 0} shown (retrieved ${rawCount})`
        + (searchSpansAllStatuses() ? ' · searching all statuses, including resolved' : '');
    }
    listHelpers.applyAttentionHighlight();
  }

  function updateTicketSelectionHighlight() {
    const dom = resolveListDom();
    if (!dom.tbody) return;
    const rows = dom.tbody.querySelectorAll('tr[data-id]');
    rows.forEach((row) => {
      row.classList.toggle('selected', ticketsState.selectedId && row.dataset.id === String(ticketsState.selectedId));
    });
  }

  function selectTicket(ticketId, options = {}) {
    const ticket = findTicketById(ticketId);
    ticketsState.selectedId = ticket ? String(ticket.id) : null;
    updateTicketSelectionHighlight();
    if (ticket) {
      listHelpers.clearTicketAttention(ticket.id);
      if (options.userInitiated) {
        listHelpers.setTicketListCollapsed(true);
      }
      listHelpers.renderTicketDetail(ticket);
      listHelpers.loadTicketComments(ticket.id);
    } else {
      listHelpers.setTicketListCollapsed(false);
      listHelpers.renderTicketDetail(null);
    }
  }

  function findTicketById(id) {
    if (!id) return null;
    const target = String(id);
    return ticketsState.latestPage.find(item => String(item.id) === target)
      || ticketsState.items.find(item => String(item.id) === target)
      || null;
  }

  function getSelectedTicket() {
    const selected = findTicketById(ticketsState.selectedId);
    if (selected) return selected;
    return listHelpers.getTicketDetailCurrent() || null;
  }

  function getSelectedTicketIdValue() {
    return ticketsState.selectedId;
  }

  // ---- Mount: list-side filters + pagination + view-mode toggle -------
  function debounceList(fn, wait) {
    let t = null;
    return (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; fn(...args); }, wait);
    };
  }

  function mountList() {
    if (listMounted) return;
    const dom = resolveListDom();
    if (!dom.view) return;

    setTicketsViewMode(ticketsState.viewMode || 'list');

    if (dom.closed) {
      dom.closed.value = ticketsState.filters.closedFilter || LIST_DEFAULT_CLOSED_FILTER;
    }
    if (dom.sort) {
      dom.sort.value = ticketsState.sort || LIST_DEFAULT_TICKET_SORT;
    }

    if (dom.search) {
      dom.search.addEventListener('input', debounceList(() => {
        clearTicketsQuickMode();
        ticketsState.filters.search = (dom.search.value || '').trim();
        ticketsState.offset = 0;
        reloadTickets();
      }, 250));
    }
    if (dom.status) {
      dom.status.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.status = dom.status.value;
        ticketsState.offset = 0;
        reloadTickets();
      });
    }
    if (dom.closed) {
      dom.closed.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.closedFilter = dom.closed.value || LIST_DEFAULT_CLOSED_FILTER;
        ticketsState.filters.closedFilterTouched = true;   // an explicit choice wins over the search widening
        ticketsState.offset = 0;
        reloadTickets();
      });
    }
    if (dom.priority) {
      dom.priority.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.priority = dom.priority.value;
        ticketsState.offset = 0;
        reloadTickets();
      });
    }
    if (dom.assignee) {
      dom.assignee.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.assignee = dom.assignee.value;
        ticketsState.filters.assigned = null;
        ticketsState.offset = 0;
        reloadTickets();
      });
    }
    if (dom.assigneeUser) {
      dom.assigneeUser.addEventListener('change', () => {
        clearTicketsQuickMode();
        const value = (dom.assigneeUser.value || '').trim();
        ticketsState.filters.assigneeUser = value;
        ticketsState.filters.assigned = null;
        ticketsState.offset = 0;
        const adminId = listHelpers.getCurrentUserId() || null;
        ticketAssigneeUserManual = value ? (adminId ? value !== adminId : true) : true;
        ticketsState.savedAssigneeUserFilter = null;
        reloadTickets();
      });
    }
    if (dom.outage) {
      dom.outage.addEventListener('input', debounceList(() => {
        clearTicketsQuickMode();
        ticketsState.filters.outage = (dom.outage.value || '').trim().toLowerCase();
        ticketsState.offset = 0;
        reloadTickets({ keepSelection: true });
      }, 250));
    }
    if (dom.sla) {
      dom.sla.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.slaOnly = !!dom.sla.checked;
        ticketsState.offset = 0;
        reloadTickets({ keepSelection: true });
      });
    }
    if (dom.type) {
      dom.type.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.type = dom.type.value || '';
        ticketsState.offset = 0;
        reloadTickets({ keepSelection: true });
      });
    }
    if (dom.age) {
      dom.age.addEventListener('change', () => {
        clearTicketsQuickMode();
        ticketsState.filters.age = dom.age.value || '';
        ticketsState.offset = 0;
        reloadTickets({ keepSelection: true });
      });
    }
    if (dom.sort) {
      dom.sort.addEventListener('change', () => {
        ticketsState.sort = dom.sort.value || LIST_DEFAULT_TICKET_SORT;
        reloadTickets({ keepSelection: true });
      });
    }
    if (dom.reset) {
      dom.reset.addEventListener('click', () => {
        resetTicketFiltersToDefault({ userInitiated: true });
      });
    }
    if (dom.prev) {
      dom.prev.addEventListener('click', () => {
        if (ticketsState.offset === 0) return;
        ticketsState.offset = Math.max(0, ticketsState.offset - ticketsState.limit);
        reloadTickets({ keepSelection: true });
      });
    }
    if (dom.next) {
      dom.next.addEventListener('click', () => {
        if (ticketsState.latestPage.length < ticketsState.limit) return;
        ticketsState.offset += ticketsState.limit;
        reloadTickets();
      });
    }
    if (dom.tbody) {
      dom.tbody.addEventListener('click', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row) return;
        selectTicket(row.dataset.id, { userInitiated: true });
      });
    }
    if (!ticketActionMenuEventsBound) {
      document.addEventListener('click', handleTicketActionMenuOutsideClick);
      ticketActionMenuEventsBound = true;
    }
    listMounted = true;
  }

  // The view loader's `initTicketsView` shim in admin.js calls this after
  // it finishes wiring the detail/comment/billing portions of the view
  // (those listeners stay in admin.js until 2.1.d.3 / 2.1.d.5 land).
  function initView({ defaultPrefilters = true } = {}) {
    const dom = resolveListDom();
    if (!dom.view) return;
    if (ticketsInitialized) {
      if (suppressNextTicketsReload) {
        suppressNextTicketsReload = false;
      } else {
        reloadTickets({ keepSelection: true });
      }
      return;
    }
    ticketsInitialized = true;
    mountList();
    listHelpers.populateTicketAssigneeUserFilter();
    applyDefaultAssigneeUserFilter();
    listHelpers.ensureTechnicianUsersLoaded().then(() => {
      listHelpers.populateTicketAssigneeUserFilter();
      if (!ticketAssigneeUserManual) {
        applyDefaultAssigneeUserFilter();
      }
    }).catch(() => {});

    if (defaultPrefilters) {
      if (ticketsPendingFilterOverride) {
        applyTicketsFilterOverride(ticketsPendingFilterOverride, { skipQuickMode: true, syncControls: true, preserve: false });
      } else {
        applyDefaultAssigneeUserFilter({ force: true });
      }
      if (suppressNextTicketsReload) {
        suppressNextTicketsReload = false;
      } else {
        reloadTickets();
      }
    }
  }

  views.tickets.list = {
    mount: mountList,
    initView,
    populateAssigneeUserFilter: populateTicketAssigneeUserFilter,
    refresh: (options) => reloadTickets(options),
    setStale: () => {}, // no remote stale flag — list refetches on demand
    isInitialized: () => ticketsInitialized,
    setInitialized: (v) => { ticketsInitialized = Boolean(v); },
    selectTicket,
    findTicketById,
    getSelectedTicket,
    getSelectedTicketIdValue,
    loadTicketDetailById,
    openFromNotification: openTicketFromNotification,
    openFromBoard: openTicketDetailFromBoard,
    openDetailAfterCreation: openTicketDetailAfterCreation,
    setViewMode: setTicketsViewMode,
    setQuickView: setTicketsQuickView,
    clearQuickMode: clearTicketsQuickMode,
    resetFiltersToDefault: resetTicketFiltersToDefault,
    applyFilterOverride: applyTicketsFilterOverride,
    prepareFiltersForBoardTicket: prepareTicketsFiltersForBoardTicket,
    applyDefaultAssigneeUserFilter,
    refreshUnassignedSnapshot,
    updateUnassignedIndicator,
    closeOpenMenus: closeOpenTicketMenus,
    getState: () => ticketsState,
    getUnassignedState: () => unassignedState,
    getPendingSelectId: () => ticketsPendingSelectId,
    setPendingSelectId: (v) => { ticketsPendingSelectId = v == null ? null : String(v); },
    getPendingFilterOverride: () => ticketsPendingFilterOverride,
    setSuppressNextReload: (v) => { suppressNextTicketsReload = Boolean(v); },
    isSuppressNextReload: () => suppressNextTicketsReload,
    setOffset: (v) => { ticketsState.offset = Math.max(0, Number(v) || 0); },
    isAssigneeUserManual: () => ticketAssigneeUserManual,
    setAssigneeUserManual: (v) => { ticketAssigneeUserManual = Boolean(v); },
    isInstallationTicket,
    resolveTicketAgeTime,
    sortTicketsForView,
    resolveTicketStatusQuery,
  };

  // ---- Create ticket dialog (Phase 2.1.d.2b) ---------------------------
  // Owns the "New ticket" form (`#ticketDlg` / `#ticketForm`):
  //   - Form-options hydration (priority/complaint/contact-method selects).
  //   - The assignment role/user dropdowns (delegates to admin.js's shared
  //     `populateAssignmentOptions` because the same helper drives the
  //     assign dialog still in admin.js).
  //   - Primary-contact requirement toggle (email-required when contact
  //     method is `email`).
  //   - The submit flow with two failure-mode branches: "connection
  //     selection required" and "active ticket exists" both open the
  //     ticket-conflict dialog (still in admin.js, 2.1.d.4).
  //
  // What stays in admin.js (injected via `configure({helpers})`):
  //   - The conflict dialog (`openTicketConflictDialog`,
  //     `closeTicketConflictDialog`, the conflict state's customer id).
  //   - The shared `populateAssignmentOptions` (also feeds the assign
  //     dialog — 2.1.d.4 will collapse them).
  //   - User-list loaders (`ensureTechnicianUsersLoaded`,
  //     `ensureSupervisorUsersLoaded`).
  //   - Role gate (`canCreateTickets`, `hasRole`).
  //   - `ticketCreatePrefill` Map (the bridge between the create flow
  //     and `list.openDetailAfterCreation`) — accessed via getter.
  //   - All the form-option constants (`TICKET_PRIORITY_OPTIONS`,
  //     `TICKET_PRIORITY_LABELS`, `TICKET_COMPLAINT_OPTIONS`,
  //     `CONTACT_METHOD_OPTIONS`, `CONTACT_METHOD_LABELS`) live in
  //     admin.js because they're shared with the assign dialog and the
  //     pending-messages cluster.
  let createDom = null;
  let createMounted = false;

  // ---- populateAssignmentOptions (moved from admin.js in Phase 3.2.c) ----
  // Used by the create dialog, the pending-message convert flow, and the
  // ticket-detail re-assign dialog. Pure DOM mutation — no admin.js state.
  function populateAssignmentOptions({
    roleSelect,
    userSelect,
    helpEl,
    helpDefault = '',
    emptyText = 'No eligible teammates available yet.',
    previousRole,
    previousUser,
  } = {}) {
    if (roleSelect) {
      const previous = typeof previousRole !== 'undefined' ? previousRole : (roleSelect.value || '');
      roleSelect.innerHTML = '';
      const unsetOption = document.createElement('option');
      unsetOption.value = '';
      unsetOption.textContent = 'Keep unassigned';
      roleSelect.appendChild(unsetOption);
      Object.entries(TICKET_ASSIGNEE_LABELS).forEach(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        roleSelect.appendChild(option);
      });
      if (previous && roleSelect.querySelector(`option[value="${previous}"]`)) {
        roleSelect.value = previous;
      } else {
        roleSelect.value = '';
      }
    }

    if (userSelect) {
      const previous = typeof previousUser !== 'undefined' ? previousUser : (userSelect.value || '');
      userSelect.innerHTML = '';
      const unsetUserOption = document.createElement('option');
      unsetUserOption.value = '';
      unsetUserOption.textContent = 'Do not assign';
      userSelect.appendChild(unsetUserOption);

      const merged = new Map();
      const addUser = (user) => {
        if (!user || typeof user.id === 'undefined' || user.id === null) return;
        const key = String(user.id);
        if (!merged.has(key)) merged.set(key, user);
      };
      const techs = getTechnicianAdminUsers();
      const supes = getSupervisorAdminUsers();
      (Array.isArray(techs) ? techs : []).forEach(addUser);
      (Array.isArray(supes) ? supes : []).forEach(addUser);

      const eligibleUsers = Array.from(merged.values())
        .filter((user) => userEligibleForTicketAssignment(user))
        .sort((a, b) => adminUserSortKey(a).localeCompare(adminUserSortKey(b)));

      eligibleUsers.forEach((user) => {
        const option = document.createElement('option');
        option.value = String(user.id);
        option.textContent = formatAssignmentOptionLabel(user);
        userSelect.appendChild(option);
      });

      if (helpEl) {
        if (!eligibleUsers.length) {
          helpEl.textContent = emptyText;
        } else {
          helpEl.textContent = helpDefault || helpEl.textContent;
        }
      }

      if (previous && userSelect.querySelector(`option[value="${previous}"]`)) {
        userSelect.value = previous;
      } else {
        userSelect.value = '';
      }
    } else if (helpEl && helpDefault) {
      helpEl.textContent = helpDefault;
    }
  }

  // Phase 3.2.c: createHelpers populated from imports + sibling lookups.
  const createHelpers = {
    canCreateTickets,
    hasRole,
    openDialog,
    ensureTechnicianUsersLoaded,
    ensureSupervisorUsersLoaded,
    populateAssignmentOptions: (opts) => populateAssignmentOptions(opts),
    openTicketConflictDialog: (opts) => openTicketConflictDialog(opts),
    closeTicketConflictDialog: () => closeTicketConflictDialog(),
    getTicketConflictSupportCustomerId: () => {
      const i = views.tickets && views.tickets.detailInteractions;
      const state = i && typeof i.getConflictState === 'function' ? i.getConflictState() : null;
      return state ? state.supportCustomerId : null;
    },
    getTicketPriorityOptions: () => TICKET_PRIORITY_OPTIONS,
    getTicketPriorityLabels: () => TICKET_PRIORITY_LABELS,
    getTicketComplaintOptions: () => TICKET_COMPLAINT_OPTIONS,
    getContactMethodOptions: () => CONTACT_METHOD_OPTIONS,
    getContactMethodLabels: () => CONTACT_METHOD_LABELS,
    getCreatePrefill: () => ticketCreatePrefill,
  };

  function resolveCreateDom() {
    if (createDom) return createDom;
    createDom = {
      addBtn: document.getElementById('addTicketBtn'),
      dlg: document.getElementById('ticketDlg'),
      form: document.getElementById('ticketForm'),
      subject: document.getElementById('ticketSubject'),
      description: document.getElementById('ticketDescription'),
      customerPhone: document.getElementById('ticketCustomerPhone'),
      customerName: document.getElementById('ticketCustomerName'),
      customerEmail: document.getElementById('ticketCustomerEmail'),
      primaryContact: document.getElementById('ticketPrimaryContact'),
      priority: document.getElementById('ticketPriority'),
      complaint: document.getElementById('ticketComplaint'),
      assignRole: document.getElementById('ticketAssignRoleCreate'),
      assignUser: document.getElementById('ticketAssignUserCreate'),
      assignUserHelp: document.getElementById('ticketAssignUserCreateHelp'),
      initialComment: document.getElementById('ticketInitialComment'),
      initialCommentVisibility: document.getElementById('ticketInitialCommentVisibility'),
    };
    return createDom;
  }

  function getAssignUserHelpDefault() {
    const dom = resolveCreateDom();
    if (!dom.assignUserHelp) return '';
    if (!dom.assignUserHelp.dataset.defaultText) {
      dom.assignUserHelp.dataset.defaultText = dom.assignUserHelp.textContent || '';
    }
    return dom.assignUserHelp.dataset.defaultText || '';
  }

  function populateCreateFormOptions() {
    const dom = resolveCreateDom();
    const priorities = createHelpers.getTicketPriorityOptions() || [];
    const priorityLabels = createHelpers.getTicketPriorityLabels() || {};
    if (dom.priority && dom.priority.options.length === 0) {
      dom.priority.innerHTML = '';
      priorities.forEach((value) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = priorityLabels[value] || value.toUpperCase();
        dom.priority.appendChild(opt);
      });
    }
    if (dom.complaint && dom.complaint.options.length === 0) {
      const first = document.createElement('option');
      first.value = '';
      first.textContent = 'Complaint type (optional)';
      dom.complaint.appendChild(first);
      const complaints = createHelpers.getTicketComplaintOptions() || [];
      [...complaints]
        .slice()
        .sort((a, b) => a.replace(/_/g, ' ').localeCompare(b.replace(/_/g, ' ')))
        .forEach((value) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value.replace(/_/g, ' ');
          dom.complaint.appendChild(opt);
        });
    }
    if (dom.priority && !dom.priority.value) {
      dom.priority.value = 'p3_high';
    }
    if (dom.complaint && dom.complaint.value === undefined) {
      dom.complaint.value = '';
    }
    if (dom.primaryContact && dom.primaryContact.options.length <= 1) {
      const methods = createHelpers.getContactMethodOptions() || [];
      const methodLabels = createHelpers.getContactMethodLabels() || {};
      methods.forEach((method) => {
        const opt = document.createElement('option');
        opt.value = method;
        opt.textContent = methodLabels[method] || method;
        dom.primaryContact.appendChild(opt);
      });
    }
    populateCreateAssignmentOptions();
    updatePrimaryContactRequirement();
  }

  function populateCreateAssignmentOptions() {
    const dom = resolveCreateDom();
    createHelpers.populateAssignmentOptions({
      roleSelect: dom.assignRole,
      userSelect: dom.assignUser,
      helpEl: dom.assignUserHelp,
      helpDefault: getAssignUserHelpDefault(),
    });
  }

  function updatePrimaryContactRequirement() {
    const dom = resolveCreateDom();
    if (!dom.primaryContact || !dom.customerEmail) return;
    const method = dom.primaryContact.value;
    const requiresEmail = method === 'email';
    dom.customerEmail.required = requiresEmail;
    dom.customerEmail.placeholder = requiresEmail ? 'Required for email contact' : 'optional';
    if (!requiresEmail) {
      dom.customerEmail.setCustomValidity('');
    }
  }

  function resetCreateForm() {
    const dom = resolveCreateDom();
    if (dom.form) {
      dom.form.reset();
      dom.form.dataset.pending = '0';
    }
    if (dom.priority) dom.priority.value = 'p3_high';
    if (dom.complaint) dom.complaint.value = '';
    if (dom.primaryContact) dom.primaryContact.value = '';
    if (dom.assignRole) dom.assignRole.value = '';
    if (dom.assignUser) dom.assignUser.value = '';
    if (dom.initialComment) dom.initialComment.value = '';
    if (dom.initialCommentVisibility) dom.initialCommentVisibility.value = 'internal';
    const helpDefault = getAssignUserHelpDefault();
    if (dom.assignUserHelp && helpDefault) dom.assignUserHelp.textContent = helpDefault;
    updatePrimaryContactRequirement();
  }

  function openCreateDialog() {
    const dom = resolveCreateDom();
    if (!dom.dlg) {
      // eslint-disable-next-line no-console
      console.warn('openCreateDialog: #ticketDlg missing — New Ticket button will not open.');
      return;
    }
    if (!createHelpers.canCreateTickets()) {
      notify.showFlash('You do not have permission to create tickets.', 'error');
      return;
    }
    createHelpers.ensureTechnicianUsersLoaded().catch(() => {});
    createHelpers.ensureSupervisorUsersLoaded().catch(() => {});
    populateCreateFormOptions();
    resetCreateForm();
    createHelpers.openDialog(dom.dlg);
    if (dom.subject) {
      setTimeout(() => dom.subject.focus(), 50);
    }
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    const dom = resolveCreateDom();
    if (!dom.form) return;
    if (dom.form.dataset.pending === '1') return;
    if (!createHelpers.canCreateTickets()) {
      notify.showFlash('You do not have permission to create tickets.', 'error');
      return;
    }

    const subject = (dom.subject && dom.subject.value ? dom.subject.value : '').trim();
    const description = (dom.description && dom.description.value ? dom.description.value : '').trim();
    const customerPhone = (dom.customerPhone && dom.customerPhone.value ? dom.customerPhone.value : '').trim();
    const customerName = (dom.customerName && dom.customerName.value ? dom.customerName.value : '').trim();
    const customerEmail = (dom.customerEmail && dom.customerEmail.value ? dom.customerEmail.value : '').trim();
    const primaryContact = dom.primaryContact ? dom.primaryContact.value : '';
    const priority = dom.priority ? dom.priority.value || 'p3_high' : 'p3_high';
    const complaint = dom.complaint ? dom.complaint.value : '';
    const assigneeRoleValue = dom.assignRole ? (dom.assignRole.value || '').trim() : '';
    const assigneeUserValue = dom.assignUser ? (dom.assignUser.value || '').trim() : '';
    const initialComment = dom.initialComment && dom.initialComment.value ? dom.initialComment.value.trim() : '';
    const initialCommentVisibility = dom.initialCommentVisibility ? (dom.initialCommentVisibility.value || 'internal') : 'internal';

    if (!subject) {
      notify.showFlash('Subject is required', 'error');
      return;
    }
    if (!description) {
      notify.showFlash('Description is required', 'error');
      return;
    }
    const isRootUser = createHelpers.hasRole('root');
    if (!customerPhone) {
      if (!isRootUser) {
        notify.showFlash('Customer phone is required for manual tickets', 'error');
        return;
      }
      if (!customerName) {
        notify.showFlash('Enter a customer name when the phone number is unknown', 'error');
        if (dom.customerName) dom.customerName.focus();
        return;
      }
    }
    if (primaryContact === 'email') {
      if (!customerEmail) {
        notify.showFlash('Enter a valid email address when email is the primary contact', 'error');
        if (dom.customerEmail) dom.customerEmail.focus();
        return;
      }
      if (dom.customerEmail && !dom.customerEmail.reportValidity()) {
        return;
      }
    }

    const payload = {
      subject,
      description,
      priority: priority || 'p3_high',
      meta: { origin: 'manual_admin' },
    };
    if (customerPhone) payload.customer_phone = customerPhone;
    if (complaint) payload.complaint_type = complaint;
    if (customerName) payload.customer_display_name = customerName;
    if (customerEmail) payload.customer_email = customerEmail;
    if (primaryContact) payload.primary_contact_method = primaryContact;
    if (assigneeRoleValue) payload.assignee_role = assigneeRoleValue;
    if (assigneeUserValue) {
      const parsedAssignee = parseInt(assigneeUserValue, 10);
      if (!Number.isNaN(parsedAssignee)) {
        payload.assignee_user_id = parsedAssignee;
      }
    }
    if (initialComment) {
      payload.initial_comment = {
        body: initialComment,
        visibility: initialCommentVisibility === 'customer' ? 'customer' : 'internal',
      };
    }

    const submitBtn = dom.form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Creating…';
    }
    dom.form.dataset.pending = '1';

    const list = views.tickets && views.tickets.list;

    const retryManualTicketCreate = async (connectionId) => {
      const resolvedId = Number.parseInt(String(connectionId), 10);
      if (Number.isNaN(resolvedId)) {
        throw new Error('Invalid connection selection. Please choose a different connection.');
      }
      const connectionPayload = {
        ...payload,
        affected_connection_id: resolvedId,
      };
      const conflictCustomerId = createHelpers.getTicketConflictSupportCustomerId();
      if (conflictCustomerId) {
        connectionPayload.support_customer_id = conflictCustomerId;
      }
      const submitBtnRetry = dom.form.querySelector('button[type="submit"]');
      if (submitBtnRetry) {
        submitBtnRetry.disabled = true;
        submitBtnRetry.textContent = 'Creating…';
      }
      dom.form.dataset.pending = '1';
      try {
        const created = await api.fetch(api.routes.adminTickets, {
          method: 'POST',
          body: JSON.stringify(connectionPayload),
        });
        resetCreateForm();
        createHelpers.closeTicketConflictDialog();
        if (dom.dlg) {
          try { dom.dlg.close(); } catch {}
        }
        notify.showFlash('Ticket created');
        if (list && list.openDetailAfterCreation) {
          list.openDetailAfterCreation(created && created.id, created);
        }
      } catch (retryErr) {
        const retryDetail = retryErr && retryErr.body && retryErr.body.detail;
        if (retryDetail && retryDetail.code === 'connection_selection_required') {
          createHelpers.openTicketConflictDialog({ source: 'manual_create', detail: retryDetail, resolve: retryManualTicketCreate });
          return;
        }
        if (retryDetail && retryDetail.code === 'active_ticket_exists') {
          createHelpers.openTicketConflictDialog({ source: 'manual_create', detail: retryDetail });
          return;
        }
        notify.showFlash(retryErr && retryErr.message ? retryErr.message : 'Failed to create ticket', 'error');
        return;
      } finally {
        dom.form.dataset.pending = '0';
        if (submitBtnRetry) {
          submitBtnRetry.disabled = false;
          submitBtnRetry.textContent = 'Create ticket';
        }
      }
    };

    try {
      const created = await api.fetch(api.routes.adminTickets, { method: 'POST', body: JSON.stringify(payload) });
      createHelpers.closeTicketConflictDialog();
      notify.showFlash('Ticket created');
      resetCreateForm();
      if (dom.dlg) {
        try { dom.dlg.close(); } catch {}
      }
      if (list && list.openDetailAfterCreation) {
        list.openDetailAfterCreation(created && created.id, created);
      }
    } catch (err) {
      const detail = err && err.body && err.body.detail;
      if (detail && detail.code === 'connection_selection_required') {
        createHelpers.openTicketConflictDialog({ source: 'manual_create', detail, resolve: retryManualTicketCreate });
      } else if (detail && detail.code === 'active_ticket_exists') {
        createHelpers.openTicketConflictDialog({ source: 'manual_create', detail });
      } else {
        notify.showFlash(err && err.message ? err.message : 'Failed to create ticket', 'error');
      }
    } finally {
      dom.form.dataset.pending = '0';
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Create ticket';
      }
    }
  }

  function mountCreate() {
    if (createMounted) return;
    const dom = resolveCreateDom();
    if (dom.addBtn) {
      dom.addBtn.addEventListener('click', openCreateDialog);
    }
    if (dom.form) {
      dom.form.addEventListener('submit', (event) => {
        handleCreateSubmit(event).catch(() => {});
      });
    }
    if (dom.primaryContact) {
      dom.primaryContact.addEventListener('change', updatePrimaryContactRequirement);
    }
    createMounted = true;
  }

  views.tickets.create = {
    mount: mountCreate,
    open: openCreateDialog,
    reset: resetCreateForm,
    populateFormOptions: populateCreateFormOptions,
    populateAssignmentOptions: populateCreateAssignmentOptions,
    updatePrimaryContactRequirement,
  };

  // ---- Ticket detail render + helpers (Phase 2.1.d.3) -----------------
  // Owns the right-side detail panel (`#ticketDetailBody` / `#ticketDetailEmpty`):
  //   - The top-of-panel render (`renderTicketDetail`) — orchestrates
  //     subject/complaint/customer/description/meta/SLA/billing/timeline.
  //   - Customer block (`renderTicketCustomer`), description
  //     (`renderTicketDescription`), meta + linked-tickets list
  //     (`renderTicketMeta`, `buildLinkedTicketsMarkup`).
  //   - SLA policy panel (`renderTicketSlaPolicy`, `populateSlaPolicySelect`,
  //     `formatSlaPolicySummary`, `ensureSlaPoliciesLoaded` + `slaPoliciesState`).
  //   - Activity timeline (`renderTicketTimeline`, `loadTicketActivities`,
  //     `ticketActivityState`, `ticketTimelineExpanded` + the activity-label
  //     formatters).
  //   - Billing card (`renderTicketBilling`, `loadTicketBilling`,
  //     `resetTicketBillingCard` + `ticketBillingState`/`ticketBillingTicketId`).
  //   - Customer/Porta helpers (`getRawPortaCustomerId`,
  //     `resolvePortaCustomerId`, `hasPlaceholderPortaCustomer`,
  //     `extractLatestCustomerSentiment`, `formatSentimentDisplay`).
  //   - The list-collapse toggle (`setTicketListCollapsed`) — the toggle
  //     button itself stays wired in admin.js since it lives in the list
  //     toolbar.
  //   - The meta show/hide toggle (`setTicketMetaVisibility`).
  //   - Selected-ticket cache (`ticketDetailCurrent`).
  //   - Ticket-card SLA computation (`computeSla`) + customer-name
  //     extraction (`getTicketCustomerName`).
  //   - Generic detail-only helpers: meta key/value formatters, origin
  //     alias resolver, title-case, activity-label formatter.
  //
  // What stays in admin.js (injected via `configure({helpers})`):
  //   - The inline editors (subject/complaint/customer-name) and their
  //     reset helpers — 2.1.d.4 territory.
  //   - The action-menu, identify/duplicate/conflict/assign dialogs —
  //     2.1.d.4 territory.
  //   - Comments cluster (`loadTicketComments`, the comments DOM refs,
  //     `resetTicketCommentAttachments`) — 2.1.d.5 territory.
  //   - Shared formatters (`formatTicketStatus`, `formatTicketPriority`,
  //     `formatTicketComplaint`, `formatTicketAssignee`, `formatRelativeTime`,
  //     `formatDateTimeShort`, `formatDateTimeFull`, `formatCurrency`,
  //     `describeDuration`, `escapeHtml`, `toPlainText`, `hydrateTicketAssignment`).
  //   - `mutateTicket`, `reloadTickets` — 2.1.d.4 will pull these in.
  //
  // Sized: ~830 lines moved from admin.js.

  const DETAIL_DESCRIPTION_EXPAND_THRESHOLD = 240;
  const DETAIL_PREVIEW_META_KEYS = new Set(['preview', 'message_preview']);
  const DETAIL_META_ORIGIN_ALIASES = new Map([
    ['manual_admin', 'Created in admin'],
    ['manual_tech', 'Created by technician'],
    ['pending_message', 'From pending message'],
    ['pending_message_pbx', 'From PBX call'],
    ['mobile_app', 'From customer mobile app'],
    ['email_inbound', 'From inbound email'],
  ]);

  let ticketDetailCurrent = null;
  let ticketActivityState = { items: [], loading: false, error: null };
  let ticketTimelineExpanded = false;
  let ticketBillingState = { data: null, loading: false, error: null };
  let ticketBillingTicketId = null;
  const slaPoliciesState = {
    items: [],
    loading: false,
    loaded: false,
    error: null,
    promise: null,
  };
  let ticketListCollapsed = false;
  let detailDom = null;

  // Phase 3.2.c: detailHelpers populated from imports + sibling lookups.
  const detailHelpers = {
    escapeHtml,
    formatRelativeTime,
    formatDateTimeShort,
    formatDateTimeFull,
    formatCurrency,
    describeDuration,
    toPlainText,
    hydrateTicketAssignment,
    formatTicketStatus,
    formatTicketPriority,
    formatTicketComplaint,
    formatTicketAssignee,
    hasRole,
    closeOpenTicketMenus: (except) => {
      const list = views.tickets && views.tickets.list;
      if (list && typeof list.closeOpenMenus === 'function') list.closeOpenMenus(except);
    },
    resetTicketCommentAttachments: () => resetTicketCommentAttachments(),
    resetTicketSubjectEditor: (value) => resetTicketSubjectEditor(value),
    resetTicketComplaintEditor: (value) => resetTicketComplaintEditor(value),
    resetTicketCustomerNameEditor: (value, canEdit) => resetTicketCustomerNameEditor(value, canEdit),
  };

  function resolveDetailDom() {
    if (detailDom) return detailDom;
    detailDom = {
      body: document.getElementById('ticketDetailBody'),
      empty: document.getElementById('ticketDetailEmpty'),
      subject: document.getElementById('ticketDetailSubject'),
      complaint: document.getElementById('ticketDetailComplaint'),
      status: document.getElementById('ticketDetailStatus'),
      priority: document.getElementById('ticketDetailPriority'),
      assignee: document.getElementById('ticketDetailAssignee'),
      escalated: document.getElementById('ticketDetailEscalated'),
      sla: document.getElementById('ticketDetailSla'),
      customer: document.getElementById('ticketDetailCustomer'),
      customerName: document.getElementById('ticketDetailCustomerName'),
      customerEditBtn: document.getElementById('ticketCustomerEditBtn'),
      description: document.getElementById('ticketDetailDescription'),
      descriptionExpandBtn: document.getElementById('ticketDescriptionExpand'),
      meta: document.getElementById('ticketDetailMeta'),
      metaToggle: document.getElementById('ticketMetaToggle'),
      previewLink: document.getElementById('ticketPreviewLink'),
      previewContent: document.getElementById('ticketPreviewContent'),
      previewDlg: document.getElementById('ticketPreviewDlg'),
      timeline: document.getElementById('ticketDetailTimeline'),
      slaPolicySection: document.getElementById('ticketSlaPolicySection'),
      slaPolicySelect: document.getElementById('ticketDetailSlaPolicySelect'),
      slaPolicySummary: document.getElementById('ticketDetailSlaPolicySummary'),
      billingCard: document.getElementById('ticketBillingCard'),
      billingMeta: document.getElementById('ticketBillingMeta'),
      billingBalance: document.getElementById('ticketBillingBalance'),
      billingStateEl: document.getElementById('ticketBillingState'),
      billingNextDue: document.getElementById('ticketBillingNextDue'),
      billingSuspendedTill: document.getElementById('ticketBillingSuspendedTill'),
      billingSuspendedWrap: document.getElementById('ticketBillingSuspendedWrap'),
      billingConnections: document.getElementById('ticketBillingConnections'),
      billingSubscriptions: document.getElementById('ticketBillingSubscriptions'),
      billingUnsuspendBtn: document.getElementById('ticketBillingUnsuspendBtn'),
      billingRefreshBtn: document.getElementById('ticketBillingRefreshBtn'),
      identifyBtn: document.getElementById('identifyCustomerBtn'),
      identifyHint: document.getElementById('identifyCustomerHint'),
      deleteBtn: document.getElementById('ticketDeleteBtn'),
      commentsList: document.getElementById('ticketCommentsList'),
      commentsStatus: document.getElementById('ticketCommentsStatus'),
      layout: document.getElementById('ticketsLayout'),
      listToggle: document.getElementById('ticketListToggle'),
      tbody: document.getElementById('ticketsTbody'),
    };
    return detailDom;
  }

  // ---- Porta customer ID helpers --------------------------------------
  function getRawPortaCustomerId(ticket) {
    if (!ticket) return '';
    const metaId = ticket.meta && ticket.meta.porta_customer_id;
    const supportId = ticket.support_customer && ticket.support_customer.porta_customer_id;
    const candidate = typeof metaId !== 'undefined' && metaId !== null && metaId !== '' ? metaId : supportId;
    return (typeof candidate !== 'undefined' && candidate !== null && candidate !== '') ? String(candidate).trim() : '';
  }

  function resolvePortaCustomerId(ticket) {
    const raw = getRawPortaCustomerId(ticket);
    if (!raw) return '';
    const normalized = raw.toLowerCase();
    if (normalized.startsWith('manual') || normalized.startsWith('crm-')) return '';
    return /^\d+$/.test(raw) ? raw : '';
  }

  function hasPlaceholderPortaCustomer(ticket) {
    const raw = getRawPortaCustomerId(ticket);
    if (!raw) return false;
    const normalized = raw.toLowerCase();
    return normalized.startsWith('manual') || normalized.startsWith('crm-') || !/^\d+$/.test(raw);
  }

  function extractLatestCustomerSentiment(ticket) {
    const history = (ticket && ticket.meta && ticket.meta.customer_feedback) || [];
    if (!Array.isArray(history) || !history.length) return null;
    const latest = history[history.length - 1] || {};
    const sentiment = latest.sentiment || null;
    const frustration = latest.frustration_level || null;
    const summary = latest.summary || null;
    const confidence = latest.confidence != null ? Number(latest.confidence) : null;
    return { sentiment, frustration, summary, confidence };
  }

  function formatSentimentDisplay(sentimentInfo) {
    if (!sentimentInfo) return '';
    const escape = detailHelpers.escapeHtml;
    const parts = [];
    if (sentimentInfo.sentiment) {
      parts.push(`<span class="sentiment-pill sentiment-${escape(sentimentInfo.sentiment.toLowerCase())}">${escape(sentimentInfo.sentiment)}</span>`);
    }
    if (sentimentInfo.frustration) {
      parts.push(`<span class="sentiment-pill sentiment-frustration">Frustration: ${escape(sentimentInfo.frustration)}</span>`);
    }
    if (sentimentInfo.summary) {
      parts.push(`<span class="sentiment-summary">${escape(sentimentInfo.summary)}</span>`);
    }
    if (sentimentInfo.confidence != null && !Number.isNaN(sentimentInfo.confidence)) {
      const pct = Math.round(Math.max(0, Math.min(1, sentimentInfo.confidence)) * 100);
      parts.push(`<span class="sentiment-confidence">Confidence: ${pct}%</span>`);
    }
    return parts.length ? `<div class="customer-sentiment">${parts.join(' ')}</div>` : '';
  }

  // ---- Badge helper ---------------------------------------------------
  function setTicketBadge(el, kind, value, label) {
    if (!el) return;
    const classes = ['ticket-badge'];
    if (kind === 'status' && value) classes.push(`status-${value}`);
    if (kind === 'priority' && value) classes.push(`priority-${value}`);
    if (kind === 'assignee') classes.push('assignee');
    el.className = classes.join(' ');
    el.textContent = label || '—';
  }

  // ---- List-collapse toggle (admin.js still wires the click) ---------
  function setTicketListCollapsed(collapsed, options = {}) {
    const dom = resolveDetailDom();
    ticketListCollapsed = Boolean(collapsed);
    if (dom.layout) {
      dom.layout.classList.toggle('tickets-list-collapsed', ticketListCollapsed);
    }
    if (dom.listToggle) {
      const icon = ticketListCollapsed ? '⤢' : '⤡';
      dom.listToggle.innerHTML = `<span aria-hidden="true">${icon}</span>`;
      dom.listToggle.setAttribute('aria-expanded', ticketListCollapsed ? 'false' : 'true');
      const label = ticketListCollapsed ? 'Show ticket list' : 'Hide ticket list';
      dom.listToggle.title = label;
      dom.listToggle.setAttribute('aria-label', label);
    }
    if (!ticketListCollapsed && dom.tbody && options.focusList) {
      const focusRow = dom.tbody.querySelector('tr.selected') || dom.tbody.querySelector('tr[data-id]');
      if (focusRow) {
        focusRow.scrollIntoView({ block: 'center' });
      }
    }
  }

  function setTicketMetaVisibility(show) {
    const dom = resolveDetailDom();
    if (!dom.meta || !dom.metaToggle) return;
    dom.meta.hidden = !show;
    dom.metaToggle.textContent = show ? 'Hide' : 'Show';
  }

  // ---- Customer / description / meta render ---------------------------
  function renderTicketCustomer(ticket) {
    const dom = resolveDetailDom();
    if (!dom.customer || !dom.customerName) return;
    const escape = detailHelpers.escapeHtml;
    const customer = ticket.support_customer || {};
    const outageTag = ticket.outage_tag_id || (ticket.meta && ticket.meta.outage_tag);
    const portaId = resolvePortaCustomerId(ticket);
    const portaPlaceholder = hasPlaceholderPortaCustomer(ticket);
    const ticketId = (typeof ticket.id !== 'undefined' && ticket.id !== null) ? String(ticket.id) : '—';
    // PortaBilling owns a linked customer's name (the nightly sync writes it), so
    // only customers without a real Porta id can be renamed here.
    const linkedToPorta = Boolean(portaId);
    const canEditCustomerName = Boolean(customer && customer.id) && !linkedToPorta;
    detailHelpers.resetTicketCustomerNameEditor(customer.display_name || '', canEditCustomerName);
    dom.customerName.textContent = customer.display_name || 'Unlinked customer';
    if (dom.customerEditBtn) {
      // Contact details are kept in PortaBilling, so they can only be edited once the
      // customer is identified there. Until then the agent identifies the customer
      // (the Identify action), which is what creates the Porta link.
      dom.customerEditBtn.hidden = !ticket.support_customer_id;
      dom.customerEditBtn.disabled = !linkedToPorta;
      dom.customerEditBtn.setAttribute('aria-disabled', linkedToPorta ? 'false' : 'true');
      dom.customerEditBtn.title = linkedToPorta
        ? 'Edit this customer\u2019s contact details'
        : 'Identify the customer in PortaBilling first \u2014 contact details live there.';
      dom.customerEditBtn.dataset.supportCustomerId = ticket.support_customer_id || '';
      dom.customerEditBtn.dataset.portaLinked = linkedToPorta ? '1' : '';
    }
    const phoneLink = customer.primary_phone
      ? `<a href="tel:${escape(customer.primary_phone)}">${escape(customer.primary_phone)}</a>`
      : '—';
    const phoneWarning = portaPlaceholder && customer.primary_phone
      ? '<span class="contact-note">Phone number not in Porta for this customer</span>'
      : '';
    const phoneValueClass = ['contact-value'];
    if (portaPlaceholder && customer.primary_phone) phoneValueClass.push('contact-value--error');
    const lines = [
      `<div class="contact-row">Phone: <span class="${phoneValueClass.join(' ')}">${phoneLink}</span>${phoneWarning}</div>`,
      `<div>Email: ${customer.primary_email ? `<a href="mailto:${escape(customer.primary_email)}">${escape(customer.primary_email)}</a>` : '—'}</div>`,
      portaId
        ? `<div>Porta ID: <a href="https://billing.insistnet.gm/ui/customer/${encodeURIComponent(portaId)}" target="_blank" rel="noopener">${escape(portaId)}</a></div>`
        : '<div>Porta ID: —</div>',
      `<div>Ticket ID: ${escape(ticketId)}</div>`,
    ];
    const sentimentInfo = extractLatestCustomerSentiment(ticket);
    const sentimentMarkup = formatSentimentDisplay(sentimentInfo);
    if (sentimentMarkup) lines.push(sentimentMarkup);
    if (outageTag) {
      lines.push(`<div>Outage tag: <span class="ticket-badge">${escape(outageTag)}</span></div>`);
    }
    dom.customer.innerHTML = lines.join('');
  }

  function renderTicketDescription(ticket) {
    const dom = resolveDetailDom();
    if (!dom.description) return;
    const rawDescription = (ticket && typeof ticket.description === 'string') ? ticket.description : '';
    const plainDescription = detailHelpers.toPlainText(rawDescription);
    dom.description.dataset.fullText = plainDescription;
    const hasContent = Boolean(plainDescription);
    dom.description.textContent = hasContent ? plainDescription : 'No description has been provided yet.';
    dom.description.classList.toggle('ticket-description--empty', !hasContent);
    const newlineCount = plainDescription ? (plainDescription.match(/\n/g) || []).length : 0;
    const shouldOfferExpand = hasContent && (plainDescription.length > DETAIL_DESCRIPTION_EXPAND_THRESHOLD || newlineCount >= 3);
    dom.description.classList.toggle('ticket-description--overflow', shouldOfferExpand);
    if (dom.descriptionExpandBtn) {
      dom.descriptionExpandBtn.hidden = !shouldOfferExpand;
      dom.descriptionExpandBtn.disabled = !shouldOfferExpand;
    }
  }

  function toTitleCase(text) {
    if (!text) return '';
    return text
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  function formatOriginToken(token) {
    if (!token) return '';
    return toTitleCase(token.replace(/[_\-]+/g, ' '));
  }

  function resolveOriginAlias(normalized) {
    for (const [pattern, label] of DETAIL_META_ORIGIN_ALIASES.entries()) {
      if (normalized === pattern) return { label, remainder: '' };
      if (normalized.startsWith(`${pattern}:`)) return { label, remainder: normalized.slice(pattern.length + 1) };
    }
    return null;
  }

  function formatOriginValue(raw) {
    if (raw == null) return '';
    const text = String(raw).trim();
    if (!text) return '';
    const normalized = text.toLowerCase();
    const alias = resolveOriginAlias(normalized);
    if (alias) {
      const extras = alias.remainder.split(':').map(formatOriginToken).filter(Boolean).join(' → ');
      return extras ? `${alias.label} (${extras})` : alias.label;
    }
    if (normalized.includes(':')) {
      return normalized.split(':').map(formatOriginToken).filter(Boolean).join(' → ');
    }
    return formatOriginToken(normalized);
  }

  function formatMetaValue(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try { return JSON.stringify(value); }
    catch { return String(value); }
  }

  function formatMetaKey(key) {
    if (!key) return '';
    return toTitleCase(String(key).replace(/_/g, ' '));
  }

  function formatMetaValueForKey(key, value) {
    if (typeof key === 'string' && key.toLowerCase() === 'origin') {
      return formatOriginValue(value);
    }
    return formatMetaValue(value);
  }

  function buildLinkedTicketsMarkup(linkedTickets) {
    if (!Array.isArray(linkedTickets) || !linkedTickets.length) return '';
    const escape = detailHelpers.escapeHtml;
    const items = linkedTickets.map((item) => {
      if (!item || !item.id) return '';
      const relation = String(item.relation || 'duplicate');
      const relationLabel = relation === 'primary' ? 'Primary ticket' : 'Duplicate';
      const code = item.short_code ? `#${item.short_code}` : String(item.id).split('-')[0] || String(item.id).slice(0, 8);
      const subject = (item.subject || '').trim() || '(no subject)';
      const statusLabel = item.status ? detailHelpers.formatTicketStatus(item.status) : '';
      const metaBits = [];
      if (statusLabel) metaBits.push(statusLabel);
      if (item.linked_at) {
        metaBits.push(`Linked ${detailHelpers.formatDateTimeShort(item.linked_at)}`);
      } else if (item.created_at) {
        metaBits.push(`Created ${detailHelpers.formatDateTimeShort(item.created_at)}`);
      }
      const metaText = metaBits.join(' • ');
      return `
        <li class="ticket-linked-item">
          <a href="#" data-linked-ticket-id="${escape(String(item.id))}" data-linked-ticket-short="${escape(item.short_code || '')}" data-linked-ticket-relation="${escape(relation)}">
            ${escape(code)}
          </a>
          <div class="ticket-linked-details">
            <span class="ticket-linked-relation">${escape(relationLabel)}</span>
            <span class="ticket-linked-title">${escape(subject)}</span>
            ${metaText ? `<span class="ticket-linked-meta">${escape(metaText)}</span>` : ''}
          </div>
        </li>`;
    }).filter(Boolean).join('');
    return `
      <details class="ticket-linked-group">
        <summary>Linked tickets (${linkedTickets.length})</summary>
        <ul class="ticket-linked-list">${items}</ul>
      </details>`;
  }

  function renderTicketMeta(ticket) {
    const dom = resolveDetailDom();
    if (!dom.meta) return;
    const escape = detailHelpers.escapeHtml;
    const meta = (ticket && typeof ticket.meta === 'object') ? ticket.meta : {};
    const linked = Array.isArray(ticket?.linked_tickets) ? ticket.linked_tickets : [];

    let previewText = '';
    const keys = Object.keys(meta).filter((key) => {
      if (DETAIL_PREVIEW_META_KEYS.has(String(key).toLowerCase())) {
        const value = meta[key];
        if (typeof value === 'string') {
          previewText = value;
        } else if (value != null) {
          try { previewText = JSON.stringify(value, null, 2); }
          catch { previewText = String(value); }
        }
        return false;
      }
      const val = meta[key];
      return val !== null && val !== '' && typeof val !== 'undefined';
    });

    if (dom.previewLink) {
      const trimmed = (previewText || '').trim();
      if (trimmed) {
        dom.previewLink.hidden = false;
        dom.previewLink.dataset.preview = trimmed;
      } else {
        dom.previewLink.hidden = true;
        dom.previewLink.dataset.preview = '';
        if (dom.previewContent && (!dom.previewDlg || !dom.previewDlg.open)) {
          dom.previewContent.textContent = '';
        }
      }
    }

    const metaMarkup = keys.length
      ? `<dl>${keys.slice(0, 14).map((key) => {
        const label = formatMetaKey(key);
        const valueText = formatMetaValueForKey(key, meta[key]);
        return `<dt>${escape(label)}</dt><dd>${escape(valueText)}</dd>`;
      }).join('')}</dl>`
      : '<em style="color: var(--sub);">No metadata for this ticket.</em>';

    const linkedMarkup = linked.length ? buildLinkedTicketsMarkup(linked) : '';
    const sections = [];
    if (linkedMarkup) sections.push(linkedMarkup);
    sections.push(metaMarkup);
    dom.meta.innerHTML = sections.join('');
    setTicketMetaVisibility(false);
  }

  // ---- SLA policy panel ----------------------------------------------
  async function ensureSlaPoliciesLoaded(options = {}) {
    if (slaPoliciesState.loaded && !options.force) return slaPoliciesState.items;
    if (slaPoliciesState.loading && slaPoliciesState.promise) return slaPoliciesState.promise;
    slaPoliciesState.loading = true;
    slaPoliciesState.error = null;
    const request = api.fetch(api.routes.adminSlaPolicies, { method: 'GET' })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        slaPoliciesState.items = items;
        slaPoliciesState.loaded = true;
        return items;
      })
      .catch((err) => {
        slaPoliciesState.error = err?.message || 'Failed to load SLA policies';
        throw err;
      })
      .finally(() => {
        slaPoliciesState.loading = false;
        slaPoliciesState.promise = null;
      });
    slaPoliciesState.promise = request;
    return request;
  }

  function populateSlaPolicySelect(selectedId) {
    const dom = resolveDetailDom();
    if (!dom.slaPolicySelect) return;
    const existing = Array.from(dom.slaPolicySelect.options).map(opt => opt.value).join('|');
    const expected = [''].concat(slaPoliciesState.items.map((policy) => String(policy.id))).join('|');
    if (existing !== expected) {
      dom.slaPolicySelect.innerHTML = '';
      const autoOption = document.createElement('option');
      autoOption.value = '';
      autoOption.textContent = 'Auto (segment default)';
      dom.slaPolicySelect.appendChild(autoOption);
      slaPoliciesState.items.forEach((policy) => {
        const option = document.createElement('option');
        option.value = String(policy.id);
        const code = (policy.code || '').toString().toUpperCase();
        option.textContent = `${code || 'POLICY'} – ${policy.description}`;
        dom.slaPolicySelect.appendChild(option);
      });
    }
    const target = selectedId ? String(selectedId) : '';
    const hasMatch = Array.from(dom.slaPolicySelect.options).some((opt) => opt.value === target);
    dom.slaPolicySelect.value = hasMatch ? target : '';
  }

  function formatSlaPolicySummary(policy) {
    if (!policy) return '';
    const parts = [];
    if (policy.description) parts.push(policy.description);
    const responseMins = Number(policy.first_response_mins || 0);
    if (responseMins > 0) parts.push(`Respond within ${detailHelpers.describeDuration(responseMins * 60000)}`);
    const resolutionMins = Number(policy.resolution_mins || 0);
    if (resolutionMins > 0) parts.push(`Resolve within ${detailHelpers.describeDuration(resolutionMins * 60000)}`);
    if (policy.for_segment) parts.push(`Segment: ${policy.for_segment}`);
    if (policy.for_complaint) parts.push(`Complaint: ${policy.for_complaint.replace(/_/g, ' ')}`);
    return parts.join(' • ');
  }

  function renderTicketSlaPolicy(ticket) {
    const dom = resolveDetailDom();
    if (!dom.slaPolicySection) return;
    if (!ticket) {
      dom.slaPolicySection.hidden = true;
      if (dom.slaPolicySelect) {
        dom.slaPolicySelect.value = '';
        dom.slaPolicySelect.disabled = true;
      }
      if (dom.slaPolicySummary) dom.slaPolicySummary.textContent = '';
      return;
    }
    dom.slaPolicySection.hidden = false;
    if (!slaPoliciesState.loaded) {
      if (dom.slaPolicySelect) {
        dom.slaPolicySelect.disabled = true;
        dom.slaPolicySelect.innerHTML = '<option value="">Loading policies…</option>';
      }
      if (dom.slaPolicySummary) {
        dom.slaPolicySummary.textContent = slaPoliciesState.error
          ? `Failed to load SLA policies: ${slaPoliciesState.error}`
          : 'Loading SLA policies…';
      }
      ensureSlaPoliciesLoaded().then(() => {
        const list = views.tickets && views.tickets.list;
        const selectedId = list ? list.getState().selectedId : null;
        const current = (list && list.findTicketById(selectedId)) || ticket;
        if (current && String(current.id) === String(ticket.id)) {
          renderTicketSlaPolicy(current);
        }
      }).catch(() => {
        if (dom.slaPolicySummary && slaPoliciesState.error) {
          dom.slaPolicySummary.textContent = `Failed to load SLA policies: ${slaPoliciesState.error}`;
        }
      });
      return;
    }
    if (dom.slaPolicySelect) {
      dom.slaPolicySelect.disabled = Boolean(dom.slaPolicySelect.dataset.pending === '1');
      populateSlaPolicySelect(ticket.sla_policy_id ? String(ticket.sla_policy_id) : '');
    }
    if (dom.slaPolicySummary) {
      dom.slaPolicySummary.textContent = ticket.sla_policy
        ? formatSlaPolicySummary(ticket.sla_policy)
        : 'Using default SLA policy based on customer segment.';
    }
  }

  // ---- Activity timeline ---------------------------------------------
  function formatActivityNote(note, activityType) {
    if (!note) return '';
    let cleaned = String(note);
    if (activityType === 'system_event') {
      cleaned = cleaned.replace(/_/g, ' ');
    }
    cleaned = cleaned.trim();
    return cleaned ? ` — ${cleaned}` : '';
  }

  function formatAssigneeTarget(activity) {
    const data = activity && activity.data ? activity.data : {};
    const userId = data.assigned_to_user_id || data.assignee_user_id || null;
    const roleRaw = activity.to_role || data.assigned_to_role || data.to_role;
    const roleLabel = roleRaw ? String(roleRaw).replace(/_/g, ' ') : '';
    const parts = [];
    if (userId != null) parts.push(`User #${userId}`);
    if (roleLabel) parts.push(roleLabel);
    return parts.join(' · ');
  }

  function formatActivityLabel(activity) {
    const actor = activity.actor_user_id != null ? `Agent #${activity.actor_user_id}` : 'System';
    const note = formatActivityNote(activity.note, activity.activity_type);
    const assigneeHint = formatAssigneeTarget(activity);
    switch (activity.activity_type) {
      case 'state_change': {
        const state = activity.data && activity.data.new_status ? String(activity.data.new_status).replace(/_/g, ' ') : 'Status updated';
        return `${actor} set ${state}${note}`;
      }
      case 'assignment': {
        const toRole = activity.to_role ? activity.to_role.replace(/_/g, ' ') : 'new assignee';
        const target = assigneeHint ? ` (${assigneeHint})` : '';
        return `${actor} reassigned ticket to ${toRole}${target}${note}`;
      }
      case 'checklist_submitted':
        return `${actor} marked on site${note}`;
      case 'comment_added':
        return `${actor} added a note${note}`;
      case 'customer_message':
        return `Customer update${note}`;
      case 'system_event':
        return `System event${note}`;
      default:
        return `${activity.activity_type}${note}`;
    }
  }

  function getSelectedTicketSafe() {
    const list = views.tickets && views.tickets.list;
    return list ? list.getSelectedTicket() : null;
  }

  function renderTicketTimeline(ticket) {
    const dom = resolveDetailDom();
    if (!dom.timeline) return;
    const escape = detailHelpers.escapeHtml;
    if (!ticket) {
      dom.timeline.innerHTML = '<div style="color: var(--sub);">Select a ticket to view history.</div>';
      return;
    }

    if (ticketActivityState.loading) {
      dom.timeline.innerHTML = '<div style="color: var(--sub);">Loading activity…</div>';
      return;
    }

    if (ticketActivityState.error) {
      dom.timeline.innerHTML = `<div style="color: var(--danger);">${escape(ticketActivityState.error)}</div>`;
      return;
    }

    const filtered = ticketActivityState.items.filter((activity) => {
      const noteText = String(activity.note || '').toLowerCase();
      if (activity.activity_type === 'system_event' && noteText.includes('pbx call completed')) return false;
      return true;
    });

    if (filtered.length) {
      const itemsToRender = ticketTimelineExpanded ? filtered : filtered.slice(0, 3);
      const timelineHtml = itemsToRender
        .map((activity) => {
          const label = formatActivityLabel(activity);
          const ts = activity.occurred_at ? detailHelpers.formatDateTimeFull(activity.occurred_at) : '—';
          return `<div class="ticket-timeline-item"><time>${escape(ts)}</time><span>${escape(label)}</span></div>`;
        })
        .join('');
      const controls = filtered.length > 3
        ? `<div class="ticket-timeline-controls"><button type="button" class="btn ghost small" id="ticketTimelineToggle">${ticketTimelineExpanded ? 'Show latest' : 'Show all'}</button></div>`
        : '';
      dom.timeline.innerHTML = `${timelineHtml}${controls}`;
      const toggleBtn = document.getElementById('ticketTimelineToggle');
      if (toggleBtn) {
        toggleBtn.addEventListener('click', () => {
          ticketTimelineExpanded = !ticketTimelineExpanded;
          renderTicketTimeline(ticket);
        });
      }
      return;
    }

    const fallback = [];
    if (ticket.created_at) fallback.push({ label: 'Created', value: ticket.created_at });
    if (ticket.first_response_at) fallback.push({ label: 'First response', value: ticket.first_response_at });
    if (ticket.due_at) fallback.push({ label: 'SLA target', value: ticket.due_at });
    if (ticket.escalated_at) fallback.push({ label: 'Escalated', value: ticket.escalated_at });
    if (ticket.last_customer_contact_at) fallback.push({ label: 'Last customer contact', value: ticket.last_customer_contact_at });
    if (ticket.closed_at) fallback.push({ label: 'Closed', value: ticket.closed_at });
    if (!fallback.length) {
      dom.timeline.innerHTML = '<div style="color: var(--sub);">No timeline events yet.</div>';
      return;
    }
    dom.timeline.innerHTML = fallback
      .map((evt) => `<div class="ticket-timeline-item"><time>${escape(detailHelpers.formatDateTimeFull(evt.value))}</time><span>${escape(evt.label)}</span></div>`)
      .join('');
  }

  function loadTicketActivities(ticketId) {
    if (!ticketId) {
      ticketActivityState = { items: [], loading: false, error: null };
      ticketTimelineExpanded = false;
      renderTicketTimeline(getSelectedTicketSafe());
      return;
    }
    ticketActivityState = { items: [], loading: true, error: null };
    ticketTimelineExpanded = false;
    renderTicketTimeline(getSelectedTicketSafe());
    api.fetch(api.routes.adminTicketActivities(ticketId), { method: 'GET' })
      .then((payload) => {
        const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
        const list = views.tickets && views.tickets.list;
        const selectedId = list ? list.getState().selectedId : null;
        if (String(selectedId) !== String(ticketId)) return;
        ticketActivityState = { items, loading: false, error: null };
        ticketTimelineExpanded = false;
        renderTicketTimeline(getSelectedTicketSafe());
      })
      .catch((err) => {
        const message = err && err.message ? err.message : 'Unable to load activity log';
        const list = views.tickets && views.tickets.list;
        const selectedId = list ? list.getState().selectedId : null;
        if (String(selectedId) !== String(ticketId)) return;
        ticketActivityState = { items: [], loading: false, error: message };
        renderTicketTimeline(getSelectedTicketSafe());
      });
  }

  // ---- Billing card --------------------------------------------------
  function resetTicketBillingCard() {
    const dom = resolveDetailDom();
    if (!dom.billingCard) return;
    ticketBillingState = { data: null, loading: false, error: null };
    ticketBillingTicketId = null;
    dom.billingCard.hidden = true;
    if (dom.billingMeta) dom.billingMeta.textContent = '';
    if (dom.billingBalance) dom.billingBalance.textContent = '—';
    if (dom.billingStateEl) dom.billingStateEl.textContent = '—';
    if (dom.billingNextDue) dom.billingNextDue.textContent = '—';
    if (dom.billingSuspendedTill) dom.billingSuspendedTill.textContent = '—';
    if (dom.billingSuspendedWrap) dom.billingSuspendedWrap.hidden = true;
    if (dom.billingConnections) dom.billingConnections.textContent = 'No connections';
    if (dom.billingSubscriptions) dom.billingSubscriptions.textContent = 'No subscriptions';
    if (dom.billingUnsuspendBtn) {
      dom.billingUnsuspendBtn.hidden = true;
      dom.billingUnsuspendBtn.disabled = false;
    }
    if (dom.billingRefreshBtn) dom.billingRefreshBtn.disabled = false;
  }

  function renderTicketBilling(data) {
    const dom = resolveDetailDom();
    if (!dom.billingCard) return;
    const escape = detailHelpers.escapeHtml;
    dom.billingCard.hidden = false;
    if (!data) {
      resetTicketBillingCard();
      dom.billingCard.hidden = false;
      if (dom.billingMeta) dom.billingMeta.textContent = 'Billing info unavailable';
      return;
    }
    const list = views.tickets && views.tickets.list;
    const currentTicketLookup = list ? list.findTicketById(ticketBillingTicketId) : null;
    const currentTicket = currentTicketLookup
      || (ticketDetailCurrent && String(ticketDetailCurrent.id) === String(ticketBillingTicketId) ? ticketDetailCurrent : null);
    const balanceDisplay = detailHelpers.formatCurrency(data.balance_due);
    if (dom.billingBalance) dom.billingBalance.textContent = balanceDisplay;
    if (dom.billingStateEl) dom.billingStateEl.textContent = data.account_state || '—';
    if (dom.billingNextDue) dom.billingNextDue.textContent = data.next_due_date ? detailHelpers.formatDateTimeShort(data.next_due_date) : '—';
    if (dom.billingSuspendedWrap && dom.billingSuspendedTill) {
      const isSuspended = String(data.account_state || '').toLowerCase() === 'suspended';
      const untilValue = data.suspension_delayed_till || data.unsuspended_till || data.effective_suspension_on || data.suspension_on || null;
      dom.billingSuspendedWrap.hidden = !isSuspended;
      dom.billingSuspendedTill.textContent = untilValue ? detailHelpers.formatDateTimeShort(untilValue) : '—';
    }
    if (dom.billingMeta) {
      const parts = [];
      if (data.refreshed_at) parts.push(`Refreshed ${detailHelpers.formatDateTimeShort(data.refreshed_at)}`);
      if (data.ttl_expires_at) parts.push(`TTL ${detailHelpers.formatDateTimeShort(data.ttl_expires_at)}`);
      dom.billingMeta.textContent = parts.length ? parts.join(' • ') : '';
    }

    if (dom.billingUnsuspendBtn) {
      const portaId = resolvePortaCustomerId(currentTicket || {});
      const portaPlaceholder = hasPlaceholderPortaCustomer(currentTicket || {});
      const isSuspended = String(data.account_state || '').toLowerCase() === 'suspended';
      const allowed = detailHelpers.hasRole('root', 'field_tech_supervisor');
      const shouldShow = Boolean(allowed && portaId && !portaPlaceholder && isSuspended);
      dom.billingUnsuspendBtn.hidden = !shouldShow;
      dom.billingUnsuspendBtn.disabled = !shouldShow || Boolean(ticketBillingState.loading);
    }

    if (dom.billingConnections) {
      if (Array.isArray(data.connections) && data.connections.length) {
        const targetConnectionId = data.selected_connection_id != null
          ? Number(data.selected_connection_id)
          : currentTicket && currentTicket.affected_connection_id != null
            ? Number(currentTicket.affected_connection_id)
            : null;
        let targetPortaConnectionId = data.selected_porta_connection_id
          || (currentTicket && currentTicket.meta
            ? currentTicket.meta.affected_connection_porta_id || currentTicket.meta.porta_connection_id || null
            : null);
        const connections = [...data.connections];
        if ((targetConnectionId !== null && !Number.isNaN(targetConnectionId)) || targetPortaConnectionId) {
          connections.sort((a, b) => {
            const aId = a && a.id != null ? Number(a.id) : Number.NaN;
            const bId = b && b.id != null ? Number(b.id) : Number.NaN;
            const aMatch = (!Number.isNaN(aId) && aId === targetConnectionId)
              || (targetPortaConnectionId && a.porta_connection_id === targetPortaConnectionId);
            const bMatch = (!Number.isNaN(bId) && bId === targetConnectionId)
              || (targetPortaConnectionId && b.porta_connection_id === targetPortaConnectionId);
            if (aMatch === bMatch) return 0;
            return aMatch ? -1 : 1;
          });
        }
        if (!targetPortaConnectionId && currentTicket && typeof currentTicket.affected_connection_label === 'string') {
          const labelText = currentTicket.affected_connection_label;
          const matchingConn = connections.find((conn) => conn && conn.porta_connection_id && labelText.includes(conn.porta_connection_id));
          if (matchingConn && matchingConn.porta_connection_id) {
            targetPortaConnectionId = matchingConn.porta_connection_id;
          }
        }
        dom.billingConnections.innerHTML = connections.map((conn) => {
          const feeLabel = conn.periodic_fee != null ? detailHelpers.formatCurrency(conn.periodic_fee) : '—';
          const statusParts = [];
          if (conn.status) statusParts.push(conn.status);
          if (conn.blocked) statusParts.push('Blocked');
          const labels = [];
          labels.push(`<span>Product: ${escape(conn.porta_product_name || '—')}</span>`);
          labels.push(`<span>Periodic: ${escape(feeLabel)}</span>`);
          if (conn.porta_id) labels.push(`<span>Porta ID: ${escape(conn.porta_id)}</span>`);
          if (conn.imsi) labels.push(`<span>IMSI: ${escape(conn.imsi)}</span>`);
          if (statusParts.length) labels.push(`<span>Status: ${escape(statusParts.join(', '))}</span>`);
          const connId = conn && conn.id != null ? Number(conn.id) : Number.NaN;
          const idMatch = targetConnectionId !== null && !Number.isNaN(targetConnectionId) && !Number.isNaN(connId) && connId === targetConnectionId;
          const portaMatch = targetPortaConnectionId && conn.porta_connection_id === targetPortaConnectionId;
          const isActive = Boolean(idMatch || portaMatch);
          const badge = isActive ? '<span class="ticket-billing-chip-badge">Linked</span>' : '';
          const connectionLabel = conn.porta_connection_id ? escape(conn.porta_connection_id) : `Connection #${escape(String(conn.id || ''))}`;
          return `<div class="ticket-billing-chip${isActive ? ' ticket-billing-chip--active' : ''}">
            <div class="title">${badge}<span>${connectionLabel}</span></div>
            <div class="meta">${labels.join('')}</div>
          </div>`;
        }).join('');
      } else {
        dom.billingConnections.textContent = 'No connections';
      }
    }

    if (dom.billingSubscriptions) {
      if (Array.isArray(data.subscriptions) && data.subscriptions.length) {
        dom.billingSubscriptions.innerHTML = data.subscriptions.map((sub) => {
          const feeLabel = sub.periodic_fee != null ? detailHelpers.formatCurrency(sub.periodic_fee) : '—';
          const labels = [];
          labels.push(`<span>Periodic: ${escape(feeLabel)}</span>`);
          if (sub.billing_period) labels.push(`<span>Period: ${escape(String(sub.billing_period))}</span>`);
          if (sub.charge_model) labels.push(`<span>Model: ${escape(String(sub.charge_model))}</span>`);
          if (sub.billed_to) labels.push(`<span>Billed to: ${escape(detailHelpers.formatDateTimeShort(sub.billed_to))}</span>`);
          if (sub.connection_id) labels.push(`<span>Connection #${escape(String(sub.connection_id))}</span>`);
          if (sub.porta_subscription_id) labels.push(`<span>Porta ID: ${escape(sub.porta_subscription_id)}</span>`);
          return `<div class="ticket-billing-chip">
            <div class="title">${escape(sub.name || 'Subscription')}</div>
            <div class="meta">${labels.join('')}</div>
          </div>`;
        }).join('');
      } else {
        dom.billingSubscriptions.textContent = 'No subscriptions';
      }
    }

    if (dom.billingRefreshBtn) dom.billingRefreshBtn.disabled = false;
  }

  async function loadTicketBilling(ticketId, { force = false } = {}) {
    const dom = resolveDetailDom();
    if (!dom.billingCard) return;
    if (!ticketId) {
      resetTicketBillingCard();
      return;
    }
    if (!force && ticketBillingState.data && ticketBillingTicketId === ticketId) {
      renderTicketBilling(ticketBillingState.data);
      return;
    }
    ticketBillingState = { data: ticketBillingState.data, loading: true, error: null };
    ticketBillingTicketId = ticketId;
    dom.billingCard.hidden = false;
    if (dom.billingMeta) dom.billingMeta.textContent = 'Loading billing data…';
    if (dom.billingUnsuspendBtn) {
      dom.billingUnsuspendBtn.hidden = true;
      dom.billingUnsuspendBtn.disabled = true;
    }
    if (dom.billingRefreshBtn) dom.billingRefreshBtn.disabled = true;
    try {
      const url = force ? api.routes.adminTicketBillingRefresh(ticketId) : api.routes.adminTicketBilling(ticketId);
      const method = force ? 'POST' : 'GET';
      const payload = await api.fetch(url, { method });
      ticketBillingState = { data: payload, loading: false, error: null };
      renderTicketBilling(payload);
    } catch (err) {
      ticketBillingState = { data: null, loading: false, error: err };
      dom.billingCard.hidden = false;
      if (dom.billingMeta) {
        const message = err?.status === 409
          ? 'Identify the customer before loading billing data.'
          : (err?.message || 'Failed to load billing data');
        dom.billingMeta.textContent = message;
      }
      if (dom.billingRefreshBtn) dom.billingRefreshBtn.disabled = false;
    }
  }

  // ---- SLA + customer-name helpers used by the list module too -------
  function computeSla(ticket) {
    if (!ticket || !ticket.due_at) {
      return { label: 'No SLA target', className: 'missing', breached: false };
    }
    try {
      const due = new Date(ticket.due_at);
      if (Number.isNaN(due.getTime())) return { label: 'No SLA target', className: 'missing', breached: false };
      const diff = due.getTime() - Date.now();
      const label = diff <= 0 ? `Overdue ${detailHelpers.describeDuration(diff)}` : `${detailHelpers.describeDuration(diff)} left`;
      const className = diff <= 0 ? 'breached' : diff <= 60 * 60 * 1000 ? 'warn' : 'ok';
      return { label, className, breached: diff <= 0 };
    } catch {
      return { label: 'No SLA target', className: 'missing', breached: false };
    }
  }

  function getTicketCustomerName(ticket) {
    const source = ticket || getSelectedTicketSafe();
    if (!source) return null;
    const support = source.support_customer || {};
    const name = support.display_name || support.customer_name || support.primary_contact_name || '';
    return (name || '').trim() || null;
  }

  // ---- Top-level render orchestrator ---------------------------------
  function renderTicketDetail(ticket) {
    const dom = resolveDetailDom();
    detailHelpers.closeOpenTicketMenus();
    if (!dom.body || !dom.empty) {
      // eslint-disable-next-line no-console
      console.warn('renderTicketDetail: ticket detail panel elements missing — selection will not render.');
      return;
    }
    if (!ticket) {
      ticketDetailCurrent = null;
      ticketActivityState = { items: [], loading: false, error: null };
      dom.body.hidden = true;
      dom.empty.hidden = false;
      setTicketListCollapsed(false);
      if (dom.commentsStatus) dom.commentsStatus.textContent = '';
      if (dom.commentsList) dom.commentsList.innerHTML = '';
      detailHelpers.resetTicketCommentAttachments();
      if (dom.escalated) {
        setTicketBadge(dom.escalated, 'assignee', null, 'Not escalated');
        dom.escalated.title = '';
      }
      renderTicketSlaPolicy(null);
      resetTicketBillingCard();
      detailHelpers.resetTicketSubjectEditor('');
      detailHelpers.resetTicketComplaintEditor('');
      detailHelpers.resetTicketCustomerNameEditor('', false);
      if (dom.customerEditBtn) {
        dom.customerEditBtn.hidden = true;
        dom.customerEditBtn.dataset.supportCustomerId = '';
      }
      if (dom.deleteBtn) {
        dom.deleteBtn.hidden = true;
        dom.deleteBtn.disabled = true;
      }
      return;
    }
    dom.empty.hidden = true;
    dom.body.hidden = false;
    resetTicketBillingCard();
    const hydratedTicket = detailHelpers.hydrateTicketAssignment(ticket);
    ticketDetailCurrent = hydratedTicket;
    const subjectValue = hydratedTicket.subject || '';
    if (dom.subject) dom.subject.textContent = subjectValue || '(no subject)';
    detailHelpers.resetTicketSubjectEditor(subjectValue);
    const complaintValue = hydratedTicket.complaint_type || '';
    if (dom.complaint) {
      dom.complaint.textContent = detailHelpers.formatTicketComplaint(complaintValue);
    }
    detailHelpers.resetTicketComplaintEditor(complaintValue || '');
    setTicketBadge(dom.status, 'status', hydratedTicket.status, detailHelpers.formatTicketStatus(hydratedTicket.status));
    setTicketBadge(dom.priority, 'priority', hydratedTicket.priority, detailHelpers.formatTicketPriority(hydratedTicket.priority));
    setTicketBadge(dom.assignee, 'assignee', hydratedTicket.assignee_role, detailHelpers.formatTicketAssignee(hydratedTicket.assignee_role, hydratedTicket.assignee_user));
    if (dom.assignee) {
      const parts = [];
      if (hydratedTicket.assignee_user && hydratedTicket.assignee_user.email) parts.push(hydratedTicket.assignee_user.email);
      if (hydratedTicket.assignee_user && hydratedTicket.assignee_user.username) parts.push(hydratedTicket.assignee_user.username);
      dom.assignee.title = parts.length ? parts.join(' • ') : detailHelpers.formatTicketAssignee(hydratedTicket.assignee_role, hydratedTicket.assignee_user);
    }
    if (dom.deleteBtn) {
      const canDelete = detailHelpers.hasRole('root');
      dom.deleteBtn.hidden = !canDelete;
      dom.deleteBtn.disabled = !canDelete;
    }
    if (dom.closeAction) {
      // Only Customer Care (or root) may close a ticket — techs/others resolve for
      // technician review instead (mirrors the server-side guard in update_ticket).
      const canClose = detailHelpers.hasRole('customer_care', 'customer_care_supervisor', 'root');
      dom.closeAction.hidden = !canClose;
    }
    if (dom.identifyBtn) {
      const portaCustomerId = resolvePortaCustomerId(hydratedTicket);
      const portaPlaceholder = hasPlaceholderPortaCustomer(hydratedTicket);
      const canIdentify = !portaCustomerId;
      dom.identifyBtn.hidden = false;
      dom.identifyBtn.disabled = !canIdentify;
      dom.identifyBtn.setAttribute('aria-disabled', canIdentify ? 'false' : 'true');
      dom.identifyBtn.title = canIdentify
        ? (portaPlaceholder ? 'Porta placeholder detected – identify and link the correct Porta customer' : 'Identify and link Porta billing customer')
        : 'Porta account already linked';
    }
    if (dom.identifyHint) {
      const portaCustomerId = resolvePortaCustomerId(hydratedTicket);
      const portaPlaceholder = hasPlaceholderPortaCustomer(hydratedTicket);
      if (!portaCustomerId) {
        dom.identifyHint.hidden = false;
        dom.identifyHint.textContent = portaPlaceholder
          ? 'Placeholder Porta ID detected — identify customer to link billing.'
          : 'No Porta ID linked — identify customer to enable billing.';
      } else {
        dom.identifyHint.hidden = true;
        dom.identifyHint.textContent = '';
      }
    }
    if (dom.escalated) {
      const hasEscalation = Boolean(hydratedTicket.escalated_to_user_id);
      if (hasEscalation) {
        setTicketBadge(dom.escalated, 'assignee', hydratedTicket.escalated_to_role, detailHelpers.formatTicketAssignee(hydratedTicket.escalated_to_role, hydratedTicket.escalated_to_user));
        const parts = [];
        if (hydratedTicket.escalated_to_user && hydratedTicket.escalated_to_user.email) parts.push(hydratedTicket.escalated_to_user.email);
        if (hydratedTicket.escalated_to_user && hydratedTicket.escalated_to_user.username) parts.push(hydratedTicket.escalated_to_user.username);
        dom.escalated.title = parts.length ? parts.join(' • ') : detailHelpers.formatTicketAssignee(hydratedTicket.escalated_to_role, hydratedTicket.escalated_to_user);
      } else {
        setTicketBadge(dom.escalated, 'assignee', null, 'Not escalated');
        dom.escalated.title = 'Ticket not escalated';
      }
    }
    const slaInfo = computeSla(hydratedTicket);
    if (dom.sla) {
      dom.sla.className = `sla-pill ${slaInfo.className}`;
      dom.sla.textContent = slaInfo.label;
      dom.sla.title = hydratedTicket.due_at ? detailHelpers.formatDateTimeFull(hydratedTicket.due_at) : 'No SLA target';
    }
    renderTicketCustomer(hydratedTicket);
    renderTicketDescription(hydratedTicket);
    renderTicketMeta(hydratedTicket);
    renderTicketSlaPolicy(hydratedTicket);
    const portaId = resolvePortaCustomerId(hydratedTicket);
    if (portaId) {
      loadTicketBilling(hydratedTicket.id);
    } else {
      resetTicketBillingCard();
    }
    ticketActivityState = { items: [], loading: true, error: null };
    renderTicketTimeline(hydratedTicket);
    loadTicketActivities(hydratedTicket.id);
    if (dom.commentsList) {
      dom.commentsList.innerHTML = '<div style="color:var(--sub);">Loading comments…</div>';
    }
    if (dom.commentsStatus) {
      dom.commentsStatus.textContent = '';
    }
    applyTicketEditGating();
  }

  // ---- Sales-officer read-only gating ---------------------------------
  // A sales officer can view/search every ticket and add (or edit their own)
  // internal comments, but cannot edit the ticket itself. The backend enforces
  // this with 403s; here we hide the controls that route through the now-gated
  // PATCH /tickets endpoint (action menu + inline subject/complaint/customer-
  // name editors) and lock the comment composer to internal-only. Controls
  // whose backend remains open under the "ticket writes only" scope (identify,
  // billing refresh, the customer-edit dialog) are intentionally left visible.
  function applyTicketEditGating() {
    if (canEditTickets()) return;
    const intDomRef = resolveIntDom();
    const hide = (el) => {
      if (!el) return;
      el.hidden = true;
      if ('disabled' in el) el.disabled = true;
    };
    hide(intDomRef.subjectEditBtn);
    hide(intDomRef.complaintEditBtn);
    hide(intDomRef.customerNameEditBtn);
    hide(intDomRef.detailActions);
    [
      document.getElementById('ticketCommentVisibility'),
      document.getElementById('ticketCommentExpandVisibility'),
    ].forEach((sel) => {
      if (!sel) return;
      sel.value = 'internal';
      const customerOpt = sel.querySelector('option[value="customer"]');
      if (customerOpt) customerOpt.hidden = true;
    });
  }

  // ---- Ticket detail interactions + dialogs (Phase 2.1.d.4) -----------
  // Owns the interactive layer of the detail panel + every secondary
  // dialog hanging off it:
  //   - Inline editors (subject/complaint/customer-name) — open/reset/submit.
  //   - Customer-edit dialog (`#ticketCustomerEditDlg`).
  //   - Identify-customer dialog (`#identifyCustomerDlg`) including the
  //     Porta candidate search + connection picker.
  //   - Link-duplicate dialog (`#ticketDuplicateDlg`).
  //   - Ticket-conflict dialog (`#ticketConflictDlg`) with both the
  //     "active ticket exists" branch and the "connection_selection_required"
  //     retry branch.
  //   - Assign dialog (`#ticketAssignDlg`) with assign + escalate modes.
  //   - Ticket-action menu (`handleTicketAction`) + `mutateTicket` +
  //     `handleTicketSlaPolicyChange`.
  //   - Ticket attention/highlight (mark/clear/apply/updateNav).
  //
  // What stays in admin.js (injected via `configure({helpers})`):
  //   - User-list scaffolding: `populateAssignmentOptions`,
  //     `formatAssignmentOptionLabel`, the admin-user formatters
  //     (`formatAdminUserLabel`, `adminUserSortKey`,
  //     `derivePrimaryAssignmentRole`, `extractRoleNames`),
  //     `userEligibleForTicketAssignment`, `userIsSupervisor`,
  //     `hydrateTicketAssignment`, the supervisor-user lists, the user
  //     loaders. They feed both the create dialog (already extracted) and
  //     the assign dialog here.
  //   - Comment thread (2.1.d.5): `loadTicketComments` etc.
  //   - The role/assignment constants (`ASSIGN_MODE_ASSIGN`,
  //     `ASSIGN_MODE_ESCALATE`, `ESCALATION_SUPERVISOR_TARGETS`,
  //     `BILLING_COMPLAINT_TYPES`, `SALES_COMPLAINT_TYPES`,
  //     `SUPERVISOR_ROLE_NAMES`, `ASSIGNABLE_ROLE_NAMES`,
  //     `CUSTOMER_CARE_ASSIGN_ROLES`, `SUPERVISOR_ROLE_DISPLAY`, role
  //     name constants, `TICKET_CLOSE_TEMPLATES`).
  //
  // The 33-key detail-render module from 2.1.d.3 remains the home for the
  // pure-render side; this module is the interactive layer that mutates
  // it. They share state via the detail module's accessors
  // (`getCurrent`, `setCurrent`, etc.).
  //
  // Sized: ~2 200 lines moved.

  let intDom = null;
  let intMounted = false;
  const ticketAttentionSet = new Set();
  const ticketAssignmentCache = new Map();
  let ticketAssignMode = 'assign';

  // Phase 3.2.c: hydrateTicketAssignment moved here from admin.js where it
  // was broken (referenced `ticketAssignmentCache` which lives only in this
  // module). Latent dead-code surfaced by the migration.
  function hydrateTicketAssignment(ticket) {
    if (!ticket || !ticket.id) return ticket;
    const key = String(ticket.id);
    const hasAssigneeUser = ticket.assignee_user_id != null && ticket.assignee_user;
    if (hasAssigneeUser) {
      ticketAssignmentCache.set(key, {
        user: ticket.assignee_user,
        role: ticket.assignee_role,
      });
      return ticket;
    }
    if (ticket.assignee_user_id != null && !ticket.assignee_user) {
      const cached = ticketAssignmentCache.get(key);
      if (cached && cached.user && Number(cached.user.id) === Number(ticket.assignee_user_id)) {
        ticket.assignee_user = cached.user;
        if (!ticket.assignee_role && cached.role) {
          ticket.assignee_role = cached.role;
        }
      }
    } else if (ticket.assignee_user_id == null) {
      ticketAssignmentCache.delete(key);
    }
    return ticket;
  }
  const identifyState = {
    ticketId: null,
    mode: 'phone',
    items: [],
    loading: false,
    selectedId: null,
    connections: [],
    connectionsLoading: false,
    selectedConnectionId: null,
    selectedPortaConnectionId: null,
    connectionError: null,
    connectionLoadSeq: 0,
  };
  const ticketDuplicateState = {
    ticketId: null,
    loading: false,
    items: [],
    selection: null,
    submitting: false,
  };
  const ticketConflictState = {
    source: null,
    code: null,
    tickets: [],
    supportCustomerId: null,
    pendingMessageId: null,
    connections: [],
    requestedConnectionId: null,
    selectedConnectionId: null,
    retryHandler: null,
  };

  // Phase 3.2.c: intHelpers populated from imports + sibling lookups.
  const intHelpers = {
    escapeHtml,
    formatRelativeTime,
    formatDateTimeShort,
    formatTicketStatus,
    formatTicketPriority,
    formatTicketAssignee,
    formatTicketComplaint,
    cssEscapeIdent,
    openDialog,
    hasRole,
    canAccessTicketing,
    getCurrentUser: getCurrentAdminUser,
    ensureTechnicianUsersLoaded,
    ensureSupervisorUsersLoaded,
    populateAssignmentOptions: (opts) => populateAssignmentOptions(opts),
    formatAssignmentOptionLabel,
    adminUserSortKey,
    extractRoleNames,
    userEligibleForTicketAssignment,
    userIsSupervisor,
    hydrateTicketAssignment,
    deriveSupervisorAdminRoles,
    describeSupervisorTargets,
    getTechnicianAdminUsers,
    getSupervisorAdminUsers,
    getAssignModeAssign: () => ASSIGN_MODE_ASSIGN,
    getAssignModeEscalate: () => ASSIGN_MODE_ESCALATE,
    getFieldTechRole: () => FIELD_TECH_ROLE,
    refreshUnassignedSnapshot: () => refreshUnassignedSnapshot(),
    loadTicketComments: (id) => loadTicketComments(id),
    queueTicketNotification: (opts) => queueTicketNotification(opts),
    resolveTicketDisplay,
    getTicketCloseTemplates: () => TICKET_CLOSE_TEMPLATES,
    getTicketCommentCache: () => ticketCommentCache,
    getTicketCommentPendingRefresh: () => ticketCommentPendingRefresh,
    setTicketCommentEditState: (state) => { ticketCommentEditState = state; },
    getTicketComplaintOptions: () => TICKET_COMPLAINT_OPTIONS,
  };

  function resolveIntDom() {
    if (intDom) return intDom;
    intDom = {
      // Inline editors
      subjectEditor: document.getElementById('ticketSubjectEditor'),
      subjectInlineInput: document.getElementById('ticketSubjectInput'),
      subjectEditBtn: document.getElementById('ticketSubjectEditBtn'),
      subjectSaveBtn: document.getElementById('ticketSubjectSaveBtn'),
      subjectCancelBtn: document.getElementById('ticketSubjectCancelBtn'),
      complaintEditor: document.getElementById('ticketComplaintEditor'),
      complaintInput: document.getElementById('ticketComplaintInput'),
      complaintEditBtn: document.getElementById('ticketComplaintEditBtn'),
      complaintSaveBtn: document.getElementById('ticketComplaintSaveBtn'),
      complaintCancelBtn: document.getElementById('ticketComplaintCancelBtn'),
      customerNameEditor: document.getElementById('ticketCustomerNameEditor'),
      customerNameInlineInput: document.getElementById('ticketCustomerNameInput'),
      customerNameEditBtn: document.getElementById('ticketCustomerNameEditBtn'),
      customerNameSaveBtn: document.getElementById('ticketCustomerNameSaveBtn'),
      customerNameCancelBtn: document.getElementById('ticketCustomerNameCancelBtn'),
      // Customer edit dialog
      customerEditBtn: document.getElementById('ticketCustomerEditBtn'),
      customerEditDlg: document.getElementById('ticketCustomerEditDlg'),
      customerEditForm: document.getElementById('ticketCustomerEditForm'),
      customerEditClose: document.getElementById('ticketCustomerEditClose'),
      customerEditCancel: document.getElementById('ticketCustomerEditCancel'),
      customerEditSave: document.getElementById('ticketCustomerEditSave'),
      customerEditStatus: document.getElementById('ticketCustomerEditStatus'),
      customerEditName: document.getElementById('ticketCustomerEditName'),
      customerEditPhone: document.getElementById('ticketCustomerEditPhone'),
      customerEditEmail: document.getElementById('ticketCustomerEditEmail'),
      customerEditRegion: document.getElementById('ticketCustomerEditRegion'),
      customerEditCity: document.getElementById('ticketCustomerEditCity'),
      customerEditAddress: document.getElementById('ticketCustomerEditAddress'),
      // Identify customer dialog
      identifyDlg: document.getElementById('identifyCustomerDlg'),
      identifyForm: document.getElementById('identifyCustomerForm'),
      identifyBtn: document.getElementById('identifyCustomerBtn'),
      identifyList: document.getElementById('identifyCustomerList'),
      identifyEmpty: document.getElementById('identifyCustomerEmpty'),
      identifyStatus: document.getElementById('identifyCustomerStatus'),
      identifySearchInput: document.getElementById('identifyCustomerSearch'),
      identifySearchBtn: document.getElementById('identifyCustomerSearchBtn'),
      identifyConfirmBtn: document.getElementById('identifyCustomerConfirm'),
      identifyConnectionSection: document.getElementById('identifyConnectionSection'),
      identifyConnectionList: document.getElementById('identifyConnectionList'),
      identifyConnectionStatus: document.getElementById('identifyConnectionStatus'),
      // Duplicate dialog
      duplicateDlg: document.getElementById('ticketDuplicateDlg'),
      duplicateList: document.getElementById('ticketDuplicateList'),
      duplicateStatus: document.getElementById('ticketDuplicateStatus'),
      duplicateConfirm: document.getElementById('ticketDuplicateConfirm'),
      // Conflict dialog
      conflictDlg: document.getElementById('ticketConflictDlg'),
      conflictTitle: document.getElementById('ticketConflictTitle'),
      conflictMessage: document.getElementById('ticketConflictMessage'),
      conflictList: document.getElementById('ticketConflictList'),
      conflictResolve: document.getElementById('ticketConflictResolve'),
      // Assign dialog
      assignDlg: document.getElementById('ticketAssignDlg'),
      assignForm: document.getElementById('ticketAssignForm'),
      assignTitle: document.getElementById('ticketAssignTitle'),
      assignSummary: document.getElementById('ticketAssignSummary'),
      assignUserSelect: document.getElementById('ticketAssignUser'),
      assignRoleSelect: document.getElementById('ticketAssignRole'),
      assignRoleField: document.getElementById('ticketAssignRoleField'),
      assignNoteField: document.getElementById('ticketAssignNoteField'),
      assignNoteHelp: document.getElementById('ticketAssignNoteHelp'),
      assignNoteInput: document.getElementById('ticketAssignNote'),
      assignUserHelp: document.getElementById('ticketAssignUserHelp'),
      assignRoleHelp: document.getElementById('ticketAssignRoleHelp'),
      assignSubmitBtn: document.getElementById('ticketAssignSubmit'),
      // Detail action menu
      detailActions: document.getElementById('ticketDetailActions'),
      // SLA policy
      slaPolicySelect: document.getElementById('ticketDetailSlaPolicySelect'),
      // Close template
      closeTemplate: document.getElementById('ticketCloseTemplate'),
      closeAction: document.getElementById('ticketCloseAction'),
      // Tbody for attention highlight
      tbody: document.getElementById('ticketsTbody'),
      // Nav for highlight indicator
      navTicketsAll: document.getElementById('navTicketsAll'),
    };
    return intDom;
  }

  function getDetailModule() {
    return views.tickets && views.tickets.detail;
  }

  function getListModule() {
    return views.tickets && views.tickets.list;
  }

  function getSelectedTicketSafeInt() {
    const list = getListModule();
    return list ? list.getSelectedTicket() : null;
  }

  function findTicketByIdInt(id) {
    const list = getListModule();
    return list ? list.findTicketById(id) : null;
  }

  function reloadTicketsInt(opts) {
    const list = getListModule();
    return list ? list.refresh(opts) : Promise.resolve();
  }

  function renderTicketDetailInt(ticket) {
    const detail = getDetailModule();
    if (detail) detail.render(ticket);
  }

  // ---- Attention highlight ------------------------------------------
  function applyTicketAttentionHighlight(targetId) {
    const dom = resolveIntDom();
    const list = getListModule();
    const selectedId = list ? list.getState().selectedId : null;
    if (!dom.tbody) return;
    if (targetId) {
      const selector = intHelpers.cssEscapeIdent(targetId);
      const row = dom.tbody.querySelector(`tr[data-id="${selector}"]`);
      if (row) {
        const shouldHighlight = ticketAttentionSet.has(targetId)
          && (!selectedId || String(selectedId) !== targetId);
        row.classList.toggle('ticket-new-message', shouldHighlight);
      }
      return;
    }
    const rows = dom.tbody.querySelectorAll('tr[data-id]');
    rows.forEach((row) => {
      const id = row.dataset.id;
      const shouldHighlight = ticketAttentionSet.has(id)
        && (!selectedId || String(selectedId) !== id);
      row.classList.toggle('ticket-new-message', shouldHighlight);
    });
  }

  function updateNavTicketsHighlight() {
    const dom = resolveIntDom();
    const hasUpdates = ticketAttentionSet.size > 0;
    if (dom.navTicketsAll) {
      dom.navTicketsAll.classList.toggle('has-updates', hasUpdates);
    }
  }

  function markTicketAttention(ticketId, options = {}) {
    if (!ticketId) return;
    const id = String(ticketId);
    const {
      silent = false,
      subject: fallbackSubject = '',
      shortCode: fallbackShortCode = '',
      message,
    } = options;
    const list = getListModule();
    const selectedId = list ? list.getState().selectedId : null;
    if (selectedId && String(selectedId) === id) {
      clearTicketAttention(id);
      return;
    }
    const added = !ticketAttentionSet.has(id);
    ticketAttentionSet.add(id);
    applyTicketAttentionHighlight(id);
    updateNavTicketsHighlight();
    if (added && !silent) {
      const ticket = findTicketByIdInt(id);
      const details = intHelpers.resolveTicketDisplay(ticket, id, fallbackSubject, fallbackShortCode);
      intHelpers.queueTicketNotification({
        ticketId: id,
        kind: 'message',
        subject: details.subject,
        shortCode: details.shortCode,
        message,
      });
    }
  }

  function clearTicketAttention(ticketId) {
    if (!ticketId) return;
    const id = String(ticketId);
    if (!ticketAttentionSet.delete(id)) return;
    applyTicketAttentionHighlight(id);
    updateNavTicketsHighlight();
  }

  // ---- mutateTicket + ticket actions -------------------------------
  async function mutateTicket(ticketId, payload) {
    const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
    if (tb) tb.setStale(true);
    return api.fetch(api.routes.adminTicket(ticketId), { method: 'PATCH', body: JSON.stringify(payload) });
  }

  function deriveAssigneeRole() {
    const me = intHelpers.getCurrentUser();
    const roles = new Set((me && me.roles) || []);
    if (roles.has('root')) return 'supervisor';
    if (roles.has('customer_care_supervisor')) return 'supervisor';
    if (roles.has('customer_care')) return 'agent';
    if (roles.has('field_tech_supervisor')) return 'tech_manager';
    if (roles.has('field_tech')) return 'tech_officer';
    if (roles.has('billing_supervisor')) return 'billing_manager';
    if (roles.has('billing_officer')) return 'billing_officer';
    if (roles.has('sales_supervisor')) return 'sales_manager';
    if (roles.has('sales_officer')) return 'sales_officer';
    return null;
  }

  async function handleTicketAction(action) {
    const ticket = getSelectedTicketSafeInt();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    const dom = resolveIntDom();
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    const ASSIGN_MODE_ESCALATE = intHelpers.getAssignModeEscalate();
    if (action === 'assign-other') {
      await openTicketAssignDialog(ticket);
      return;
    }
    if (action === 'request-equipment') {
      await openTicketEquipmentDialog(ticket);
      return;
    }
    try {
      if (action === 'assign') {
        const role = deriveAssigneeRole();
        if (!role) {
          notify.showFlash('Your admin role cannot be mapped to an assignee bucket.', 'error');
          return;
        }
        const me = intHelpers.getCurrentUser();
        await openTicketAssignDialog(ticket, {
          mode: ASSIGN_MODE_ASSIGN,
          presetAssigneeId: me && typeof me.id !== 'undefined' ? me.id : null,
          lockAssignee: true,
          focusNote: true,
        });
      } else if (action === 'waiting') {
        const payload = {
          status: 'waiting_customer',
          waiting_since_at: new Date().toISOString(),
        };
        const updated = await mutateTicket(ticket.id, payload);
        notify.showFlash('Ticket marked waiting on customer');
        await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
        intHelpers.loadTicketComments(updated.id);
        intHelpers.refreshUnassignedSnapshot();
      } else if (action === 'progress') {
        const payload = { status: 'in_progress' };
        if (!ticket.first_response_at) payload.first_response_at = new Date().toISOString();
        const updated = await mutateTicket(ticket.id, payload);
        notify.showFlash('Ticket marked in progress');
        await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
        intHelpers.loadTicketComments(updated.id);
        intHelpers.refreshUnassignedSnapshot();
      } else if (action === 'escalate') {
        await openTicketAssignDialog(ticket, { mode: ASSIGN_MODE_ESCALATE });
      } else if (action === 'close-template') {
        if (!intHelpers.hasRole('customer_care', 'customer_care_supervisor', 'root')) {
          notify.showFlash('Only Customer Care (or root) can close a ticket.', 'error');
          return;
        }
        const templateKey = dom.closeTemplate ? dom.closeTemplate.value : '';
        if (!templateKey) {
          notify.showFlash('Select a closure template first', 'error');
          return;
        }
        if (templateKey === 'duplicate') {
          await openTicketDuplicateDialog(ticket);
          return;
        }
        const templates = intHelpers.getTicketCloseTemplates();
        const template = templates[templateKey];
        const updated = await mutateTicket(ticket.id, { status: 'closed', closed_at: new Date().toISOString() });
        if (template) {
          try {
            await api.fetch(api.routes.adminTicketComments(ticket.id), { method: 'POST', body: JSON.stringify(template) });
          } catch (err) {
            notify.showFlash(err.message || 'Ticket closed, but failed to post template comment', 'error');
          }
        }
        if (dom.closeTemplate) dom.closeTemplate.value = '';
        notify.showFlash('Ticket closed');
        await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
        intHelpers.loadTicketComments(updated.id);
        intHelpers.refreshUnassignedSnapshot();
      } else if (action === 'delete') {
        if (!intHelpers.hasRole('root')) {
          notify.showFlash('Only root users can delete tickets.', 'error');
          return;
        }
        const shortCode = ticket.meta && ticket.meta.ticket_short_code ? `#${ticket.meta.ticket_short_code}` : null;
        const confirmLabel = shortCode || (ticket.subject ? `"${ticket.subject}"` : `ticket ${String(ticket.id).slice(0, 8)}`);
        if (!window.confirm(`Delete ${confirmLabel}? This cannot be undone.`)) return;
        await api.fetch(api.routes.adminTicket(ticket.id), { method: 'DELETE' });
        const cache = intHelpers.getTicketCommentCache();
        const pendingRefresh = intHelpers.getTicketCommentPendingRefresh();
        if (cache && typeof cache.delete === 'function') cache.delete(String(ticket.id));
        if (pendingRefresh && typeof pendingRefresh.delete === 'function') pendingRefresh.delete(String(ticket.id));
        const list = getListModule();
        if (list) list.getState().selectedId = null;
        renderTicketDetailInt(null);
        notify.showFlash('Ticket deleted');
        await reloadTicketsInt({ keepSelection: false });
        intHelpers.refreshUnassignedSnapshot();
        const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb) tb.markChanged();
      }
    } catch (err) {
      notify.showFlash(err.message || 'Ticket update failed', 'error');
    }
  }

  // ---- Request materials (supervisor → Inventory stock request) ----------
  // Surfaces only customer-facing + consumable SKUs (hybrid picker); posts a
  // stock request the storekeeper reserves/issues against the ticket's customer.
  let _ticketEquipSkuLoaded = false;
  let _ticketEquipTargetId = null;
  let _ticketEquipItems = [];   // [{sku, name}] for name→SKU resolution

  async function ensureTicketEquipmentSkuList() {
    const list = document.getElementById('ticketEquipmentSkuList');
    if (!list || _ticketEquipSkuLoaded) return;
    try {
      const [cust, cons] = await Promise.all([
        api.fetch(api.routes.inventoryItems('classification=customer'), { method: 'GET' }),
        api.fetch(api.routes.inventoryItems('classification=consumable'), { method: 'GET' }),
      ]);
      _ticketEquipItems = [...(cust.items || []), ...(cons.items || [])];
      // Datalist value is the item NAME (+ SKU for uniqueness) so users search by
      // name, not SKU; submit resolves the picked/typed text back to a SKU.
      list.innerHTML = _ticketEquipItems
        .map((i) => `<option value="${escapeHtml(i.name)} — ${escapeHtml(i.sku)}"></option>`)
        .join('');
      _ticketEquipSkuLoaded = true;
    } catch (_) { /* inventory off / unreachable — free text still allowed */ }
  }

  // Resolve a user-typed/picked value (name, "Name — SKU", or a raw SKU) back to
  // a SKU. Users search by name; power users can still type a SKU directly.
  function resolveSkuFromText(text, items) {
    const t = (text || '').trim();
    if (!t) return '';
    const lower = t.toLowerCase();
    const list = items || [];
    const disp = list.find((i) => `${i.name} — ${i.sku}`.toLowerCase() === lower);
    if (disp) return disp.sku;
    const byName = list.filter((i) => String(i.name || '').toLowerCase() === lower);
    if (byName.length === 1) return byName[0].sku;
    const bySku = list.find((i) => String(i.sku || '').toLowerCase() === lower);
    if (bySku) return bySku.sku;
    const dash = t.lastIndexOf('—');
    if (dash >= 0) {
      const tail = t.slice(dash + 1).trim().toLowerCase();
      const m = list.find((i) => String(i.sku || '').toLowerCase() === tail);
      if (m) return m.sku;
    }
    return t;  // fall back to raw text (free-text SKU still allowed)
  }

  function addTicketEquipmentRow() {
    const wrap = document.getElementById('ticketEquipmentLines');
    if (!wrap) return;
    const row = document.createElement('div');
    row.className = 'ticket-equip-row';
    row.style.cssText = 'display:flex; gap:6px; margin-bottom:4px;';
    row.innerHTML = `<input class="ticket-equip-sku" type="text" list="ticketEquipmentSkuList" placeholder="Search item by name…" autocomplete="off" style="flex:1;">
      <input class="ticket-equip-qty" type="number" min="1" step="1" value="1" placeholder="qty" style="width:80px;">
      <button type="button" class="btn ghost ticket-equip-del" aria-label="Remove item">✕</button>`;
    row.querySelector('.ticket-equip-del').addEventListener('click', () => row.remove());
    wrap.appendChild(row);
  }

  async function openTicketEquipmentDialog(ticket) {
    const dlg = document.getElementById('ticketEquipmentDlg');
    if (!dlg) return;
    const note = document.getElementById('ticketEquipmentNote');
    const lines = document.getElementById('ticketEquipmentLines');
    if (note) note.value = '';
    if (lines) lines.innerHTML = '';
    _ticketEquipTargetId = ticket.id;
    await ensureTicketEquipmentSkuList();
    addTicketEquipmentRow();
    openDialog(dlg);
  }

  async function submitTicketEquipmentRequest(event) {
    event.preventDefault();
    const dlg = document.getElementById('ticketEquipmentDlg');
    const noteEl = document.getElementById('ticketEquipmentNote');
    const note = noteEl ? noteEl.value.trim() : '';
    if (!note) { notify.showFlash('A note is required', 'error'); return; }
    const items = [];
    document.querySelectorAll('#ticketEquipmentLines .ticket-equip-row').forEach((row) => {
      const sku = resolveSkuFromText(row.querySelector('.ticket-equip-sku').value, _ticketEquipItems);
      const qtyRaw = row.querySelector('.ticket-equip-qty').value.trim();
      const qty = Number(qtyRaw || '1');
      if (sku && qty > 0) items.push({ sku, quantity: qtyRaw || '1' });
    });
    if (!items.length) { notify.showFlash('Add at least one item (search by name)', 'error'); return; }
    if (!_ticketEquipTargetId) return;
    try {
      await api.fetch(api.routes.adminTicketRequestEquipment(_ticketEquipTargetId), {
        method: 'POST',
        body: JSON.stringify({ note, items }),
      });
      notify.showFlash('Materials requested — sent to Inventory');
      if (dlg) closeDialog(dlg);
      await reloadTicketsInt({ keepSelection: true, selectId: _ticketEquipTargetId });
      intHelpers.loadTicketComments(_ticketEquipTargetId);
    } catch (err) {
      notify.showFlash(err.message || 'Failed to request materials', 'error');
    }
  }

  async function handleTicketSlaPolicyChange(event) {
    const select = event.target;
    const list = getListModule();
    const selectedId = list ? list.getState().selectedId : null;
    if (!selectedId) return;
    const currentTicket = getSelectedTicketSafeInt();
    if (!currentTicket) return;
    if (!select || select.dataset.pending === '1') return;
    const value = select.value;
    const payload = {};
    payload.sla_policy_id = value ? value : null;
    select.dataset.pending = '1';
    select.disabled = true;
    try {
      const updated = await mutateTicket(currentTicket.id, payload);
      notify.showFlash('SLA policy updated');
      const detail = getDetailModule();
      if (detail) detail.renderSlaPolicy(updated);
      await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to update SLA policy', 'error');
      select.value = currentTicket.sla_policy_id ? String(currentTicket.sla_policy_id) : '';
    } finally {
      select.dataset.pending = '0';
      select.disabled = false;
    }
  }

  // ---- Inline editors ----------------------------------------------
  function resetTicketSubjectEditor(value = '') {
    const dom = resolveIntDom();
    if (dom.subjectInlineInput) dom.subjectInlineInput.value = value || '';
    if (dom.subjectEditor) {
      dom.subjectEditor.dataset.value = value || '';
      dom.subjectEditor.dataset.pending = '0';
      dom.subjectEditor.style.display = 'none';
    }
    if (dom.subjectEditBtn) {
      dom.subjectEditBtn.hidden = false;
      dom.subjectEditBtn.disabled = false;
    }
    if (dom.subjectSaveBtn) dom.subjectSaveBtn.disabled = false;
    if (dom.subjectCancelBtn) dom.subjectCancelBtn.disabled = false;
  }

  function openTicketSubjectEditor(value = '') {
    const dom = resolveIntDom();
    if (!dom.subjectEditor || !dom.subjectInlineInput) return;
    const next = typeof value === 'string' ? value : (dom.subjectEditor.dataset.value || '');
    dom.subjectEditor.dataset.value = next || '';
    dom.subjectEditor.dataset.pending = dom.subjectEditor.dataset.pending || '0';
    dom.subjectInlineInput.value = next || '';
    dom.subjectEditor.style.display = 'flex';
    if (dom.subjectEditBtn) dom.subjectEditBtn.hidden = true;
    setTimeout(() => {
      dom.subjectInlineInput.focus();
      dom.subjectInlineInput.select();
    }, 20);
  }

  async function submitTicketSubject() {
    const dom = resolveIntDom();
    if (!dom.subjectEditor || dom.subjectEditor.dataset.pending === '1') return;
    const ticket = getSelectedTicketSafeInt();
    if (!ticket) return;
    const newSubject = dom.subjectInlineInput ? dom.subjectInlineInput.value.trim() : '';
    if (!newSubject) {
      notify.showFlash('Subject cannot be empty', 'error');
      if (dom.subjectInlineInput) dom.subjectInlineInput.focus();
      return;
    }
    const previousValue = dom.subjectEditor.dataset.value || '';
    if (newSubject === previousValue) {
      resetTicketSubjectEditor(previousValue);
      return;
    }
    dom.subjectEditor.dataset.pending = '1';
    if (dom.subjectSaveBtn) dom.subjectSaveBtn.disabled = true;
    if (dom.subjectCancelBtn) dom.subjectCancelBtn.disabled = true;
    try {
      const updated = await mutateTicket(ticket.id, { subject: newSubject });
      notify.showFlash('Subject updated');
      renderTicketDetailInt(updated);
      await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to update subject', 'error');
      resetTicketSubjectEditor(previousValue);
    } finally {
      dom.subjectEditor.dataset.pending = '0';
      if (dom.subjectSaveBtn) dom.subjectSaveBtn.disabled = false;
      if (dom.subjectCancelBtn) dom.subjectCancelBtn.disabled = false;
    }
  }

  function ensureComplaintOptions(select) {
    if (!select) return;
    if (select.options.length) return;
    const noneOption = document.createElement('option');
    noneOption.value = '';
    noneOption.textContent = 'No complaint type';
    select.appendChild(noneOption);
    const complaints = (intHelpers.getTicketComplaintOptions && intHelpers.getTicketComplaintOptions()) || [];
    [...complaints]
      .slice()
      .sort((a, b) => a.replace(/_/g, ' ').localeCompare(b.replace(/_/g, ' ')))
      .forEach((value) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = value.replace(/_/g, ' ');
        select.appendChild(opt);
      });
  }

  function resetTicketComplaintEditor(value = '') {
    const dom = resolveIntDom();
    ensureComplaintOptions(dom.complaintInput);
    if (dom.complaintInput) dom.complaintInput.value = value || '';
    if (dom.complaintEditor) {
      dom.complaintEditor.dataset.value = value || '';
      dom.complaintEditor.dataset.pending = '0';
      dom.complaintEditor.style.display = 'none';
    }
    if (dom.complaintEditBtn) {
      dom.complaintEditBtn.hidden = false;
      dom.complaintEditBtn.disabled = false;
    }
    if (dom.complaintSaveBtn) dom.complaintSaveBtn.disabled = false;
    if (dom.complaintCancelBtn) dom.complaintCancelBtn.disabled = false;
  }

  function openTicketComplaintEditor(value = '') {
    const dom = resolveIntDom();
    ensureComplaintOptions(dom.complaintInput);
    if (!dom.complaintEditor || !dom.complaintInput) return;
    const next = typeof value === 'string' ? value : (dom.complaintEditor.dataset.value || '');
    dom.complaintEditor.dataset.value = next || '';
    dom.complaintEditor.dataset.pending = dom.complaintEditor.dataset.pending || '0';
    dom.complaintInput.value = next || '';
    dom.complaintEditor.style.display = 'flex';
    if (dom.complaintEditBtn) dom.complaintEditBtn.hidden = true;
    setTimeout(() => { dom.complaintInput.focus(); }, 20);
  }

  async function submitTicketComplaint() {
    const dom = resolveIntDom();
    if (!dom.complaintEditor || dom.complaintEditor.dataset.pending === '1') return;
    const ticket = getSelectedTicketSafeInt();
    if (!ticket) return;
    ensureComplaintOptions(dom.complaintInput);
    const selected = dom.complaintInput ? dom.complaintInput.value : '';
    const previousValue = dom.complaintEditor.dataset.value || '';
    if (selected === previousValue) {
      resetTicketComplaintEditor(previousValue);
      return;
    }
    dom.complaintEditor.dataset.pending = '1';
    if (dom.complaintSaveBtn) dom.complaintSaveBtn.disabled = true;
    if (dom.complaintCancelBtn) dom.complaintCancelBtn.disabled = true;
    try {
      const payload = { complaint_type: selected || null };
      const updated = await mutateTicket(ticket.id, payload);
      notify.showFlash(selected ? 'Complaint type updated' : 'Complaint type cleared');
      renderTicketDetailInt(updated);
      await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to update complaint type', 'error');
      resetTicketComplaintEditor(previousValue);
    } finally {
      dom.complaintEditor.dataset.pending = '0';
      if (dom.complaintSaveBtn) dom.complaintSaveBtn.disabled = false;
      if (dom.complaintCancelBtn) dom.complaintCancelBtn.disabled = false;
    }
  }

  function resetTicketCustomerNameEditor(value = '', canEdit = false) {
    const dom = resolveIntDom();
    if (dom.customerNameInlineInput) dom.customerNameInlineInput.value = value || '';
    if (dom.customerNameEditor) {
      dom.customerNameEditor.dataset.value = value || '';
      dom.customerNameEditor.dataset.pending = '0';
      dom.customerNameEditor.style.display = 'none';
    }
    if (dom.customerNameEditBtn) {
      dom.customerNameEditBtn.hidden = !canEdit;
      dom.customerNameEditBtn.disabled = !canEdit;
    }
    if (dom.customerNameSaveBtn) dom.customerNameSaveBtn.disabled = false;
    if (dom.customerNameCancelBtn) dom.customerNameCancelBtn.disabled = false;
  }

  function openTicketCustomerNameEditor(value = '') {
    const dom = resolveIntDom();
    if (!dom.customerNameEditor || !dom.customerNameInlineInput) return;
    const next = typeof value === 'string' ? value : (dom.customerNameEditor.dataset.value || '');
    dom.customerNameEditor.dataset.value = next || '';
    dom.customerNameEditor.dataset.pending = dom.customerNameEditor.dataset.pending || '0';
    dom.customerNameInlineInput.value = next || '';
    dom.customerNameEditor.style.display = 'flex';
    if (dom.customerNameEditBtn) dom.customerNameEditBtn.hidden = true;
    setTimeout(() => {
      dom.customerNameInlineInput.focus();
      dom.customerNameInlineInput.select();
    }, 20);
  }

  async function submitTicketCustomerName() {
    const dom = resolveIntDom();
    if (!dom.customerNameEditor || dom.customerNameEditor.dataset.pending === '1') return;
    const ticket = getSelectedTicketSafeInt();
    if (!ticket) return;
    if (!ticket.support_customer_id) {
      notify.showFlash('Link a customer before editing the name.', 'error');
      resetTicketCustomerNameEditor('', false);
      return;
    }
    const newName = dom.customerNameInlineInput ? dom.customerNameInlineInput.value.trim() : '';
    if (!newName) {
      notify.showFlash('Customer name cannot be empty', 'error');
      if (dom.customerNameInlineInput) dom.customerNameInlineInput.focus();
      return;
    }
    const previousValue = dom.customerNameEditor.dataset.value || '';
    if (newName === previousValue) {
      resetTicketCustomerNameEditor(previousValue, true);
      return;
    }
    dom.customerNameEditor.dataset.pending = '1';
    if (dom.customerNameSaveBtn) dom.customerNameSaveBtn.disabled = true;
    if (dom.customerNameCancelBtn) dom.customerNameCancelBtn.disabled = true;
    try {
      const updated = await mutateTicket(ticket.id, { customer_display_name: newName });
      notify.showFlash('Customer name updated');
      renderTicketDetailInt(updated);
      await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to update customer name', 'error');
      resetTicketCustomerNameEditor(previousValue, true);
    } finally {
      dom.customerNameEditor.dataset.pending = '0';
      if (dom.customerNameSaveBtn) dom.customerNameSaveBtn.disabled = false;
      if (dom.customerNameCancelBtn) dom.customerNameCancelBtn.disabled = false;
    }
  }

  // ---- Customer-edit dialog ----------------------------------------
  function closeTicketCustomerEditDialog() {
    const dom = resolveIntDom();
    if (!dom.customerEditDlg) return;
    try { dom.customerEditDlg.close(); }
    catch { dom.customerEditDlg.open = false; }
  }

  async function openTicketCustomerEditDialog() {
    const dom = resolveIntDom();
    if (!dom.customerEditDlg) {
      // eslint-disable-next-line no-console
      console.warn('openTicketCustomerEditDialog: #ticketCustomerEditDlg missing.');
      return;
    }
    const detail = getDetailModule();
    const current = detail ? detail.getCurrent() : null;
    if (!current || !current.support_customer_id) {
      notify.showFlash('Link a customer before editing the profile.', 'error');
      return;
    }
    if (dom.customerEditBtn && dom.customerEditBtn.dataset.portaLinked !== '1') {
      notify.showFlash('Identify the customer in PortaBilling before editing their details.', 'error');
      return;
    }
    try { await views.tickets.regions.ensureLoaded(); } catch { /* ignore */ }
    if (dom.customerEditStatus) dom.customerEditStatus.textContent = '';
    if (dom.customerEditSave) dom.customerEditSave.disabled = true;
    const id = current.support_customer_id;
    try {
      const profile = await api.fetch(api.routes.adminSupportCustomer(id), { method: 'GET' });
      if (dom.customerEditName) {
        dom.customerEditName.value = profile.display_name || '';
        // Linked to PortaBilling: the name is managed there and synced nightly.
        const fromPorta = /^\d+$/.test(String(profile.porta_customer_id || '').trim());
        dom.customerEditName.disabled = fromPorta;
        dom.customerEditName.title = fromPorta
          ? 'This name comes from PortaBilling. Change it there; the nightly sync updates it here.'
          : '';
        const field = dom.customerEditName.parentElement;
        let hint = field ? field.querySelector('[data-porta-name-hint]') : null;
        if (!hint && field) {
          hint = document.createElement('div');
          hint.dataset.portaNameHint = '1';
          hint.className = 'crm-detail-sub';
          hint.style.fontSize = '12px';
          field.appendChild(hint);
        }
        if (hint) {
          hint.textContent = fromPorta ? 'Managed in PortaBilling — change the name there.' : '';
          hint.hidden = !fromPorta;
        }
      }
      if (dom.customerEditPhone) dom.customerEditPhone.value = profile.primary_phone || '';
      if (dom.customerEditEmail) dom.customerEditEmail.value = profile.primary_email || '';
      views.tickets.regions.refreshSelect();
      if (dom.customerEditRegion) dom.customerEditRegion.value = profile.region || '';
      if (dom.customerEditCity) dom.customerEditCity.value = profile.city || '';
      if (dom.customerEditAddress) dom.customerEditAddress.value = profile.address || '';
    } catch (err) {
      if (dom.customerEditStatus) dom.customerEditStatus.textContent = err?.message || 'Failed to load customer profile.';
    } finally {
      if (dom.customerEditSave) dom.customerEditSave.disabled = false;
    }
    intHelpers.openDialog(dom.customerEditDlg);
  }

  async function handleCustomerEditSubmit(event) {
    event.preventDefault();
    const dom = resolveIntDom();
    const detail = getDetailModule();
    const current = detail ? detail.getCurrent() : null;
    if (!current || !current.support_customer_id) {
      notify.showFlash('Link a customer before editing the profile.', 'error');
      return;
    }
    if (dom.customerEditStatus) dom.customerEditStatus.textContent = '';
    if (dom.customerEditSave) dom.customerEditSave.disabled = true;
    const payload = {
      display_name: dom.customerEditName ? dom.customerEditName.value.trim() || null : null,
      primary_phone: dom.customerEditPhone ? dom.customerEditPhone.value.trim() || null : null,
      primary_email: dom.customerEditEmail ? dom.customerEditEmail.value.trim() || null : null,
      region: dom.customerEditRegion ? dom.customerEditRegion.value.trim() || null : null,
      city: dom.customerEditCity ? dom.customerEditCity.value.trim() || null : null,
      address: dom.customerEditAddress ? dom.customerEditAddress.value.trim() || null : null,
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '') payload[key] = null;
    });
    try {
      const profile = await api.fetch(api.routes.adminSupportCustomer(current.support_customer_id), {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      // Mutate the live `ticketDetailCurrent` reference so other read sites
      // see the update; the detail module's `getCurrent()` returns the same ref.
      current.support_customer = Object.assign({}, current.support_customer || {}, {
        id: profile.id,
        display_name: profile.display_name,
        primary_phone: profile.primary_phone,
        primary_email: profile.primary_email,
        porta_customer_id: profile.porta_customer_id,
      });
      const detailMod = getDetailModule();
      if (detailMod) detailMod.renderCustomer(current);
      const list = getListModule();
      if (list) {
        const state = list.getState();
        state.items = state.items.map((item) => {
          if (String(item.id) === String(current.id)) {
            return { ...item, support_customer: { ...(item.support_customer || {}), ...current.support_customer } };
          }
          return item;
        });
      }
      notify.showFlash('Customer profile updated.', 'success');
      closeTicketCustomerEditDialog();
    } catch (err) {
      const message = (err && err.detail) || err?.message || 'Failed to update customer.';
      if (dom.customerEditStatus) dom.customerEditStatus.textContent = message;
    } finally {
      if (dom.customerEditSave) dom.customerEditSave.disabled = false;
    }
  }

  // ---- Identify-customer dialog ------------------------------------
  function setIdentifyStatus(message, type = 'info') {
    const dom = resolveIntDom();
    if (!dom.identifyStatus) return;
    dom.identifyStatus.textContent = message || '';
    const colorMap = {
      info: 'var(--sub)',
      warn: '#b26b00',
      error: 'var(--danger)',
    };
    dom.identifyStatus.style.color = colorMap[type] || colorMap.info;
  }

  // A 409 from identify means an open ticket already holds the connection. Show
  // which one, in what state and on whose record — a plain search often misses it:
  // linking moves this ticket onto the record that already owns the Porta customer,
  // and parked / resolved-by-technician tickets count as open here too.
  function setIdentifyConflict(detail) {
    const dom = resolveIntDom();
    if (!dom.identifyStatus) return false;
    const tickets = (detail && Array.isArray(detail.tickets)) ? detail.tickets : [];
    if (!tickets.length) return false;
    const escape = detailHelpers.escapeHtml;
    const holder = detail.support_customer_name
      ? `<div style="margin-top:4px;">On customer <strong>${escape(detail.support_customer_name)}</strong>.</div>`
      : '';
    const rows = tickets.map((t) => {
      const code = t.short_code || (t.id ? String(t.id).slice(0, 8) : '—');
      const state = String(t.status || '').replace(/_/g, ' ');
      const label = t.affected_connection_label ? ` · ${escape(t.affected_connection_label)}` : '';
      return `<div style="display:flex; align-items:center; gap:8px; margin-top:4px;">
        <span><strong>${escape(code)}</strong> <span class="crm-detail-sub">${escape(state)}${label}</span></span>
        <button type="button" class="btn" data-conflict-open="${escape(t.id || '')}" style="font-size:11px; padding:2px 8px;">Open ticket</button>
      </div>`;
    }).join('');
    dom.identifyStatus.innerHTML =
      `<div>That connection already has an open ticket. Work on it, or close it first.</div>${holder}${rows}`;
    dom.identifyStatus.style.color = 'var(--danger)';
    dom.identifyStatus.querySelectorAll('[data-conflict-open]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.conflictOpen;
        closeIdentifyCustomerDialog();
        if (id) await reloadTicketsInt({ keepSelection: true, selectId: id });
      });
    });
    return true;
  }

  function updateIdentifyConfirmState() {
    const dom = resolveIntDom();
    if (!dom.identifyConfirmBtn) return;
    const requireConnectionSelection = identifyState.connections.length > 1;
    const hasConnectionSelection = Boolean(identifyState.selectedConnectionId || identifyState.selectedPortaConnectionId);
    const disabled = identifyState.loading
      || !identifyState.selectedId
      || identifyState.connectionsLoading
      || (requireConnectionSelection && !hasConnectionSelection);
    dom.identifyConfirmBtn.disabled = disabled;
  }

  function renderIdentifyConnectionList() {
    const dom = resolveIntDom();
    if (!dom.identifyConnectionSection || !dom.identifyConnectionStatus) return;
    if (!identifyState.selectedId) {
      dom.identifyConnectionSection.hidden = true;
      if (dom.identifyConnectionList) dom.identifyConnectionList.innerHTML = '';
      dom.identifyConnectionStatus.textContent = '';
      return;
    }
    dom.identifyConnectionSection.hidden = false;
    if (identifyState.connectionsLoading) {
      dom.identifyConnectionStatus.textContent = 'Loading connections…';
      if (dom.identifyConnectionList) dom.identifyConnectionList.innerHTML = '';
      return;
    }
    if (identifyState.connectionError) {
      dom.identifyConnectionStatus.textContent = identifyState.connectionError;
      if (dom.identifyConnectionList) dom.identifyConnectionList.innerHTML = '';
      return;
    }
    if (!identifyState.connections.length) {
      dom.identifyConnectionStatus.textContent = 'No active connections were found for this Porta account.';
      if (dom.identifyConnectionList) dom.identifyConnectionList.innerHTML = '';
      return;
    }
    if (identifyState.connections.length === 1) {
      dom.identifyConnectionStatus.textContent = 'Only one connection found. It will be linked automatically.';
    } else {
      dom.identifyConnectionStatus.textContent = 'Select the affected connection.';
    }
    if (!dom.identifyConnectionList) return;
    dom.identifyConnectionList.innerHTML = '';
    identifyState.connections.forEach((conn) => {
      const li = document.createElement('li');
      li.className = 'identify-connection';
      const label = document.createElement('label');
      label.className = 'identify-connection-entry';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'identifyConnection';
      input.value = conn.porta_connection_id || (conn.id != null ? String(conn.id) : '');
      input.checked = Boolean(
        (conn.porta_connection_id && identifyState.selectedPortaConnectionId === conn.porta_connection_id)
        || (conn.id != null && identifyState.selectedConnectionId === String(conn.id)),
      );
      input.addEventListener('change', () => {
        identifyState.selectedPortaConnectionId = conn.porta_connection_id || null;
        identifyState.selectedConnectionId = conn.id != null ? String(conn.id) : null;
        updateIdentifyConfirmState();
      });

      const details = document.createElement('div');
      details.className = 'identify-connection-details';
      const title = document.createElement('div');
      title.className = 'identify-connection-label';
      title.textContent = conn.label || conn.porta_connection_id || (conn.id != null ? `Connection #${conn.id}` : 'Connection');
      details.appendChild(title);
      if (conn.porta_product_name || conn.status) {
        const meta = document.createElement('div');
        meta.className = 'identify-connection-meta';
        const parts = [];
        if (conn.porta_product_name) parts.push(conn.porta_product_name);
        if (conn.status) parts.push(conn.status);
        if (conn.blocked === true) parts.push('blocked');
        meta.textContent = parts.join(' • ');
        details.appendChild(meta);
      }

      label.appendChild(input);
      label.appendChild(details);
      li.appendChild(label);
      dom.identifyConnectionList.appendChild(li);
    });
  }

  function renderIdentifyCustomerList() {
    const dom = resolveIntDom();
    const escape = intHelpers.escapeHtml;
    if (!dom.identifyList || !dom.identifyEmpty) return;
    dom.identifyList.innerHTML = '';
    if (!identifyState.items.length) {
      dom.identifyEmpty.style.display = '';
      dom.identifyList.style.display = 'none';
      identifyState.connections = [];
      identifyState.selectedConnectionId = null;
      identifyState.selectedPortaConnectionId = null;
      identifyState.connectionError = null;
      renderIdentifyConnectionList();
      return;
    }
    dom.identifyEmpty.style.display = 'none';
    dom.identifyList.style.display = '';
    identifyState.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'identify-item';
      li.style.padding = '6px';
      li.style.borderBottom = '1px solid var(--border)';
      const label = document.createElement('label');
      label.style.display = 'flex';
      label.style.alignItems = 'flex-start';
      label.style.gap = '8px';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'identifyCandidate';
      input.value = String(item.id);
      input.checked = identifyState.selectedId === String(item.id);
      input.addEventListener('change', () => {
        const selectedId = String(item.id);
        const changed = identifyState.selectedId !== selectedId;
        identifyState.selectedId = selectedId;
        identifyState.selectedConnectionId = null;
        identifyState.selectedPortaConnectionId = null;
        identifyState.connections = [];
        identifyState.connectionError = null;
        renderIdentifyConnectionList();
        updateIdentifyConfirmState();
        if (changed && identifyState.ticketId) {
          loadIdentifyCustomerConnections(identifyState.ticketId, selectedId);
        }
      });
      const details = document.createElement('div');
      details.style.flex = '1';
      details.innerHTML = `
        <div style="font-weight:600;">${escape(item.name || 'Unknown customer')}</div>
        <div style="color:var(--sub);">${escape(item.phone || 'No phone')} • ${escape(item.bill_status || 'status?')}</div>
        <div style="color:var(--sub);">${escape(item.address || 'No address')}${item.city ? `, ${escape(item.city)}` : ''}${item.state ? `, ${escape(item.state)}` : ''}</div>
      `;
      label.appendChild(input);
      label.appendChild(details);
      li.appendChild(label);
      dom.identifyList.appendChild(li);
    });
    updateIdentifyConfirmState();
    renderIdentifyConnectionList();
  }

  function resetIdentifyCustomerDialog() {
    const dom = resolveIntDom();
    identifyState.ticketId = null;
    identifyState.items = [];
    identifyState.selectedId = null;
    identifyState.loading = false;
    identifyState.mode = 'phone';
    identifyState.connections = [];
    identifyState.connectionsLoading = false;
    identifyState.selectedConnectionId = null;
    identifyState.selectedPortaConnectionId = null;
    identifyState.connectionError = null;
    identifyState.connectionLoadSeq = 0;
    if (dom.identifyList) dom.identifyList.innerHTML = '';
    if (dom.identifyEmpty) {
      dom.identifyEmpty.style.display = '';
      dom.identifyEmpty.textContent = 'Searching Porta billing…';
    }
    if (dom.identifyStatus) dom.identifyStatus.textContent = '';
    if (dom.identifySearchInput) dom.identifySearchInput.value = '';
    if (dom.identifyConnectionList) dom.identifyConnectionList.innerHTML = '';
    if (dom.identifyConnectionStatus) dom.identifyConnectionStatus.textContent = '';
    if (dom.identifyConnectionSection) dom.identifyConnectionSection.hidden = true;
    updateIdentifyConfirmState();
    renderIdentifyConnectionList();
  }

  async function loadIdentifyCustomerCandidates(ticketId, options = {}) {
    if (!api.routes.adminTicketPortaCandidates) return;
    const dom = resolveIntDom();
    const mode = options.mode || identifyState.mode || 'phone';
    const query = options.query ? String(options.query).trim() : '';
    identifyState.loading = true;
    identifyState.selectedId = null;
    identifyState.mode = mode;
    identifyState.items = [];
    identifyState.ticketId = ticketId;
    setIdentifyStatus(mode === 'phone' ? 'Searching Porta billing by phone…' : 'Searching Porta billing by name…', 'info');
    renderIdentifyCustomerList();
    try {
      const data = await api.fetch(api.routes.adminTicketPortaCandidates(ticketId, mode, query), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      identifyState.items = items.map((item) => ({
        id: item.id,
        name: item.name,
        phone: item.phone,
        address: item.address,
        city: item.city,
        state: item.state,
        bill_status: item.bill_status,
      }));
      if (!items.length) {
        if (mode === 'phone') {
          setIdentifyStatus('No matches found by phone number.', 'warn');
          if (dom.identifyEmpty) dom.identifyEmpty.textContent = 'No Porta billing matches were found for this phone number.';
        } else {
          setIdentifyStatus('No matches found for that name.', 'warn');
          if (dom.identifyEmpty) dom.identifyEmpty.textContent = 'No Porta billing matches were found for that name.';
        }
      } else {
        setIdentifyStatus(`Found ${items.length} potential match${items.length === 1 ? '' : 'es'}.`, 'info');
      }
    } catch (err) {
      setIdentifyStatus(err?.message || 'Failed to query Porta billing', 'error');
    } finally {
      identifyState.loading = false;
      renderIdentifyCustomerList();
    }
  }

  async function loadIdentifyCustomerConnections(ticketId, portaCustomerId) {
    if (!ticketId || !portaCustomerId || !api.routes.adminTicketPortaConnections) return;
    identifyState.connectionLoadSeq += 1;
    const currentSeq = identifyState.connectionLoadSeq;
    identifyState.connectionsLoading = true;
    identifyState.connectionError = null;
    identifyState.connections = [];
    identifyState.selectedConnectionId = null;
    identifyState.selectedPortaConnectionId = null;
    renderIdentifyConnectionList();
    updateIdentifyConfirmState();
    try {
      const data = await api.fetch(api.routes.adminTicketPortaConnections(ticketId, portaCustomerId), { method: 'GET' });
      if (identifyState.connectionLoadSeq !== currentSeq) return;
      const items = Array.isArray(data?.items) ? data.items : [];
      identifyState.connections = items.map((item) => {
        const idValue = typeof item?.id === 'number' ? item.id : Number.isFinite(Number(item?.id)) ? Number(item.id) : null;
        const portaId = item?.porta_connection_id ? String(item.porta_connection_id) : null;
        let label = item?.label;
        if (!label) {
          const parts = [];
          if (item?.porta_product_name) parts.push(String(item.porta_product_name));
          if (portaId) parts.push(portaId);
          if (!parts.length && idValue != null) parts.push(`Connection #${idValue}`);
          label = parts.join(' - ') || 'Connection';
        }
        return {
          id: idValue,
          porta_connection_id: portaId,
          porta_product_name: item?.porta_product_name || null,
          status: item?.status || null,
          blocked: Boolean(item?.blocked),
          label,
        };
      });
      if (identifyState.connections.length === 1) {
        const single = identifyState.connections[0];
        identifyState.selectedConnectionId = single.id != null ? String(single.id) : null;
        identifyState.selectedPortaConnectionId = single.porta_connection_id;
      }
    } catch (err) {
      if (identifyState.connectionLoadSeq === currentSeq) {
        identifyState.connectionError = err?.message || 'Failed to load connections.';
        identifyState.connections = [];
        identifyState.selectedConnectionId = null;
        identifyState.selectedPortaConnectionId = null;
      }
    } finally {
      if (identifyState.connectionLoadSeq === currentSeq) {
        identifyState.connectionsLoading = false;
        renderIdentifyConnectionList();
        updateIdentifyConfirmState();
      }
    }
  }

  async function openIdentifyCustomerDialog(ticket) {
    const dom = resolveIntDom();
    if (!dom.identifyDlg || !ticket || !ticket.support_customer) {
      notify.showFlash('Customer lookup unavailable for this ticket', 'error');
      return;
    }
    resetIdentifyCustomerDialog();
    identifyState.ticketId = ticket.id;
    intHelpers.openDialog(dom.identifyDlg);
    await loadIdentifyCustomerCandidates(ticket.id, { mode: 'phone' });
  }

  function closeIdentifyCustomerDialog() {
    const dom = resolveIntDom();
    if (!dom.identifyDlg) return;
    try { dom.identifyDlg.close(); }
    catch { dom.identifyDlg.open = false; }
    resetIdentifyCustomerDialog();
  }

  // ---- Duplicate dialog --------------------------------------------
  function resetTicketDuplicateDialog() {
    const dom = resolveIntDom();
    ticketDuplicateState.ticketId = null;
    ticketDuplicateState.loading = false;
    ticketDuplicateState.items = [];
    ticketDuplicateState.selection = null;
    ticketDuplicateState.submitting = false;
    if (dom.duplicateStatus) dom.duplicateStatus.textContent = '';
    if (dom.duplicateList) dom.duplicateList.innerHTML = '';
    if (dom.duplicateConfirm) {
      dom.duplicateConfirm.disabled = true;
      dom.duplicateConfirm.dataset.pending = '0';
    }
  }

  function closeTicketDuplicateDialog() {
    const dom = resolveIntDom();
    resetTicketDuplicateDialog();
    if (dom.duplicateDlg) {
      try { dom.duplicateDlg.close(); }
      catch { dom.duplicateDlg.open = false; }
    }
  }

  function renderTicketDuplicateList() {
    const dom = resolveIntDom();
    if (!dom.duplicateList) return;
    dom.duplicateList.innerHTML = '';
    const items = Array.isArray(ticketDuplicateState.items) ? ticketDuplicateState.items : [];
    if (!items.length) {
      dom.duplicateList.innerHTML = '<li class="ticket-duplicate-empty">No open tickets available.</li>';
      return;
    }
    const fragment = document.createDocumentFragment();
    items.forEach((item, index) => {
      if (!item || !item.id) return;
      const li = document.createElement('li');
      li.className = 'ticket-duplicate-item';
      const label = document.createElement('label');
      label.className = 'ticket-duplicate-option';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'ticketDuplicateChoice';
      input.value = String(item.id);
      input.dataset.index = String(index);
      input.dataset.shortCode = item.short_code || '';
      if (ticketDuplicateState.selection && String(ticketDuplicateState.selection.id) === String(item.id)) {
        input.checked = true;
      }
      label.appendChild(input);

      const title = document.createElement('span');
      title.className = 'ticket-duplicate-title';
      const code = item.short_code ? `#${item.short_code}` : (String(item.id).split('-')[0] || String(item.id).slice(0, 8));
      title.textContent = `${code} — ${item.subject ? item.subject : '(no subject)'}`;
      const meta = document.createElement('span');
      meta.className = 'ticket-duplicate-meta';
      const bits = [];
      if (item.status) bits.push(intHelpers.formatTicketStatus(item.status));
      if (item.created_at) {
        bits.push(`Created ${intHelpers.formatDateTimeShort(item.created_at)}`);
      }
      if (item.closed_at) bits.push(`Closed ${intHelpers.formatDateTimeShort(item.closed_at)}`);
      meta.textContent = bits.join(' • ');
      const info = document.createElement('div');
      info.className = 'ticket-duplicate-info';
      info.appendChild(title);
      info.appendChild(meta);
      label.appendChild(info);

      li.appendChild(label);
      fragment.appendChild(li);
    });
    dom.duplicateList.appendChild(fragment);
    if (dom.duplicateConfirm) {
      dom.duplicateConfirm.disabled = !ticketDuplicateState.selection;
    }
  }

  async function loadTicketDuplicateCandidates() {
    const dom = resolveIntDom();
    if (!ticketDuplicateState.ticketId) return;
    ticketDuplicateState.loading = true;
    ticketDuplicateState.selection = null;
    if (dom.duplicateConfirm) dom.duplicateConfirm.disabled = true;
    if (dom.duplicateStatus) dom.duplicateStatus.textContent = 'Loading open tickets…';
    try {
      const data = await api.fetch(api.routes.adminTicketDuplicateCandidates(ticketDuplicateState.ticketId, '', 100));
      const items = Array.isArray(data?.items) ? data.items : [];
      ticketDuplicateState.items = items;
      renderTicketDuplicateList();
      if (dom.duplicateStatus) {
        dom.duplicateStatus.textContent = items.length
          ? 'Select the ticket that should remain active.'
          : 'No open tickets found for this customer.';
      }
    } catch (err) {
      ticketDuplicateState.items = [];
      renderTicketDuplicateList();
      if (dom.duplicateStatus) {
        dom.duplicateStatus.textContent = err?.message || 'Failed to load open tickets.';
      }
      throw err;
    } finally {
      ticketDuplicateState.loading = false;
    }
  }

  async function openTicketDuplicateDialog(ticket) {
    const dom = resolveIntDom();
    if (!ticket || !ticket.id) return;
    if (!ticket.support_customer_id) {
      notify.showFlash('Link this ticket to a customer before marking it as a duplicate.', 'error');
      return;
    }
    resetTicketDuplicateDialog();
    ticketDuplicateState.ticketId = ticket.id;
    if (dom.duplicateStatus) {
      dom.duplicateStatus.textContent = 'Loading open tickets…';
    }
    if (dom.duplicateDlg) {
      intHelpers.openDialog(dom.duplicateDlg);
    }
    try {
      await loadTicketDuplicateCandidates();
    } catch (err) {
      if (dom.duplicateStatus) {
        dom.duplicateStatus.textContent = err?.message || 'Failed to load open tickets.';
      }
    }
  }

  async function submitTicketDuplicateSelection() {
    const dom = resolveIntDom();
    if (ticketDuplicateState.submitting) return;
    const ticketId = ticketDuplicateState.ticketId;
    if (!ticketId) return;
    const selection = ticketDuplicateState.selection;
    if (!selection || !selection.id) {
      if (dom.duplicateStatus) dom.duplicateStatus.textContent = 'Select a ticket before linking.';
      return;
    }
    ticketDuplicateState.submitting = true;
    if (dom.duplicateConfirm) {
      dom.duplicateConfirm.disabled = true;
      dom.duplicateConfirm.dataset.pending = '1';
    }
    if (dom.duplicateStatus) {
      dom.duplicateStatus.textContent = 'Linking ticket…';
    }
    try {
      const payload = { primary_ticket_id: selection.id };
      const updated = await api.fetch(api.routes.adminTicketLinkDuplicate(ticketId), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (dom.closeTemplate) dom.closeTemplate.value = '';
      const templates = intHelpers.getTicketCloseTemplates();
      const template = templates.duplicate;
      if (template) {
        try {
          await api.fetch(api.routes.adminTicketComments(ticketId), {
            method: 'POST',
            body: JSON.stringify(template),
          });
        } catch (err) {
          notify.showFlash(err?.message || 'Ticket linked, but failed to post closure note.', 'warn');
        }
      }
      const primaryLabel = selection.short_code ? `#${selection.short_code}` : selection.subject || 'ticket';
      notify.showFlash(`Ticket closed as duplicate and linked to ${primaryLabel}.`);
      closeTicketDuplicateDialog();
      await reloadTicketsInt({ keepSelection: false, selectId: selection.id });
      intHelpers.loadTicketComments(selection.id);
      const detailMod = getDetailModule();
      if (detailMod) detailMod.loadTicketActivities(selection.id);
      intHelpers.refreshUnassignedSnapshot();
    } catch (err) {
      if (dom.duplicateStatus) {
        dom.duplicateStatus.textContent = err?.message || 'Failed to close ticket.';
      }
      notify.showFlash(err?.message || 'Failed to close ticket as duplicate.', 'error');
    } finally {
      ticketDuplicateState.submitting = false;
      if (dom.duplicateConfirm) {
        dom.duplicateConfirm.disabled = !ticketDuplicateState.selection;
        dom.duplicateConfirm.dataset.pending = '0';
      }
    }
  }

  // ---- Conflict dialog --------------------------------------------
  function resetTicketConflictDialog() {
    const dom = resolveIntDom();
    ticketConflictState.source = null;
    ticketConflictState.code = null;
    ticketConflictState.tickets = [];
    ticketConflictState.supportCustomerId = null;
    ticketConflictState.pendingMessageId = null;
    ticketConflictState.connections = [];
    ticketConflictState.requestedConnectionId = null;
    ticketConflictState.selectedConnectionId = null;
    ticketConflictState.retryHandler = null;
    if (dom.conflictMessage) dom.conflictMessage.textContent = '';
    if (dom.conflictList) dom.conflictList.innerHTML = '';
    if (dom.conflictResolve) {
      dom.conflictResolve.hidden = true;
      dom.conflictResolve.disabled = true;
    }
  }

  function renderTicketConflictDialog() {
    const dom = resolveIntDom();
    const escape = intHelpers.escapeHtml;
    if (!dom.conflictList) return;
    const connections = Array.isArray(ticketConflictState.connections) ? ticketConflictState.connections : [];
    const showResolve = ticketConflictState.code === 'connection_selection_required' && connections.length > 0;
    if (dom.conflictResolve) {
      dom.conflictResolve.hidden = !showResolve;
      dom.conflictResolve.disabled = !showResolve || ticketConflictState.selectedConnectionId == null;
    }
    const parts = [];
    const code = ticketConflictState.code;

    if (code === 'connection_selection_required') {
      if (!connections.length) {
        parts.push('<div class="ticket-conflict-empty">No connections were returned for this customer. Refresh the customer details and try again.</div>');
      } else {
        const selectedId = ticketConflictState.selectedConnectionId != null
          ? Number(ticketConflictState.selectedConnectionId)
          : null;
        const connectionItems = connections.map((conn) => {
          const idValue = Number(conn.id);
          const isChecked = !Number.isNaN(idValue) && idValue === selectedId;
          const label = (conn.label || conn.porta_product_name || conn.porta_connection_id || `Connection #${idValue}` || 'Connection').toString();
          const metaBits = [];
          if (conn.porta_connection_id) metaBits.push(`ID ${escape(String(conn.porta_connection_id))}`);
          if (conn.status) metaBits.push(`Status ${escape(String(conn.status))}`);
          if (conn.blocked) metaBits.push('Blocked');
          const metaText = metaBits.length ? metaBits.join(' • ') : '';
          return `
            <label class="ticket-conflict-connection">
              <input type="radio" name="ticketConflictConnection" value="${escape(String(idValue))}" ${isChecked ? 'checked' : ''}>
              <span class="ticket-conflict-connection-body">
                <span class="title">${escape(label)}</span>
                ${metaText ? `<span class="meta">${metaText}</span>` : ''}
              </span>
            </label>
          `;
        }).join('');
        parts.push(`<div class="ticket-conflict-connections">${connectionItems}</div>`);
      }
    }

    const tickets = Array.isArray(ticketConflictState.tickets) ? ticketConflictState.tickets : [];
    if (tickets.length) {
      const ticketCards = tickets.map((ticket) => {
        if (!ticket || !ticket.id) return '';
        const ticketId = String(ticket.id);
        const codeLabel = ticket.short_code ? `#${escape(ticket.short_code)}` : `Ticket ${escape(ticketId.slice(0, 8))}`;
        const statusLabel = intHelpers.formatTicketStatus(ticket.status);
        const priorityLabel = intHelpers.formatTicketPriority(ticket.priority);
        const createdLabel = ticket.created_at ? intHelpers.formatDateTimeShort(ticket.created_at) : null;
        const relativeCreated = ticket.created_at ? intHelpers.formatRelativeTime(ticket.created_at) : null;
        const metaParts = [];
        if (statusLabel && statusLabel !== '—') metaParts.push(statusLabel);
        if (priorityLabel && priorityLabel !== '—') metaParts.push(`Priority ${priorityLabel}`);
        if (createdLabel) metaParts.push(`Created ${createdLabel}`);
        else if (relativeCreated) metaParts.push(`Created ${relativeCreated}`);
        if (ticket.affected_connection_label) {
          metaParts.push(`Connection ${escape(String(ticket.affected_connection_label))}`);
        }
        const metaText = metaParts.join(' • ');
        const subject = (ticket.subject || '').trim() || '(no subject)';
        const openButton = `<button type="button" class="btn" data-conflict-action="open" data-ticket-id="${escape(ticketId)}">Open ticket</button>`;
        const linkButton = ticketConflictState.source === 'pending_message'
          ? `<button type="button" class="btn primary" data-conflict-action="link" data-ticket-id="${escape(ticketId)}">Link message to this ticket</button>`
          : '';
        return `
          <div class="ticket-conflict-item">
            <header>
              <span class="ticket-conflict-code">${codeLabel}</span>
              <span class="ticket-conflict-status">${escape(statusLabel || '—')}</span>
            </header>
            <p class="ticket-conflict-subject">${escape(subject)}</p>
            ${metaText ? `<div class="ticket-conflict-meta">${escape(metaText)}</div>` : ''}
            <div class="ticket-conflict-actions-row">
              ${openButton}
              ${linkButton}
            </div>
          </div>`;
      }).filter(Boolean);
      if (ticketCards.length) {
        parts.push(ticketCards.join(''));
      }
    }
    if (!parts.length) {
      dom.conflictList.innerHTML = '<div class="ticket-conflict-empty">No active tickets were found for this customer.</div>';
    } else {
      dom.conflictList.innerHTML = parts.join('');
    }
  }

  function openTicketConflictDialog(options = {}) {
    const dom = resolveIntDom();
    resetTicketConflictDialog();
    const detail = options.detail || {};
    ticketConflictState.source = options.source || null;
    ticketConflictState.code = detail.code || null;
    const conflictTickets = Array.isArray(detail.tickets) ? detail.tickets : [];
    const otherTickets = Array.isArray(detail.other_open_tickets) ? detail.other_open_tickets : [];
    ticketConflictState.tickets = [...conflictTickets, ...otherTickets];
    ticketConflictState.supportCustomerId = detail.support_customer_id || null;
    ticketConflictState.pendingMessageId = options.pendingMessageId || detail.pending_message_id || null;
    const availableConnections = Array.isArray(detail.available_connections)
      ? detail.available_connections
      : Array.isArray(detail.connections)
        ? detail.connections
        : [];
    ticketConflictState.connections = availableConnections;
    const requestedIdRaw = detail.requested_connection_id;
    const requestedId = requestedIdRaw != null && requestedIdRaw !== ''
      ? Number.parseInt(String(requestedIdRaw), 10)
      : null;
    const defaultCandidate = availableConnections.length
      ? Number.parseInt(String(availableConnections[0].id ?? ''), 10)
      : null;
    const defaultConnectionId = Number.isNaN(requestedId) || requestedId === null
      ? defaultCandidate
      : requestedId;
    ticketConflictState.requestedConnectionId = Number.isNaN(defaultConnectionId) ? null : defaultConnectionId;
    ticketConflictState.selectedConnectionId = ticketConflictState.requestedConnectionId;
    ticketConflictState.retryHandler = typeof options.resolve === 'function' ? options.resolve : null;
    if (dom.conflictTitle) {
      if (ticketConflictState.code === 'connection_selection_required') {
        dom.conflictTitle.textContent = 'Select connection';
      } else {
        dom.conflictTitle.textContent = ticketConflictState.source === 'pending_message'
          ? 'Link to existing ticket'
          : 'Active ticket exists';
      }
    }
    const baseMessage = typeof detail.message === 'string' && detail.message.trim()
      ? detail.message.trim()
      : 'An active ticket already exists for this customer.';
    if (dom.conflictMessage) {
      if (ticketConflictState.code === 'connection_selection_required') {
        dom.conflictMessage.textContent = 'Select the connection this ticket is about before creating it.';
      } else if (ticketConflictState.source === 'pending_message') {
        dom.conflictMessage.textContent = `${baseMessage} Link this intake message to the existing ticket instead.`;
      } else {
        dom.conflictMessage.textContent = `${baseMessage} Open the existing ticket to continue working.`;
      }
    }
    renderTicketConflictDialog();
    if (dom.conflictDlg) {
      intHelpers.openDialog(dom.conflictDlg);
    }
  }

  function closeTicketConflictDialog() {
    const dom = resolveIntDom();
    resetTicketConflictDialog();
    if (dom.conflictDlg) {
      try { dom.conflictDlg.close(); }
      catch { dom.conflictDlg.open = false; }
    }
  }

  async function linkPendingMessageConflictTicket(ticketId, button) {
    const messageId = ticketConflictState.pendingMessageId;
    if (!messageId) {
      notify.showFlash('Pending message not available for linking.', 'error');
      return;
    }
    if (button) {
      button.disabled = true;
      button.dataset.pending = '1';
    }
    try {
      await api.fetch(api.routes.adminPendingMessageFollowup(messageId), {
        method: 'POST',
        body: JSON.stringify({ ticket_id: ticketId }),
      });
      notify.showFlash('Message linked to existing ticket.', 'success');
      closeTicketConflictDialog();
      const pmDlg = document.getElementById('pendingMessageDlg');
      if (pmDlg) {
        try { pmDlg.close(); }
        catch { pmDlg.open = false; }
      }
      await views.tickets.pendingMessages.list.refresh({ silent: false });
      reloadTicketsInt({ keepSelection: true, selectId: ticketId }).catch(() => {});
    } catch (err) {
      const message = err && err.message ? err.message : 'Failed to link message to ticket';
      notify.showFlash(message, 'error');
    } finally {
      if (button) {
        button.disabled = false;
        button.dataset.pending = '0';
      }
    }
  }

  function handleTicketConflictChange(event) {
    const dom = resolveIntDom();
    const input = event.target && event.target.closest('input[type="radio"][name="ticketConflictConnection"]');
    if (!input) return;
    const parsed = Number.parseInt(String(input.value ?? ''), 10);
    ticketConflictState.selectedConnectionId = Number.isNaN(parsed) ? null : parsed;
    if (dom.conflictResolve && ticketConflictState.code === 'connection_selection_required') {
      dom.conflictResolve.disabled = ticketConflictState.selectedConnectionId == null;
    }
  }

  function handleTicketConflictClick(event) {
    const actionBtn = event.target.closest('[data-conflict-action]');
    if (!actionBtn) return;
    event.preventDefault();
    const action = actionBtn.dataset.conflictAction;
    if (action === 'cancel') {
      closeTicketConflictDialog();
      return;
    }
    const ticketId = actionBtn.dataset.ticketId;
    if (action === 'open') {
      if (!ticketId) return;
      closeTicketConflictDialog();
      reloadTicketsInt({ keepSelection: true, selectId: ticketId }).catch(() => {});
    } else if (action === 'link') {
      if (!ticketId) return;
      if (actionBtn.dataset.pending === '1') return;
      linkPendingMessageConflictTicket(ticketId, actionBtn).catch(() => {});
    } else if (action === 'resolve-connection') {
      if (actionBtn.dataset.pending === '1') return;
      const selectedId = ticketConflictState.selectedConnectionId;
      if (!ticketConflictState.retryHandler) {
        notify.showFlash('Unable to retry ticket creation without a handler.', 'error');
        return;
      }
      if (selectedId == null || Number.isNaN(selectedId)) {
        notify.showFlash('Select a connection before continuing.', 'error');
        return;
      }
      const resolvedId = Number.parseInt(String(selectedId), 10);
      if (Number.isNaN(resolvedId)) {
        notify.showFlash('Invalid connection selection. Please try again.', 'error');
        return;
      }
      actionBtn.dataset.pending = '1';
      actionBtn.disabled = true;
      Promise.resolve(ticketConflictState.retryHandler(resolvedId))
        .catch((err) => {
          if (err && err.message) {
            notify.showFlash(err.message, 'error');
          }
        })
        .finally(() => {
          actionBtn.dataset.pending = '0';
          actionBtn.disabled = false;
        });
    }
  }

  // ---- Assign dialog ----------------------------------------------
  function resetTicketAssignDialog() {
    const dom = resolveIntDom();
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    if (dom.assignForm) dom.assignForm.reset();
    if (dom.assignUserSelect) dom.assignUserSelect.innerHTML = '';
    if (dom.assignRoleSelect) dom.assignRoleSelect.innerHTML = '';
    if (dom.assignSummary) dom.assignSummary.textContent = '';
    if (dom.assignDlg) {
      delete dom.assignDlg.dataset.ticketId;
      delete dom.assignDlg.dataset.mode;
    }
    ticketAssignMode = ASSIGN_MODE_ASSIGN;
    if (dom.assignUserSelect) dom.assignUserSelect.required = true;
    if (dom.assignRoleField) dom.assignRoleField.hidden = false;
    if (dom.assignTitle) dom.assignTitle.textContent = 'Assign Ticket';
    if (dom.assignUserHelp) dom.assignUserHelp.textContent = 'Select a staff member or choose Unassigned to clear the owner.';
    if (dom.assignRoleHelp) dom.assignRoleHelp.textContent = 'Leave blank to keep the current role.';
    if (dom.assignSubmitBtn) dom.assignSubmitBtn.disabled = false;
  }

  function populateAssignUserOptions(ticket, options = {}) {
    const dom = resolveIntDom();
    if (!dom.assignUserSelect) return;
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    const ASSIGN_MODE_ESCALATE = intHelpers.getAssignModeEscalate();
    const FIELD_TECH_ROLE = intHelpers.getFieldTechRole();
    const currentTicket = intHelpers.hydrateTicketAssignment(ticket);
    const mode = options.mode || ticketAssignMode || ASSIGN_MODE_ASSIGN;
    dom.assignUserSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = mode === ASSIGN_MODE_ESCALATE ? 'Select supervisor…' : 'Select user…';
    placeholder.disabled = true;
    dom.assignUserSelect.appendChild(placeholder);

    const supervisorRolesForTicket = mode === ASSIGN_MODE_ESCALATE ? intHelpers.deriveSupervisorAdminRoles(currentTicket) : null;
    const me = intHelpers.getCurrentUser();
    const actorRoleSet = new Set(Array.isArray(me?.roles) ? me.roles : []);

    const sourceUsers = mode === ASSIGN_MODE_ESCALATE
      ? (intHelpers.getSupervisorAdminUsers() || []).slice()
      : (intHelpers.getTechnicianAdminUsers() || []).slice();

    const allowUnassigned = mode === ASSIGN_MODE_ASSIGN && !actorRoleSet.has(FIELD_TECH_ROLE);
    if (allowUnassigned) {
      const noneOption = document.createElement('option');
      noneOption.value = '__unassigned__';
      noneOption.textContent = 'Unassigned';
      dom.assignUserSelect.appendChild(noneOption);
    } else if (mode === ASSIGN_MODE_ESCALATE) {
      const autoOption = document.createElement('option');
      autoOption.value = '__auto_supervisor__';
      const autoTargetLabel = intHelpers.describeSupervisorTargets(supervisorRolesForTicket);
      autoOption.textContent = autoTargetLabel
        ? `Auto (first available ${autoTargetLabel})`
        : 'Auto (first available supervisor)';
      dom.assignUserSelect.appendChild(autoOption);
    }

    const eligibleUsers = sourceUsers
      .filter((user) => user && typeof user.id !== 'undefined')
      .filter((user) => (mode === ASSIGN_MODE_ESCALATE
        ? intHelpers.userIsSupervisor(user, supervisorRolesForTicket)
        : intHelpers.userEligibleForTicketAssignment(user)))
      .sort((a, b) => intHelpers.adminUserSortKey(a).localeCompare(intHelpers.adminUserSortKey(b)));

    let hasCurrent = false;
    eligibleUsers.forEach((user) => {
      const option = document.createElement('option');
      option.value = String(user.id);
      option.textContent = intHelpers.formatAssignmentOptionLabel(user);
      if (
        mode === ASSIGN_MODE_ASSIGN
        && currentTicket
        && currentTicket.assignee_user_id != null
        && Number(user.id) === Number(currentTicket.assignee_user_id)
      ) {
        hasCurrent = true;
      }
      if (
        mode === ASSIGN_MODE_ESCALATE
        && currentTicket
        && currentTicket.escalated_to_user_id != null
        && Number(user.id) === Number(currentTicket.escalated_to_user_id)
      ) {
        hasCurrent = true;
      }
      dom.assignUserSelect.appendChild(option);
    });

    let currentValue;
    if (mode === ASSIGN_MODE_ASSIGN) {
      currentValue = currentTicket && currentTicket.assignee_user_id != null ? String(currentTicket.assignee_user_id) : '__unassigned__';
      if (!allowUnassigned && currentValue === '__unassigned__') {
        currentValue = '';
      }
      if (currentValue && currentValue !== '__unassigned__' && !hasCurrent) {
        const fallback = document.createElement('option');
        fallback.value = currentValue;
        const fallbackUser = sourceUsers.find((user) => user && String(user.id) === currentValue);
        fallback.textContent = intHelpers.formatAssignmentOptionLabel(fallbackUser) || `User #${currentValue}`;
        dom.assignUserSelect.appendChild(fallback);
      }
    } else {
      currentValue = currentTicket && currentTicket.escalated_to_user_id != null ? String(currentTicket.escalated_to_user_id) : '__auto_supervisor__';
      if (currentValue !== '__auto_supervisor__' && !eligibleUsers.some((user) => String(user.id) === currentValue)) {
        const fallback = document.createElement('option');
        fallback.value = currentValue;
        const fallbackUser = sourceUsers.find((user) => user && String(user.id) === currentValue);
        fallback.textContent = intHelpers.formatAssignmentOptionLabel(fallbackUser) || `Supervisor #${currentValue}`;
        dom.assignUserSelect.appendChild(fallback);
      }
    }

    dom.assignUserSelect.value = currentValue;
  }

  function populateAssignRoleOptions(ticket, options = {}) {
    const dom = resolveIntDom();
    if (!dom.assignRoleSelect) return;
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    const ASSIGN_MODE_ESCALATE = intHelpers.getAssignModeEscalate();
    const mode = options.mode || ticketAssignMode || ASSIGN_MODE_ASSIGN;
    dom.assignRoleSelect.innerHTML = '';
    dom.assignRoleSelect.disabled = true;
    if (mode === ASSIGN_MODE_ESCALATE) return;
    const hydratedTicket = intHelpers.hydrateTicketAssignment(ticket);
    const currentRole = hydratedTicket && hydratedTicket.assignee_role ? String(hydratedTicket.assignee_role) : null;
    const noteOption = document.createElement('option');
    noteOption.value = '';
    const roleLabel = currentRole ? intHelpers.formatTicketAssignee(currentRole, null) : null;
    noteOption.textContent = roleLabel
      ? `Current role: ${roleLabel} (auto-derived)`
      : 'Role is assigned automatically';
    dom.assignRoleSelect.appendChild(noteOption);
  }

  async function openTicketAssignDialog(ticket, options = {}) {
    const dom = resolveIntDom();
    if (!dom.assignDlg || !dom.assignForm || !dom.assignUserSelect || !dom.assignRoleSelect) {
      notify.showFlash('Assignment dialog unavailable', 'error');
      return;
    }
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    const ASSIGN_MODE_ESCALATE = intHelpers.getAssignModeEscalate();
    const mode = options.mode || ASSIGN_MODE_ASSIGN;
    ticketAssignMode = mode;
    dom.assignDlg.dataset.mode = mode;
    if (dom.assignTitle) dom.assignTitle.textContent = mode === ASSIGN_MODE_ESCALATE ? 'Escalate Ticket' : 'Assign Ticket';
    if (dom.assignUserHelp) {
      dom.assignUserHelp.textContent = mode === ASSIGN_MODE_ESCALATE
        ? 'Choose the supervisor to escalate to, or leave on Auto to let the system select.'
        : 'Select a staff member or choose Unassigned to clear the owner.';
    }
    if (dom.assignRoleHelp) {
      dom.assignRoleHelp.textContent = mode === ASSIGN_MODE_ESCALATE
        ? 'Escalation role is determined automatically.'
        : 'Leave blank to keep the current role.';
    }
    if (dom.assignUserSelect) {
      const lockAssignee = Boolean(options.lockAssignee);
      dom.assignUserSelect.disabled = lockAssignee;
      dom.assignUserSelect.required = mode === ASSIGN_MODE_ASSIGN && !lockAssignee;
    }
    if (dom.assignRoleField) dom.assignRoleField.hidden = mode === ASSIGN_MODE_ESCALATE;
    if (dom.assignNoteField) dom.assignNoteField.hidden = mode !== ASSIGN_MODE_ASSIGN;
    if (dom.assignNoteHelp) {
      dom.assignNoteHelp.textContent = mode === ASSIGN_MODE_ASSIGN
        ? 'Required when changing the assignee.'
        : '';
    }

    if (mode === ASSIGN_MODE_ESCALATE) {
      await intHelpers.ensureSupervisorUsersLoaded({ force: true });
    } else {
      await intHelpers.ensureTechnicianUsersLoaded({ force: true });
    }
    dom.assignForm.reset();
    if (dom.assignNoteInput) dom.assignNoteInput.value = '';
    populateAssignUserOptions(ticket, { mode });
    populateAssignRoleOptions(ticket, { mode });
    if (dom.assignSummary) {
      const subject = ticket.subject || '(no subject)';
      const label = mode === ASSIGN_MODE_ESCALATE ? 'Escalate' : 'Assign';
      dom.assignSummary.textContent = `${label}: ${subject} • ${ticket.id}`;
    }
    const hydratedTicket = intHelpers.hydrateTicketAssignment(ticket);
    const originalAssigneeId = hydratedTicket && hydratedTicket.assignee_user_id != null
      ? String(hydratedTicket.assignee_user_id)
      : '';
    dom.assignDlg.dataset.originalAssignee = mode === ASSIGN_MODE_ASSIGN ? originalAssigneeId : '';
    if (options.presetAssigneeId != null && dom.assignUserSelect) {
      const desired = String(options.presetAssigneeId);
      const hasOption = Array.from(dom.assignUserSelect.options).some((opt) => opt.value === desired);
      if (hasOption) {
        dom.assignUserSelect.value = desired;
      }
    }
    dom.assignDlg.dataset.ticketId = String(ticket.id);
    intHelpers.openDialog(dom.assignDlg);
    if (options.focusNote && dom.assignNoteInput) {
      setTimeout(() => { dom.assignNoteInput.focus(); }, 0);
    }
  }

  function closeTicketAssignDialog() {
    const dom = resolveIntDom();
    if (!dom.assignDlg) return;
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    try { dom.assignDlg.close(); }
    catch { dom.assignDlg.open = false; }
    delete dom.assignDlg.dataset.originalAssignee;
    if (dom.assignUserSelect) {
      dom.assignUserSelect.disabled = false;
      dom.assignUserSelect.required = dom.assignDlg.dataset.mode === ASSIGN_MODE_ASSIGN;
    }
    if (dom.assignNoteInput) dom.assignNoteInput.value = '';
  }

  async function handleTicketAssignSubmit(event) {
    const dom = resolveIntDom();
    event.preventDefault();
    if (!dom.assignDlg) return;
    const ticketId = dom.assignDlg.dataset.ticketId;
    if (!ticketId) {
      notify.showFlash('Ticket not selected', 'error');
      return;
    }
    const ASSIGN_MODE_ASSIGN = intHelpers.getAssignModeAssign();
    const ASSIGN_MODE_ESCALATE = intHelpers.getAssignModeEscalate();
    const mode = dom.assignDlg.dataset.mode || ticketAssignMode || ASSIGN_MODE_ASSIGN;
    const payload = {};
    if (mode === ASSIGN_MODE_ESCALATE) {
      payload.status = 'escalated';
      payload.escalated_at = new Date().toISOString();
      if (dom.assignUserSelect) {
        const value = dom.assignUserSelect.value;
        if (value && value !== '__auto_supervisor__') {
          payload.escalated_to_user_id = Number(value);
        }
      }
    } else {
      if (dom.assignUserSelect) {
        const value = dom.assignUserSelect.value;
        if (value === '__unassigned__') {
          payload.assignee_user_id = null;
        } else if (value) {
          payload.assignee_user_id = Number(value);
        }
      }
      if (dom.assignRoleSelect && dom.assignRoleSelect.value) {
        payload.assignee_role = dom.assignRoleSelect.value;
      }
      if (!Object.prototype.hasOwnProperty.call(payload, 'assignee_user_id') && !payload.assignee_role) {
        notify.showFlash('Select a user or role to update', 'error');
        return;
      }
      const previousAssigneeRaw = dom.assignDlg.dataset.originalAssignee || '';
      const previousAssigneeId = previousAssigneeRaw ? Number(previousAssigneeRaw) : null;
      let nextAssigneeId = previousAssigneeId;
      if (dom.assignUserSelect) {
        const value = dom.assignUserSelect.value;
        if (value === '__unassigned__') {
          nextAssigneeId = null;
        } else if (value) {
          nextAssigneeId = Number(value);
        }
      }
      const assignmentNote = dom.assignNoteInput ? dom.assignNoteInput.value.trim() : '';
      const assigneeChanged = nextAssigneeId !== previousAssigneeId;
      if (assigneeChanged && !assignmentNote) {
        notify.showFlash('Add a reassignment note before changing the assignee.', 'error');
        return;
      }
      if (assignmentNote) {
        payload.assignment_note = assignmentNote;
      }
    }
    if (dom.assignSubmitBtn) dom.assignSubmitBtn.disabled = true;
    try {
      const updated = await mutateTicket(ticketId, payload);
      notify.showFlash(mode === ASSIGN_MODE_ESCALATE ? 'Ticket escalated' : 'Assignment updated');
      closeTicketAssignDialog();
      await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
      intHelpers.loadTicketComments(updated.id);
      intHelpers.refreshUnassignedSnapshot();
    } catch (err) {
      notify.showFlash(err.message || (mode === ASSIGN_MODE_ESCALATE ? 'Failed to escalate ticket' : 'Failed to update assignment'), 'error');
    } finally {
      if (dom.assignSubmitBtn) dom.assignSubmitBtn.disabled = false;
    }
  }

  // ---- Mount ------------------------------------------------------
  function mountInteractions() {
    if (intMounted) return;
    const dom = resolveIntDom();
    // Detail-panel actions (the action menu router)
    if (dom.detailActions) {
      dom.detailActions.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-ticket-action]');
        if (!btn) return;
        const action = btn.dataset.ticketAction;
        if (!action) return;
        await handleTicketAction(action);
        const parentMenu = btn.closest('details.action-menu');
        if (parentMenu && typeof parentMenu.open !== 'undefined') {
          parentMenu.open = false;
        }
      });
    }
    // Request-materials dialog wiring
    const equipForm = document.getElementById('ticketEquipmentForm');
    if (equipForm && !equipForm.dataset.bound) {
      equipForm.dataset.bound = '1';
      equipForm.addEventListener('submit', (e) => { submitTicketEquipmentRequest(e).catch(() => {}); });
    }
    const equipAdd = document.getElementById('ticketEquipmentAddLine');
    if (equipAdd && !equipAdd.dataset.bound) {
      equipAdd.dataset.bound = '1';
      equipAdd.addEventListener('click', () => addTicketEquipmentRow());
    }
    // Customer edit dialog wiring
    if (dom.customerEditBtn && dom.customerEditDlg) {
      dom.customerEditBtn.addEventListener('click', openTicketCustomerEditDialog);
    }
    if (dom.customerEditClose) {
      dom.customerEditClose.addEventListener('click', closeTicketCustomerEditDialog);
    }
    if (dom.customerEditCancel) {
      dom.customerEditCancel.addEventListener('click', closeTicketCustomerEditDialog);
    }
    if (dom.customerEditForm && dom.customerEditDlg) {
      dom.customerEditForm.addEventListener('submit', (event) => {
        handleCustomerEditSubmit(event).catch(() => {});
      });
    }
    // Identify customer
    if (dom.identifyBtn) {
      dom.identifyBtn.addEventListener('click', async () => {
        const ticket = getSelectedTicketSafeInt();
        if (!ticket) {
          notify.showFlash('Select a ticket first', 'error');
          return;
        }
        await openIdentifyCustomerDialog(ticket);
      });
    }
    if (dom.identifyForm) {
      dom.identifyForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!identifyState.ticketId || !identifyState.selectedId) {
          setIdentifyStatus('Select a customer before linking.', 'warn');
          updateIdentifyConfirmState();
          return;
        }
        if (identifyState.loading) return;
        setIdentifyStatus('Linking customer to ticket…', 'info');
        identifyState.loading = true;
        updateIdentifyConfirmState();
        try {
          const payload = { i_customer: identifyState.selectedId };
          if (identifyState.selectedConnectionId != null) {
            const numericId = Number(identifyState.selectedConnectionId);
            if (!Number.isNaN(numericId)) {
              payload.affected_connection_id = numericId;
            }
          }
          if (identifyState.selectedPortaConnectionId) {
            payload.porta_connection_id = identifyState.selectedPortaConnectionId;
          }
          const updated = await api.fetch(api.routes.adminIdentifyCustomer(identifyState.ticketId), { method: 'POST', body: JSON.stringify(payload) });
          notify.showFlash('Customer linked to ticket');
          closeIdentifyCustomerDialog();
          if (updated && updated.id) {
            await reloadTicketsInt({ keepSelection: true, selectId: updated.id });
            intHelpers.loadTicketComments(updated.id);
            intHelpers.refreshUnassignedSnapshot();
          } else {
            await reloadTicketsInt({ keepSelection: true });
          }
        } catch (err) {
          const detail = err && err.body && err.body.detail;
          if (err?.status !== 409 || !setIdentifyConflict(detail)) {
            setIdentifyStatus(err?.message || 'Failed to link customer', 'error');
          }
        } finally {
          identifyState.loading = false;
          updateIdentifyConfirmState();
        }
      });
    }
    if (dom.identifyDlg) {
      dom.identifyDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeIdentifyCustomerDialog();
      });
      dom.identifyDlg.addEventListener('close', resetIdentifyCustomerDialog);
    }
    if (dom.identifySearchBtn) {
      dom.identifySearchBtn.addEventListener('click', async () => {
        if (!identifyState.ticketId) return;
        const value = (dom.identifySearchInput && dom.identifySearchInput.value ? dom.identifySearchInput.value.trim() : '');
        if (!value) {
          setIdentifyStatus('Enter a name to search Porta billing.', 'warn');
          return;
        }
        await loadIdentifyCustomerCandidates(identifyState.ticketId, { mode: 'name', query: value });
      });
    }
    if (dom.identifySearchInput) {
      dom.identifySearchInput.addEventListener('keydown', async (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!identifyState.ticketId) return;
          const value = dom.identifySearchInput.value.trim();
          if (!value) {
            setIdentifyStatus('Enter a name to search Porta billing.', 'warn');
            return;
          }
          await loadIdentifyCustomerCandidates(identifyState.ticketId, { mode: 'name', query: value });
        }
      });
    }
    // Assign dialog
    if (dom.assignForm) {
      dom.assignForm.addEventListener('submit', (event) => {
        handleTicketAssignSubmit(event).catch(() => {});
      });
    }
    if (dom.assignDlg) {
      dom.assignDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeTicketAssignDialog();
      });
      dom.assignDlg.addEventListener('close', resetTicketAssignDialog);
    }
    // SLA policy
    if (dom.slaPolicySelect) {
      dom.slaPolicySelect.addEventListener('change', (event) => {
        handleTicketSlaPolicyChange(event).catch(() => {});
      });
    }
    // Conflict dialog wiring
    if (dom.conflictDlg) {
      dom.conflictDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeTicketConflictDialog();
      });
      dom.conflictDlg.addEventListener('close', resetTicketConflictDialog);
      dom.conflictDlg.addEventListener('click', handleTicketConflictClick);
    }
    if (dom.conflictList) {
      dom.conflictList.addEventListener('change', handleTicketConflictChange);
    }
    const conflictClose = document.getElementById('ticketConflictClose');
    if (conflictClose) {
      conflictClose.addEventListener('click', () => closeTicketConflictDialog());
    }
    // Duplicate dialog wiring
    if (dom.duplicateDlg) {
      dom.duplicateDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeTicketDuplicateDialog();
      });
    }
    const duplicateForm = document.getElementById('ticketDuplicateForm');
    if (duplicateForm) {
      duplicateForm.addEventListener('submit', (event) => {
        event.preventDefault();
        submitTicketDuplicateSelection().catch(() => {});
      });
    }
    if (dom.duplicateList) {
      dom.duplicateList.addEventListener('change', (event) => {
        const input = event.target && event.target.closest('input[type="radio"][name="ticketDuplicateChoice"]');
        if (!input) return;
        const index = Number(input.dataset.index || '-1');
        const selection = index >= 0 ? ticketDuplicateState.items[index] : null;
        ticketDuplicateState.selection = selection ? { ...selection } : null;
        if (dom.duplicateConfirm) {
          dom.duplicateConfirm.disabled = !ticketDuplicateState.selection || ticketDuplicateState.submitting;
        }
      });
    }
    intMounted = true;
  }

  // ---- Ticket comments + attachments + expand dialog (Phase 2.1.d.5) --
  // Owns the comment thread under each ticket detail panel:
  //   - Comment list render (`#ticketCommentsList`) with per-comment
  //     visibility/review-state pills, AI feedback + history, and the
  //     edit/retry/override action buttons.
  //   - Comment edit/submit/retry/override (`submitTicketComment`,
  //     `startTicketCommentEdit`, `saveTicketCommentEdit`,
  //     `retryTicketComment`, `overrideTicketComment`,
  //     `applyCommentSuggestion`).
  //   - The Quill comment editor and its expand-dialog twin.
  //   - Attachments: file picker, list rendering, replace/remove,
  //     per-file image conversion, the `ticketCommentAttachmentsState`.
  //   - The expand dialog (`#ticketCommentExpandDlg`) — open/close + sync
  //     between the inline composer and modal editor.
  //   - Comment-specific HTML helpers (`commentHtmlToPlain`,
  //     `plainTextToCommentHtml`, `sanitizeTicketCommentHtml`).
  //   - State: `ticketCommentCache`, `ticketCommentPendingRefresh`,
  //     `autoStatusUpdateInFlight`, `ticketCommentEditState`.
  //
  // What stays in admin.js (injected):
  //   - Generic HTML helpers (`escapeHtml`, `escapeRegExp`, `looksLikeHtml`,
  //     `stripHtml`, `stripZeroWidth`, `toPlainText`, `sanitizeHtml`).
  //   - Date formatters (`formatDateTimeFull`, `formatDateTimeShort`).
  //   - Quill integration helpers (`getQuillHtml`, `setQuillHtml`,
  //     `ensureQuillGreeting`, `stripQuillGreetingIfOnly`,
  //     `dedupeQuillGreeting`, `ensureCustomerGreeting`,
  //     `dedupeGreetingText`, `updateInlineComposerStorage`).
  //   - Constants (`TICKET_VISIBILITY_LABELS`, `COMMENT_REVIEW_LABELS`,
  //     `COMMENT_EDITABLE_STATES`, `COMMENT_RETRY_STATES`,
  //     `COMMENT_OVERRIDE_STATES`, `COMMENT_SUPERVISOR_ROLES`).
  //   - Sized: ~1 700 lines moved.

  let commDom = null;
  let commMounted = false;
  const ticketCommentCache = new Map();
  const ticketCommentPendingRefresh = new Set();
  const autoStatusUpdateInFlight = new Set();
  let ticketCommentEditState = { commentId: null, draft: '', skipReview: false };
  const ticketCommentAttachmentsState = { items: [] };
  const COMMENT_ATTACHMENT_LIMIT = 6;
  const COMMENT_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;
  let ticketCommentQuill = null;
  let ticketCommentExpandQuill = null;

  // Phase 3.2.c: Quill / greeting / composer helpers (moved here from
  // admin.js — they operate on tickets-cluster Quill editors and DOM).
  const COMMENT_EDITOR_TOOLBAR = [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ];
  const COMMENT_EDITOR_OPTIONS = {
    theme: 'snow',
    modules: { toolbar: COMMENT_EDITOR_TOOLBAR },
    placeholder: 'Share an update for the team or customer…',
  };
  const hasQuill = typeof window !== 'undefined' && typeof window.Quill !== 'undefined';

  function stripZeroWidth(value) {
    return String(value || '')
      .replace(/[​‌‍⁠﻿]/g, '')
      .replace(/[  ]/g, ' ');
  }

  function dedupeGreetingText(text, customerName) {
    if (!customerName) return text || '';
    const name = String(customerName || '').trim();
    if (!name) return text || '';
    const normalized = stripZeroWidth(text || '');
    const pattern = new RegExp(`^(?:\\s*Dear\\s+${escapeRegExp(name)}\\b[^\\n]*\\n?\\s*){2,}`, 'i');
    const match = normalized.match(pattern);
    if (!match) return text || '';
    const single = `Dear ${name},\n\n`;
    const remainder = normalized.slice(match[0].length);
    return `${single}${remainder}`;
  }

  function dedupeQuillGreeting(editor, customerName) {
    if (!editor || !customerName) return;
    const name = String(customerName || '').trim();
    if (!name) return;
    const text = stripZeroWidth(editor.getText() || '');
    const pattern = new RegExp(`^(?:\\s*Dear\\s+${escapeRegExp(name)}\\b[^\\n]*\\n?\\s*){2,}`, 'i');
    const match = text.match(pattern);
    if (!match) return;
    const single = `Dear ${name},\n\n`;
    const toRemove = match[0].length - single.length;
    if (toRemove > 0) {
      editor.deleteText(single.length, toRemove, 'user');
    }
  }

  function ensureCustomerGreeting(body, customerName) {
    if (!customerName) return body || '';
    const name = String(customerName || '').trim();
    if (!name) return body || '';
    const text = stripZeroWidth(body || '').replace(/^\s+/, '');
    const greetingRegex = new RegExp(`^dear\\s+${escapeRegExp(name)}\\b`, 'i');
    if (greetingRegex.test(text)) {
      return text;
    }
    const greetingLine = `Dear ${name},`;
    return text ? `${greetingLine}\n\n${text}` : `${greetingLine}\n\n`;
  }

  function normalizeQuillHtml(html) {
    if (!html) return '';
    const trimmed = String(html).trim();
    if (!trimmed) return '';
    const blankPattern = /^(?:<p><br><\/p>|<div><br><\/div>|\s)+$/i;
    if (blankPattern.test(trimmed)) return '';
    return trimmed;
  }

  function getQuillHtml(editor) {
    const ticketCommentBody = document.getElementById('ticketCommentBody');
    if (!editor || !editor.root) {
      return normalizeQuillHtml(ticketCommentBody ? ticketCommentBody.value : '');
    }
    return normalizeQuillHtml(editor.root.innerHTML);
  }

  function setQuillHtml(editor, html) {
    if (!editor) return;
    const content = normalizeQuillHtml(html);
    if (!content) {
      editor.setText('');
      return;
    }
    editor.clipboard.dangerouslyPasteHTML(content);
  }

  function updateInlineComposerStorage(html) {
    const ticketCommentBody = document.getElementById('ticketCommentBody');
    if (!ticketCommentBody) return;
    if (typeof html === 'undefined') {
      ticketCommentBody.value = ticketCommentQuill ? getQuillHtml(ticketCommentQuill) : (ticketCommentBody.value || '');
    } else {
      ticketCommentBody.value = normalizeQuillHtml(html);
    }
  }

  function ensureQuillGreeting(editor, customerName) {
    if (!editor || !customerName) return;
    const name = String(customerName || '').trim();
    if (!name) return;
    const plain = stripZeroWidth(editor.getText() || '').replace(/^\s+/, '');
    const greetingRegex = new RegExp(`^Dear\\s+${escapeRegExp(name)}\\b`, 'i');
    if (greetingRegex.test(plain)) return;
    editor.insertText(0, `Dear ${name},\n\n`, 'user');
  }

  function stripQuillGreetingIfOnly(editor, customerName) {
    if (!editor || !customerName) return;
    const name = String(customerName || '').trim();
    if (!name) return;
    const plain = stripZeroWidth(editor.getText() || '').trim();
    if (!plain) {
      editor.setText('');
      return;
    }
    const greetingLine = `Dear ${name},`;
    if (plain === greetingLine) {
      editor.setText('');
    }
  }

  // Phase 3.2.c: commHelpers populated from imports + sibling lookups.
  const commHelpers = {
    escapeHtml,
    escapeRegExp,
    formatDateTimeFull,
    formatDateTimeShort,
    sanitizeHtml,
    stripHtml,
    stripZeroWidth,
    looksLikeHtml,
    hasRole,
    getCurrentUser: getCurrentAdminUser,
    getSelectedTicket: () => {
      const list = views.tickets && views.tickets.list;
      return list && typeof list.getSelectedTicket === 'function' ? list.getSelectedTicket() : null;
    },
    getSelectedTicketIdValue: () => {
      const list = views.tickets && views.tickets.list;
      return list && typeof list.getSelectedTicketIdValue === 'function' ? list.getSelectedTicketIdValue() : null;
    },
    findTicketById: (id) => {
      const list = views.tickets && views.tickets.list;
      return list && typeof list.findTicketById === 'function' ? list.findTicketById(id) : null;
    },
    getTicketCustomerName: (ticket) => {
      const detail = views.tickets && views.tickets.detail;
      return detail && typeof detail.getTicketCustomerName === 'function' ? detail.getTicketCustomerName(ticket) : null;
    },
    mutateTicket: (ticketId, payload) => mutateTicket(ticketId, payload),
    reloadTickets: (opts) => reloadTickets(opts),
    refreshUnassignedSnapshot: () => refreshUnassignedSnapshot(),
    clearTicketAttention: (id) => clearTicketAttention(id),
    getQuillHtml,
    setQuillHtml,
    ensureQuillGreeting,
    stripQuillGreetingIfOnly,
    dedupeQuillGreeting,
    ensureCustomerGreeting,
    dedupeGreetingText,
    updateInlineComposerStorage,
    getTicketVisibilityLabels: () => TICKET_VISIBILITY_LABELS,
    getCommentReviewLabels: () => COMMENT_REVIEW_LABELS,
    getCommentEditableStates: () => COMMENT_EDITABLE_STATES,
    getCommentRetryStates: () => COMMENT_RETRY_STATES,
    getCommentOverrideStates: () => COMMENT_OVERRIDE_STATES,
    getCommentSupervisorRoles: () => COMMENT_SUPERVISOR_ROLES,
    getCommentEditorOptions: () => COMMENT_EDITOR_OPTIONS,
    hasQuill: () => hasQuill,
    QuillCtor: typeof window !== 'undefined' ? window.Quill : null,
  };

  function resolveCommDom() {
    if (commDom) return commDom;
    commDom = {
      list: document.getElementById('ticketCommentsList'),
      status: document.getElementById('ticketCommentsStatus'),
      form: document.getElementById('ticketCommentForm'),
      submitBtn: null,
      editorEl: document.getElementById('ticketCommentEditor'),
      body: document.getElementById('ticketCommentBody'),
      visibility: document.getElementById('ticketCommentVisibility'),
      expandBtn: document.getElementById('ticketCommentExpand'),
      expandDlg: document.getElementById('ticketCommentExpandDlg'),
      expandForm: document.getElementById('ticketCommentExpandForm'),
      expandSubmit: null,
      expandVisibility: document.getElementById('ticketCommentExpandVisibility'),
      expandThread: document.getElementById('ticketCommentExpandThread'),
      expandClose: document.getElementById('ticketCommentExpandClose'),
      expandCancel: document.getElementById('ticketCommentExpandCancel'),
      expandEditorEl: document.getElementById('ticketCommentExpandEditor'),
      attachmentList: document.getElementById('ticketCommentAttachmentList'),
      attachmentModalList: document.getElementById('ticketCommentAttachmentModalList'),
      attachmentAddBtn: document.getElementById('ticketCommentAttachmentAdd'),
      attachmentModalAddBtn: document.getElementById('ticketCommentAttachmentModalAdd'),
      attachmentInput: document.getElementById('ticketCommentAttachmentInput'),
    };
    if (commDom.form) commDom.submitBtn = commDom.form.querySelector('button[type="submit"]');
    if (commDom.expandForm) commDom.expandSubmit = commDom.expandForm.querySelector('button[type="submit"]');
    return commDom;
  }

  // ---- Comment HTML helpers -----------------------------------------
  function commentHtmlToPlain(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.innerText || tmp.textContent || '';
    return commHelpers.stripZeroWidth(text).replace(/\r\n/g, '\n');
  }

  function plainTextToCommentHtml(text) {
    const normalized = commHelpers.stripZeroWidth(text || '').replace(/\r\n/g, '\n');
    if (!normalized.trim()) return '';
    const escape = commHelpers.escapeHtml;
    const paragraphs = normalized
      .split(/\n{2,}/)
      .map((block) => {
        const safe = escape(block).replace(/\n/g, '<br>');
        return `<p>${safe}</p>`;
      })
      .filter(Boolean)
      .join('');
    return paragraphs || '<p></p>';
  }

  function sanitizeTicketCommentHtml(html) {
    const raw = String(html == null ? '' : html);
    if (!raw.trim()) return '';
    let doc;
    try {
      const parser = new DOMParser();
      doc = parser.parseFromString(raw, 'text/html');
    } catch (err) {
      console.warn('Failed to parse comment html', err);
      return commHelpers.escapeHtml(raw);
    }
    const allowedTags = new Set([
      'p','div','span','strong','b','em','i','u','s','a','ul','ol','li','br','hr','pre','code','blockquote',
    ]);
    const blockedTags = new Set(['script','style','base','iframe','object','embed','link','meta']);
    const urlAttrs = new Set(['href','src','background','action','formaction','srcset']);
    const isUnsafeUrl = (value) => {
      if (!value) return false;
      const trimmed = String(value).trim();
      if (!trimmed) return false;
      const lower = trimmed.toLowerCase();
      if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) return true;
      if (lower.startsWith('cid:')) return true;
      if (lower.startsWith('data:')) {
        return !(lower.startsWith('data:image/') || lower.startsWith('data:audio/') || lower.startsWith('data:video/'));
      }
      return false;
    };
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, null);
    const nodesToRemove = [];
    while (walker.nextNode()) {
      const el = walker.currentNode;
      if (!el || !el.tagName) continue;
      const tag = el.tagName.toLowerCase();
      if (blockedTags.has(tag) || !allowedTags.has(tag)) {
        nodesToRemove.push(el);
        continue;
      }
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name === 'style' || name === 'srcdoc' || name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if (urlAttrs.has(name)) {
          if (isUnsafeUrl(attr.value)) {
            el.removeAttribute(attr.name);
          }
        }
      });
    }
    nodesToRemove.forEach((node) => {
      while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
      node.remove();
    });
    return doc.body.innerHTML;
  }

  // ---- Comment list rendering ---------------------------------------
  function isCustomerFeedbackComment(comment) {
    if (!comment) return false;
    const directKind = typeof comment.comment_kind === 'string' ? comment.comment_kind : '';
    if (String(directKind).toLowerCase() === 'customer_feedback') return true;
    const aiKind = comment?.ai_validation?.comment_kind;
    return String(aiKind || '').toLowerCase() === 'customer_feedback';
  }

  function canEditComment(comment) {
    if (!comment) return false;
    const editableStates = commHelpers.getCommentEditableStates();
    const state = String(comment.review_state || '').toLowerCase();
    if (!editableStates.has(state)) return false;
    if (isCustomerFeedbackComment(comment)) return false;
    if (comment.editable_until) {
      try {
        if (new Date(comment.editable_until).getTime() < Date.now()) return false;
      } catch {}
    }
    const me = commHelpers.getCurrentUser();
    if (!me) return false;
    const authorMatches = me.id != null && Number(comment.author_user_id) === Number(me.id);
    const supervisorRoles = Array.from(commHelpers.getCommentSupervisorRoles() || []);
    return authorMatches || commHelpers.hasRole(...supervisorRoles);
  }

  function canRetryComment(comment) {
    if (!comment) return false;
    const retryStates = commHelpers.getCommentRetryStates();
    const state = String(comment.review_state || '').toLowerCase();
    if (!retryStates.has(state)) return false;
    const me = commHelpers.getCurrentUser();
    if (!me) return false;
    const authorMatches = me.id != null && Number(comment.author_user_id) === Number(me.id);
    const supervisorRoles = Array.from(commHelpers.getCommentSupervisorRoles() || []);
    return authorMatches || commHelpers.hasRole(...supervisorRoles);
  }

  function canOverrideComment(comment) {
    if (!comment) return false;
    const overrideStates = commHelpers.getCommentOverrideStates();
    const state = String(comment.review_state || '').toLowerCase();
    if (!overrideStates.has(state)) return false;
    const visibility = String(comment.visibility || '').toLowerCase();
    if (visibility !== 'customer') return false;
    const supervisorRoles = Array.from(commHelpers.getCommentSupervisorRoles() || []);
    return commHelpers.hasRole(...supervisorRoles);
  }

  function findCommentInCache(ticketId, commentId) {
    const key = String(ticketId);
    const list = ticketCommentCache.get(key) || [];
    return list.find((item) => String(item.id) === String(commentId));
  }

  function buildTicketCommentAttachmentsMarkup(attachments) {
    const escape = commHelpers.escapeHtml;
    const items = Array.isArray(attachments) ? attachments.filter((item) => item && typeof item === 'object') : [];
    if (!items.length) return '';
    const cards = items.map((item) => {
      const name = item.filename || item.name || item.key || 'attachment';
      const mime = String(item.mime_type || '').toLowerCase();
      const sizeLabel = item.size_bytes ? formatAttachmentSize(item.size_bytes) : '';
      const downloadUrl = item.download_url || item.url || item.presigned_url || item.data_url || '';
      const thumbnailUrl = item.thumbnail_url || item.preview_url || item.download_url || item.data_url || '';
      const isImage = mime.startsWith('image/');
      if (isImage && (thumbnailUrl || downloadUrl)) {
        const thumbSrc = escape(thumbnailUrl || downloadUrl);
        const href = escape(downloadUrl || thumbnailUrl);
        const caption = `${escape(name)}${sizeLabel ? `<span>${escape(sizeLabel)}</span>` : ''}`;
        return `
          <figure class="comment-attachment-card image">
            <a href="${href}" target="_blank" rel="noopener">
              <img src="${thumbSrc}" alt="${escape(name)}" loading="lazy" />
            </a>
            <figcaption>${caption}</figcaption>
          </figure>`;
      }
      const linkTarget = downloadUrl ? ` href="${escape(downloadUrl)}" target="_blank" rel="noopener"` : '';
      return `
        <div class="comment-attachment-card file">
          <a${linkTarget}>${escape(name)}</a>
          ${sizeLabel ? `<div class="meta">${escape(sizeLabel)}</div>` : ''}
        </div>`;
    }).join('');
    return `<div class="comment-attachments">${cards}</div>`;
  }

  function buildCommentFeedback(comment) {
    const escape = commHelpers.escapeHtml;
    const reviewLabels = commHelpers.getCommentReviewLabels();
    const aiValidation = comment && comment.ai_validation && typeof comment.ai_validation === 'object'
      ? comment.ai_validation : {};
    const parts = [];
    if (aiValidation.feedback_summary) {
      parts.push(`<div class="feedback-summary">${escape(aiValidation.feedback_summary)}</div>`);
    }
    if (aiValidation.suggested_response) {
      const suggestionEncoded = encodeURIComponent(aiValidation.suggested_response);
      const actions = canEditComment(comment)
        ? `<div class="suggestion-actions"><button type="button" class="btn ghost small comment-action" data-comment-action="apply-suggestion" data-comment-id="${escape(String(comment.id || ''))}" data-comment-suggestion="${suggestionEncoded}">Use suggestion</button></div>`
        : '';
      parts.push(`<div class="feedback-suggestion"><strong>Suggested response</strong><pre>${escape(aiValidation.suggested_response)}</pre>${actions}</div>`);
    }
    const checks = Array.isArray(aiValidation.checks) ? aiValidation.checks : [];
    if (checks.length) {
      const checkItems = checks.map((entry) => {
        const status = String(entry.status || 'pass').toLowerCase();
        const statusLabel = status.toUpperCase();
        const category = entry.category ? escape(entry.category) : 'Check';
        const details = entry.details ? `<div class="check-details">${escape(entry.details)}</div>` : '';
        return `<li><span class="check-status ${escape(status)}">${escape(statusLabel)}</span><span class="check-category">${category}</span>${details}</li>`;
      }).join('');
      parts.push(`<div class="feedback-checks"><strong>Checks</strong><ul>${checkItems}</ul></div>`);
    }
    if (aiValidation.error) parts.push(`<div class="feedback-error">AI error: ${escape(aiValidation.error)}</div>`);
    if (aiValidation.delivery_error) parts.push(`<div class="feedback-error">Delivery error: ${escape(aiValidation.delivery_error)}</div>`);
    const history = Array.isArray(comment.reviews) ? comment.reviews : [];
    if (history.length) {
      const historyItems = history.map((review) => {
        const statusKey = String(review.status || '').toLowerCase();
        const statusLabel = reviewLabels[statusKey] || review.status || '—';
        const reviewerType = review.reviewer_type ? String(review.reviewer_type).replace(/_/g, ' ') : 'AI';
        const reviewerRef = review.reviewer_user_id != null ? ` • User #${escape(String(review.reviewer_user_id))}` : '';
        const created = review.created_at ? ` • ${escape(commHelpers.formatDateTimeFull(review.created_at))}` : '';
        const feedback = review.feedback ? `<div class="history-feedback">${escape(review.feedback)}</div>` : '';
        return `<li><span class="history-status">${escape(statusLabel)}</span><span class="history-meta">${escape(reviewerType)}${reviewerRef}${created}</span>${feedback}</li>`;
      }).join('');
      parts.push(`<div class="feedback-history"><strong>Review history</strong><ul>${historyItems}</ul></div>`);
    }
    if (!parts.length) return '';
    return `<details class="comment-feedback"><summary>AI feedback &amp; history</summary>${parts.join('')}</details>`;
  }

  function renderTicketComments(comments = []) {
    const dom = resolveCommDom();
    const escape = commHelpers.escapeHtml;
    if (!dom.list) return;
    dom.list.innerHTML = '';
    const list = Array.isArray(comments) ? [...comments] : [];
    if (!list.length) {
      dom.list.innerHTML = '<div style="color:var(--sub);">No comments yet.</div>';
      return;
    }
    list.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    const nowMs = Date.now();
    const visibilityLabels = commHelpers.getTicketVisibilityLabels();
    const reviewLabels = commHelpers.getCommentReviewLabels();

    list.forEach((comment) => {
      const el = document.createElement('div');
      const commentId = String(comment.id || '');
      const visibility = String(comment.visibility || 'internal').toLowerCase();
      const reviewState = String(comment.review_state || 'pending').toLowerCase();
      const aiValidation = comment && typeof comment.ai_validation === 'object' ? comment.ai_validation || {} : {};
      const commentKind = String(aiValidation.comment_kind || '').toLowerCase();
      const authorLabel = comment.author_user && (comment.author_user.username || comment.author_user.email)
        ? `${comment.author_user.username || comment.author_user.email}`
        : (comment.author_user_id != null ? `Agent #${comment.author_user_id}` : 'System');
      const createdLabel = commHelpers.formatDateTimeFull(comment.created_at);
      let visibilityLabel = visibilityLabels[visibility] || visibility;
      if (visibility === 'customer') {
        if (commentKind === 'customer_feedback') visibilityLabel = 'Customer reply';
        else if (commentKind === 'customer_reply' || commentKind === 'customer') visibilityLabel = 'External';
      }
      const reviewClass = reviewState.replace(/[^a-z0-9_-]/gi, '') || 'pending';
      const isCustomerOrigin = commentKind === 'customer_feedback';
      let reviewLabel = reviewLabels[reviewState] || reviewState;
      if (isCustomerOrigin) reviewLabel = 'Sent from customer';
      const editableUntilIso = comment.editable_until;
      const editableUntilTs = editableUntilIso ? new Date(editableUntilIso).getTime() : null;
      const editWindowExpired = editableUntilTs ? editableUntilTs < nowMs : false;
      const editWindowLabel = editableUntilIso
        ? (editWindowExpired ? `Edit window expired ${commHelpers.formatDateTimeShort(editableUntilIso)}` : `Editable until ${commHelpers.formatDateTimeShort(editableUntilIso)}`)
        : '';
      const isEditing = ticketCommentEditState.commentId === commentId;
      const canEdit = canEditComment(comment);
      const canRetry = canRetryComment(comment);
      const canOverride = canOverrideComment(comment);
      const attachmentsMarkup = buildTicketCommentAttachmentsMarkup(comment.attachments);

      const actionButtons = [];
      if (!isEditing && canEdit) actionButtons.push(`<button type="button" class="btn ghost small comment-action" data-comment-action="edit" data-comment-id="${escape(commentId)}">Edit &amp; Resubmit</button>`);
      if (!isEditing && canRetry) actionButtons.push(`<button type="button" class="btn ghost small comment-action" data-comment-action="retry" data-comment-id="${escape(commentId)}">AI review</button>`);
      if (!isEditing && canOverride) actionButtons.push(`<button type="button" class="btn warn small comment-action" data-comment-action="override" data-comment-id="${escape(commentId)}">Override &amp; Send</button>`);

      let actionsMarkup = '';
      if (isEditing) {
        actionsMarkup = `
          <div class="edit-actions">
            <button type="button" class="btn primary small comment-action" data-comment-action="save" data-comment-id="${escape(commentId)}">Save changes</button>
            <button type="button" class="btn ghost small comment-action" data-comment-action="cancel-edit" data-comment-id="${escape(commentId)}">Cancel</button>
          </div>`;
      } else if (actionButtons.length) {
        actionsMarkup = `<div class="comment-actions">${actionButtons.join('')}</div>`;
      }

      const editDraft = isEditing
        ? (ticketCommentEditState.draft != null ? ticketCommentEditState.draft : commentHtmlToPlain(comment.body || ''))
        : '';
      const bodyMarkup = isEditing
        ? `<div class="body editing"><textarea data-comment-edit-input="${escape(commentId)}" placeholder="Update comment">${escape(editDraft)}</textarea></div>`
        : `<div class="body">${sanitizeTicketCommentHtml(comment.body || '')}</div>`;

      const editWindowMarkup = editWindowLabel
        ? `<span class="editable-window ${editWindowExpired ? 'expired' : ''}">${escape(editWindowLabel)}</span>`
        : '';

      const overrideJustification = aiValidation.override_justification || aiValidation.justification || '';
      const overrideByRaw = (Object.prototype.hasOwnProperty.call(aiValidation, 'override_by') ? aiValidation.override_by : aiValidation.overwritten_by);
      const overrideAtIso = aiValidation.override_at || aiValidation.overwritten_at;
      let overrideMarkup = '';
      if (overrideJustification) {
        const overrideMetaParts = [];
        if (overrideByRaw !== undefined && overrideByRaw !== null && `${overrideByRaw}`.trim() !== '') {
          overrideMetaParts.push(`User #${overrideByRaw}`);
        }
        if (overrideAtIso) {
          let overrideAtLabel = '';
          try { overrideAtLabel = commHelpers.formatDateTimeShort(overrideAtIso); }
          catch { overrideAtLabel = String(overrideAtIso); }
          if (overrideAtLabel) overrideMetaParts.push(overrideAtLabel);
        }
        const metaHtml = overrideMetaParts.length
          ? `<span class="meta">${overrideMetaParts.map((part) => escape(String(part))).join(' • ')}</span>`
          : '';
        overrideMarkup = `
          <div class="comment-override-note">
            <div class="note-header"><strong>Supervisor override</strong>${metaHtml}</div>
            <div class="note-body">${escape(overrideJustification)}</div>
          </div>`;
      }

      const feedbackMarkup = buildCommentFeedback(comment);

      el.className = `ticket-comment ${visibility}`;
      el.dataset.commentId = commentId;
      el.innerHTML = `
        <div class="meta">
          <div class="meta-group">
            <span class="visibility">${escape(visibilityLabel)}</span>
            <span class="review-state ${escape(reviewClass)}">${escape(reviewLabel)}</span>
            ${editWindowMarkup}
          </div>
          <div class="meta-group">
            <span>${escape(authorLabel)}</span>
            <span>${escape(createdLabel)}</span>
          </div>
        </div>
        ${bodyMarkup}
        ${overrideMarkup}
        ${attachmentsMarkup}
        ${actionsMarkup}
        ${feedbackMarkup}`;
      dom.list.appendChild(el);
    });
  }

  function updateTicketCommentsStatus(count, error = null) {
    const dom = resolveCommDom();
    if (!dom.status) return;
    if (error) {
      dom.status.textContent = error && error.status === 404 ? 'Comments unavailable' : 'Failed to load comments';
      return;
    }
    if (typeof count !== 'number') {
      dom.status.textContent = '';
      return;
    }
    dom.status.textContent = `${count} comment${count === 1 ? '' : 's'}`;
  }

  function renderTicketCommentsForTicket(ticketId) {
    const dom = resolveCommDom();
    const key = String(ticketId);
    const comments = ticketCommentCache.get(key) || [];
    renderTicketComments(comments);
    updateTicketCommentsStatus(comments.length);
    if (dom.expandDlg && dom.expandDlg.open) {
      refreshTicketCommentExpandThread();
    }
  }

  // ---- Attachments --------------------------------------------------
  function generateAttachmentId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  function formatAttachmentSize(bytes) {
    const value = Number(bytes) || 0;
    if (value >= 1024 * 1024) {
      const mb = (value / (1024 * 1024)).toFixed(1);
      return `${mb} MB`;
    }
    const kb = Math.max(1, Math.round(value / 1024));
    return `${kb} KB`;
  }

  function isImageMimeType(mime) {
    return typeof mime === 'string' && mime.toLowerCase().startsWith('image/');
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : String(reader.result || '');
        resolve(result);
      };
      reader.onerror = () => {
        const err = new Error('read_failed');
        err.code = 'read_failed';
        reject(err);
      };
      reader.readAsDataURL(file);
    });
  }

  function getImageDimensions(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => {
        const err = new Error('decode_failed');
        err.code = 'decode_failed';
        reject(err);
      };
      img.src = dataUrl;
    });
  }

  async function buildAttachmentFromFile(file, options = {}) {
    if (!file) {
      const err = new Error('no_file');
      err.code = 'no_file';
      throw err;
    }
    if (!isImageMimeType(file.type)) {
      const err = new Error('unsupported_type');
      err.code = 'unsupported_type';
      throw err;
    }
    if (file.size > COMMENT_ATTACHMENT_MAX_BYTES) {
      const err = new Error('too_large');
      err.code = 'too_large';
      err.size = file.size;
      throw err;
    }
    const dataUrl = await readFileAsDataUrl(file);
    const { width, height } = await getImageDimensions(dataUrl);
    const sanitizedName = (file.name || 'image').trim() || `image-${Date.now()}`;
    return {
      id: options.existingId || generateAttachmentId(),
      filename: sanitizedName,
      mime_type: file.type || 'image/jpeg',
      size_bytes: file.size,
      data_url: dataUrl,
      width: width || null,
      height: height || null,
      is_image: true,
    };
  }

  function handleAttachmentError(err) {
    if (!err) return;
    if (err.name === 'AbortError') return;
    const code = err.code || err.message;
    if (code === 'unsupported_type') {
      notify.showFlash('Only image files are supported for ticket comments right now', 'error');
      return;
    }
    if (code === 'too_large') {
      notify.showFlash('Images must be 2 MB or smaller before upload', 'error');
      return;
    }
    if (code === 'read_failed') {
      notify.showFlash('Failed to read the selected image', 'error');
      return;
    }
    if (code === 'decode_failed') {
      notify.showFlash('Could not process the selected image', 'error');
      return;
    }
    if (code === 'attachment_limit') {
      notify.showFlash(`You can attach up to ${COMMENT_ATTACHMENT_LIMIT} images per comment`, 'error');
      return;
    }
    console.warn('Attachment error', err);
    notify.showFlash('Unable to process the selected image', 'error');
  }

  function renderAttachmentItem(item, index = 0) {
    const escape = commHelpers.escapeHtml;
    const id = escape(String(item.id || index));
    const name = escape(item.filename || `image-${index + 1}`);
    const sizeLabel = escape(formatAttachmentSize(item.size_bytes));
    const dataUrl = typeof item.data_url === 'string' ? escape(item.data_url) : '';
    const thumb = dataUrl
      ? `<img src="${dataUrl}" alt="${name}" loading="lazy" />`
      : `<div class="placeholder">${name}</div>`;
    return `
      <div class="comment-attachment-item" data-attachment-id="${id}">
        <div class="comment-attachment-thumb">${thumb}</div>
        <div class="comment-attachment-actions">
          <button type="button" class="btn ghost small icon-only" data-attachment-replace="${id}" title="Replace image" aria-label="Replace image"><span aria-hidden="true">↺</span></button>
          <button type="button" class="btn ghost small icon-only" data-attachment-remove="${id}" title="Remove image" aria-label="Remove image"><span aria-hidden="true">✕</span></button>
        </div>
        <div class="comment-attachment-meta" title="${name}">${name}<br><span>${sizeLabel}</span></div>
      </div>`;
  }

  function renderTicketCommentAttachments() {
    const dom = resolveCommDom();
    const items = ticketCommentAttachmentsState.items;
    const limitReached = items.length >= COMMENT_ATTACHMENT_LIMIT;
    const markup = items.map((item, idx) => renderAttachmentItem(item, idx)).join('');
    const emptyMarkup = '<div class="comment-attachment-empty">No images attached.</div>';
    if (dom.attachmentList) dom.attachmentList.innerHTML = items.length ? markup : emptyMarkup;
    if (dom.attachmentModalList) dom.attachmentModalList.innerHTML = items.length ? markup : emptyMarkup;
    if (dom.attachmentAddBtn) {
      dom.attachmentAddBtn.disabled = limitReached;
      dom.attachmentAddBtn.title = limitReached ? 'Attachment limit reached' : '';
    }
    if (dom.attachmentModalAddBtn) {
      dom.attachmentModalAddBtn.disabled = limitReached;
      dom.attachmentModalAddBtn.title = limitReached ? 'Attachment limit reached' : '';
    }
  }

  function resetTicketCommentAttachments() {
    if (ticketCommentAttachmentsState.items.length === 0) {
      renderTicketCommentAttachments();
      return;
    }
    ticketCommentAttachmentsState.items = [];
    renderTicketCommentAttachments();
  }

  function openTicketAttachmentPicker(options = {}) {
    const dom = resolveCommDom();
    if (!dom.attachmentInput) return;
    const { replaceId = null } = options;
    dom.attachmentInput.value = '';
    if (replaceId) {
      dom.attachmentInput.dataset.mode = 'replace';
      dom.attachmentInput.dataset.replaceId = String(replaceId);
    } else {
      dom.attachmentInput.dataset.mode = 'add';
      delete dom.attachmentInput.dataset.replaceId;
    }
    dom.attachmentInput.click();
  }

  function removeTicketCommentAttachment(attachmentId) {
    const before = ticketCommentAttachmentsState.items.length;
    ticketCommentAttachmentsState.items = ticketCommentAttachmentsState.items.filter((item) => String(item.id) !== String(attachmentId));
    if (ticketCommentAttachmentsState.items.length !== before) {
      renderTicketCommentAttachments();
    }
  }

  function handleTicketAttachmentListClick(event) {
    const replaceBtn = event.target.closest('button[data-attachment-replace]');
    if (replaceBtn && replaceBtn.dataset.attachmentReplace) {
      openTicketAttachmentPicker({ replaceId: replaceBtn.dataset.attachmentReplace });
      return;
    }
    const removeBtn = event.target.closest('button[data-attachment-remove]');
    if (removeBtn && removeBtn.dataset.attachmentRemove) {
      removeTicketCommentAttachment(removeBtn.dataset.attachmentRemove);
    }
  }

  async function addTicketCommentAttachments(files) {
    if (!Array.isArray(files) || !files.length) return;
    for (const file of files) {
      if (ticketCommentAttachmentsState.items.length >= COMMENT_ATTACHMENT_LIMIT) {
        notify.showFlash(`You can attach up to ${COMMENT_ATTACHMENT_LIMIT} images per comment`, 'error');
        break;
      }
      try {
        const attachment = await buildAttachmentFromFile(file);
        ticketCommentAttachmentsState.items.push(attachment);
      } catch (err) {
        handleAttachmentError(err);
      }
    }
    renderTicketCommentAttachments();
  }

  async function replaceTicketCommentAttachment(attachmentId, file) {
    if (!attachmentId || !file) return;
    const idx = ticketCommentAttachmentsState.items.findIndex((item) => String(item.id) === String(attachmentId));
    if (idx === -1) {
      notify.showFlash('Attachment no longer available to replace', 'error');
      return;
    }
    try {
      const replacement = await buildAttachmentFromFile(file, { existingId: ticketCommentAttachmentsState.items[idx].id });
      ticketCommentAttachmentsState.items[idx] = replacement;
      renderTicketCommentAttachments();
    } catch (err) {
      handleAttachmentError(err);
    }
  }

  async function handleTicketAttachmentInputChange(event) {
    const input = event.currentTarget;
    if (!input) return;
    const files = Array.from(input.files || []);
    input.value = '';
    const mode = input.dataset.mode || 'add';
    const replaceId = input.dataset.replaceId || null;
    delete input.dataset.mode;
    delete input.dataset.replaceId;
    if (!files.length) return;
    try {
      if (mode === 'replace' && replaceId) {
        await replaceTicketCommentAttachment(replaceId, files[0]);
      } else {
        await addTicketCommentAttachments(files);
      }
    } catch (err) {
      handleAttachmentError(err);
    }
  }

  // ---- Expand dialog ------------------------------------------------
  function ensureTicketCommentExpandEditor() {
    const dom = resolveCommDom();
    if (ticketCommentExpandQuill || !commHelpers.hasQuill()) return ticketCommentExpandQuill;
    if (!dom.expandEditorEl) return null;
    try {
      const Quill = commHelpers.QuillCtor || (typeof window !== 'undefined' ? window.Quill : null);
      if (Quill) {
        ticketCommentExpandQuill = new Quill(dom.expandEditorEl, commHelpers.getCommentEditorOptions());
      }
    } catch (err) {
      console.error('Failed to initialise expanded comment editor', err);
      ticketCommentExpandQuill = null;
    }
    return ticketCommentExpandQuill;
  }

  function refreshTicketCommentExpandThread() {
    const dom = resolveCommDom();
    if (!dom.expandThread) return;
    if (!dom.list) {
      dom.expandThread.innerHTML = '<div class="comment-expand-empty">Select a ticket to review its comment history.</div>';
      return;
    }
    const content = dom.list.innerHTML || '';
    if (!content.trim()) {
      dom.expandThread.innerHTML = '<div class="comment-expand-empty">No comments yet.</div>';
      return;
    }
    dom.expandThread.innerHTML = content;
  }

  function openTicketCommentExpandDialog() {
    const dom = resolveCommDom();
    if (!dom.expandDlg) return;
    const ticket = commHelpers.getSelectedTicket();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    const editor = ensureTicketCommentExpandEditor();
    if (!editor) {
      notify.showFlash('Comment editor is not available', 'error');
      return;
    }
    const currentHtml = ticketCommentQuill ? commHelpers.getQuillHtml(ticketCommentQuill) : (dom.body ? dom.body.value : '');
    commHelpers.setQuillHtml(editor, currentHtml);
    if (dom.expandVisibility && dom.visibility) {
      dom.expandVisibility.value = dom.visibility.value || 'internal';
    }
    if (dom.expandVisibility && dom.expandVisibility.value === 'customer') {
      commHelpers.ensureQuillGreeting(editor, commHelpers.getTicketCustomerName(ticket));
      commHelpers.dedupeQuillGreeting(editor, commHelpers.getTicketCustomerName(ticket));
    }
    renderTicketCommentAttachments();
    refreshTicketCommentExpandThread();
    try {
      const interactions = views.tickets && views.tickets.detailInteractions;
      if (typeof window !== 'undefined' && typeof window.openDialog === 'function') {
        window.openDialog(dom.expandDlg);
      } else {
        try { dom.expandDlg.showModal(); }
        catch { dom.expandDlg.open = true; }
      }
      setTimeout(() => editor.focus(), 60);
    } catch (err) {
      console.error('Failed to open comment workspace dialog', err);
    }
  }

  function closeTicketCommentExpandDialog(options = {}) {
    const dom = resolveCommDom();
    if (!dom.expandDlg || !dom.expandDlg.open) return;
    const { syncEditor = true } = options;
    if (syncEditor && ticketCommentExpandQuill) {
      const html = commHelpers.getQuillHtml(ticketCommentExpandQuill);
      if (ticketCommentQuill) {
        commHelpers.setQuillHtml(ticketCommentQuill, html);
      } else if (dom.body) {
        dom.body.value = html || '';
      }
      commHelpers.updateInlineComposerStorage(html);
      if (dom.expandVisibility && dom.visibility) {
        dom.visibility.value = dom.expandVisibility.value || 'internal';
      }
    }
    try { dom.expandDlg.close(); }
    catch { dom.expandDlg.open = false; }
    if (syncEditor && dom.visibility) {
      handleTicketCommentVisibilityChange(dom.visibility.value);
    }
  }

  // ---- Comment actions / submit / edit / retry / override -----------
  async function loadTicketComments(ticketId) {
    const dom = resolveCommDom();
    const escape = commHelpers.escapeHtml;
    if (!dom.list) return;
    const cacheKey = String(ticketId);
    try {
      commHelpers.clearTicketAttention(cacheKey);
      const comments = await api.fetch(api.routes.adminTicketComments(ticketId), { method: 'GET' });
      const items = Array.isArray(comments) ? comments : [];
      ticketCommentCache.set(cacheKey, items);
      ticketCommentPendingRefresh.delete(cacheKey);
      if (ticketCommentEditState.commentId && !items.some((item) => String(item.id) === ticketCommentEditState.commentId)) {
        ticketCommentEditState = { commentId: null, draft: '', skipReview: false };
      }
      renderTicketComments(items);
      updateTicketCommentsStatus(items.length);
      if (dom.expandDlg && dom.expandDlg.open) {
        refreshTicketCommentExpandThread();
      }
      maybeAutoUpdateStatusFromComments(ticketId, items).catch(() => {});
    } catch (err) {
      ticketCommentCache.delete(cacheKey);
      dom.list.innerHTML = `<div style="color:var(--sub);">${escape(err.message || 'Failed to load comments')}</div>`;
      updateTicketCommentsStatus(null, err);
    }
  }

  async function maybeAutoUpdateStatusFromComments(ticketId, comments) {
    if (!Array.isArray(comments) || !comments.length) return;
    if (autoStatusUpdateInFlight.has(ticketId)) return;
    const ticket = commHelpers.findTicketById(ticketId);
    if (!ticket) return;
    const currentStatus = String(ticket.status || '').toLowerCase();
    if (currentStatus !== 'waiting_customer') return;

    const latestCustomer = [...comments].reverse().find((comment) => {
      const visibility = String(comment.visibility || '').toLowerCase();
      if (visibility !== 'customer') return false;
      const originTextRaw = comment && comment.ai_validation && comment.ai_validation.origin;
      const originText = originTextRaw ? String(originTextRaw).toLowerCase() : '';
      const looksCustomerOrigin = originText.startsWith('customer') || originText.includes('customer_reply') || originText.includes('customer_response');
      const hasAuthorDetails = Boolean(comment && comment.author_user && (comment.author_user.username || comment.author_user.email));
      const hasAuthorId = comment && comment.author_user_id != null && comment.author_user_id !== 0;
      if (looksCustomerOrigin) return true;
      if (!hasAuthorDetails && !hasAuthorId) return true;
      return false;
    });
    if (!latestCustomer) return;

    autoStatusUpdateInFlight.add(ticketId);
    try {
      const updated = await commHelpers.mutateTicket(ticketId, { status: 'in_progress' });
      notify.showFlash('Customer responded – ticket marked in progress');
      await commHelpers.reloadTickets({ keepSelection: true, selectId: updated.id });
      commHelpers.refreshUnassignedSnapshot();
    } catch (err) {
      console.warn('Failed to auto-update ticket to in_progress', err);
    } finally {
      autoStatusUpdateInFlight.delete(ticketId);
    }
  }

  function handleTicketCommentClick(event) {
    const button = event.target.closest('button[data-comment-action]');
    if (!button) return;
    event.preventDefault();
    const action = button.dataset.commentAction;
    const commentId = button.dataset.commentId;
    if (!action || !commentId) return;
    if (action === 'edit') startTicketCommentEdit(commentId);
    else if (action === 'cancel-edit') cancelTicketCommentEdit();
    else if (action === 'save') saveTicketCommentEdit(commentId, button);
    else if (action === 'retry') retryTicketComment(commentId, button);
    else if (action === 'override') overrideTicketComment(commentId, button);
    else if (action === 'apply-suggestion') applyCommentSuggestion(commentId, button);
  }

  function handleTicketCommentInput(event) {
    const textarea = event.target.closest('textarea[data-comment-edit-input]');
    if (!textarea) return;
    const commentId = textarea.dataset.commentEditInput;
    if (!commentId || ticketCommentEditState.commentId !== commentId) return;
    ticketCommentEditState.draft = textarea.value;
  }

  function handleTicketCommentVisibilityChange(eventOrValue) {
    const dom = resolveCommDom();
    const visibility = typeof eventOrValue === 'string'
      ? eventOrValue
      : eventOrValue && eventOrValue.target ? eventOrValue.target.value : (dom.visibility ? dom.visibility.value : 'internal');
    const ticket = commHelpers.getSelectedTicket();
    const customerName = commHelpers.getTicketCustomerName(ticket);
    if (ticketCommentQuill) {
      if (visibility === 'customer' && customerName) {
        commHelpers.ensureQuillGreeting(ticketCommentQuill, customerName);
        commHelpers.dedupeQuillGreeting(ticketCommentQuill, customerName);
      } else if (visibility !== 'customer' && customerName) {
        commHelpers.stripQuillGreetingIfOnly(ticketCommentQuill, customerName);
      }
      commHelpers.updateInlineComposerStorage();
    } else if (dom.body) {
      if (visibility === 'customer' && customerName) {
        dom.body.value = commHelpers.dedupeGreetingText(
          commHelpers.ensureCustomerGreeting(dom.body.value, customerName),
          customerName,
        );
      } else if (visibility !== 'customer' && customerName) {
        const greetingOnly = new RegExp(`^\\s*${commHelpers.escapeRegExp(`Dear ${customerName},`)}\\s*$`, 'i');
        if (greetingOnly.test(dom.body.value || '')) {
          dom.body.value = '';
        }
      }
    }
  }

  function handleTicketCommentExpandVisibilityChange(eventOrValue) {
    const dom = resolveCommDom();
    const visibility = typeof eventOrValue === 'string'
      ? eventOrValue
      : eventOrValue && eventOrValue.target ? eventOrValue.target.value : (dom.expandVisibility ? dom.expandVisibility.value : 'internal');
    const ticket = commHelpers.getSelectedTicket();
    const customerName = commHelpers.getTicketCustomerName(ticket);
    const editor = ensureTicketCommentExpandEditor();
    if (!editor) return;
    if (visibility === 'customer' && customerName) {
      commHelpers.ensureQuillGreeting(editor, customerName);
      commHelpers.dedupeQuillGreeting(editor, customerName);
    } else if (visibility !== 'customer' && customerName) {
      commHelpers.stripQuillGreetingIfOnly(editor, customerName);
    }
  }

  async function submitTicketComment({ source = 'inline' } = {}) {
    const dom = resolveCommDom();
    const usingModal = source === 'modal';
    const ticket = commHelpers.getSelectedTicket();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    let editor = ticketCommentQuill;
    if (usingModal) editor = ensureTicketCommentExpandEditor() || ticketCommentQuill;
    const visibilitySelect = usingModal ? dom.expandVisibility : dom.visibility;
    const visibility = visibilitySelect ? visibilitySelect.value || 'internal' : 'internal';
    const customerName = commHelpers.getTicketCustomerName(ticket);
    if (editor) {
      if (visibility === 'customer' && customerName) {
        commHelpers.ensureQuillGreeting(editor, customerName);
        commHelpers.dedupeQuillGreeting(editor, customerName);
      } else if (visibility !== 'customer' && customerName) {
        commHelpers.stripQuillGreetingIfOnly(editor, customerName);
      }
    } else if (dom.body) {
      if (visibility === 'customer' && customerName) {
        dom.body.value = commHelpers.dedupeGreetingText(
          commHelpers.ensureCustomerGreeting(dom.body.value, customerName),
          customerName,
        );
      } else if (visibility !== 'customer' && customerName) {
        const greetingOnly = new RegExp(`^\\s*${commHelpers.escapeRegExp(`Dear ${customerName},`)}\\s*$`, 'i');
        if (greetingOnly.test(dom.body.value || '')) {
          dom.body.value = '';
        }
      }
    }
    const plainText = editor ? (editor.getText() || '').trim() : (dom.body ? dom.body.value.trim() : '');
    if (!plainText) {
      notify.showFlash('Comment cannot be empty', 'error');
      return;
    }
    const bodyContent = editor ? commHelpers.getQuillHtml(editor) : (dom.body ? dom.body.value.trim() : '');
    if (!bodyContent) {
      notify.showFlash('Comment cannot be empty', 'error');
      return;
    }
    const attachmentsPayload = ticketCommentAttachmentsState.items.map((item) => ({
      id: item.id,
      filename: item.filename,
      mime_type: item.mime_type,
      size_bytes: item.size_bytes,
      data_url: item.data_url,
      width: item.width,
      height: item.height,
      is_image: true,
    }));
    const payload = { body: bodyContent, visibility };
    if (attachmentsPayload.length) payload.attachments = attachmentsPayload;
    const submitBtn = usingModal ? dom.expandSubmit : dom.submitBtn;
    let previousText;
    if (submitBtn) {
      previousText = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Posting…';
    }
    let flashMessage = 'Comment added';
    try {
      await api.fetch(api.routes.adminTicketComments(ticket.id), { method: 'POST', body: JSON.stringify(payload) });
      if (visibility === 'customer') flashMessage = 'Customer reply queued for delivery';
      notify.showFlash(flashMessage);
      if (ticketCommentQuill) ticketCommentQuill.setText('');
      if (dom.body) dom.body.value = '';
      if (dom.visibility) dom.visibility.value = 'internal';
      if (dom.expandVisibility) dom.expandVisibility.value = 'internal';
      if (usingModal && ticketCommentExpandQuill) ticketCommentExpandQuill.setText('');
      resetTicketCommentAttachments();
      commHelpers.updateInlineComposerStorage('');
      if (usingModal) closeTicketCommentExpandDialog({ syncEditor: false });
      await loadTicketComments(ticket.id);
      if (ticketCommentQuill && !usingModal) ticketCommentQuill.focus();
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to add comment', 'error');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = previousText || 'Post comment';
      }
    }
  }

  function startTicketCommentEdit(commentId) {
    const ticketId = commHelpers.getSelectedTicketIdValue();
    if (!ticketId) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    const comment = findCommentInCache(ticketId, commentId);
    if (!comment) {
      notify.showFlash('Comment not found', 'error');
      return;
    }
    if (!canEditComment(comment)) {
      notify.showFlash('You cannot edit this comment', 'error');
      return;
    }
    ticketCommentEditState = {
      commentId: String(commentId),
      draft: commentHtmlToPlain(comment.body || ''),
      skipReview: false,
    };
    renderTicketCommentsForTicket(ticketId);
  }

  function cancelTicketCommentEdit() {
    if (!ticketCommentEditState.commentId) return;
    const ticketId = commHelpers.getSelectedTicketIdValue();
    ticketCommentEditState = { commentId: null, draft: '', skipReview: false };
    if (ticketId) {
      if (ticketCommentPendingRefresh.has(ticketId)) {
        ticketCommentPendingRefresh.delete(ticketId);
        loadTicketComments(ticketId);
      } else {
        renderTicketCommentsForTicket(ticketId);
      }
    }
  }

  async function saveTicketCommentEdit(commentId, button) {
    const ticket = commHelpers.getSelectedTicket();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    if (ticketCommentEditState.commentId !== String(commentId)) {
      notify.showFlash('Comment edit state lost, please retry', 'error');
      return;
    }
    let draft = (ticketCommentEditState.draft || '').trim();
    if (!draft) {
      notify.showFlash('Comment cannot be empty', 'error');
      return;
    }
    const cached = findCommentInCache(ticket.id, commentId);
    if (cached && cached.visibility === 'customer') {
      const customerName = commHelpers.getTicketCustomerName(ticket);
      if (customerName) {
        draft = commHelpers.dedupeGreetingText(
          commHelpers.ensureCustomerGreeting(draft, customerName),
          customerName,
        );
      }
    }
    ticketCommentEditState.draft = draft;
    let restoreText;
    if (button) {
      restoreText = button.textContent;
      button.textContent = 'Saving…';
      button.disabled = true;
    }
    try {
      const draftHtml = plainTextToCommentHtml(draft);
      const payload = { body: draftHtml };
      if (ticketCommentEditState.skipReview && cached && String(cached.visibility || '').toLowerCase() === 'internal') {
        payload.bypass_ai_review = true;
      }
      await api.fetch(api.routes.adminTicketComment(ticket.id, commentId), {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      notify.showFlash('Comment updated');
      ticketCommentEditState = { commentId: null, draft: '', skipReview: false };
      await loadTicketComments(ticket.id);
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to update comment', 'error');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = restoreText || 'Save changes';
      }
    }
  }

  async function retryTicketComment(commentId, button) {
    const ticket = commHelpers.getSelectedTicket();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    let restoreText;
    if (button) {
      restoreText = button.textContent;
      button.textContent = 'Retrying…';
      button.disabled = true;
    }
    try {
      await api.fetch(api.routes.adminTicketCommentRetry(ticket.id, commentId), { method: 'POST' });
      notify.showFlash('AI review retry requested');
      await loadTicketComments(ticket.id);
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to retry AI review', 'error');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = restoreText || 'AI review';
      }
    }
  }

  async function overrideTicketComment(commentId, button) {
    const ticket = commHelpers.getSelectedTicket();
    if (!ticket) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    const justification = window.prompt('Provide justification for overriding the AI decision:');
    if (!justification || !justification.trim()) {
      notify.showFlash('Override cancelled - justification required.', 'error');
      return;
    }
    let restoreText;
    if (button) {
      restoreText = button.textContent;
      button.textContent = 'Overriding…';
      button.disabled = true;
    }
    try {
      await api.fetch(api.routes.adminTicketCommentOverride(ticket.id, commentId), {
        method: 'POST',
        body: JSON.stringify({ justification: justification.trim() }),
      });
      notify.showFlash('Comment override recorded');
      ticketCommentEditState = { commentId: null, draft: '', skipReview: false };
      await loadTicketComments(ticket.id);
    } catch (err) {
      notify.showFlash(err?.message || 'Failed to override comment', 'error');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = restoreText || 'Override & Send';
      }
    }
  }

  function applyCommentSuggestion(commentId, button) {
    const ticketId = commHelpers.getSelectedTicketIdValue();
    if (!ticketId) {
      notify.showFlash('Select a ticket first', 'error');
      return;
    }
    const encoded = button.dataset.commentSuggestion || '';
    const suggestion = encoded ? decodeURIComponent(encoded) : '';
    if (!suggestion) {
      notify.showFlash('No suggestion available', 'error');
      return;
    }
    if (ticketCommentEditState.commentId !== String(commentId)) {
      startTicketCommentEdit(commentId);
    }
    const cached = findCommentInCache(ticketId, commentId);
    const visibility = cached && cached.visibility ? String(cached.visibility).toLowerCase() : '';
    const shouldBypass = visibility === 'internal';
    ticketCommentEditState = {
      commentId: String(commentId),
      draft: suggestion,
      skipReview: shouldBypass,
    };
    renderTicketCommentsForTicket(ticketId);
    notify.showFlash('AI suggestion applied. Review and save the comment.', 'success', 3500);
  }

  function mountComments() {
    if (commMounted) return;
    const dom = resolveCommDom();
    // Initialise Quill on the inline editor if available.
    if (commHelpers.hasQuill() && dom.editorEl) {
      try {
        const Quill = commHelpers.QuillCtor || (typeof window !== 'undefined' ? window.Quill : null);
        if (Quill) {
          ticketCommentQuill = new Quill(dom.editorEl, commHelpers.getCommentEditorOptions());
          ticketCommentQuill.on('text-change', () => commHelpers.updateInlineComposerStorage());
          commHelpers.updateInlineComposerStorage();
        }
      } catch (err) {
        console.error('Failed to initialise comment editor', err);
      }
    } else if (dom.body) {
      dom.body.hidden = false;
      dom.body.style.display = 'block';
    }
    if (!ticketCommentQuill && dom.expandBtn) {
      dom.expandBtn.style.display = 'none';
    }
    if (dom.list) {
      dom.list.addEventListener('click', handleTicketCommentClick);
      dom.list.addEventListener('input', handleTicketCommentInput, true);
    }
    if (dom.visibility) {
      dom.visibility.addEventListener('change', handleTicketCommentVisibilityChange);
    }
    if (dom.expandVisibility) {
      dom.expandVisibility.addEventListener('change', handleTicketCommentExpandVisibilityChange);
    }
    if (dom.expandBtn) {
      dom.expandBtn.addEventListener('click', openTicketCommentExpandDialog);
    }
    if (dom.expandClose) {
      dom.expandClose.addEventListener('click', () => closeTicketCommentExpandDialog());
    }
    if (dom.expandCancel) {
      dom.expandCancel.addEventListener('click', () => closeTicketCommentExpandDialog());
    }
    if (dom.expandDlg) {
      dom.expandDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeTicketCommentExpandDialog();
      });
    }
    if (dom.expandThread) {
      dom.expandThread.addEventListener('click', handleTicketCommentClick);
      dom.expandThread.addEventListener('input', handleTicketCommentInput, true);
    }
    if (dom.form) {
      dom.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await submitTicketComment({ source: 'inline' });
      });
    }
    if (dom.expandForm) {
      dom.expandForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await submitTicketComment({ source: 'modal' });
      });
    }
    if (dom.attachmentAddBtn) {
      dom.attachmentAddBtn.addEventListener('click', () => openTicketAttachmentPicker());
    }
    if (dom.attachmentModalAddBtn) {
      dom.attachmentModalAddBtn.addEventListener('click', () => openTicketAttachmentPicker());
    }
    if (dom.attachmentList) {
      dom.attachmentList.addEventListener('click', handleTicketAttachmentListClick);
    }
    if (dom.attachmentModalList) {
      dom.attachmentModalList.addEventListener('click', handleTicketAttachmentListClick);
    }
    if (dom.attachmentInput) {
      dom.attachmentInput.addEventListener('change', handleTicketAttachmentInputChange);
    }
    renderTicketCommentAttachments();
    commMounted = true;
  }

  // ---- Tech closure review (Phase 2.1.e) -----------------------------
  // Owns the supervisor's tech-closure review dialog (`#techReviewDlg`),
  // opened from the technician board's "Review" action on a ticket
  // resolved by a technician. Surface:
  //   - Pre-fill the dialog from the ticket's `meta.tech_closure_submission`
  //     (problem summary, resolution steps, GPS) and `meta.tech_closure_ai`
  //     (score/summary/flags/recommendations).
  //   - "Re-run AI feedback" button that invokes the AI scoring endpoint.
  //   - Customer-care agent picker (loaded from the active-roster endpoint).
  //   - Approve/Return decision flow that PATCHes the ticket and either
  //     transfers to the chosen agent or returns to the technician for
  //     rework.
  //   - Loads the closure-comment attachments (`tech_closure` kind) into
  //     the photo strip via the detailComments module's helper.
  //
  // What stays in admin.js (injected):
  //   - `openDialog` (the generic dialog opener).
  //   - `reloadTickets` and the technician-board's `markChanged()` —
  //     called after a decision lands.
  //   - Date formatters (`formatRelativeTime`, `formatDateTimeShort`).
  //   - The detailComments module's `buildAttachmentsMarkup` — used by
  //     the photo strip.
  //
  // Sized: ~310 lines moved.

  let trDom = null;
  let trMounted = false;
  const techReviewState = {
    ticket: null,
    closureComment: null,
    submitting: false,
    agents: [],
    agentsLoading: false,
  };

  // Phase 3.2.c: techReview takes its helpers via direct imports above.
  // `reloadTickets` reaches into the list sub-view's refresh; both live
  // in this same file (`views.tickets.list.refresh`).
  const trHelpers = {
    openDialog,
    reloadTickets: (opts) => {
      const list = views.tickets && views.tickets.list;
      return list ? list.refresh(opts) : Promise.resolve();
    },
    formatRelativeTime,
    formatDateTimeShort,
  };

  function resolveTrDom() {
    if (trDom) return trDom;
    trDom = {
      dlg: document.getElementById('techReviewDlg'),
      form: document.getElementById('techReviewForm'),
      close: document.getElementById('techReviewClose'),
      problem: document.getElementById('techReviewProblem'),
      steps: document.getElementById('techReviewSteps'),
      gps: document.getElementById('techReviewGps'),
      photos: document.getElementById('techReviewPhotos'),
      aiScore: document.getElementById('techReviewAiScore'),
      aiSummary: document.getElementById('techReviewAiSummary'),
      aiFlags: document.getElementById('techReviewAiFlags'),
      aiRecommendations: document.getElementById('techReviewAiRecommendations'),
      aiRetryBtn: document.getElementById('techReviewAiRetryBtn'),
      note: document.getElementById('techReviewNote'),
      status: document.getElementById('techReviewStatus'),
      returnBtn: document.getElementById('techReviewReturnBtn'),
      approveBtn: document.getElementById('techReviewApproveBtn'),
      agentSelect: document.getElementById('techReviewAgentSelect'),
    };
    return trDom;
  }

  function renderTechReviewAiMeta(aiMeta) {
    const dom = resolveTrDom();
    if (dom.aiScore) {
      dom.aiScore.textContent = aiMeta.score != null ? `Score ${Math.round(Number(aiMeta.score))}` : 'Score —';
    }
    if (dom.aiSummary) {
      dom.aiSummary.textContent = aiMeta.summary || 'No AI feedback available.';
    }
    if (dom.aiFlags) {
      dom.aiFlags.innerHTML = '';
      const flags = Array.isArray(aiMeta.flags) ? aiMeta.flags : [];
      flags.forEach((flag) => {
        const li = document.createElement('li');
        li.textContent = flag;
        dom.aiFlags.appendChild(li);
      });
    }
    if (dom.aiRecommendations) {
      dom.aiRecommendations.innerHTML = '';
      const recs = Array.isArray(aiMeta.recommendations) ? aiMeta.recommendations : [];
      if (recs.length) {
        recs.forEach((rec) => {
          const div = document.createElement('div');
          div.textContent = rec;
          dom.aiRecommendations.appendChild(div);
        });
      } else {
        dom.aiRecommendations.textContent = 'No recommendations.';
      }
    }
  }

  async function openTechClosureReview(ticket) {
    const dom = resolveTrDom();
    if (!dom.dlg) return;
    techReviewState.ticket = ticket;
    techReviewState.closureComment = null;
    techReviewState.submitting = false;
    if (dom.note) dom.note.value = '';
    if (dom.status) dom.status.textContent = '';
    techReviewState.agents = [];
    techReviewState.agentsLoading = true;
    renderTechReviewAgents();
    refreshTechReviewAgents().catch(() => {});

    const submission = ticket && ticket.meta ? (ticket.meta.tech_closure_submission || {}) : {};
    if (dom.problem) dom.problem.textContent = submission.problem_summary || '—';
    if (dom.steps) dom.steps.textContent = submission.resolution_steps || '—';
    if (dom.gps) {
      const gps = submission.gps || {};
      if (gps.latitude != null && gps.longitude != null) {
        const accuracy = gps.accuracy != null ? `±${gps.accuracy}m` : '—';
        dom.gps.textContent = `${gps.latitude}, ${gps.longitude} (${accuracy})`;
      } else {
        dom.gps.textContent = '—';
      }
    }

    const aiMeta = ticket && ticket.meta ? (ticket.meta.tech_closure_ai || {}) : {};
    renderTechReviewAiMeta(aiMeta);
    if (dom.aiRetryBtn) {
      dom.aiRetryBtn.hidden = !!(aiMeta && (aiMeta.summary || aiMeta.score != null || aiMeta.status === 'completed'));
      dom.aiRetryBtn.disabled = false;
      dom.aiRetryBtn.textContent = 'Re-run';
      dom.aiRetryBtn.dataset.ticketId = ticket && ticket.id ? String(ticket.id) : '';
    }
    if (dom.status) {
      const reviewMeta = ticket && ticket.meta ? (ticket.meta.tech_closure_review || {}) : {};
      dom.status.textContent = reviewMeta.status ? `Last decision: ${reviewMeta.status}` : 'Pending supervisor review';
    }
    if (dom.photos) {
      dom.photos.textContent = 'Loading photos…';
    }

    trHelpers.openDialog(dom.dlg);
    loadTechReviewAttachments(ticket.id).catch(() => {});
  }

  async function handleTechReviewAiRetry() {
    const dom = resolveTrDom();
    if (!dom.aiRetryBtn) return;
    const ticketId = dom.aiRetryBtn.dataset.ticketId;
    if (!ticketId) return;
    dom.aiRetryBtn.disabled = true;
    dom.aiRetryBtn.textContent = 'Re-running…';
    try {
      const updated = await api.fetch(api.routes.adminTicketTechReviewAiRetry(ticketId), { method: 'POST' });
      const aiMeta = updated && updated.meta ? (updated.meta.tech_closure_ai || {}) : {};
      renderTechReviewAiMeta(aiMeta);
      if (dom.aiRetryBtn) {
        dom.aiRetryBtn.hidden = !!(aiMeta && (aiMeta.summary || aiMeta.score != null || aiMeta.status === 'completed'));
        dom.aiRetryBtn.textContent = 'Re-run';
      }
      notify.showFlash('AI feedback refreshed', 'success');
    } catch (err) {
      dom.aiRetryBtn.textContent = 'Re-run';
      dom.aiRetryBtn.disabled = false;
      notify.showFlash('Failed to re-run AI feedback', 'error');
      return;
    }
    dom.aiRetryBtn.disabled = false;
  }

  async function loadTechReviewAttachments(ticketId) {
    const dom = resolveTrDom();
    if (!dom.photos) return;
    try {
      const comments = await api.fetch(api.routes.adminTicketComments(ticketId), { method: 'GET' });
      const items = Array.isArray(comments) ? comments : [];
      const closureComment = items.find((item) => {
        const kind = item?.ai_validation?.comment_kind || item?.ai_validation?.commentKind;
        const origin = item?.ai_validation?.origin || '';
        const normalizedKind = String(kind || '').toLowerCase();
        const normalizedOrigin = String(origin || '').toLowerCase();
        return normalizedKind === 'tech_closure' || normalizedOrigin.includes('technician.resolve');
      });
      techReviewState.closureComment = closureComment || null;
      if (closureComment && Array.isArray(closureComment.attachments) && closureComment.attachments.length) {
        const comments_mod = views.tickets && views.tickets.detailComments;
        const markup = comments_mod ? comments_mod.buildAttachmentsMarkup(closureComment.attachments) : '';
        dom.photos.innerHTML = markup || 'No photos attached.';
      } else {
        dom.photos.textContent = 'No photos attached.';
      }
    } catch (err) {
      dom.photos.textContent = err && err.message ? err.message : 'Failed to load attachments';
    }
  }

  function formatTechReviewAgentLabel(agent) {
    if (!agent) return '';
    const base =
      (agent.display_name && agent.display_name.trim())
      || (agent.username && agent.username.trim())
      || (agent.email && agent.email.trim())
      || `Agent ${agent.user_id}`;
    const details = [];
    const lines = Array.isArray(agent.assigned_lines) ? agent.assigned_lines.filter(Boolean) : [];
    if (lines.length) details.push(lines.join(', '));
    if (agent.login_at) details.push(`since ${trHelpers.formatRelativeTime(agent.login_at)}`);
    if (agent.expected_logout_at) details.push(`until ${trHelpers.formatDateTimeShort(agent.expected_logout_at)}`);
    return details.length ? `${base} • ${details.join(' • ')}` : base;
  }

  function renderTechReviewAgents() {
    const dom = resolveTrDom();
    if (!dom.agentSelect) return;
    const loading = techReviewState.agentsLoading;
    const agents = Array.isArray(techReviewState.agents) ? techReviewState.agents : [];
    dom.agentSelect.innerHTML = '';
    if (!agents.length) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = loading ? 'Loading customer care supervisors…' : 'No customer care supervisors available';
      option.disabled = true;
      dom.agentSelect.appendChild(option);
      dom.agentSelect.disabled = true;
      if (dom.approveBtn && !techReviewState.submitting) dom.approveBtn.disabled = true;
      if (!loading && dom.status) {
        dom.status.textContent = 'No customer care supervisors are available to hand this to.';
      }
      return;
    }
    dom.agentSelect.disabled = false;
    const autoSelect = agents.length === 1;
    if (!autoSelect) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select customer care supervisor…';
      dom.agentSelect.appendChild(placeholder);
    }
    agents.forEach((agent, index) => {
      const option = document.createElement('option');
      option.value = String(agent.user_id);
      option.textContent = formatTechReviewAgentLabel(agent);
      if (autoSelect && index === 0) option.selected = true;
      dom.agentSelect.appendChild(option);
    });
    if (dom.approveBtn && !techReviewState.submitting) {
      dom.approveBtn.disabled = !dom.agentSelect.value;
    }
    if (dom.status && !techReviewState.submitting) {
      dom.status.textContent = '';
    }
  }

  async function refreshTechReviewAgents() {
    const dom = resolveTrDom();
    if (!dom.agentSelect) return;
    techReviewState.agentsLoading = true;
    techReviewState.agents = [];
    renderTechReviewAgents();
    try {
      const response = await api.fetch(api.routes.adminCustomerCareSupervisors, { method: 'GET' });
      const items = Array.isArray(response?.items) ? response.items : [];
      techReviewState.agents = items;
    } catch (err) {
      techReviewState.agents = [];
      if (dom.status) {
        dom.status.textContent = err && err.message
          ? err.message
          : 'Failed to load customer care supervisors.';
      }
    } finally {
      techReviewState.agentsLoading = false;
      renderTechReviewAgents();
    }
  }

  function closeTechReviewDialog() {
    const dom = resolveTrDom();
    if (!dom.dlg) return;
    try { dom.dlg.close(); }
    catch { dom.dlg.open = false; }
    techReviewState.ticket = null;
    techReviewState.closureComment = null;
    techReviewState.submitting = false;
    techReviewState.agents = [];
    techReviewState.agentsLoading = false;
    if (dom.note) dom.note.value = '';
    if (dom.status) dom.status.textContent = '';
    if (dom.photos) dom.photos.textContent = 'No photos attached.';
    if (dom.aiFlags) dom.aiFlags.innerHTML = '';
    if (dom.aiRecommendations) dom.aiRecommendations.innerHTML = '';
    if (dom.agentSelect) {
      dom.agentSelect.innerHTML = '';
      dom.agentSelect.disabled = false;
    }
  }

  async function submitTechClosureDecision(action) {
    const dom = resolveTrDom();
    if (!techReviewState.ticket) return;
    if (techReviewState.submitting) return;
    const note = dom.note ? dom.note.value.trim() : '';
    if (action === 'return' && !note) {
      notify.showFlash('Add a note before returning for rework', 'error');
      if (dom.note) dom.note.focus();
      return;
    }
    if (action === 'approve') {
      const selectedAgent = dom.agentSelect ? dom.agentSelect.value.trim() : '';
      if (!selectedAgent) {
        notify.showFlash('Select a customer care supervisor before approving.', 'error');
        if (dom.agentSelect) dom.agentSelect.focus();
        return;
      }
    }
    techReviewState.submitting = true;
    if (dom.status) {
      dom.status.textContent = action === 'approve' ? 'Approving technician resolution…' : 'Returning to technician…';
    }
    if (dom.approveBtn) dom.approveBtn.disabled = true;
    if (dom.returnBtn) dom.returnBtn.disabled = true;
    try {
      const payload = { action, note: note || null };
      if (action === 'approve' && dom.agentSelect) {
        const assigneeId = Number(dom.agentSelect.value);
        if (Number.isNaN(assigneeId)) {
          throw new Error('Invalid customer care supervisor selection');
        }
        payload.assignee_user_id = assigneeId;
      }
      const updated = await api.fetch(api.routes.adminTicketTechReview(techReviewState.ticket.id), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      notify.showFlash(action === 'approve' ? 'Technician resolution approved' : 'Returned to technician for rework');
      closeTechReviewDialog();
      const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
      if (tb) tb.markChanged();
      await trHelpers.reloadTickets({ keepSelection: true, selectId: updated.id });
    } catch (err) {
      if (dom.status) dom.status.textContent = err && err.message ? err.message : 'Failed to submit review';
    } finally {
      techReviewState.submitting = false;
      if (dom.approveBtn) {
        dom.approveBtn.disabled = dom.agentSelect ? !dom.agentSelect.value : false;
      }
      if (dom.returnBtn) dom.returnBtn.disabled = false;
    }
  }

  function mountTechReview() {
    if (trMounted) return;
    const dom = resolveTrDom();
    if (dom.aiRetryBtn) {
      dom.aiRetryBtn.addEventListener('click', () => { handleTechReviewAiRetry().catch(() => {}); });
    }
    if (dom.returnBtn) {
      dom.returnBtn.addEventListener('click', () => { submitTechClosureDecision('return').catch(() => {}); });
    }
    if (dom.approveBtn) {
      dom.approveBtn.addEventListener('click', () => { submitTechClosureDecision('approve').catch(() => {}); });
    }
    if (dom.agentSelect) {
      dom.agentSelect.addEventListener('change', () => {
        if (dom.approveBtn && !techReviewState.submitting) {
          dom.approveBtn.disabled = !dom.agentSelect.value;
        }
      });
    }
    if (dom.close) {
      dom.close.addEventListener('click', () => closeTechReviewDialog());
    }
    if (dom.dlg) {
      dom.dlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeTechReviewDialog();
      });
    }
    trMounted = true;
  }

  views.tickets.techReview = {
    mount: mountTechReview,
    open: openTechClosureReview,
    close: closeTechReviewDialog,
    submit: submitTechClosureDecision,
    refreshAgents: refreshTechReviewAgents,
    renderAgents: renderTechReviewAgents,
    handleAiRetry: handleTechReviewAiRetry,
    loadAttachments: loadTechReviewAttachments,
    getState: () => techReviewState,
  };

  views.tickets.detailComments = {
    mount: mountComments,
    // Render
    renderComments: renderTicketComments,
    renderForTicket: renderTicketCommentsForTicket,
    updateStatus: updateTicketCommentsStatus,
    buildAttachmentsMarkup: buildTicketCommentAttachmentsMarkup,
    buildFeedback: buildCommentFeedback,
    // Actions
    loadComments: loadTicketComments,
    submitComment: submitTicketComment,
    startEdit: startTicketCommentEdit,
    cancelEdit: cancelTicketCommentEdit,
    saveEdit: saveTicketCommentEdit,
    retryComment: retryTicketComment,
    overrideComment: overrideTicketComment,
    applySuggestion: applyCommentSuggestion,
    maybeAutoUpdateStatus: maybeAutoUpdateStatusFromComments,
    // Permission helpers
    canEditComment,
    canRetryComment,
    canOverrideComment,
    isCustomerFeedbackComment,
    findCommentInCache,
    // Click/input handlers
    handleClick: handleTicketCommentClick,
    handleInput: handleTicketCommentInput,
    handleVisibilityChange: handleTicketCommentVisibilityChange,
    handleExpandVisibilityChange: handleTicketCommentExpandVisibilityChange,
    // Expand dialog
    ensureExpandEditor: ensureTicketCommentExpandEditor,
    refreshExpandThread: refreshTicketCommentExpandThread,
    openExpandDialog: openTicketCommentExpandDialog,
    closeExpandDialog: closeTicketCommentExpandDialog,
    // Attachments
    openAttachmentPicker: openTicketAttachmentPicker,
    addAttachments: addTicketCommentAttachments,
    replaceAttachment: replaceTicketCommentAttachment,
    removeAttachment: removeTicketCommentAttachment,
    renderAttachments: renderTicketCommentAttachments,
    resetAttachments: resetTicketCommentAttachments,
    handleAttachmentListClick: handleTicketAttachmentListClick,
    handleAttachmentInputChange: handleTicketAttachmentInputChange,
    // HTML helpers
    sanitizeCommentHtml: sanitizeTicketCommentHtml,
    commentHtmlToPlain,
    plainTextToCommentHtml,
    // State accessors
    getCommentCache: () => ticketCommentCache,
    getCommentPendingRefresh: () => ticketCommentPendingRefresh,
    getAutoStatusUpdateInFlight: () => autoStatusUpdateInFlight,
    getEditState: () => ticketCommentEditState,
    setEditState: (state) => { ticketCommentEditState = state; },
    getAttachmentsState: () => ticketCommentAttachmentsState,
    getAttachmentLimit: () => COMMENT_ATTACHMENT_LIMIT,
    getQuill: () => ticketCommentQuill,
    getExpandQuill: () => ticketCommentExpandQuill,
  };

  views.tickets.detailInteractions = {
    mount: mountInteractions,
    // Inline editors
    resetSubjectEditor: resetTicketSubjectEditor,
    openSubjectEditor: openTicketSubjectEditor,
    submitSubject: submitTicketSubject,
    resetComplaintEditor: resetTicketComplaintEditor,
    openComplaintEditor: openTicketComplaintEditor,
    submitComplaint: submitTicketComplaint,
    resetCustomerNameEditor: resetTicketCustomerNameEditor,
    openCustomerNameEditor: openTicketCustomerNameEditor,
    submitCustomerName: submitTicketCustomerName,
    // Customer edit dialog
    openCustomerEditDialog: openTicketCustomerEditDialog,
    closeCustomerEditDialog: closeTicketCustomerEditDialog,
    // Identify-customer
    openIdentifyDialog: openIdentifyCustomerDialog,
    closeIdentifyDialog: closeIdentifyCustomerDialog,
    resetIdentifyDialog: resetIdentifyCustomerDialog,
    // Duplicate
    openDuplicateDialog: openTicketDuplicateDialog,
    closeDuplicateDialog: closeTicketDuplicateDialog,
    resetDuplicateDialog: resetTicketDuplicateDialog,
    submitDuplicateSelection: submitTicketDuplicateSelection,
    getDuplicateState: () => ticketDuplicateState,
    // Conflict
    openConflictDialog: openTicketConflictDialog,
    closeConflictDialog: closeTicketConflictDialog,
    resetConflictDialog: resetTicketConflictDialog,
    handleConflictChange: handleTicketConflictChange,
    handleConflictClick: handleTicketConflictClick,
    getConflictState: () => ticketConflictState,
    // Assign
    openAssignDialog: openTicketAssignDialog,
    closeAssignDialog: closeTicketAssignDialog,
    resetAssignDialog: resetTicketAssignDialog,
    populateAssignUserOptions,
    populateAssignRoleOptions,
    // Actions / mutate
    handleAction: handleTicketAction,
    mutateTicket,
    handleSlaPolicyChange: handleTicketSlaPolicyChange,
    deriveAssigneeRole,
    // Attention
    markAttention: markTicketAttention,
    clearAttention: clearTicketAttention,
    applyAttentionHighlight: applyTicketAttentionHighlight,
    updateNavHighlight: updateNavTicketsHighlight,
    getAttentionSet: () => ticketAttentionSet,
    // State accessors
    getAssignmentCache: () => ticketAssignmentCache,
    getAssignMode: () => ticketAssignMode,
    setAssignMode: (m) => { ticketAssignMode = m; },
    getIdentifyState: () => identifyState,
    handleAssignSubmit: handleTicketAssignSubmit,
  };

  views.tickets.detail = {
    render: renderTicketDetail,
    renderCustomer: renderTicketCustomer,
    renderDescription: renderTicketDescription,
    renderMeta: renderTicketMeta,
    renderSlaPolicy: renderTicketSlaPolicy,
    renderTimeline: renderTicketTimeline,
    populateSlaPolicySelect,
    formatSlaPolicySummary,
    ensureSlaPoliciesLoaded,
    loadTicketActivities,
    loadTicketBilling,
    renderBilling: renderTicketBilling,
    resetBillingCard: resetTicketBillingCard,
    setListCollapsed: setTicketListCollapsed,
    isListCollapsed: () => ticketListCollapsed,
    setMetaVisibility: setTicketMetaVisibility,
    setBadge: setTicketBadge,
    getRawPortaCustomerId,
    resolvePortaCustomerId,
    hasPlaceholderPortaCustomer,
    extractLatestCustomerSentiment,
    buildLinkedTicketsMarkup,
    computeSla,
    getTicketCustomerName,
    getCurrent: () => ticketDetailCurrent,
    setCurrent: (ticket) => { ticketDetailCurrent = ticket; },
    getBillingState: () => ticketBillingState,
    setBillingState: (state) => { ticketBillingState = state; },
    getBillingTicketId: () => ticketBillingTicketId,
    setBillingTicketId: (id) => { ticketBillingTicketId = id; },
    getActivityState: () => ticketActivityState,
    getSlaPoliciesState: () => slaPoliciesState,
  };
